/* AG Admin. Inventory tool.
   Tabbed dashboard:
     today        Hero KPIs + alert panels + sparklines (default landing)
     reorder      Reorder_Engine table with filters
     sales        Daily charts + top sellers + gateway breakdown
     profit       Per-SKU + finished-product profitability
     pos          PO list + new PO + receive
     preorders    Open pre-orders with slot tracker
     system       Run log + FX + cache state

   Calls Apps Script via window.AG_ADMIN.api(). Each tab lazy-loads
   its payload and caches in localStorage for stale-while-revalidate.
*/

(function () {
  const CACHE_KEY = 'agInvDash:v2';
  const SETTINGS_KEY = 'agInvDashSettings:v1';

  /* Declared BEFORE state because state.settings calls loadSettings()
     which references DEFAULT_SETTINGS, const has temporal dead zone
     so referencing it before this line throws ReferenceError. */
  const DEFAULT_SETTINGS = {
    default_tab: 'today',
    default_period: 30,
    preorder_alert_pct: 80,
    hidden_tabs: [],
    theme: 'light',           // 'light' | 'dark'
    alert_order: null         // null = default; array of keys = user's preferred order
  };
  const ALERT_KEYS_DEFAULT = ['oos', 'demand_spikes', 'eta_slips', 'pos', 'preorders', 'stagnant', 'bis', 'best_margin_promote'];

  /* Tabs are split into two sections. Operations (daily stock work)
     and Analytics (weekly/monthly performance review). Section is
     persisted in state.section; URL hash records it (#ops / #analytics).
     System + Info are common chrome, accessible from both sections via
     the admin bar dropdown, so they live in the 'common' section and
     render in either tab bar at the end. */
  const TABS = [
    /* Operations, daily stock management surfaces */
    { id: 'today',     label: 'Today',        section: 'ops' },
    { id: 'reorder',   label: 'Reorder',      section: 'ops' },
    { id: 'forecast',  label: 'Forecast',     section: 'ops' },
    { id: 'stock',     label: 'Stock',        section: 'ops' },
    { id: 'pos',       label: 'Purchase Orders', section: 'ops' },
    { id: 'losses',    label: 'Losses',       section: 'ops' },
    { id: 'preorders', label: 'Pre-orders',   section: 'ops' },
    { id: 'bis',       label: 'BIS',          section: 'ops' },
    /* Analytics, weekly/monthly performance review */
    { id: 'pulse',     label: 'Pulse',        section: 'analytics' },
    { id: 'sales',     label: 'Sales',        section: 'analytics' },
    { id: 'profit',    label: 'Profitability', section: 'analytics' },
    { id: 'customers', label: 'Customers',    section: 'analytics' },
    { id: 'channels',  label: 'Channels',     section: 'analytics' },
    { id: 'discounts', label: 'Discount Codes', section: 'analytics' },
    { id: 'subscriptions', label: 'Subscriptions', section: 'analytics' },
    { id: 'totalcosts',label: 'Total',         section: 'costs' },
    { id: 'marketing', label: 'Marketing',     section: 'costs' },
    { id: 'costs',     label: 'Operating',     section: 'costs' },
    /* Common, visible at the end of both tab bars */
    { id: 'system',    label: 'System',       section: 'common' },
    { id: 'info',      label: 'Info',         section: 'common' }
  ];
  const SECTIONS = [
    { id: 'ops',       label: 'Stock Management' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'costs',     label: 'Costs' }
  ];
  /* View scoping (2026-06-25): the same FE + backend powers either the full
     tool (all three section groups) OR a scoped admin bracket, chosen by the
     section's data-ag-view attribute: 'stock' (Stock Management only) or
     'analytics' (Costs & Analytics). This lets /pages/admin-stock and
     /pages/admin-analytics be SEPARATE admin tools off ONE shared backend.
     'full' (default) preserves the original single-page behaviour. */
  const VIEW_SECTIONS = {
    full:      ['ops', 'analytics', 'costs'],
    stock:     ['ops'],
    analytics: ['analytics', 'costs']
  };
  const VIEW = (function () {
    try {
      var ds = (document.querySelector('[data-ag-tool="inventory"]') || {}).dataset;
      var v = ds && ds.agView;
      return VIEW_SECTIONS[v] ? v : 'full';
    } catch (e) { return 'full'; }
  })();
  const ALLOWED_SECTIONS = VIEW_SECTIONS[VIEW];
  function allowedSectionDefs() { return SECTIONS.filter(s => ALLOWED_SECTIONS.indexOf(s.id) >= 0); }
  function isSectionAllowed(id) { return ALLOWED_SECTIONS.indexOf(id) >= 0; }

  const state = {
    tab: null,
    section: (function () {
      /* persisted section is shared across admin pages, only honour it if
         it's valid for THIS view, else fall back to the view's first group. */
      var s = localStorage.getItem('ag-inv-section');
      return (s && ALLOWED_SECTIONS.indexOf(s) >= 0) ? s : ALLOWED_SECTIONS[0];
    })(),
    settings: loadSettings(),
    cache: loadCache(),       // { tabName: { savedAt, payload } }
    bootstrap: null,          // suppliers, skus, statuses (for PO form)
    salesDays: 30,
    profitMode: 'finished',   // 'sku' | 'finished'
    poFilter: '',
    showInactive: false,      // Reorder grid hides Inactive (OOS + no 12mo sales) by default
    dataAsOf: null,           // ISO of the last pipeline pull → real data age (NOT browser fetch time)
    lastSynced: null          // when THIS browser last fetched (secondary "synced" stamp)
  };

  function loadSettings() {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      return Object.assign({}, DEFAULT_SETTINGS, raw ? JSON.parse(raw) : {});
    } catch (_) { return Object.assign({}, DEFAULT_SETTINGS); }
  }
  function saveSettings() {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings)); } catch (_) {}
  }
  function loadCache() {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch (_) { return {}; }
  }
  function saveCache() {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(state.cache)); } catch (_) {}
  }
  /* Range-aware cache signature: analytics tabs depend on the global date
     range, so a payload computed for one range must NOT be shown for
     another (otherwise a stale 365-day payload leaks into a 30-day view).
     Non-range tabs get a stable ''. */
  function rangeSigFor(tab) {
    try { return (ANALYTICS_RANGE_TABS.indexOf(tab) >= 0) ? JSON.stringify(rangeArgs()) : ''; }
    catch (e) { return ''; }
  }
  function getCached(tab, maxAgeMs) {
    const ent = state.cache[tab];
    if (!ent) return null;
    if (maxAgeMs && (Date.now() - ent.savedAt) > maxAgeMs) return null;
    if ((ent.rangeSig || '') !== rangeSigFor(tab)) return null;   // computed for a different range
    return ent.payload;
  }
  function setCached(tab, payload) {
    state.cache[tab] = { savedAt: Date.now(), payload, rangeSig: rangeSigFor(tab) };
    saveCache();
  }

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  /* ─── Tab nav ────────────────────────────────────────────── */
  /* Tabs visible for the current section + the two common tabs at
     the end (System, Info). Hidden_tabs from settings still apply. */
  function tabsForSection(section) {
    return TABS.filter(t => {
      if (state.settings.hidden_tabs.includes(t.id)) return false;
      if (t.section === 'common') return true;
      return t.section === section;
    });
  }
  /* Section toggle row above the tab bar. Two pills. Stock Management
     and Analytics, click switches the active section, swapping the
     tab bar and jumping to the section's first tab. */
  function renderSectionToggle() {
    const el = $('#inv-section-toggle');
    if (!el) return;
    const defs = allowedSectionDefs();
    /* A scoped view with a single section group (e.g. Stock) needs no toggle. */
    if (defs.length <= 1) { el.innerHTML = ''; el.style.display = 'none'; return; }
    el.style.display = '';
    el.innerHTML = defs.map(s =>
      `<button class="ag-section-btn ${state.section === s.id ? 'active' : ''}" data-section="${s.id}">${esc(s.label)}</button>`
    ).join('');
    el.querySelectorAll('button[data-section]').forEach(b => {
      b.addEventListener('click', () => setSection(b.dataset.section));
    });
  }
  function setSection(section) {
    if (!isSectionAllowed(section)) return;
    if (state.section === section) return;
    state.section = section;
    localStorage.setItem('ag-inv-section', section);
    renderSectionToggle();
    renderTabs();
    /* Jump to the section's first non-common tab (or stay on a common
       tab if already on one). */
    const currentTab = TABS.find(t => t.id === state.tab);
    if (!currentTab || (currentTab.section !== 'common' && currentTab.section !== section)) {
      const firstTab = tabsForSection(section).find(t => t.section !== 'common');
      if (firstTab) setTab(firstTab.id);
    }
  }
  /* ── Global analytics date range (2026-06-23) ──────────────────────
     ONE range applies to every analytics tab. Presets computed client-
     side → {from,to}; 'since_cutover' sends a flag the backend resolves. */
  const RANGE_PRESETS = [
    { id: 'today', label: 'Today' }, { id: 'yesterday', label: 'Yesterday' },
    { id: 'last_7d', label: 'Last 7 days' }, { id: 'last_30d', label: 'Last 30 days' },
    { id: 'last_90d', label: 'Last 90 days' }, { id: 'mtd', label: 'Month to date' },
    { id: 'last_month', label: 'Last month' }, { id: 'qtd', label: 'Quarter to date' },
    { id: 'ytd', label: 'Year to date' }, { id: 'year', label: 'Last 365 days' },
    { id: 'since_cutover', label: 'Since cutover' }, { id: 'all_time', label: 'All time' },
    { id: 'custom', label: 'Custom…' }
  ];
  /* Tabs that actually use the global date range (windowed analytics).
     Customers/Cohorts (lifetime), Pulse (fixed 7d/30d overview), and
     Subscriptions don't, the range bar is hidden on them + their caches
     aren't range-keyed. */
  const ANALYTICS_RANGE_TABS = ['profit', 'sales', 'channels', 'discounts'];
  function _iso(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  function ensureRange() {
    if (!state.range) {
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('ag-inv-range') || 'null'); } catch (e) {}
      state.range = (saved && saved.preset) ? saved : { preset: 'last_30d', from: null, to: null };
    }
    return state.range;
  }
  function saveRange() { try { localStorage.setItem('ag-inv-range', JSON.stringify(state.range)); } catch (e) {} }
  function presetRange(preset) {
    const now = new Date(); const today = _iso(now);
    const shift = n => { const d = new Date(now); d.setDate(d.getDate() + n); return _iso(d); };
    switch (preset) {
      case 'today': return { from: today, to: today };
      case 'yesterday': return { from: shift(-1), to: shift(-1) };
      case 'last_7d': return { from: shift(-6), to: today };
      case 'last_30d': return { from: shift(-29), to: today };
      case 'last_90d': return { from: shift(-89), to: today };
      case 'mtd': return { from: _iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
      case 'last_month': return { from: _iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: _iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
      case 'qtd': return { from: _iso(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)), to: today };
      case 'ytd': return { from: _iso(new Date(now.getFullYear(), 0, 1)), to: today };
      case 'year': return { from: shift(-364), to: today };
      case 'all_time': return { from: '2000-01-01', to: today };
      case 'custom': return { from: (state.range && state.range.from) || shift(-29), to: (state.range && state.range.to) || today };
      default: return { from: shift(-29), to: today };
    }
  }
  function rangeArgs() {
    const r = ensureRange();
    if (r.preset === 'since_cutover') return { since_cutover: true };
    const w = (r.preset === 'custom') ? { from: r.from, to: r.to } : presetRange(r.preset);
    return { from: w.from, to: w.to };
  }
  function rangeLabelText() {
    const r = ensureRange();
    if (r.preset === 'since_cutover') return 'since cutover';
    const w = (r.preset === 'custom') ? { from: r.from, to: r.to } : presetRange(r.preset);
    return (w.from || '?') + ' → ' + (w.to || '?');
  }
  function clearAnalyticsCaches() { ANALYTICS_RANGE_TABS.forEach(t => { delete state.cache[t]; }); saveCache(); }
  /* "+X% vs prev" pill for vs-previous-period comparisons (windowed tabs). */
  function rangeDeltaPill(cur, prev) {
    if (prev == null || prev === 0 || cur == null) return '';
    const pct = Math.round(((cur - prev) / Math.abs(prev)) * 100);
    return `<div style="margin-top:4px;"><span class="ag-pill ${pct >= 0 ? 'ok' : 'bad'}" style="font-size:10px;">${pct >= 0 ? '+' : ''}${pct}% vs prev</span></div>`;
  }
  /* Stat card that can carry a comparison delta (statCard() takes no extra HTML). */
  function statCardCmp(label, value, cls, deltaHtml) {
    return `<div class="ag-stat ${cls || ''}"><div class="label">${esc(label)}</div><div class="value">${value}</div>${deltaHtml || ''}</div>`;
  }
  function renderRangeBar() {
    const r = ensureRange();
    const isCustom = r.preset === 'custom';
    const cust = isCustom ? presetRange('custom') : null;
    const opts = RANGE_PRESETS.map(x => `<option value="${x.id}" ${r.preset === x.id ? 'selected' : ''}>${esc(x.label)}</option>`).join('');
    return `<div class="inv-range-bar" style="flex-basis:100%;display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:8px;padding-top:8px;border-top:1px solid var(--ag-border,#e5e5e5);">
      <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:var(--ag-muted,#888);font-weight:600;">Date range</span>
      <select id="inv-range-preset" style="padding:5px 8px;font-size:12px;border-radius:4px;">${opts}</select>
      ${isCustom
        ? `<input type="date" id="inv-range-from" value="${esc(cust.from)}" style="padding:4px;font-size:12px;"><span style="color:var(--ag-muted,#888);">→</span><input type="date" id="inv-range-to" value="${esc(cust.to)}" style="padding:4px;font-size:12px;">`
        : `<span class="ag-muted" style="font-size:11px;">${esc(rangeLabelText())}</span>`}
    </div>`;
  }
  function bindRangeBar() {
    const sel = $('#inv-range-preset');
    if (sel) sel.addEventListener('change', e => {
      state.rangeUserSet = true; // operator chose a range: stop auto-defaulting profit to since-cutover
      ensureRange().preset = e.target.value;
      if (state.range.preset === 'custom' && (!state.range.from || !state.range.to)) {
        const pr = presetRange('last_30d'); state.range.from = state.range.from || pr.from; state.range.to = state.range.to || pr.to;
      }
      saveRange(); clearAnalyticsCaches(); renderTabs(); renderTab(state.tab);
    });
    const f = $('#inv-range-from'), t = $('#inv-range-to');
    if (f) f.addEventListener('change', e => { ensureRange().from = e.target.value; saveRange(); clearAnalyticsCaches(); renderTab(state.tab); });
    if (t) t.addEventListener('change', e => { ensureRange().to = e.target.value; saveRange(); clearAnalyticsCaches(); renderTab(state.tab); });
  }

  function renderTabs() {
    const visible = tabsForSection(state.section);
    /* Two parallel renderings of the tab nav:
       - .ag-inv-tabs-buttons: visible on desktop, hidden on mobile
       - #inv-tab-select: visible on mobile, hidden on desktop
       CSS toggles which one shows so the tab row stays readable on phone. */
    const buttons = visible.map(t => `<button data-tab="${t.id}" class="${state.tab === t.id ? 'active' : ''}">${esc(t.label)}</button>`).join('');
    const options = visible.map(t => `<option value="${t.id}" ${state.tab === t.id ? 'selected' : ''}>${esc(t.label)}</option>`).join('');
    const html = `<div class="ag-inv-tabs-buttons">${buttons}</div>` +
      `<select id="inv-tab-select" class="ag-inv-tabs-select" aria-label="Switch tab">${options}</select>` +
      '<span class="spacer"></span>' +
      `<span class="meta" id="inv-last-updated"></span>` +
      `<button id="inv-pull-tab" title="Pull only what this tab needs (faster)" onclick="AGInv.pullTab()">Pull this tab</button>` +
      `<button id="inv-pull-now" title="Full pipeline pull (~30-60s)" onclick="AGInv.manualPull()">Pull all</button>` +
      `<button title="Export current tab to CSV" onclick="AGInv.exportTabCsv()">⤓ CSV</button>` +
      `<button title="Refresh from cache" onclick="AGInv.refresh()">⟳</button>` +
      `<button title="Settings" onclick="AGInv.openSettings()">⚙</button>`;
    const showRange = ANALYTICS_RANGE_TABS.indexOf(state.tab) >= 0;
    $('#inv-tabs').innerHTML = html + (showRange ? renderRangeBar() : '');
    $$('#inv-tabs button[data-tab]').forEach(b => {
      b.addEventListener('click', () => setTab(b.dataset.tab));
    });
    const sel = $('#inv-tab-select');
    if (sel) sel.addEventListener('change', e => setTab(e.target.value));
    if (showRange) bindRangeBar();
  }
  /* Tab → backend scope mapping. Drives "Pull this tab" so each tab only
     refreshes what it actually needs (faster than the global pipeline). */
  const TAB_PULL_SCOPES = {
    today:     ['inventory', 'orders', 'backlog', 'engine', 'bis'],
    pulse:     ['orders'],
    reorder:   ['inventory', 'engine', 'bis'],
    stock:     ['inventory'],
    sales:     ['orders'],
    profit:    ['orders'],
    pos:       [], // pure-read tab
    preorders: ['inventory', 'backlog'],
    bis:       ['bis'],
    customers: ['orders'],
    channels:  ['orders'],
    discounts: ['orders'],
    system:    ['fx']
  };
  /* ST4: poll the heartbeat during a background full pull so the operator sees live progress
     + an auto-refresh on completion. Instead of "watch the System tab" guesswork. */
  function pollFullPull(btn, origLabel) {
    const startedAt = Date.now(); const MAX_MS = 5 * 60 * 1000;
    btn.disabled = true;
    const tick = async () => {
      let sys = null; try { sys = await AG_ADMIN.api('ui_system', {}); } catch (_) {}
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      let stepLabel = '';
      try {
        const run = sys && sys.pipeline_heartbeat && sys.pipeline_heartbeat.runs && sys.pipeline_heartbeat.runs[0];
        const hung = run && run.hung_steps && run.hung_steps[0];
        const lastStep = run && run.steps && run.steps[run.steps.length - 1];
        stepLabel = (hung && hung.step) || (lastStep && lastStep.step) || '';
      } catch (_) {}
      btn.innerHTML = `<span class="ag-spinner"></span> Pulling… ${elapsed}s`;
      btn.title = stepLabel ? ('Running: ' + stepLabel) : 'Pulling…';
      let done = false;
      try { done = ((sys && sys.runs) || []).some(r => new Date(r.started_at).getTime() > startedAt - 60000 && /all compute steps done|OK \(compute\)/.test(String(r.message || '') + ' ' + String(r.status || ''))); } catch (_) {}
      if (done || Date.now() - startedAt > MAX_MS) {
        if (sys) setDataAsOf(sys);
        state.cache = {}; try { localStorage.removeItem(CACHE_KEY); } catch (_) {}
        if (state.tab && !state.skuPageSku) renderTab(state.tab);
        AG_ADMIN.toast(done ? 'Refresh complete. Dashboard updated' : 'Pull still running in the background; data will fill in shortly', !done);
        btn.disabled = false; btn.textContent = origLabel; btn.title = '';
        return;
      }
      setTimeout(tick, 6000);
    };
    setTimeout(tick, 6000);
  }

  async function manualPull(scopes) {
    const btn = scopes && scopes.length ? ($('#inv-pull-tab') || $('#inv-pull-now')) : $('#inv-pull-now');
    if (!btn || btn.disabled) return;
    const orig = btn.textContent;
    let asyncHandled = false;
    btn.disabled = true;
    btn.innerHTML = '<span class="ag-spinner"></span> Pulling…';
    try {
      const payload = scopes && scopes.length ? { scopes } : {};
      const r = await AG_ADMIN.api('ui_manual_pull', payload);
      if (r.async) {
        /* Full refresh runs as a background, resumable job (can't fit one 6-min request).
           ST4: track its progress + auto-refresh on completion instead of the old "watch the
           System tab" toast. */
        asyncHandled = true;
        AG_ADMIN.toast(r.message || 'Full refresh started. Tracking progress…', false);
        pollFullPull(btn, orig);
        return;
      }
      const secs = Math.round((r.duration_ms || 0) / 1000);
      AG_ADMIN.toast(r.ok ? `Pulled in ${secs}s` : 'Partial pull. Check the System tab', !r.ok);
      /* Drop only the client caches affected by the scopes pulled. */
      if (scopes && scopes.length) {
        scopes.forEach(s => {
          if (s === 'inventory' || s === 'engine') { delete state.cache.today; delete state.cache.reorder; }
          if (s === 'orders') { delete state.cache.today; delete state.cache.sales; delete state.cache.profit; }
          if (s === 'backlog') { delete state.cache.today; delete state.cache.preorders; }
          if (s === 'fx') { delete state.cache.system; delete state.cache.today; }
          if (s === 'bis') { delete state.cache.today; delete state.cache.reorder; }
        });
        saveCache();
      } else {
        state.cache = {};
        try { localStorage.removeItem(CACHE_KEY); } catch (_) {}
      }
      if (state.tab) renderTab(state.tab);
    } catch (e) {
      AG_ADMIN.toast('Pull failed: ' + (e.message || e), true);
    } finally {
      if (!asyncHandled) { btn.disabled = false; btn.textContent = orig; } // async path: pollFullPull owns the button
    }
  }
  function pullTab() {
    const scopes = TAB_PULL_SCOPES[state.tab] || [];
    if (!scopes.length) { AG_ADMIN.toast('This tab is read-only, nothing to pull.'); return; }
    manualPull(scopes);
  }
  /* Internal alias so bareword setView('...') calls work (it's also exposed as
     AGInv.setView). Without this, save/sign-off/loss success paths threw
     "setView is not defined" and, e.g., the PO list never refreshed after save. */
  var setView = setTab;
  function setTab(tab) {
    /* View guard. A deep link or the cross-page-shared default_tab could
       point at a tab in the OTHER bracket; redirect to this view's first tab
       so a scoped page never renders a tab it doesn't own. No-op for 'full'. */
    const _td = TABS.find(t => t.id === tab);
    if (!_td || (_td.section !== 'common' && !isSectionAllowed(_td.section))) {
      const _f = tabsForSection(state.section).find(t => t.section !== 'common') || tabsForSection(state.section)[0];
      if (_f) tab = _f.id;
    }
    state.tab = tab;
    /* Profitability defaults to "Since cutover" (2026-06-11, the new AGPB app):
       pre-cutover bundle data is unreliable (editions fragmented across many SKU
       keys with inconsistent costs), so the post-cutover window is the only one
       to trust for margin. Auto-applies only until the operator picks a range. */
    if (tab === 'profit' && !state.rangeUserSet && ensureRange().preset !== 'since_cutover') {
      state.range = { preset: 'since_cutover', from: null, to: null };
      saveRange(); clearAnalyticsCaches();
    }
    /* Auto-switch section if the tab lives in the other section (so
       deep links / URL hash routing always show the right tab bar). */
    const tabDef = TABS.find(t => t.id === tab);
    if (tabDef && tabDef.section !== 'common' && tabDef.section !== state.section && isSectionAllowed(tabDef.section)) {
      state.section = tabDef.section;
      localStorage.setItem('ag-inv-section', state.section);
      renderSectionToggle();
    }
    try { localStorage.setItem('agInvLastTab', tab); } catch (_) {}
    renderTabs();
    renderTab(tab);
  }
  /* ── Honest data-freshness stamp ──────────────────────────────────
     The data is a once-daily ~06:00 pipeline snapshot. Show its REAL age
     (from the last pipeline run). NOT the browser fetch time, which used
     to read "Updated 16:05" all day and made the dashboard look live when
     it wasn't. Amber when >26h = also a stalled-pipeline alarm. */
  function setDataAsOf(systemPayload) {
    /* The newest real pipeline run (the System tab's own freshness reading), not just the newest
       Run_Log row: since 2 Oct event rows (WARN/KICK/DROPPED) are logged there too. */
    try { var pl = systemPayload && systemPayload.pipeline; if (pl) { if (pl.last_run_at) state.dataAsOf = pl.last_run_at; } else { var r = systemPayload && systemPayload.runs && systemPayload.runs[0]; if (r && r.started_at) state.dataAsOf = r.started_at; } } catch (_) {}
    renderFreshness();
  }
  function loadFreshness() { AG_ADMIN.api('ui_system', {}).then(setDataAsOf).catch(() => {}); }
  function renderFreshness() {
    const el = $('#inv-last-updated'); if (!el) return;
    const synced = state.lastSynced ? state.lastSynced.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
    const syncedSpan = synced ? ` <span class="ag-muted" style="font-size:10px;">· synced ${esc(synced)}</span>` : '';
    if (!state.dataAsOf) { el.innerHTML = syncedSpan ? `<span class="ag-muted">${syncedSpan}</span>` : ''; return; }
    const d = new Date(state.dataAsOf); const ageMs = Date.now() - d.getTime(); const ageH = ageMs / 3600000;
    const ago = ageH < 1 ? `${Math.max(1, Math.round(ageMs / 60000))}m ago` : ageH < 48 ? `${Math.round(ageH)}h ago` : `${Math.round(ageH / 24)}d ago`;
    const asOf = d.toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    const stale = ageH > 26;
    el.innerHTML = `<span style="${stale ? 'color:#b45309;font-weight:600;' : ''}" title="Last pipeline pull. The dashboard reflects data as of this time. Use 'Pull all' to refresh.">${stale ? '⚠ ' : ''}Data as of ${esc(asOf)} · ${esc(ago)}</span>${syncedSpan}`;
  }

  async function renderTab(tab) {
    const root = $('#inv-content');
    /* Stale-while-revalidate. */
    const cached = getCached(tab, 24 * 3600 * 1000);
    if (cached) {
      paintTab(tab, cached);
      /* QW5 throttle: the data is a once-daily snapshot, so re-fetching a <10-min-old
         payload on every tab click just burns a round-trip for no change. Revalidate
         only when the cache is older; otherwise just tick the freshness stamp. */
      const ent = state.cache[tab];
      const ageMs = ent ? Date.now() - ent.savedAt : Infinity;
      if (ageMs > 10 * 60 * 1000) refreshTab(tab, true); else renderFreshness();
    } else {
      root.innerHTML = '<div class="ag-empty"><span class="ag-spinner"></span> Loading…</div>';
      refreshTab(tab, false);
    }
  }
  async function refreshTab(tab, silent, force) {
    /* force=true → bust the server-side ui_bootstrap cache (15-min TTL) so the
       Refresh button reflects just-changed data (e.g. a dropped invoice) now. */
    const bootArgs = force ? { force: true } : {};
    /* Pin the range this fetch is for. A slow response (esp. the custom-range
       retry, up to ~90s) must not paint or cache over a newer range the user
       has since selected. That was the "date range isn't updating" bug. */
    const startSig = rangeSigFor(tab);
    /* Refreshing-pip indicator. When called silently (cached paint
       was rendered first, this is the background fetch), show a
       small "refreshing…" hint next to the Updated timestamp so the
       operator knows fresher data is on the way. */
    const updEl = $('#inv-last-updated');
    let pipEl = null;
    if (silent && updEl) {
      pipEl = document.createElement('span');
      pipEl.id = 'inv-refreshing-pip';
      pipEl.style.cssText = 'margin-left:8px;display:inline-flex;align-items:center;gap:4px;font-size:11px;color:var(--ag-text-dim,#777);opacity:0.7;';
      pipEl.innerHTML = '<span class="ag-spinner" style="width:10px;height:10px;border-width:1.5px;"></span> refreshing…';
      const existing = document.getElementById('inv-refreshing-pip');
      if (existing) existing.remove();
      updEl.appendChild(pipEl);
    }
    try {
      let payload;
      switch (tab) {
        case 'today':     payload = await AG_ADMIN.api('ui_today', {}); break;
        case 'pulse': {
          /* Pulse fetches today + cost feeds + subs + shipping in
             parallel so the 7d/30d windows can deduct overhead spend
             AND courier shipping from gross profit, and surface MRR
             alongside the operational headers. */
          const [today, mkt, ops, subs, shipping] = await Promise.all([
            AG_ADMIN.api('ui_today', {}),
            AG_ADMIN.api('ui_marketing', {}),
            AG_ADMIN.api('ui_operating_costs', {}),
            AG_ADMIN.api('ui_subscriptions', {}).catch(() => ({ hero: {} })),
            AG_ADMIN.api('ui_shipping_mtd', {}).catch(() => ({}))
          ]);
          payload = Object.assign({}, today, {
            _costs: { marketing: mkt, operating: ops },
            _subs: subs,
            _shipping: shipping
          });
          break;
        }
        case 'reorder':   payload = await AG_ADMIN.api('ui_bootstrap', bootArgs); break;
        case 'forecast':  payload = await AG_ADMIN.api('ui_bootstrap', bootArgs); break;
        case 'losses':    payload = await AG_ADMIN.api('ui_losses', {}); break;
        case 'stock':     payload = await AG_ADMIN.api('ui_stock', {}); break;
        case 'sales': {
          /* On the default last-30d range, serve from the edge (<100ms). The keep-warm
             pushes uiSales(30) under the 'ui_sales' edge key. Other ranges fall through. */
          const _salesEdge = (ensureRange().preset === 'last_30d') ? { _edge: 'ui_sales' } : {};
          payload = await AG_ADMIN.api('ui_sales', Object.assign({ sku_only: state.salesSkuOnly !== false }, rangeArgs(), _salesEdge));
          break;
        }
        case 'profit':    {
          payload = await AG_ADMIN.api('ui_profitability', rangeArgs());
          break;
        }
        case 'pos':       payload = await AG_ADMIN.api('ui_bootstrap', bootArgs); break;
        case 'preorders': payload = await AG_ADMIN.api('ui_preorders', {}); break;
        case 'bis':       payload = await AG_ADMIN.api('ui_bis', {}); break;
        case 'customers': {
          /* Customers tab renders both the per-customer table AND the cohort
             retention grid below it. Fetched in parallel so the slower of
             the two dominates wall-clock, not the sum. */
          const [cust, coh] = await Promise.all([
            AG_ADMIN.api('ui_customers', {}),
            AG_ADMIN.api('ui_cohorts', {})
          ]);
          payload = Object.assign({}, cust, { cohorts: coh });
          break;
        }
        case 'channels':  payload = await AG_ADMIN.api('ui_channels', rangeArgs()); break;
        case 'discounts': {
          payload = await AG_ADMIN.api('ui_discounts', rangeArgs());
          break;
        }
        case 'subscriptions': payload = await AG_ADMIN.api('ui_subscriptions', {}); break;
        case 'marketing': payload = await AG_ADMIN.api('ui_marketing', {}); break;
        case 'costs': payload = await AG_ADMIN.api('ui_operating_costs', {}); break;
        case 'totalcosts': {
          /* The month so far as ONE Pulse window (audit 2026-10-01): its net profit is the sum of the
             Pulse days, so this tab and the Pulse cannot disagree. */
          const _nowD = new Date();
          const monthFrom = _iso(new Date(_nowD.getFullYear(), _nowD.getMonth(), 1)), monthTo = _iso(_nowD);
          const [mkt, ops, cogs, shipping, vat, month] = await Promise.all([
            AG_ADMIN.api('ui_marketing', {}),
            AG_ADMIN.api('ui_operating_costs', {}),
            AG_ADMIN.api('ui_cogs_mtd', {}).catch(() => ({ cogs_mtd_gbp: 0 })),
            AG_ADMIN.api('ui_shipping_mtd', {}).catch(() => ({ shipping_mtd_gbp: 0 })),
            AG_ADMIN.api('ui_vat_payable', {}).catch(() => null),
            AG_ADMIN.api('ui_pulse_window', { from: monthFrom, to: monthTo }).catch(() => null)
          ]);
          payload = { marketing: mkt, operating: ops, cogs, shipping, vat, month: month && month.ok ? month : null };
          break;
        }
        case 'system':    payload = await AG_ADMIN.api('ui_system', {}); break;
        case 'info':      payload = {}; break; // static content. No backend call
      }
      /* Cache under the range this fetch was ACTUALLY for (startSig). Not the
         current range, which may have changed mid-flight. Caching under the
         new range would poison the cache with stale-range data. */
      state.cache[tab] = { savedAt: Date.now(), payload, rangeSig: startSig };
      saveCache();
      if (tab === 'reorder' || tab === 'pos' || tab === 'forecast') state.bootstrap = payload;
      /* Tab + range guard: the user may have switched tabs OR changed the date
         range while this fetch was in flight. Skip the paint so a stale-range
         response can't clobber what they're now looking at (fixes both the
         "tab bounces back" and "date range isn't updating" glitches). */
      if (state.tab !== tab || rangeSigFor(tab) !== startSig) return;
      paintTab(tab, payload);
      state.lastSynced = new Date();
      if (tab === 'system' && payload && payload.runs) setDataAsOf(payload); else renderFreshness();
    } catch (e) {
      if (!silent) $('#inv-content').innerHTML = `<div class="ag-empty">Error: ${esc(e.message || e)}</div>`;
    } finally {
      const lingering = document.getElementById('inv-refreshing-pip');
      if (lingering) lingering.remove();
    }
  }
  function paintTab(tab, payload) {
    const root = $('#inv-content');
    root.classList.add('ag-inv-content');
    switch (tab) {
      case 'today':     renderToday(root, payload); break;
      case 'pulse':     renderPulse(root, payload); break;
      case 'reorder':   renderReorder(root, payload); break;
      case 'forecast':  renderForecast(root, payload); break;
      case 'losses':    renderLosses(root, payload); break;
      case 'stock':     renderStock(root, payload); break;
      case 'sales':     renderSales(root, payload); break;
      case 'profit':    renderProfit(root, payload); break;
      case 'pos':       renderPosList(root, payload); break;
      case 'preorders': renderPreorders(root, payload); break;
      case 'bis':       renderBis(root, payload); break;
      case 'customers': renderCustomers(root, payload); break;
      case 'channels':  renderChannels(root, payload); break;
      case 'discounts': renderDiscounts(root, payload); break;
      case 'subscriptions': renderSubscriptions(root, payload); break;
      case 'marketing': renderMarketing(root, payload); break;
      case 'costs': renderCosts(root, payload); break;
      case 'totalcosts': renderTotalCosts(root, payload); break;
      case 'system':    renderSystem(root, payload); break;
      case 'info':      renderInfo(root); break;
    }
    applyCollapsibleSections(root, tab);
  }

  /* Make every <h2 class="ag-h2"> in the active tab collapsible on
     mobile. Click toggles the section: everything between this h2
     and the next h2 (or end of root) hides/shows. State is keyed by
     tab + heading text and persisted in localStorage so collapsed
     sections stay closed across re-paints + page reloads.
     No-ops on desktop (≥769px). Sections are always visible there. */
  function applyCollapsibleSections(root, tab) {
    if (window.innerWidth > 768) return;
    const headings = root.querySelectorAll('h2.ag-h2');
    headings.forEach(h => {
      if (h.dataset.collapsibleWired) return;
      h.dataset.collapsibleWired = '1';
      const labelText = (h.textContent || '').slice(0, 60).trim();
      const key = `ag-collapse:${tab}:${labelText}`;
      const startCollapsed = localStorage.getItem(key) === '1';
      h.classList.add('ag-h2-collapsible');
      if (startCollapsed) h.classList.add('collapsed');
      /* Insert a chevron span. CSS rotates it based on .collapsed. */
      const chev = document.createElement('span');
      chev.className = 'ag-h2-chev';
      chev.setAttribute('aria-hidden', 'true');
      h.appendChild(chev);
      /* Find siblings up to the next h2. Those are the section body. */
      const siblings = [];
      let el = h.nextElementSibling;
      while (el && !(el.tagName === 'H2' && el.classList.contains('ag-h2'))) {
        siblings.push(el);
        el = el.nextElementSibling;
      }
      function apply(collapsed) {
        siblings.forEach(s => {
          if (collapsed) s.setAttribute('data-collapsed-by-h2', '1');
          else s.removeAttribute('data-collapsed-by-h2');
        });
      }
      apply(startCollapsed);
      h.addEventListener('click', () => {
        const willCollapse = !h.classList.contains('collapsed');
        h.classList.toggle('collapsed', willCollapse);
        localStorage.setItem(key, willCollapse ? '1' : '0');
        apply(willCollapse);
      });
    });
  }
  function refresh() { renderTab(state.tab); }
  /* Manual list refresh. Clears the client cache for the current tab (and its
     ui_bootstrap siblings) and force re-fetches + repaints, bypassing the
     stale-while-revalidate throttle so new/changed rows show immediately. */
  async function refreshList(btn) {
    const tab = state.tab;
    delete state.cache[tab];
    if (['pos', 'reorder', 'forecast'].indexOf(tab) !== -1) { delete state.cache.pos; delete state.cache.reorder; delete state.cache.forecast; }
    saveCache();
    const orig = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = '↻ Refreshing…'; }
    try { await refreshTab(tab, true, true); }
    finally { if (btn && btn.isConnected) { btn.disabled = false; btn.textContent = orig; } }
  }

  /* Low cost-coverage warning. Fires when ≥5% of active sellers
     lack a unit cost. Those SKUs distort Profitability (margins
     look infinite when COGS=0) and contaminate the Best Margin
     promote list. Surfaces inline above the alert grid so it's
     impossible to miss when present. */
  function renderCostCoverageWarning(cc) {
    if (!cc || !cc.warn) return '';
    const examples = (cc.examples || []).map(e => `
      <div style="display:flex;justify-content:space-between;gap:8px;padding:4px 0;border-top:1px solid #fef3c7;font-size:11.5px;">
        <div style="min-width:0;flex:1;">
          <strong>${esc(e.sku)}</strong>
          <span class="ag-muted" style="margin-left:6px;">${esc(e.product_title)}${e.variant_title && e.variant_title !== 'Default Title' ? ' · ' + esc(e.variant_title) : ''}</span>
        </div>
        <div class="num ag-muted">${e.on_hand} on hand</div>
      </div>
    `).join('');
    const moreCount = (cc.active_missing || 0) - (cc.examples || []).length;
    return `
      <div style="margin:0 0 16px 0;background:#fff8e6;border:1px solid #fde68a;border-radius:8px;padding:14px 16px;">
        <div style="display:flex;align-items:flex-start;gap:10px;">
          <div style="flex:0 0 28px;height:28px;background:#d97706;color:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;">!</div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;font-weight:600;color:#92400e;">Cost data is missing on ${cc.active_missing} active SKU${cc.active_missing === 1 ? '' : 's'} (${cc.active_missing_pct}% of catalogue)</div>
            <div class="ag-muted" style="font-size:11px;margin-top:2px;line-height:1.4;">These SKUs show as 100% margin in Profitability and can pollute Best Margin Promote. Fill costs on the <a href="#stock" onclick="event.preventDefault();AGInv.setView('stock');" style="color:#92400e;text-decoration:underline;cursor:pointer;">Stock tab</a> or via the Cost Gaps Queue.</div>
            <div style="margin-top:10px;">
              ${examples}
              ${moreCount > 0 ? `<div class="ag-muted" style="font-size:11px;margin-top:6px;">… and ${moreCount} more</div>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /* ─── Tab 1: Today ───────────────────────────────────────── */
  function renderToday(root, p) {
    const k = p.kpis || {};
    const a = p.alerts || {};
    const s = p.sparklines || {};
    root.innerHTML = `
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:24px;">
        ${statCard('Active SKUs', k.active_skus, '')}
        ${statCard('Out of stock', k.oos_count, k.oos_count > 0 ? 'bad' : 'ok')}
        ${statCard('Open POs', k.open_po_count, '')}
        ${(() => {
          /* Stock value KPI, split into owned + Collective + tagged so
             the month-end figure isn't inflated by dropshipped stock. */
          const collective = k.stock_value_collective_gbp || 0;
          const tagged = k.stock_value_excluded_tagged_gbp || 0;
          const excludedNote = (collective || tagged)
            ? `<div class="ag-muted" style="font-size:10.5px;margin-top:4px;line-height:1.3;">excl. ${collective ? `£${fmtNum(collective)} Collective` : ''}${collective && tagged ? ' · ' : ''}${tagged ? `£${fmtNum(tagged)} tagged` : ''}</div>`
            : '';
          return `<div class="ag-stat">
            <div class="label">Stock value (owned)</div>
            <div class="value">£${fmtNum(k.stock_value_gbp)}</div>
            ${excludedNote}
          </div>`;
        })()}
        ${statCard('Revenue 30d (inc-VAT)', '£' + fmtNum(k.revenue_30d), 'ok')}
        ${statCard('Open backlog', k.open_backlog_units + ' units', k.open_backlog_units > 0 ? 'warn' : '')}
      </div>
      ${renderCostCoverageWarning(p && p.cost_coverage)}
      <h2 class="ag-h2">Action Today <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">drag to reorder</span></h2>
      <div class="ag-alerts" id="ag-alerts-row">
        ${renderAlertsInOrder(a)}
      </div>
      <p class="ag-muted" style="font-size:11px;margin-top:16px;">For 30-day trends and sparklines, see the <a href="#" onclick="event.preventDefault();AGInv.setView('pulse');" style="color:var(--ag-text-dim);text-decoration:underline;">Pulse tab in Analytics</a>.</p>`;
    /* Click-through on alert rows → SKU drawer. */
    root.querySelectorAll('.ag-alert li.clickable[data-sku]').forEach(li => {
      li.addEventListener('click', () => {
        const sku = li.dataset.sku;
        if (sku) openSkuDrawer(sku);
      });
    });
    /* PO rows open the PO drawer. */
    root.querySelectorAll('.ag-alert li[data-po]').forEach(li => {
      li.style.cursor = 'pointer';
      li.addEventListener('click', () => editPo(li.dataset.po));
    });
    /* Sparkline cards open the full Chart.js view in the drawer. */
    root.querySelectorAll('.ag-sparkline-card[data-spark]').forEach(card => {
      card.addEventListener('click', () => openSparklineDrawer(card.dataset.spark));
    });
    /* Make the alert cards reorderable via drag-and-drop. */
    wireAlertDragAndDrop(root);
  }
  /* ─── Tab: Pulse (Analytics landing) ─────────────────────────────
     Trend-focused overview for the analytics section. Same uiToday
     payload as Today (KPIs + sparklines), rendered without the
     operational alerts. Operators glance at Today for "what needs
     doing now"; the merchant glances at Pulse for "how's the
     business this week vs last". */
  /* ── Pulse period pickers ────────────────────────────────────────
     The Today card carries a day picker and the Last-7-days card a week
     picker. renderPulse stashes its windowBlock / pulseBlock closures
     (they capture deltaPill, fmtNum, etc.) + the live windows so these
     module-level handlers can re-render a single card in place from the
     ui_pulse_window endpoint, no full-tab repaint. */
  let _pulseWindowBlock = null;   // windowBlock(label, days, rev, revNet, units, orders, profit, spend, deltas, extra)
  let _pulseBlockFn = null;       // pulseBlock(win), renders a live window straight from the payload
  let _pulseWindows = null;       // last k.windows, for reset-to-live
  const _pulsePick = { day: null, week: null };
  let _pulseDayDefaulted = false;   // default the detail day-picker to YESTERDAY once (today already lives in the top section)
  function _isoToday() { return _iso(new Date()); }
  function _isoShiftDays(n) { const d = new Date(); d.setDate(d.getDate() + n); return _iso(d); }
  function _pulsePctDelta(cur, prev) { if (prev == null || !prev) return null; return Math.round(((cur - prev) / prev) * 1000) / 10; }
  const _MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function _pulseDayLabel(iso) {
    const p = String(iso || '').split('-'); if (p.length < 3) return iso || '';
    const d = new Date(+p[0], +p[1] - 1, +p[2]);
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] + ' ' + (+p[2]) + ' ' + _MON[+p[1] - 1];
  }
  function _pulseWeekLabel(toIso) {
    const p = String(toIso || '').split('-'); if (p.length < 3) return 'Week';
    return 'Week to ' + (+p[2]) + ' ' + _MON[+p[1] - 1];
  }
  async function _pulseRenderWindow(name, from, to, label) {
    const card = document.getElementById('pulse-card-' + name);
    if (!card || !_pulseWindowBlock) return;
    const body = card.querySelector('.ag-pulse-body');
    if (!body) return;
    body.style.opacity = '0.45';
    try {
      const r = await AG_ADMIN.api('ui_pulse_window', { from: from, to: to });
      if (!r || !r.ok) throw new Error('no data');
      const pv = r.prev || {};
      const deltas = {
        rev: _pulsePctDelta(r.revenue, pv.revenue),
        units: _pulsePctDelta(r.units_parent, pv.units_parent),
        orders: _pulsePctDelta(r.orders, pv.orders),
        profit: _pulsePctDelta(r.profit, pv.profit),
        spend: null
      };
      const spend = r.spend || { marketing: 0, operating: 0, shipping: 0, ads_only: 0, total: 0 };
      body.innerHTML = _pulseWindowBlock(label, r.days, r.revenue, r.revenue_net, r.units_parent, r.orders, r.profit, spend, deltas,
        { refunds: r.refunds_processed, cancelled: r.cancelled, newCustomers: r.new_customers, returningCustomers: r.returning_customers, from: r.from, to: r.to, isToday: false });
    } catch (e) {
      body.innerHTML = '<p class="ag-muted" style="padding:12px;font-size:12px;">Couldn’t load that period. Try again.</p>';
    } finally { body.style.opacity = ''; }
  }
  /* Restore a picked card after a full-tab repaint (SWR / refresh). The
     toolbar already shows the picked date; the body was painted live. */
  function _pulseRestorePicks() {
    if (_pulsePick.day) { const iso = _pulsePick.day; _pulseRenderWindow('today', iso, iso, _pulseDayLabel(iso)); }
    if (_pulsePick.week) {
      const to = _pulsePick.week, p = to.split('-'), d = new Date(+p[0], +p[1] - 1, +p[2]);
      d.setDate(d.getDate() - 6);
      _pulseRenderWindow('last7', _iso(d), to, _pulseWeekLabel(to));
    }
  }
  function _pulseRestoreLive(name, winKey) {
    const card = document.getElementById('pulse-card-' + name);
    if (card && _pulseBlockFn && _pulseWindows) {
      const b = card.querySelector('.ag-pulse-body');
      if (b) b.innerHTML = _pulseBlockFn(_pulseWindows[winKey]);
    }
  }
  function pulsePickDay(iso) {
    if (!iso) return;
    if (iso >= _isoToday()) return pulseResetDay();
    _pulsePick.day = iso;
    const inp = document.getElementById('pulse-day-input'); if (inp) inp.value = iso;
    const live = document.getElementById('pulse-day-live'); if (live) live.style.display = '';
    _pulseRenderWindow('today', iso, iso, _pulseDayLabel(iso));
  }
  function pulseStepDay(n) {
    const base = _pulsePick.day || _isoToday(), p = base.split('-'), d = new Date(+p[0], +p[1] - 1, +p[2]);
    d.setDate(d.getDate() + n);
    pulsePickDay(_iso(d));
  }
  function pulseResetDay() {
    _pulsePick.day = null;
    const inp = document.getElementById('pulse-day-input'); if (inp) inp.value = _isoToday();
    const live = document.getElementById('pulse-day-live'); if (live) live.style.display = 'none';
    _pulseRestoreLive('today', 'today');
  }
  function pulsePickWeek(toIso) {
    if (!toIso) return;
    const yest = _isoShiftDays(-1);
    if (toIso > yest) toIso = yest;
    if (toIso === yest) return pulseResetWeek();
    _pulsePick.week = toIso;
    const inp = document.getElementById('pulse-week-input'); if (inp) inp.value = toIso;
    const live = document.getElementById('pulse-week-live'); if (live) live.style.display = '';
    const p = toIso.split('-'), d = new Date(+p[0], +p[1] - 1, +p[2]);
    d.setDate(d.getDate() - 6);
    _pulseRenderWindow('last7', _iso(d), toIso, _pulseWeekLabel(toIso));
  }
  function pulseStepWeek(n) {
    const base = _pulsePick.week || _isoShiftDays(-1), p = base.split('-'), d = new Date(+p[0], +p[1] - 1, +p[2]);
    d.setDate(d.getDate() + n * 7);
    pulsePickWeek(_iso(d));
  }
  function pulseResetWeek() {
    _pulsePick.week = null;
    const inp = document.getElementById('pulse-week-input'); if (inp) inp.value = _isoShiftDays(-1);
    const live = document.getElementById('pulse-week-live'); if (live) live.style.display = 'none';
    _pulseRestoreLive('last7', 'last7');
  }

  function renderPulse(root, p) {
    const k = p.kpis || {};
    const W = k.windows || {};
    /* The detail day-picker defaults to YESTERDAY (today is fully shown in the top
       section), so there aren't two "today" blocks. Set once; respect later picks. */
    if (!_pulseDayDefaulted) { _pulsePick.day = _isoShiftDays(-1); _pulseDayDefaulted = true; }
    const s = p.sparklines || {};
    const costs = p._costs || {};
    const subs  = p._subs || {};
    const subsHero = (subs && subs.hero) || {};

    /* Generic period delta, sum the trailing `days` window vs the
       preceding `days` window from a value series. Returns a percent
       or null when the series is too short / prior is 0. */
    function periodDelta(series, days) {
      if (!series || series.length < days * 2) return null;
      const recent = series.slice(-days).reduce((a, p) => a + (p.value || 0), 0);
      const prior  = series.slice(-days * 2, -days).reduce((a, p) => a + (p.value || 0), 0);
      if (!prior) return null;
      return Math.round(((recent - prior) / prior) * 1000) / 10;
    }
    function deltaPill(pct, label) {
      label = label || 'WoW';
      if (pct == null) return '<span class="ag-muted" style="font-size:11px;">-</span>';
      const cls = pct >= 0 ? 'ok' : 'err';
      const sign = pct >= 0 ? '+' : '';
      return `<span class="ag-pill ${cls}" style="font-size:10px;">${sign}${pct}% ${label}</span>`;
    }

    /* Spend series from the cost feeds. Pulled once per render and
       sliced for both 7d and 30d windows + sparklines. */
    const mktSeries = (costs.marketing && costs.marketing.series) || [];
    const opsSeries = (costs.operating && costs.operating.series) || [];
    const adSpendOnly = mktSeries.map(x => ({
      date: x.date,
      value: (x.meta || 0) + (x.google || 0) + (x.other_ad || 0)
    }));
    const totalSpendSeries = (() => {
      const opsBy = {}; opsSeries.forEach(x => { opsBy[x.date] = x; });
      return mktSeries.map(x => {
        const o = opsBy[x.date] || {};
        return {
          date: x.date,
          value: (x.recurring || 0) + (x.adhoc || 0) + (x.meta || 0) + (x.google || 0) + (x.other_ad || 0)
            + (o.recurring || 0) + (o.adhoc || 0)
        };
      });
    })();
    const shipping = p._shipping || {};
    function sumSpend(days) {
      const tail = arr => arr.slice(-days);
      const mktTotal = tail(mktSeries).reduce((a, x) =>
        a + (x.recurring || 0) + (x.adhoc || 0) + (x.meta || 0) + (x.google || 0) + (x.other_ad || 0), 0);
      const adsOnly = tail(mktSeries).reduce((a, x) =>
        a + (x.meta || 0) + (x.google || 0) + (x.other_ad || 0), 0);
      const opsTotal = tail(opsSeries).reduce((a, x) =>
        a + (x.recurring || 0) + (x.adhoc || 0), 0);
      const shipTotal = days <= 7
        ? (shipping.shipping_7d_gbp || 0)
        : (shipping.shipping_30d_gbp || 0);
      return { marketing: mktTotal, operating: opsTotal, ads_only: adsOnly, shipping: shipTotal, total: mktTotal + opsTotal + shipTotal };
    }
    const spend7  = sumSpend(7);
    const spend30 = sumSpend(30);

    /* ── 4-window Pulse helpers ──────────────────────────────────────
       Sum marketing + operating spend over an explicit [from,to] range
       (the complete windows exclude today), shipping from the backend's
       per-window map. */
    function sumSpendRange(fromStr, toStr) {
      const inR = x => x.date >= fromStr && x.date <= toStr;
      const mkt = mktSeries.filter(inR).reduce((a, x) => a + (x.recurring || 0) + (x.adhoc || 0) + (x.meta || 0) + (x.google || 0) + (x.other_ad || 0), 0);
      const ads = mktSeries.filter(inR).reduce((a, x) => a + (x.meta || 0) + (x.google || 0) + (x.other_ad || 0), 0);
      const ops = opsSeries.filter(inR).reduce((a, x) => a + (x.recurring || 0) + (x.adhoc || 0), 0);
      return { marketing: mkt, ads_only: ads, operating: ops };
    }
    const shipByWin = (p._shipping && p._shipping.shipping_by_window) || {};
    function spendForWindow(win) {
      /* NET outbound shipping = carrier COST − the shipping REVENUE the customer paid (ex-VAT).
         Pass-through shipping nets to ~£0; only genuinely free-shipping orders leave a real cost.
         Previously only the cost was subtracted while the customer's shipping payment was dropped
         from revenue, double-penalising profit. (Fixed 2026-06-30.) */
      const shipCost = shipByWin[win.name] || 0;
      const shipRev = win.shipping_rev_exvat || 0;
      const shipNet = shipCost - shipRev;
      /* Prefer the server's full-window spend bundle (2026-09-03). The local
         mktSeries/opsSeries are the 60-day CHART series, so summing them for a
         longer window silently dropped its older spend: the since-cutover card
         charged 83 days of revenue with 60 days of marketing and operating
         spend and reported 16% net when the true figure was ~6-7%. The local
         sum remains only as a fallback for older cached payloads. */
      /* ☠ And SHIPPING from it too (audit 2026-10-01). shipByWin has no 'yesterday' key on an older
         backend, so the day card painted yesterday's label cost as £0 (30 Sep: -£55 shipping, net
         £466) until the picker repainted it (£235, net £184). The server's figure nets real label
         costs against shipping revenue for any window, and carries the ads-estimated flag. */
      if (win.spend && typeof win.spend.marketing === 'number') {
        const s = win.spend;
        const ship = typeof s.shipping === 'number' ? s.shipping : shipNet;
        return { marketing: s.marketing, ads_only: s.ads_only, operating: s.operating, shipping: ship, total: s.marketing + s.operating + ship,
                 ads_estimated: !!s.ads_estimated, ads_estimated_gbp: s.ads_estimated_gbp || 0 };
      }
      /* No server spend (audit 2026-10-01): never a SILENT fallback. The chart series only spans 60
         days, so for a longer window its sum drops older spend and the card would show a net profit
         that looks real and is not (since-cutover once read 16% for a true ~6%). Longer than 60
         days: spend is unavailable and the card says so. Within 60: the series sum, marked approx. */
      const why = win.spend_error ? 'server spend failed (' + win.spend_error + ')' : 'server spend missing';
      const spanDays = win.from && win.to ? Math.round((Date.parse(win.to + 'T12:00:00Z') - Date.parse(win.from + 'T12:00:00Z')) / 86400000) + 1 : 0;
      if (!spanDays || spanDays > 60) {
        return { unavailable: true, note: why, marketing: 0, ads_only: 0, operating: 0, shipping: 0, total: 0 };
      }
      const m = sumSpendRange(win.from, win.to);
      return { marketing: m.marketing, ads_only: m.ads_only, operating: m.operating, shipping: shipNet, total: m.marketing + m.operating + shipNet,
               approx: true, note: why };
    }
    function pctDelta(cur, prev) {
      if (prev == null || !prev) return null;
      return Math.round(((cur - prev) / prev) * 1000) / 10;
    }
    function pulseBlock(win) {
      if (!win) return '';
      const deltas = win.prev ? {
        rev: pctDelta(win.revenue, win.prev.revenue),
        units: pctDelta(win.units_parent, win.prev.units_parent),
        orders: pctDelta(win.orders, win.prev.orders),
        profit: pctDelta(win.profit, win.prev.profit),
        spend: null
      } : { rev: null, units: null, orders: null, profit: null, spend: null };
      /* Robustness banners, show WHY a window's numbers can't be trusted rather
         than presenting a collapsed/inconsistent total as real (backend flags). */
      let warn = '';
      const warnStyle = 'background:#fff4f4;border:1px solid #f0c0c0;color:#c8102e;font-size:11px;padding:6px 8px;border-radius:6px;margin-bottom:8px;line-height:1.3;';
      if (win.data_short) {
        warn += `<div style="${warnStyle}">⚠ Sales data only starts ${esc(win.data_from || '?')}. This window is <strong>under-covered</strong>, so revenue/orders/units are understated. Rebuild Daily_Aggregates.</div>`;
      }
      if (win.customer_mismatch) {
        warn += `<div style="${warnStyle}">⚠ New customers (${win.new_customers}) exceed orders (${win.orders}). Customer and order data are out of sync for this window; CAC/ROAS unreliable.</div>`;
      }
      return warn + windowBlock(win.label, win.days, win.revenue, win.revenue_net, win.units_parent, win.orders, win.profit,
        spendForWindow(win), deltas,
        { refunds: win.refunds_processed, newCustomers: win.new_customers, returningCustomers: win.returning_customers,
          cancelled: win.cancelled, from: win.from, to: win.to, isToday: win.name === 'today' });
    }

    /* Wrap each window in a card container so a single card can be swapped
       in place when the operator picks a day/week. The Today card gets a
       day picker; the Last-7 card a week picker (both call the module-level
       pulse* handlers, which re-fetch via ui_pulse_window). */
    function dayToolbar() {
      const active = _pulsePick.day, val = active || _isoToday();
      return `<div class="ag-pulse-tools">
        <button class="ag-pulse-nav" title="Previous day" onclick="AGInv.pulseStepDay(-1)">‹</button>
        <input type="date" class="ag-pulse-datein" id="pulse-day-input" max="${_isoToday()}" value="${val}" onchange="AGInv.pulsePickDay(this.value)" aria-label="Pick a day">
        <button class="ag-pulse-nav" title="Next day" onclick="AGInv.pulseStepDay(1)">›</button>
        <button class="ag-pulse-live" id="pulse-day-live" onclick="AGInv.pulseResetDay()" style="${active ? '' : 'display:none;'}">Today</button>
      </div>`;
    }
    function weekToolbar() {
      const active = _pulsePick.week, val = active || _isoShiftDays(-1);
      return `<div class="ag-pulse-tools">
        <span class="ag-muted" style="font-size:11px;">week to</span>
        <button class="ag-pulse-nav" title="Previous week" onclick="AGInv.pulseStepWeek(-1)">‹</button>
        <input type="date" class="ag-pulse-datein" id="pulse-week-input" max="${_isoToday()}" value="${val}" onchange="AGInv.pulsePickWeek(this.value)" aria-label="Pick a week (ending date)">
        <button class="ag-pulse-nav" title="Next week" onclick="AGInv.pulseStepWeek(1)">›</button>
        <button class="ag-pulse-live" id="pulse-week-live" onclick="AGInv.pulseResetWeek()" style="${active ? '' : 'display:none;'}">This week</button>
      </div>`;
    }
    function pulseCardWrap(name, inner, toolbar) {
      return `<div class="ag-pulse-card" id="pulse-card-${name}">${toolbar || ''}<div class="ag-pulse-body">${inner}</div></div>`;
    }
    function pulseCards(W) {
      /* Detail day-picker card defaults to YESTERDAY (rendered from the backend
         window for an instant paint; _pulseRestorePicks then refines it, e.g.
         gap-filled ads, via ui_pulse_window). Navigable to any day incl. today. */
      const dayWin = (_pulsePick.day && _pulsePick.day === _isoShiftDays(-1) && W.yesterday) ? W.yesterday : W.today;
      return pulseCardWrap('today', pulseBlock(dayWin), dayToolbar())
        + pulseCardWrap('last7', pulseBlock(W.last7), weekToolbar())
        + pulseCardWrap('last30', pulseBlock(W.last30), '')
        + pulseCardWrap('since_cutover', pulseBlock(W.since_cutover), '');
    }

    /* Build a daily net-profit series for the sparkline: gross profit
       from sparklines (revenue - cost from backend) minus that day's
       spend. Used by Net profit sparkline. */
    const grossProfitSeries = s.gross_profit || [];
    const totalSpendBy = {}; totalSpendSeries.forEach(x => { totalSpendBy[x.date] = x.value; });
    const netProfitSeries = grossProfitSeries.map(x => ({
      date: x.date,
      value: (x.value || 0) - (totalSpendBy[x.date] || 0)
    }));

    /* Today snapshot, trailing-30 daily avg is the yardstick. Revenue today
       MUST be on the same basis as that average (kpis.revenue_30d) and the
       Today window card: inc-VAT gross + shipping − refunds. The sparkline
       tail is ex-VAT NET, so reading it here made "Revenue today" show ~17%
       low and the pace pill read "behind" on a perfectly normal day. Prefer
       the inc-VAT window; fall back to the sparkline only if an older backend
       hasn't shipped k.windows yet. */
    const revToday = (W.today && W.today.revenue != null)
      ? W.today.revenue
      : ((s.revenue && s.revenue.length) ? s.revenue[s.revenue.length - 1].value : 0);
    /* Orders + units today on the SAME basis as the window cards: window
       orders (incl cancelled, matches Shopify's count) and PARENT units
       (excludes £0 bundle/AGPB component lines). The raw sparkline tails
       counted all lines / dropped cancelled, so they disagreed with the
       cards below. Sparkline fallback for older backends without k.windows. */
    const ordersToday = (W.today && W.today.orders != null) ? W.today.orders
      : ((s.orders && s.orders.length) ? s.orders[s.orders.length - 1].value : 0);
    const unitsToday = (W.today && W.today.units_parent != null) ? W.today.units_parent
      : ((s.sales_units && s.sales_units.length) ? s.sales_units[s.sales_units.length - 1].value : 0);
    const avgDailyRev30d = (k.revenue_30d || 0) / 30;
    /* Pace vs time-of-day: compare today's running revenue against where an
       average day would be by NOW (avg day × fraction of day elapsed), so a
       half-finished day isn't flagged 'behind' just for being unfinished.
       Day fraction uses the operator's local clock (≈ store TZ, UK). */
    const _now = new Date();
    const _sod = new Date(_now); _sod.setHours(0, 0, 0, 0);
    const dayElapsedFrac = Math.min(1, Math.max(0, (_now - _sod) / 86400000));
    const dayElapsedPct = Math.round(dayElapsedFrac * 100);
    const expectedByNow = avgDailyRev30d * dayElapsedFrac;
    const paceDeltaGbp = Math.round(revToday - expectedByNow);
    const paceDeltaPct = expectedByNow > 0 ? Math.round(((revToday - expectedByNow) / expectedByNow) * 100) : null;
    const paceAhead = revToday >= expectedByNow;
    const paceCls = avgDailyRev30d <= 0 ? 'mute' : paceAhead ? 'ok' : (paceDeltaPct != null && paceDeltaPct >= -15 ? 'warn' : 'bad');
    let pacePillHtml;
    if (avgDailyRev30d <= 0) {
      pacePillHtml = '<span class="ag-muted" style="font-size:11px;">-</span>';
    } else if (expectedByNow <= 0) {
      pacePillHtml = `<span class="ag-pill mute" style="font-size:10px;">${dayElapsedPct}% of day elapsed</span>`;
    } else {
      const dir = paceAhead ? '£' + fmtNum(paceDeltaGbp) + ' ahead' : '£' + fmtNum(Math.abs(paceDeltaGbp)) + ' behind';
      const sign = paceDeltaPct >= 0 ? '+' : '';
      pacePillHtml = `<span class="ag-pill ${paceCls}" style="font-size:10px;">${dayElapsedPct}% of day · ${dir} (${sign}${paceDeltaPct}%)</span>`;
    }

    /* Period deltas, 7d vs prior 7d, 30d vs prior 30d. Backend now
       returns 60d series so MoM works client-side. */
    const revDelta7   = periodDelta(s.revenue, 7);
    const unitsDelta7 = periodDelta(s.sales_units, 7);
    const ordersDelta7 = periodDelta(s.orders, 7);
    const profitDelta7 = periodDelta(s.gross_profit, 7);
    const spendDelta7  = periodDelta(totalSpendSeries, 7);

    const revDelta30   = periodDelta(s.revenue, 30);
    const unitsDelta30 = periodDelta(s.sales_units, 30);
    const ordersDelta30 = periodDelta(s.orders, 30);
    const profitDelta30 = periodDelta(s.gross_profit, 30);
    const spendDelta30  = periodDelta(totalSpendSeries, 30);

    /* Headline block, same layout for 7d / 30d, parameterised by
       window length. Pulse-level KPIs: Revenue, Orders, AOV, Gross
       profit, Spend, Net profit, ROAS. */
    function windowBlock(label, days, rev, revNet, units, orders, profit, spend, deltas, extra) {
      extra = extra || {};
      /* `orders` now INCLUDES cancelled (to match Shopify's Orders count); AOV is
         the average of PAID orders so cancelled (£0) don't dilute it. */
      const cancelled = extra.cancelled || 0;
      const paidOrders = Math.max(0, orders - cancelled);
      /* AOV on the PRE-refund figure: `rev` nets refunds PROCESSED in the window,
         which on heavy-refund days divides money from OTHER days' sales across
         today's orders (2026-07-20 EU: card said €165 while the day's 5 customers
         paid ~€570 avg). What a customer paid at the till is the pre-refund gross. */
      const aov = paidOrders > 0 ? (rev + (extra.refunds || 0)) / paidOrders : 0;
      /* Margins are % of NET (ex-VAT, products-only) revenue. `rev` is inc-VAT +
         shipping, so using it understated the margin. AOV stays on inc-VAT (what
         the customer actually paid). */
      const base = revNet > 0 ? revNet : rev;
      const margin = base > 0 ? Math.round((profit / base) * 100) : null;
      const spendGone = !!(spend && spend.unavailable);
      const net = spendGone ? null : (profit || 0) - (spend ? spend.total : 0);
      const netMargin = net != null && base > 0 ? Math.round((net / base) * 100) : null;
      /* ROAS tiers. ALL ex-VAT (net revenue ÷ ex-VAT spend) so the bases match;
         ad spend is ex-VAT, so inc-VAT revenue would overstate the return.
         ads → all marketing (ads + recurring + adhoc) → all spend (mkt+ops+ship). */
      const roasAds = spend && spend.ads_only > 0 ? base / spend.ads_only : null;
      const roasMkt = spend && spend.marketing > 0 ? base / spend.marketing : null;
      const roasAll = spend && spend.total > 0 ? base / spend.total : null;
      const roasCard = (lbl, val, denomLbl, denomVal) =>
        `<div class="ag-stat ${val == null ? '' : val >= 3 ? 'ok' : val >= 1.5 ? '' : 'bad'}">
            <div class="label">${lbl}</div>
            <div class="value">${val == null ? '-' : val.toFixed(2) + '×'}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">${denomVal > 0 ? 'ex-VAT rev ÷ ' + denomLbl + ' (£' + fmtNum(Math.round(denomVal)) + ')' : 'n/a'}</div>
          </div>`;
      /* Break-even ROAS (gross-margin basis): the ad ROAS at which gross profit
         from ad-driven revenue just covers ad spend. = 1 / gross-margin. */
      const breakEvenRoas = margin > 0 ? 100 / margin : null;
      /* Contribution per order, what each order nets after COGS + marketing +
         shipping (variable costs; excludes fixed ops). */
      /* Per PAID order (audit 2026-10-01): a cancelled order earned nothing, so counting it diluted
         the figure (it divided by orders INCLUDING cancelled). Same base as AOV. */
      const contribPerOrder = !spendGone && paidOrders > 0 ? (profit - (spend ? (spend.marketing + (spend.shipping || 0)) : 0)) / paidOrders : null;
      /* Refund rate, refunds ÷ the sales they came out of (inc-VAT). `rev` already has the
         refunds taken off, so refunds ÷ rev read a true 10% as 11.1% (audit 2026-10-01). */
      const grossBeforeRefunds = rev + (extra.refunds || 0);
      const refundRate = grossBeforeRefunds > 0 ? ((extra.refunds || 0) / grossBeforeRefunds) * 100 : null;
      /* CAC, ex-VAT ad spend ÷ new customers acquired in the window. */
      const cac = !spendGone && extra.newCustomers > 0 && spend ? spend.ads_only / extra.newCustomers : null;
      const deltaLabel = days === 1 ? 'DoD' : days === 7 ? 'WoW' : 'MoM';
      /* Subtitle: a friendly date range for complete windows ("18 Jun – 24 Jun"),
         "live · partial day" for Today, else the legacy N-day label. */
      const fmtD = iso => { const p = String(iso || '').split('-'); if (p.length < 3) return iso || ''; return (+p[2]) + ' ' + ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(+p[1]) - 1]; };
      const subtitle = extra.isToday ? 'live · partial day so far'
        : (extra.from && extra.to
            ? (extra.from === extra.to ? 'single day' : fmtD(extra.from) + ' – ' + fmtD(extra.to))
            : days + '-day window');
      return `
        <h2 class="ag-h2">${esc(label)} <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${esc(subtitle)}</span></h2>
        <div class="ag-muted" style="font-size:10px;margin:2px 0 12px;line-height:1.4;">Revenue takes refunds off on the day they are refunded (as Shopify's Total sales). Gross profit, margins and ROAS take them off the original sale date. So a day with refunds of older sales shows them in Revenue but not in profit.</div>
        <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:24px;">
          <div class="ag-stat">
            <div class="label">Revenue</div>
            <div class="value">£${fmtNum(rev || 0)}</div>
            <div style="margin-top:4px;">${deltaPill(deltas.rev, deltaLabel)}</div>
          </div>
          <div class="ag-stat">
            <div class="label">Orders</div>
            <div class="value">${orders || 0}</div>
            <div style="margin-top:4px;">${deltaPill(deltas.orders, deltaLabel)}</div>
          </div>
          <div class="ag-stat${cancelled > 0 ? ' bad' : ''}">
            <div class="label">Cancelled</div>
            <div class="value">${cancelled}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">of ${orders || 0} orders</div>
          </div>
          <div class="ag-stat">
            <div class="label">AOV</div>
            <div class="value">£${fmtNum(Math.round(aov))}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">${cancelled > 0 ? 'avg of ' + paidOrders + ' paid · pre-refund' : 'avg order value · pre-refund'}</div>
          </div>
          <div class="ag-stat">
            <div class="label">Units sold</div>
            <div class="value">${units || 0}</div>
            <div style="margin-top:4px;">${deltaPill(deltas.units, deltaLabel)}</div>
          </div>
          <div class="ag-stat ${profit >= 0 ? 'ok' : 'bad'}">
            <div class="label">Gross profit${margin != null ? ' · ' + margin + '%' : ''}</div>
            <div class="value">£${fmtNum(profit || 0)}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">${margin != null ? 'rev − COGS − fees − returns · % of £' + fmtNum(revNet || 0) + ' net (ex-VAT, sale-date returns)' : ''}</div>
            <div style="margin-top:4px;">${deltaPill(deltas.profit, deltaLabel)}</div>
          </div>
          <div class="ag-stat">
            <div class="label">Spend (mkt + ops + ship)</div>
            <div class="value">${spendGone ? '-' : '£' + fmtNum(Math.round(spend.total))}</div>
            ${spend && (spend.unavailable || spend.approx) ? `<div style="font-size:10px;margin-top:2px;color:#b45309;">${spend.unavailable ? 'unavailable' : 'approx.'}: ${esc(spend.note || '')}</div>` : ''}
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(Math.round(spend.marketing))} mkt · £${fmtNum(Math.round(spend.operating))} ops · £${fmtNum(Math.round(spend.shipping || 0))} ship${spend && spend.ads_estimated ? ' · <span style="color:#b45309;">ads est.</span>' : ''}</div>
          </div>
          <div class="ag-stat ${net == null ? '' : net >= 0 ? 'ok' : 'bad'}">
            <div class="label">Net profit${netMargin != null ? ' · ' + netMargin + '%' : ''}</div>
            <div class="value">${net == null ? '-' : '£' + fmtNum(Math.round(net))}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">gross − spend</div>
          </div>
          ${roasCard('ROAS · ads', roasAds, 'ad spend' + (spend && spend.ads_estimated ? ' (est)' : ''), spend.ads_only)}
          ${roasCard('ROAS · all marketing', roasMkt, 'all marketing', spend.marketing)}
          ${roasCard('ROAS · all spend', roasAll, 'mkt+ops+ship', spend.total)}
          <div class="ag-stat ${breakEvenRoas == null ? '' : (roasAds != null && roasAds >= breakEvenRoas ? 'ok' : 'bad')}">
            <div class="label">Break-even ROAS</div>
            <div class="value">${breakEvenRoas == null ? '-' : breakEvenRoas.toFixed(2) + '×'}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">${roasAds != null && breakEvenRoas != null ? 'ads at ' + roasAds.toFixed(2) + '× ' + (roasAds >= breakEvenRoas ? '✓ above' : '✗ below') : 'at ' + (margin || 0) + '% margin'}</div>
          </div>
          <div class="ag-stat">
            <div class="label">CAC</div>
            <div class="value">${cac == null ? '-' : '£' + fmtNum(Math.round(cac))}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">${(extra.newCustomers || 0)} new · ${(extra.returningCustomers || 0)} returning</div>
          </div>
          <div class="ag-stat">
            <div class="label">Contribution / order</div>
            <div class="value">${contribPerOrder == null ? '-' : '£' + fmtNum(Math.round(contribPerOrder))}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">after COGS + mkt + ship</div>
          </div>
          <div class="ag-stat ${refundRate == null ? '' : refundRate < 3 ? 'ok' : refundRate < 6 ? '' : 'bad'}">
            <div class="label">Refund rate</div>
            <div class="value">${refundRate == null ? '-' : refundRate.toFixed(1) + '%'}</div>
            <div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(Math.round(extra.refunds || 0))} refunded · by refund date</div>
          </div>
        </div>`;
    }

    /* Days of inventory cover, how many days of current revenue is
       sat in stock. Above 60 = capital-tied up; under 10 = restock
       imminent. */
    const stockValue = k.stock_value_gbp || 0;
    /* Days of cover = current stock UNITS ÷ avg daily parent-units sold (30d).
       Was stock VALUE (at cost) ÷ inc-VAT daily revenue, a cost basis divided
       by a retail+VAT basis, which systematically understated the runway. Units
       ÷ units is the honest depletion horizon. */
    const stockUnitsOnHand = (s.stock_total && s.stock_total.length) ? (s.stock_total[s.stock_total.length - 1].value || 0) : 0;
    const avgDailyUnits30d = (k.units_parent_30d || 0) / 30;
    const daysCover = avgDailyUnits30d > 0 && stockUnitsOnHand > 0 ? Math.round(stockUnitsOnHand / avgDailyUnits30d) : null;
    const coverCls = daysCover == null ? 'mute' : daysCover < 10 ? 'bad' : daysCover > 90 ? 'warn' : 'ok';
    const mrr = subsHero.mrr_gbp || 0;
    const activeSubs = subsHero.active || 0;

    root.innerHTML = `
      <style>
        .ag-pulse-tools{display:flex;align-items:center;gap:6px;margin:0 0 6px;flex-wrap:wrap;}
        .ag-pulse-nav{border:1px solid var(--ag-border);background:var(--ag-panel-2);color:var(--ag-text);border-radius:6px;width:26px;height:26px;line-height:1;padding:0;cursor:pointer;font-size:15px;}
        .ag-pulse-nav:hover{border-color:var(--ag-accent);color:var(--ag-accent);}
        .ag-pulse-datein{border:1px solid var(--ag-border);background:var(--ag-panel);color:var(--ag-text);border-radius:6px;padding:3px 6px;font-size:12px;font-family:inherit;}
        .ag-pulse-live{border:1px solid var(--ag-accent);background:transparent;color:var(--ag-accent);border-radius:6px;padding:3px 10px;font-size:11px;cursor:pointer;}
        .ag-pulse-live:hover{background:var(--ag-accent);color:#fff;}
        .ag-pulse-body{transition:opacity .15s;}
      </style>
      <h2 class="ag-h2">Pulse <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· business trends</span></h2>

      <h3 style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px;color:var(--ag-muted,#666);">Today</h3>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:20px;">
        <div class="ag-stat">
          <div class="label">Revenue today</div>
          <div class="value">£${fmtNum(Math.round(revToday))}</div>
          <div style="margin-top:4px;">${pacePillHtml}</div>
        </div>
        ${statCard('Orders today', ordersToday, '')}
        ${statCard('Units today', unitsToday, '')}
        ${statCard('Avg day (30d)', '£' + fmtNum(Math.round(avgDailyRev30d)), '')}
        ${W.today ? statCard('Gross profit today', '£' + fmtNum(Math.round(W.today.profit || 0)), '') : ''}
        ${W.today ? (() => { const sp = spendForWindow(W.today); const net = Math.round((W.today.profit || 0) - sp.total); return `<div class="ag-stat"><div class="label">Spend today</div><div class="value">£${fmtNum(Math.round(sp.total))}</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">mkt + ops + ship</div></div><div class="ag-stat ${net >= 0 ? 'ok' : 'bad'}"><div class="label">Net today</div><div class="value">£${fmtNum(net)}</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">gross − spend · partial day</div></div>`; })() : ''}
      </div>

      <h3 style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px;color:var(--ag-muted,#666);">Footing</h3>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:24px;">
        ${statCard('Stock value', '£' + fmtNum(stockValue), '')}
        <div class="ag-stat ${coverCls}">
          <div class="label">Days of cover</div>
          <div class="value">${daysCover == null ? '-' : daysCover + 'd'}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">stock units ÷ avg daily units sold</div>
        </div>
        ${mrr > 0 ? `<div class="ag-stat ok">
          <div class="label">MRR</div>
          <div class="value">£${fmtNum(mrr)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">${activeSubs} active subscription${activeSubs === 1 ? '' : 's'}</div>
        </div>` : ''}
      </div>

      ${(() => {
        /* 4-window Pulse: Today (live, partial) + Last 7d / Last 30d / Since
           cutover (all COMPLETE, ending yesterday). Falls back to the legacy
           7d/30d blocks if an older backend hasn't shipped k.windows yet. */
        if (W && W.today) {
          _pulseWindowBlock = windowBlock; _pulseBlockFn = pulseBlock; _pulseWindows = W;
          return pulseCards(W);
        }
        return windowBlock('Last 7 days', 7, k.revenue_7d, k.revenue_7d_net, k.units_parent_7d, k.orders_7d, k.profit_7d, spend7,
          { rev: revDelta7, units: unitsDelta7, orders: ordersDelta7, profit: profitDelta7, spend: spendDelta7 },
          { refunds: k.refunds_processed_7d, newCustomers: k.new_customers_7d, returningCustomers: k.returning_customers_7d })
        + windowBlock('Last 30 days', 30, k.revenue_30d, k.revenue_30d_net, k.units_parent_30d, k.orders_30d, k.profit_30d, spend30,
          { rev: revDelta30, units: unitsDelta30, orders: ordersDelta30, profit: profitDelta30, spend: spendDelta30 },
          { refunds: k.refunds_processed_30d, newCustomers: k.new_customers_30d, returningCustomers: k.returning_customers_30d });
      })()}

      <h2 class="ag-h2">Trends · 60 days</h2>
      <div class="ag-sparkline-row">
        ${sparkCard('Revenue', s.revenue, '£' + fmtNum(sumValues(s.revenue)), { caption: 'order-date basis' })}
        ${sparkCard('Gross profit', s.gross_profit, '£' + fmtNum(sumValues(s.gross_profit)))}
        ${sparkCard('Net profit (excl. ship)', netProfitSeries, '£' + fmtNum(sumValues(netProfitSeries)))}
        ${sparkCard('Orders', s.orders, sumValues(s.orders))}
        ${sparkCard('Units sold', s.sales_units, sumValues(s.sales_units))}
        ${sparkCard('Total spend (mkt + ops)', totalSpendSeries, '£' + fmtNum(sumValues(totalSpendSeries)))}
        ${sparkCard('Ad spend only', adSpendOnly, '£' + fmtNum(sumValues(adSpendOnly)))}
        ${sparkCard('Stock on hand', s.stock_total, lastValue(s.stock_total))}
        ${sparkCard('Open backlog', s.backlog_total, lastValue(s.backlog_total))}
        ${sparkCard('GBP/USD rate', s.fx, lastValue(s.fx, 5), { decimals: 5, meanLine: true })}
      </div>

      <p class="ag-muted" style="font-size:11px;margin-top:16px;">Revenue = inc-VAT line gross + shipping inc-VAT − refunds. Matches Shopify Reports → Total Sales. Units = parent product count (excludes bundle component lines). Gross profit = net revenue − COGS − payment fees − returns. Returns are handled per the operator rule: a refund cancels the sale on the date it was made (deduct its ex-VAT revenue, and credit its COGS back only if the unit was restocked) but the payment fee is kept because the processor never returns it. Net profit = gross − (marketing + operating spend + outbound courier shipping). Outbound shipping is computed per order as (first parcel + (boxes − 1) × extra) × (1 + fuel%) + per-consignment fixed fees, currently mainland rate only; offshore zones (Highlands, NI, IOM) deferred. ROAS = revenue ÷ ad spend; healthy &gt;3×. AOV = revenue ÷ orders. Days of cover = current stock units ÷ trailing-30d daily units sold (parent products). MoM compares last 30 days against the prior 30 days from the 60d sparkline series.</p>`;

    /* Sparklines remain clickable → full Chart.js drawer. */
    root.querySelectorAll('.ag-sparkline-card[data-spark]').forEach(card => {
      card.addEventListener('click', () => openSparklineDrawer(card.dataset.spark));
    });

    /* If a day/week was picked before this repaint, restore those cards. */
    _pulseRestorePicks();
  }

  /* Phrase a projected-stockout date: "ran out 3 days ago", "runs out in 2 days",
     "runs out today". Pass falsy → "-". */
  function runOutPhrase(v) {
    if (!v) return 'runs out: -';
    const friendly = fmtFriendly(v);
    const ymd = fmtDate(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const d = new Date(ymd + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const past = d < today;
      if (friendly === 'today') return 'runs out today';
      if (friendly === 'yesterday' || friendly.endsWith('days ago')) return 'ran out ' + friendly;
      if (friendly === 'tomorrow' || friendly.startsWith('in ')) return 'runs out ' + friendly;
      return (past ? 'ran out ' : 'runs out ') + friendly;
    }
    return 'runs out: ' + friendly;
  }
  /* For already-OOS rows we have last_sale_date (the actual day stock
     dropped to zero, near enough). Always past-tense, fallback to "-". */
  function ranOutPhrase(v) {
    if (!v) return 'ran out: unknown';
    const friendly = fmtFriendly(v);
    if (friendly === 'today') return 'ran out today';
    if (friendly === 'yesterday' || friendly.endsWith('days ago')) return 'ran out ' + friendly;
    return 'ran out ' + friendly;
  }
  /* Stagnant rows have ~zero velocity, so "Nd cover" is meaningless. Show
     days since last sale instead. That's what made the row stagnant. */
  function stagnantPhrase(r) {
    if (!r.last_sale_date) return 'no recent sales';
    const ymd = fmtDate(r.last_sale_date);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const d = new Date(ymd + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diffDays = Math.max(0, Math.round((today - d) / 86400000));
      if (diffDays === 0) return 'last sale today';
      if (diffDays === 1) return 'last sale yesterday';
      return 'last sale ' + diffDays + 'd ago';
    }
    return 'last sale ' + fmtFriendly(r.last_sale_date);
  }
  function statCard(label, value, cls) {
    return `<div class="ag-stat ${cls}"><div class="label">${esc(label)}</div><div class="value">${esc(value)}</div></div>`;
  }
  function alertCard(title, items, rowFn, key) {
    items = items || [];
    const dataKey = key ? ` data-alert-key="${esc(key)}" draggable="true"` : '';
    return `<div class="ag-alert"${dataKey}>
      <h3>${esc(title)}${items.length ? `<span class="count">${items.length}</span>` : ''}</h3>
      ${items.length ? `<ul>${items.map(rowFn).join('')}</ul>` : '<div class="empty-state">All clear.</div>'}
    </div>`;
  }
  /* Each alert is keyed so renderAlertsInOrder can lay them out in the
     user's preferred order (saved in state.settings.alert_order). New
     alert keys appended later still appear. They slot at the end if
     they aren't in the saved order. */
  function renderAlertsInOrder(a) {
    const cards = {
      oos: () => alertCard('Out of stock · high demand', a.oos_with_demand, r => `
        <li class="compact clickable" data-sku="${esc(r.sku)}">${productCell(r, { compact: true })}
          <div class="meta">${r.annual_units_sold || 0} units/yr · ${ranOutPhrase(r.last_sale_date)}</div></li>`, 'oos'),
      pos: () => alertCard('POs needing action', a.pos_awaiting, r => `
        <li class="compact" data-po="${esc(r.po_number)}">
          <div class="ag-prod compact"><span class="name">PO ${esc(r.po_number)}</span><span class="variant">${esc(r.supplier || '')}</span></div>
          <div class="meta">${esc(r.status)} · ETA ${esc(fmtFriendly(r.eta))}</div></li>`, 'pos'),
      eta_slips: () => alertCard('PO ETA slipped, supplier moved date back', a.eta_slips, r => `
        <li class="compact" data-po="${esc(r.po_number)}">
          <div class="ag-prod compact"><span class="name">PO ${esc(r.po_number)}</span><span class="variant">${esc(r.supplier || '')}</span></div>
          <div class="meta">${esc(fmtFriendly(r.prev_eta))} → <strong style="color:#c8102e;">${esc(fmtFriendly(r.new_eta))}</strong> · +${r.days_slipped}d</div></li>`, 'eta_slips'),
      preorders: () => alertCard('Pre-orders ≥' + state.settings.preorder_alert_pct + '% of cap',
        (a.preorders_near_cap || []).filter(r => r.pct >= state.settings.preorder_alert_pct), r => `
        <li class="compact clickable" data-sku="${esc(r.sku)}">${productCell({ product_title: r.title, variant_title: r.variant_title, sku: r.sku }, { compact: true })}
          <div class="meta">${r.open}/${r.max} (${r.pct}%)</div>
          <div class="ag-slot-bar"><div class="fill ${r.pct >= 95 ? 'critical' : ''}" style="width:${Math.min(r.pct, 100)}%"></div></div></li>`, 'preorders'),
      stagnant: () => alertCard('Stagnant, capital tied up', a.stagnant, r => `
        <li class="compact clickable" data-sku="${esc(r.sku)}">${productCell(r, { compact: true })}
          <div class="meta">${r.on_hand || 0} on hand · ${stagnantPhrase(r)}</div></li>`, 'stagnant'),
      bis: () => alertCard('Back-in-stock signups', a.bis_signups, r => `
        <li class="compact clickable" data-sku="${esc(r.sku || '')}">${productCell(r, { compact: true })}
          <div class="meta"><strong style="color:var(--ag-accent);">${r.signups || 0}</strong> signups · last ${esc(fmtFriendly(r.last_signup))}</div></li>`, 'bis'),
      demand_spikes: () => alertCard('Demand spikes, selling faster than usual', a.demand_spikes, r => `
        <li class="compact clickable" data-sku="${esc(r.sku || '')}">${productCell(r, { compact: true })}
          <div class="meta"><strong style="color:var(--ag-accent);">+${r.spike_pct}%</strong> · ${r.velocity_7d}/d (was ${r.velocity_30d}/d)</div></li>`, 'demand_spikes'),
      best_margin_promote: () => alertCard('Best margin · promote these', a.best_margin_promote, r => `
        <li class="compact clickable" data-sku="${esc(r.sku || '')}">${productCell(r, { compact: true })}
          <div class="meta"><strong style="color:var(--ag-success,#0a7);">${fmtNum(r.gross_margin_pct)}% margin</strong> · £${fmtNum(r.gross_profit_annual)} · ${r.units_sold_annual} units</div></li>`, 'best_margin_promote')
    };
    const saved = (state.settings.alert_order && Array.isArray(state.settings.alert_order))
      ? state.settings.alert_order.filter(k => cards[k]) : [];
    const remaining = ALERT_KEYS_DEFAULT.filter(k => !saved.includes(k));
    const order = saved.concat(remaining);
    return order.map(k => cards[k]()).join('');
  }
  function wireAlertDragAndDrop(root) {
    const row = root.querySelector('#ag-alerts-row');
    if (!row) return;
    let dragKey = null;
    row.querySelectorAll('.ag-alert[data-alert-key]').forEach(card => {
      card.addEventListener('dragstart', e => {
        dragKey = card.dataset.alertKey;
        card.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        /* Some browsers need data to be set or the drag is rejected. */
        try { e.dataTransfer.setData('text/plain', dragKey); } catch (_) {}
      });
      card.addEventListener('dragend', () => {
        card.classList.remove('dragging');
        row.querySelectorAll('.ag-alert').forEach(c => c.classList.remove('drag-over'));
        dragKey = null;
      });
      card.addEventListener('dragover', e => {
        if (!dragKey || dragKey === card.dataset.alertKey) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        card.classList.add('drag-over');
      });
      card.addEventListener('dragleave', () => card.classList.remove('drag-over'));
      card.addEventListener('drop', e => {
        e.preventDefault();
        card.classList.remove('drag-over');
        if (!dragKey || dragKey === card.dataset.alertKey) return;
        const order = Array.from(row.querySelectorAll('.ag-alert[data-alert-key]')).map(c => c.dataset.alertKey);
        const fromIdx = order.indexOf(dragKey);
        const toIdx = order.indexOf(card.dataset.alertKey);
        if (fromIdx < 0 || toIdx < 0) return;
        order.splice(toIdx, 0, order.splice(fromIdx, 1)[0]);
        state.settings.alert_order = order;
        saveSettings();
        /* DOM reorder: move the dragged node before the drop-target node. */
        const fromNode = row.querySelector(`.ag-alert[data-alert-key="${dragKey}"]`);
        if (fromNode && card && fromNode !== card) {
          /* Insert before/after based on bounding box mid-line. */
          const targetRect = card.getBoundingClientRect();
          const insertBefore = e.clientX < (targetRect.left + targetRect.width / 2);
          row.insertBefore(fromNode, insertBefore ? card : card.nextSibling);
        }
      });
    });
  }
  /* Cache the most recent Today sparkline payload so click-to-expand
     can render the full Chart.js version without re-fetching. */
  state.sparklines = state.sparklines || {};
  /* Open the full Chart.js view of a sparkline in the existing drawer.
     Keys map to label / series / formatter pulled from cached state. */
  function openSparklineDrawer(key) {
    const entry = state.sparklines[key];
    if (!entry) return;
    const drawer = $('#inv-drawer');
    $('#inv-drawer-title').textContent = entry.label;
    $('#inv-drawer-body').innerHTML = `
      <div class="ag-prod" style="margin-bottom:12px;">
        <span class="name" style="font-size:14px;">${esc(entry.label)}</span>
        <span class="variant" style="font-size:12px;">${esc(entry.summary || '')}</span>
      </div>
      <div class="ag-chart" style="height:320px;"><canvas id="ch-spark-expanded"></canvas></div>
    `;
    drawer.classList.remove('hidden');
    chartLine('ch-spark-expanded', entry.series || [], entry.label, entry.opts || {});
  }
  function sparkCard(label, series, value, opts) {
    series = series || [];
    opts = opts || {};
    /* Stash the series under a stable key so the click handler can
       pull it back without re-fetching. Key = label slug. */
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    state.sparklines[key] = { label, series, summary: String(value || ''), opts };
    return `<div class="ag-sparkline-card clickable" data-spark="${key}" title="Click to expand">
      <div class="label">${esc(label)}</div>
      <div class="value">${esc(value)}</div>
      ${opts.caption ? `<div class="ag-muted" style="font-size:9px;margin-top:1px;">${esc(opts.caption)}</div>` : ''}
      ${sparklineSvg(series)}
    </div>`;
  }
  function sparklineSvg(series) {
    if (!series || !series.length) return '';
    const vals = series.map(p => p.value || 0);
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = (max - min) || 1;
    const w = 200, h = 40;
    const pts = vals.map((v, i) => {
      const x = (i / (vals.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"><polyline fill="none" stroke="var(--ag-accent)" stroke-width="1.5" points="${pts}"/></svg>`;
  }

  /* ─── Tab 2: Reorder ─────────────────────────────────────── */
  /* Forecast. Sales/day → time to run out. Reuses the Reorder engine's
     blended velocity + days_cover + run_out_date (no separate compute). */
  function renderLosses(root, p) {
    const lg = p.logged || {}, inf = p.inferred || {};
    const reasons = p.reasons || ['damaged', 'replacement', 'soiled', 'other'];
    const skus = (state.bootstrap || {}).skus || [];
    const reasonChips = Object.keys(lg.by_reason || {}).length
      ? Object.keys(lg.by_reason).map(r => `<span class="ag-pill mute">${esc(r)}: £${fmtNum(Math.round(lg.by_reason[r]))}</span>`).join(' ')
      : '<span class="ag-muted">none logged yet</span>';
    root.innerHTML = `
      <h2 class="ag-h2">Losses, damaged · replacement · soiled</h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:18px;">
        ${statCardCmp('Logged losses', '£' + fmtNum(Math.round(lg.total_value || 0)), '', '')}
        ${statCardCmp('Inferred (from stock)', '£' + fmtNum(Math.round(inf.total_value || 0)), '', `<div class="delta" style="font-size:12px;color:var(--ag-muted,#888);">${inf.total_units || 0} units</div>`)}
        ${statCardCmp('Uncategorised', '£' + fmtNum(Math.round(p.uncategorised_value || 0)), (p.uncategorised_value > 0 ? 'warn' : 'ok'), '')}
      </div>
      <div style="font-size:12px;margin-bottom:12px;">By reason: ${reasonChips}</div>

      <div style="border:1px solid var(--ag-border,#e5e5e5);border-radius:8px;padding:14px;margin-bottom:18px;">
        <strong style="font-size:13px;">Log a loss</strong>
        <div class="ag-form-grid" style="grid-template-columns:1.4fr 0.6fr 1fr 1.6fr auto;gap:8px;align-items:end;margin-top:8px;">
          <label><span class="label">SKU</span><input class="ag-input" id="loss-sku" list="loss-sku-list" placeholder="type to search…"></label>
          <label><span class="label">Qty</span><input class="ag-input" id="loss-qty" type="number" min="1" step="1" value="1"></label>
          <label><span class="label">Reason</span><select class="ag-select" id="loss-reason">${reasons.map(r => `<option value="${esc(r)}">${esc(r)}</option>`).join('')}</select></label>
          <label><span class="label">Notes</span><input class="ag-input" id="loss-notes" placeholder="optional"></label>
          <button class="ag-btn" onclick="AGInv.logLoss()">Log loss</button>
        </div>
        <label style="font-size:11px;display:inline-flex;gap:5px;margin-top:8px;color:var(--ag-muted,#888);"><input type="checkbox" id="loss-reduce"> Also reduce Shopify stock by this qty</label>
        <datalist id="loss-sku-list">${skus.map(s => `<option value="${esc(s.sku)}">`).join('')}</datalist>
        <div class="ag-muted" style="font-size:11px;margin-top:4px;">Valued at the SKU's active batch cost.</div>
      </div>

      <h3 style="font-size:13px;margin:18px 0 6px;">Recent losses</h3>
      <table class="ag-table"><thead><tr><th>Date</th><th>Order</th><th>SKU</th><th class="num">Qty</th><th>Reason</th><th class="num">Value</th><th>Notes</th><th></th></tr></thead><tbody>
        ${(lg.recent || []).length ? lg.recent.map(l => `<tr>
          <td>${esc(l.date)}</td>
          <td style="font-size:11px;">${l.order_name ? `<span class="ag-pill mute">${esc(l.order_name)}</span>` : ''}</td>
          <td>${esc(l.sku)}</td><td class="num">${l.qty}</td>
          <td><span class="ag-pill mute">${esc(l.reason)}</span></td><td class="num">£${fmtNum(l.value_gbp || 0)}</td>
          <td style="font-size:11px;">${esc(l.notes || '')}</td>
          <td><button class="ag-btn secondary" onclick="AGInv.deleteLoss('${esc(l.loss_id)}')" title="Delete this entry">×</button></td>
        </tr>`).join('') : '<tr><td colspan="8" class="ag-muted">No losses logged yet.</td></tr>'}
      </tbody></table>

      ${(lg.by_order || []).length ? `
      <h3 style="font-size:13px;margin:20px 0 6px;">Replacements by order <span class="ag-muted" style="font-weight:400;">(recorded from the order page)</span></h3>
      <table class="ag-table"><thead><tr><th>Order</th><th class="num">Items</th><th class="num">Qty</th><th class="num">Value</th></tr></thead><tbody>
        ${lg.by_order.map(o => `<tr>
          <td><span class="ag-pill mute">${esc(o.order_name)}</span></td>
          <td class="num">${o.count}</td><td class="num">${o.qty}</td><td class="num">£${fmtNum(o.value || 0)}</td>
        </tr>`).join('')}
      </tbody></table>` : ''}

      <h3 style="font-size:13px;margin:20px 0 6px;">Inferred non-sale by SKU <span class="ag-muted" style="font-weight:400;">(stock that left without a sale, estimate at active batch cost)</span></h3>
      <table class="ag-table"><thead><tr><th>SKU</th><th class="num">Units</th><th class="num">Est. value</th></tr></thead><tbody>
        ${(inf.by_sku || []).length ? inf.by_sku.map(s => `<tr><td>${esc(s.sku)}</td><td class="num">${s.units}</td><td class="num">£${fmtNum(s.value || 0)}</td></tr>`).join('') : '<tr><td colspan="3" class="ag-muted">None detected.</td></tr>'}
      </tbody></table>`;
  }
  async function logLoss() {
    const sku = ($('#loss-sku').value || '').trim();
    const qty = parseInt($('#loss-qty').value, 10) || 0;
    const reason = $('#loss-reason').value;
    const notes = $('#loss-notes').value || '';
    const reduce = $('#loss-reduce').checked;
    if (!sku) { AG_ADMIN.toast('Pick a SKU', true); return; }
    if (qty <= 0) { AG_ADMIN.toast('Qty must be > 0', true); return; }
    try {
      const r = await AG_ADMIN.api('ui_log_loss', { sku, qty, reason, notes, reduce_shopify: reduce });
      AG_ADMIN.toast(`Logged ${reason} loss · £${(r.value_gbp || 0).toFixed(2)}`);
      try { delete state.cache.losses; saveCache(); } catch (_) {}
      setView('losses');
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  async function deleteLoss(lossId) {
    if (!confirm('Delete this loss entry?')) return;
    try {
      await AG_ADMIN.api('ui_delete_loss', { loss_id: lossId });
      try { delete state.cache.losses; saveCache(); } catch (_) {}
      setView('losses');
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  function renderForecast(root, p) {
    const rows = (p && p.reorder) || [];
    const fc = rows.map(r => ({
      sku: r.sku,
      title: r.product_title || r.sku,
      onHand: parseInt(r.on_hand, 10) || 0,
      vel: parseFloat(r.daily_velocity) || 0,
      cover: parseFloat(r.days_cover),
      runOut: r.run_out_date ? String(r.run_out_date).slice(0, 10) : '',
      status: r.status || ''
    })).filter(r => r.onHand > 0 && r.vel > 0 && isFinite(r.cover) && r.cover >= 0)
       .sort((a, b) => a.cover - b.cover);
    const out7 = fc.filter(r => r.cover <= 7).length;
    const out14 = fc.filter(r => r.cover <= 14).length;
    const out30 = fc.filter(r => r.cover <= 30).length;
    const coverCls = c => c <= 7 ? 'bad' : c <= 14 ? 'warn' : c <= 30 ? '' : 'ok';
    const tableRows = fc.map(r => `
      <tr>
        <td>${esc(r.title)}<div class="ag-muted" style="font-size:10px;">${esc(r.sku)}</div></td>
        <td class="num">${r.onHand}</td>
        <td class="num">${r.vel.toFixed(2)}</td>
        <td class="num"><span class="ag-pill ${coverCls(r.cover)}">${Math.round(r.cover)}d</span></td>
        <td class="num">${esc(r.runOut || '-')}</td>
        <td>${esc(r.status || '')}</td>
      </tr>`).join('');
    root.innerHTML = `
      <h2 class="ag-h2">Forecast <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· sales/day → time to run out</span></h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:20px;">
        <div class="ag-stat ${out7 ? 'bad' : 'ok'}"><div class="label">Out in ≤7 days</div><div class="value">${out7}</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">urgent restock</div></div>
        <div class="ag-stat ${out14 ? 'warn' : 'ok'}"><div class="label">Out in ≤14 days</div><div class="value">${out14}</div></div>
        <div class="ag-stat"><div class="label">Out in ≤30 days</div><div class="value">${out30}</div></div>
        <div class="ag-stat ok"><div class="label">Selling SKUs tracked</div><div class="value">${fc.length}</div></div>
      </div>
      <table class="ag-table"><thead><tr>
        <th>Product</th><th class="num">On hand</th><th class="num">Sales/day</th><th class="num">Days to run out</th><th class="num">Run-out date</th><th>Status</th>
      </tr></thead><tbody>${tableRows || '<tr><td colspan="6" class="ag-muted">No in-stock selling SKUs with velocity yet.</td></tr>'}</tbody></table>
      <p class="ag-muted" style="font-size:11px;margin-top:12px;">Sales/day = blended 28/84-day velocity (Reorder engine). Days to run out = on-hand ÷ sales/day. In-stock selling SKUs only, soonest first. For reorder quantities + lead times, see the <a href="#" onclick="event.preventDefault();AGInv.setView('reorder');" style="color:var(--ag-text-dim);text-decoration:underline;">Reorder tab</a>.</p>`;
  }

  function renderReorder(root, p) {
    const rows = (p && p.reorder) || [];
    /* Compact (default) vs Expert column sets.
       Compact = the decision-relevant numbers only: status badge,
       on-hand, days cover, suggested-qty, BIS demand signal.
       Expert = everything (effective, velocities, annual units, etc.). */
    const COMPACT_COLS = [
      { key: 'on_hand',                 label: 'On hand' },
      { key: 'days_cover',              label: 'Days cover' },
      /* Incoming: the engine already subtracts open-PO arrivals from the
         suggested qty, but the operator could not SEE that it had. A suggestion
         of 0 on an out-of-stock line reads as a bug until you know 200 units are
         landing next month. on_order_qty + next_eta have always been on the row. */
      { key: 'on_order_qty',            label: 'Incoming' },
      { key: 'suggested_order_units',   label: 'Suggested qty' },
      { key: 'bis_signups',             label: 'BIS 30d' }
    ];
    const EXPERT_COLS = [
      { key: 'on_hand',                 label: 'On hand' },
      { key: 'effective_on_hand',       label: 'Effective' },
      { key: 'daily_velocity',          label: 'Velocity/d' },
      { key: 'daily_velocity_in_stock', label: 'Velocity/d (in stock)' },
      { key: 'days_cover',              label: 'Days cover' },
      { key: 'annual_units_sold',       label: 'Annual units' },
      { key: 'reorder_frequency_days',  label: 'Reorder every' },
      { key: 'on_order_qty',            label: 'Incoming' },
      { key: 'velocity_basis',          label: 'Velocity basis' },
      { key: 'suggested_order_units',   label: 'Suggested qty' },
      { key: 'bis_signups',             label: 'BIS 30d' }
    ];
    if (state.reorderExpert == null) state.reorderExpert = false;
    const numCols = state.reorderExpert ? EXPERT_COLS : COMPACT_COLS;

    const inactiveCount = rows.filter(r => r.status === 'Inactive').length;
    const liveOverlayCount = rows.filter(r => r.stock_changed_since_engine).length;

    /* Reorder-by-date window. Show only SKUs that need action within
       the chosen horizon. 'overdue' = reorder_by_date is in the past.
       Computed against engine's reorder_by_date column. */
    if (state.reorderWindow == null) state.reorderWindow = 'all';
    const WINDOWS = [
      { id: 'overdue', label: 'Overdue', days: 0 },
      { id: '1d',      label: 'Next 1d', days: 1 },
      { id: '7d',      label: 'Next 7d', days: 7 },
      { id: '30d',     label: 'Next 30d', days: 30 },
      { id: '90d',     label: 'Next 90d', days: 90 },
      { id: 'all',     label: 'All',     days: null }
    ];
    /* Count rows per window for badge display. */
    const today = new Date(); today.setHours(0, 0, 0, 0);
    function reorderDays(r) {
      if (!r.reorder_by_date) return null;
      const ymd = String(r.reorder_by_date).slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
      const d = new Date(ymd + 'T00:00:00');
      if (isNaN(d.getTime())) return null;
      return Math.round((d - today) / 86400000);
    }
    function matchesWindow(r, winId) {
      if (winId === 'all') return true;
      const days = reorderDays(r);
      if (days == null) return false;
      if (winId === 'overdue') return days < 0;
      const max = WINDOWS.find(w => w.id === winId).days;
      return days >= 0 && days <= max;
    }
    const windowCounts = {};
    WINDOWS.forEach(w => { windowCounts[w.id] = rows.filter(r => matchesWindow(r, w.id)).length; });

    root.innerHTML = `
      <h2 class="ag-h2">Reorder Engine</h2>
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:6px;">
        ${WINDOWS.map(w => `<button class="ag-btn ${state.reorderWindow === w.id ? '' : 'secondary'}" data-window="${w.id}">${esc(w.label)}${windowCounts[w.id] ? ` <span class="ag-pill mute" style="margin-left:4px;">${windowCounts[w.id]}</span>` : ''}</button>`).join('')}
      </div>
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:8px;">
        <input class="ag-input" id="reorder-search" placeholder="Filter by SKU or title…" style="max-width:300px;">
        <select class="ag-select" id="reorder-status" style="width:auto;"><option value="">All statuses</option></select>
        <label style="display:flex;gap:6px;align-items:center;font-size:12px;" class="ag-muted">
          <input type="checkbox" id="reorder-show-inactive" ${state.showInactive ? 'checked' : ''}>
          Show inactive${inactiveCount ? ` <span class="ag-pill mute">${inactiveCount}</span>` : ''}
        </label>
        <label style="display:flex;gap:6px;align-items:center;font-size:12px;" class="ag-muted">
          <input type="checkbox" id="reorder-expert" ${state.reorderExpert ? 'checked' : ''}>
          Expert view (all columns)
        </label>
        <select class="ag-select" id="reorder-topn" style="width:auto;" title="Show only the top-N bestsellers by annual units sold">
          <option value="0"   ${!state.reorderTopN ? 'selected' : ''}>All SKUs</option>
          <option value="10"  ${state.reorderTopN === 10 ? 'selected' : ''}>Top 10 bestsellers</option>
          <option value="20"  ${state.reorderTopN === 20 ? 'selected' : ''}>Top 20</option>
          <option value="50"  ${state.reorderTopN === 50 ? 'selected' : ''}>Top 50</option>
          <option value="100" ${state.reorderTopN === 100 ? 'selected' : ''}>Top 100</option>
        </select>
        <button class="ag-btn" id="reorder-draft-pos" title="Generate draft POs grouped by supplier from engine recommendations">📋 Draft POs by supplier</button>
        <span class="spacer" style="flex:1;"></span>
        ${liveOverlayCount ? `<span class="ag-pill info" title="On-hand updated since engine last ran">${liveOverlayCount} live-updated</span>` : ''}
        <span class="ag-muted" id="reorder-count">${rows.length} SKUs</span>
      </div>
      <table class="ag-table">
        <thead><tr>
          <th>Status</th>
          <th>Product</th>
          ${numCols.map(c => `<th class="num">${esc(c.label)}</th>`).join('')}
        </tr></thead>
        <tbody id="reorder-tbody"></tbody>
      </table>`;
    const tbody = $('#reorder-tbody');
    const search = $('#reorder-search');
    const statusSel = $('#reorder-status');
    const inactiveTgl = $('#reorder-show-inactive');
    const expertTgl = $('#reorder-expert');
    const countLbl = $('#reorder-count');
    const statuses = Array.from(new Set(rows.map(r => r.status))).filter(Boolean).sort();
    statuses.forEach(s => { const o = document.createElement('option'); o.value = s; o.textContent = s; statusSel.appendChild(o); });
    /* Action priority for row sort. Overdue > Order now > Stockout imminent > Order soon > OOS > Stagnant > OK > others. */
    const ACTION_RANK = { 'Overdue': 0, 'Order now': 1, 'Stockout imminent': 2, 'Order soon': 3, 'Out of stock': 4, 'Stagnant': 5, 'OK': 6 };
    function rank(r) { return ACTION_RANK[r.status] != null ? ACTION_RANK[r.status] : 7; }
    function paint() {
      const q = search.value.toLowerCase();
      const st = statusSel.value;
      let filtered = rows.filter(r => {
        if (st && r.status !== st) return false;
        if (!state.showInactive && st !== 'Inactive' && r.status === 'Inactive') return false;
        if (!matchesWindow(r, state.reorderWindow)) return false;
        if (q && !(
          String(r.sku || '').toLowerCase().includes(q) ||
          String(r.product_title || '').toLowerCase().includes(q) ||
          String(r.variant_title || '').toLowerCase().includes(q)
        )) return false;
        return true;
      });
      /* Sort by action priority so what needs doing today is at the top. */
      filtered.sort((a, b) => {
        const ra = rank(a), rb = rank(b);
        if (ra !== rb) return ra - rb;
        return (b.annual_units_sold || 0) - (a.annual_units_sold || 0);
      });
      /* Most-popular cap. Slice to top N by annual units sold AFTER
         all other filters. Lets the operator focus on the bestsellers. */
      if (state.reorderTopN) {
        const sortedByPop = filtered.slice().sort((a, b) => (b.annual_units_sold || 0) - (a.annual_units_sold || 0));
        const topSkus = new Set(sortedByPop.slice(0, state.reorderTopN).map(r => r.sku));
        filtered = filtered.filter(r => topSkus.has(r.sku));
      }
      const capLabel = state.reorderTopN ? ` · top ${state.reorderTopN} by units` : '';
      countLbl.textContent = filtered.length + ' of ' + rows.length + ' SKUs' + capLabel;
      progressiveRows(tbody, filtered, r => {
        /* On-hand cell shows the engine-stamped value alongside the
           live value when they differ, so the operator can see at a
           glance that stock has moved since the engine ran. */
        const liveDiff = r.stock_changed_since_engine
          ? `<span class="ag-muted" style="font-size:10px;display:block;line-height:1;">(was ${r.on_hand_engine})</span>` : '';
        return `<tr class="clickable ${r.stock_changed_since_engine ? 'live-updated' : ''}" data-sku="${esc(r.sku)}">
          <td><span class="ag-pill ${pillClass(r.status)}">${esc(r.status || '')}</span></td>
          <td>${productCell(r, { compact: true })}</td>
          ${numCols.map(c => {
            const v = r[c.key];
            if (c.key === 'on_hand') {
              return `<td class="num"><strong>${esc(fmtCell(v))}</strong>${liveDiff}</td>`;
            }
            if (c.key === 'bis_signups') {
              return `<td class="num">${v ? `<strong style="color:var(--ag-accent);">${v}</strong>` : '<span class="ag-muted">-</span>'}</td>`;
            }
            if (c.key === 'on_order_qty') {
              /* Qty plus when it lands. An estimated ETA (no operator-entered
                 eta on the PO) is marked so the date is not read as firm. */
              const eta = String(r.next_eta || '').slice(0, 10);
              if (!(v > 0)) return '<td class="num"><span class="ag-muted">-</span></td>';
              return `<td class="num"><strong>${v}</strong>${eta
                ? `<div class="ag-muted" style="font-size:10px;">${esc(fmtFriendly(eta))}</div>` : ''}</td>`;
            }
            if (c.key === 'velocity_basis') {
              const damped = /damped/i.test(String(v || ''));
              return `<td class="num">${v
                ? `<span class="ag-pill ${damped ? 'warn' : 'mute'}" title="${damped
                    ? 'Recent sales looked like a spike (often a pre-order backlog shipping at once), so the rate was capped against the annual average.'
                    : 'Suggested qty uses the measured recent sales rate.'}">${esc(String(v))}</span>`
                : '<span class="ag-muted">-</span>'}</td>`;
            }
            if (c.key === 'suggested_order_units') {
              return `<td class="num">${v > 0 ? `<strong>${v}</strong>` : '<span class="ag-muted">-</span>'}</td>`;
            }
            return `<td class="num">${esc(fmtCell(v))}</td>`;
          }).join('')}
        </tr>`;
      });
    }
    /* Delegated row click, attached once per render, not per row per paint. */
    tbody.addEventListener('click', e => { const tr = e.target.closest('tr[data-sku]'); if (tr) openSkuDrawer(tr.dataset.sku); });
    search.addEventListener('input', debounce(paint));
    expertTgl.addEventListener('change', e => { state.reorderExpert = e.target.checked; renderReorder(root, p); });
    /* Reorder-by-date window buttons (Overdue / 1d / 7d / 30d / 90d / All). */
    root.querySelectorAll('[data-window]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.reorderWindow = btn.dataset.window;
        renderReorder(root, p);
      });
    });
    /* Top-N bestseller cap. */
    const topNSel = $('#reorder-topn');
    if (topNSel) topNSel.addEventListener('change', e => {
      const n = parseInt(e.target.value, 10) || 0;
      state.reorderTopN = n > 0 ? n : null;
      paint();
    });
    statusSel.addEventListener('change', paint);
    inactiveTgl.addEventListener('change', e => { state.showInactive = e.target.checked; paint(); });
    /* Draft POs button, opens supplier-picker modal with engine-suggested items. */
    const draftBtn = $('#reorder-draft-pos');
    if (draftBtn) draftBtn.addEventListener('click', () => openDraftPosBySupplier(rows));
    paint();
  }

  /* ─── PO drafter from Reorder engine ──────────────────────────
     Click "Draft POs by supplier" on the Reorder tab → modal lists
     each supplier with the count + qty + £-value of items needing
     reorder. Click a supplier → opens the New PO drawer pre-filled
     with their items (sku, suggested qty, current cost).
     Operator can still edit before saving. */
  function openDraftPosBySupplier(reorderRows) {
    /* Filter to items where the engine actually suggests action. Pre-2026-06
       engine columns: suggested_qty + status. Treat status containing
       "Reorder" / "Overdue" / "Out of stock" / "Below safety" as actionable.
       Also include any row with suggested_qty > 0 (engine's positive signal). */
    function isActionable(r) {
      const qty = parseInt(r.suggested_order_units, 10) || parseInt(r.suggested_qty, 10) || 0;
      if (qty <= 0) return false;
      const status = String(r.status || '').toLowerCase();
      if (status.indexOf('reorder') >= 0) return true;
      if (status.indexOf('overdue') >= 0) return true;
      if (status.indexOf('out of stock') >= 0) return true;
      if (status.indexOf('below') >= 0) return true;
      /* If the engine says positive suggested_qty without a recognised
         status, still include, engine has a recommendation. */
      return true;
    }
    const actionable = (reorderRows || []).filter(isActionable);
    if (!actionable.length) {
      AG_ADMIN.toast('No reorder-suggested SKUs to draft. (Engine has no positive suggested_qty.)');
      return;
    }
    /* Lookup map: sku → supplier + cost via state.bootstrap.skus. */
    const skuMeta = {};
    ((state.bootstrap && state.bootstrap.skus) || []).forEach(s => { skuMeta[s.sku] = s; });
    /* Group actionable rows by supplier. */
    const bySupplier = {};
    actionable.forEach(r => {
      const meta = skuMeta[r.sku] || {};
      const supplier = String(meta.supplier || '').trim() || '(no supplier)';
      const qty = parseInt(r.suggested_order_units, 10) || parseInt(r.suggested_qty, 10) || 0;
      const cost = parseFloat(meta.unit_cost) || 0;
      const lead = parseInt(r.total_lead_days, 10) || 0;
      const rby = String(r.reorder_by_date || '').trim();
      if (!bySupplier[supplier]) bySupplier[supplier] = { items: [], total_qty: 0, total_cost: 0, max_lead: 0, earliest_reorder_by: '' };
      const grp = bySupplier[supplier];
      grp.items.push({ sku: r.sku, title: meta.title || r.product_title || '', qty: qty, unit_cost: cost, line_total: qty * cost });
      grp.total_qty += qty;
      grp.total_cost += qty * cost;
      if (lead > grp.max_lead) grp.max_lead = lead;
      if (rby && (!grp.earliest_reorder_by || rby < grp.earliest_reorder_by)) grp.earliest_reorder_by = rby;
    });
    /* Sort by URGENCY (earliest reorder-by date) first, then by value, so the
       supplier you need to order from soonest is at the top. No-date suppliers last. */
    const suppliers = Object.keys(bySupplier).sort((a, b) => {
      const ra = bySupplier[a].earliest_reorder_by || '9999-99', rb = bySupplier[b].earliest_reorder_by || '9999-99';
      if (ra !== rb) return ra < rb ? -1 : 1;
      return bySupplier[b].total_cost - bySupplier[a].total_cost;
    });
    const _todayStr = new Date().toISOString().slice(0, 10);

    /* Modal listing each supplier. Click → opens pre-filled PO drawer. */
    const overlay = document.createElement('div');
    overlay.className = 'ag-modal-overlay';
    overlay.innerHTML = `<div class="ag-modal" style="max-width:600px;">
      <div class="ag-modal-head">
        <h3>Draft PO by supplier</h3>
        <button class="ag-modal-close" type="button">×</button>
      </div>
      <div class="ag-modal-body">
        <p class="ag-muted" style="font-size:12px;">${actionable.length} SKUs need reordering across ${suppliers.length} suppliers. Click a supplier to open a pre-filled PO drawer (you can still edit before saving).</p>
        <table class="ag-table">
          <thead><tr><th>Supplier</th><th>Reorder by</th><th class="num">SKUs</th><th class="num">Total qty</th><th class="num">Est. value</th></tr></thead>
          <tbody>
            ${suppliers.map(s => {
              const rby = bySupplier[s].earliest_reorder_by;
              const overdue = rby && rby < _todayStr;
              const rbyCell = rby ? `<span style="${overdue ? 'color:#c8102e;font-weight:600;' : ''}">${esc(rby)}${overdue ? ' ⚠' : ''}</span>` : '<span class="ag-muted">-</span>';
              return `<tr class="clickable" data-supplier="${esc(s)}">
              <td><strong>${esc(s)}</strong></td>
              <td style="font-size:12px;">${rbyCell}</td>
              <td class="num">${bySupplier[s].items.length}</td>
              <td class="num">${bySupplier[s].total_qty}</td>
              <td class="num">£${fmtNum(bySupplier[s].total_cost)}</td>
            </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    overlay.querySelector('.ag-modal-close').addEventListener('click', close);
    overlay.querySelectorAll('tr[data-supplier]').forEach(tr => {
      tr.addEventListener('click', () => {
        const supplier = tr.dataset.supplier;
        const items = bySupplier[supplier].items;
        const leadDays = bySupplier[supplier].max_lead;
        close();
        openDraftPoDrawer(supplier === '(no supplier)' ? '' : supplier, items, leadDays);
      });
    });
  }

  function openDraftPoDrawer(supplier, items, leadDays) {
    /* Build a draft PO object the existing paintPoDrawer can consume.
       Header: supplier + status='draft' + today as date_ordered + an estimated
       ETA of today + the supplier's (longest) lead time, so the drawer opens with
       a sensible warehouse arrival date the operator can adjust.
       Lines: one per actionable SKU with qty + GBP unit cost. */
    const today = new Date().toISOString().slice(0, 10);
    let eta = '';
    if (leadDays && leadDays > 0) {
      const d = new Date(); d.setDate(d.getDate() + leadDays); eta = d.toISOString().slice(0, 10);
    }
    const draftPo = {
      header: {
        po_number: '', // operator fills (or auto-generated on save)
        supplier: supplier,
        status: 'draft',
        date_ordered: today,
        eta: eta, // estimated: today + supplier lead time
        external_ref: 'Auto-drafted from reorder engine'
      },
      lines: items.map(it => ({
        sku: it.sku,
        title: it.title,
        qty_ordered: it.qty,
        qty_received: 0,
        unit_cost_usd: 0,
        landed_unit_cost_gbp: it.unit_cost,
        line_status: 'open'
      }))
    };
    $('#inv-drawer').classList.remove('hidden');
    paintPoDrawer(draftPo);
  }
  function pillClass(s) {
    s = String(s || '').toLowerCase();
    if (s.includes('out of stock')) return 'bad';
    if (s.includes('imminent') || s.includes('overdue') || s.includes('order now')) return 'warn';
    if (s.includes('order soon')) return 'info';
    if (s === 'ok') return 'ok';
    if (s === 'stagnant') return 'stagnant';
    return 'mute';
  }

  /* ─── Tab 3: Sales ───────────────────────────────────────── */
  function renderSales(root, p) {
    const skuOnly = state.salesSkuOnly !== false; // default true
    const periodLabel = rangeLabelText(); // driven by the global date-range picker
    const t = (p && p.totals) || {};
    const c = (p && p.compare) || null;
    root.innerHTML = `
      <div class="ag-toolbar" style="flex-wrap:wrap;">
        <h2 class="ag-h2" style="margin:0 16px 0 0;">Sales <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${esc(periodLabel)}</span></h2>
        <span class="spacer" style="flex:1;"></span>
        <label style="display:flex;gap:6px;align-items:center;font-size:12px;" class="ag-muted">
          <input type="checkbox" id="sales-skuonly" ${skuOnly ? 'checked' : ''}> SKUs only (hide [no-sku])
        </label>
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px;">
        ${statCardCmp('Revenue (ex-VAT)', '£' + fmtNum(t.revenue || 0), 'ok', c ? rangeDeltaPill(t.revenue, c.revenue) : '')}
        ${statCardCmp('Units sold', fmtNum(t.units || 0), '', c ? rangeDeltaPill(t.units, c.units) : '')}
        ${statCardCmp('Orders', fmtNum(t.orders || 0), '', c ? rangeDeltaPill(t.orders, c.orders) : '')}
      </div>
      <div class="ag-chart-row">
        <div class="ag-chart"><div class="label">Daily units sold</div><canvas id="ch-units"></canvas></div>
        <div class="ag-chart"><div class="label">Daily revenue (£, ex-VAT)</div><canvas id="ch-revenue"></canvas></div>
      </div>
      <div class="ag-chart-row">
        <div class="ag-chart" style="height:auto;"><div class="label">Top 10 by units (${esc(periodLabel)})</div><div id="top-units"></div></div>
        <div class="ag-chart" style="height:auto;"><div class="label">Top 10 by revenue (${esc(periodLabel)})</div><div id="top-revenue"></div></div>
      </div>
      <div class="ag-chart" style="height:auto"><div class="label">Payment gateway mix (${esc(periodLabel)})</div><canvas id="ch-gateways" style="max-height:240px;"></canvas></div>
    `;
    /* Period is driven by the global date-range picker in the header. */
    /* SKU-only toggle. */
    $('#sales-skuonly').addEventListener('change', e => {
      state.salesSkuOnly = e.target.checked;
      invalidateSalesCache_();
      renderTab('sales');
    });
    /* Top tables. Fixed column widths for consistency between
       the two side-by-side tables. Product col carries name + variant + SKU. */
    function topTable(rows) {
      return `<table class="ag-table" style="table-layout:fixed;"><colgroup>
          <col><col style="width:18%"><col style="width:22%">
        </colgroup><thead><tr>
          <th>Product</th><th class="num">Units</th><th class="num">Revenue</th>
        </tr></thead><tbody>${
        rows.map(r => `<tr class="clickable" data-sku="${esc(r.sku)}">
          <td>${productCell({ product_title: r.product_title || r.title, variant_title: r.variant_title, sku: r.sku }, { compact: true })}</td>
          <td class="num">${r.units}</td>
          <td class="num">£${fmtNum(r.revenue)}</td>
        </tr>`).join('')
      }</tbody></table>`;
    }
    $('#top-units').innerHTML = topTable(p.top_units || []);
    $('#top-revenue').innerHTML = topTable(p.top_revenue || []);
    root.querySelectorAll('tr[data-sku]').forEach(tr => tr.addEventListener('click', () => openSkuDrawer(tr.dataset.sku)));
    /* Charts. */
    chartLine('ch-units', p.sales_units, 'Units');
    chartLine('ch-revenue', p.revenue, 'Revenue (£)');
    chartGateway('ch-gateways', p.gateways || []);
  }
  function invalidateSalesCache_() {
    Object.keys(state.cache).forEach(k => { if (k === 'sales') delete state.cache[k]; });
    saveCache();
  }

  /* ─── Tab 4: Profitability ───────────────────────────────── */
  /* ─── Profitability time-series margin chart ───
     Weekly buckets (last 12 weeks). Margin % per week =
     (revenue − cogs) / revenue. Catches margin erosion early.
     watch the line trend over time.

     Cost uses today's basis (Shopify-first → SKU_Settings fallback)
     so this is a TREND view, not historical-cost audit. */
  function renderMarginTimeSeriesChart(series) {
    if (!series || !series.length) return '';
    /* Build a payload Chart.js renders via the existing chartLine helper.
       Render after the table is in the DOM (the renderProfit flow paints
       innerHTML then runs handlers). */
    const id = 'ch-margin-trend';
    const data = series.map(s => ({ date: s.week_start, value: s.margin_pct == null ? 0 : s.margin_pct }));
    /* Queue the chart render. Must happen after DOM insertion. */
    setTimeout(() => {
      try { chartLine(id, data, 'Weekly margin (%)', { decimals: 1 }); } catch (_) {}
    }, 0);
    return `
      <div style="margin-bottom:24px;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Margin trend · trailing 12 weeks <span style="font-weight:400;text-transform:none;letter-spacing:0;color:#888;">(fixed window, not tied to the date range above)</span></h3>
        <p class="ag-muted" style="font-size:11px;margin:0 0 8px 0;max-width:720px;">Revenue-weighted weekly margin % from Sales_History × current (ex-VAT) cost basis. SKUs with no cost set are excluded (they'd read as 100% margin and spike the line). Watch the trend, not absolute numbers. Current cost basis means historical reanalysis isn't perfectly accurate.</p>
        <div style="height:120px;"><canvas id="${id}"></canvas></div>
      </div>
    `;
  }

  /* ─── By-category profitability rollup ───
     Groups all SKUs by Shopify product_type and shows revenue,
     profit, margin per category. Reveals which categories carry the
     business. Sortable; default = profit desc. */
  function renderCategoryRollup(cats) {
    if (!cats || !cats.length) return '';
    /* Only show categories with activity in the period. Was cluttered with dozens
       of 0-unit / £0 rows (Machines, Bodyweight, etc.). */
    const active = cats.filter(c => (c.units_sold_annual || 0) > 0 || (c.revenue_net_annual || 0) > 0);
    if (!active.length) return '';
    const hidden = cats.length - active.length;
    return `
      <div style="margin-bottom:24px;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Profitability by category</h3>
        <p class="ag-muted" style="font-size:11px;margin:0 0 8px 0;max-width:720px;">Grouped by Shopify product_type. Only categories with sales in the period are shown${hidden > 0 ? ` (${hidden} with no activity hidden)` : ''}. Excluded (Collective / resell / tagged) SKUs are skipped, so this won't foot exactly to the headline total.</p>
        <table class="ag-table" style="font-size:12px;">
          <thead><tr>
            <th>Category</th>
            <th class="num">SKUs</th>
            <th class="num">Units</th>
            <th class="num">Revenue</th>
            <th class="num">Cost</th>
            <th class="num">Profit</th>
            <th class="num">Margin</th>
          </tr></thead>
          <tbody>
            ${active.map(c => `<tr>
              <td><strong>${esc(c.category)}</strong></td>
              <td class="num">${c.sku_count}</td>
              <td class="num">${fmtNum(c.units_sold_annual, 0)}</td>
              <td class="num">£${fmtNum(c.revenue_net_annual)}</td>
              <td class="num">£${fmtNum(c.cost_annual)}</td>
              <td class="num"><strong>£${fmtNum(c.gross_profit_annual)}</strong></td>
              <td class="num"><span class="ag-pill ${marginClass(c.gross_margin_pct)}">${fmtNum(c.gross_margin_pct)}%</span></td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  /* ─── Profitability insights panel. Outlier-by-bucket cards ───
     Collapsible (default open). Four buckets: cash cows (top earners
     to protect), negative margins (urgent), hidden gems (promote),
     return offenders (fix or drop). Empty buckets get a "no rows"
     placeholder rather than disappearing. Keeps the layout stable
     across windows where one category happens to be empty. */
  function renderProfitInsights(outliers) {
    if (!outliers) return '';
    if (state.profitInsightsOpen == null) state.profitInsightsOpen = true;
    const open = state.profitInsightsOpen;

    function bucketCard(title, tone, hint, rows, columns) {
      const isEmpty = !rows || !rows.length;
      const body = isEmpty
        ? '<div class="ag-muted" style="font-size:12px;padding:8px 0;">No SKUs match this bucket.</div>'
        : `<table class="ag-table" style="font-size:12px;">
            <thead><tr>${columns.map(c => `<th class="${c.num ? 'num' : ''}">${esc(c.label)}</th>`).join('')}</tr></thead>
            <tbody>${rows.map(r => `<tr>${columns.map(c => {
              const v = c.cell(r);
              return `<td class="${c.num ? 'num' : ''}">${v}</td>`;
            }).join('')}</tr>`).join('')}</tbody>
          </table>`;
      return `<div class="ag-card" style="padding:12px;">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;margin-bottom:2px;">
          <span class="ag-pill ${tone}">${esc(title)}</span>
        </div>
        <div class="ag-muted" style="font-size:11px;margin-bottom:8px;">${esc(hint)}</div>
        ${body}
      </div>`;
    }

    /* Compact two-line SKU cell. Title on top (large), bare SKU below
       (small + muted). Falls back to just the SKU when no title is set.
       Solves the "[no-sku]56583653097859 with no title" eyesore. */
    function skuCell(r) {
      const title = String(r.product_title || '').trim();
      const sku = String(r.sku || '');
      if (!title) return `<strong style="font-size:11px;">${esc(sku)}</strong>`;
      return `<div style="line-height:1.2;">
        <div style="font-weight:600;">${esc(title)}</div>
        <div class="ag-muted" style="font-size:10px;">${esc(sku)}</div>
      </div>`;
    }

    const cashCows = bucketCard(
      'Cash cows', 'ok',
      'Top earners with ≥30% margin. Protect these.',
      outliers.cash_cows,
      [
        { label: 'Product',  cell: skuCell },
        { label: 'Profit',   num: true, cell: r => `£${fmtNum(r.gross_profit_annual)}` },
        { label: 'Margin',   num: true, cell: r => `${fmtNum(r.gross_margin_pct)}%` }
      ]
    );
    const negativeMargins = bucketCard(
      'Negative margins', 'err',
      'Selling at a loss after returns. Raise price or stop selling.',
      outliers.negative_margins,
      [
        { label: 'Product',  cell: skuCell },
        { label: 'Units',    num: true, cell: r => r.units_sold_annual || 0 },
        { label: 'Margin',   num: true, cell: r => `<span class="ag-pill err">${fmtNum(r.gross_margin_pct)}%</span>` },
        { label: 'Net margin', num: true, cell: r => r.net_margin_after_returns_pct != null ? `${fmtNum(r.net_margin_after_returns_pct)}%` : '-' }
      ]
    );
    const hiddenGems = bucketCard(
      'Hidden gems', 'mute',
      '50%+ margin, 5-100 units/yr. Promote these.',
      outliers.hidden_gems,
      [
        { label: 'Product',  cell: skuCell },
        { label: 'Margin',   num: true, cell: r => `<span class="ag-pill ok">${fmtNum(r.gross_margin_pct)}%</span>` },
        { label: 'Units',    num: true, cell: r => r.units_sold_annual || 0 },
        { label: 'Profit',   num: true, cell: r => `£${fmtNum(r.gross_profit_annual)}` }
      ]
    );
    const returnOffenders = bucketCard(
      'Return offenders', 'warn',
      '≥20% return rate. Returns are eating the margin.',
      outliers.return_offenders,
      [
        { label: 'Product',  cell: skuCell },
        { label: 'Return',   num: true, cell: r => `<span class="ag-pill warn">${fmtNum(r.return_rate_pct)}%</span>` },
        { label: 'Profit lost', num: true, cell: r => `£${fmtNum(r._return_impact || 0)}` },
        { label: 'Units',    num: true, cell: r => r.units_sold_annual || 0 }
      ]
    );

    return `
      <div style="margin-bottom:24px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;cursor:pointer;" onclick="AGInv.toggleProfitInsights()">
          <span style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Insights · per-SKU outliers</span>
          <span class="ag-muted" style="font-size:11px;">(${open ? 'click to hide' : 'click to show'})</span>
        </div>
        ${open ? `<div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:12px;">
          ${cashCows}
          ${negativeMargins}
          ${hiddenGems}
          ${returnOffenders}
        </div>` : ''}
      </div>
    `;
  }

  /* Digital & Services. 100%-profit non-inventory items (warranty / PDF /
     training plans). Excluded from the product margin tables above; surfaced
     here so the real (near-100%-margin) revenue is still tracked. */
  function renderServicesSection(services) {
    if (!services || !services.length) return '';
    const totalRev = services.reduce((a, s) => a + (s.revenue_net || 0), 0);
    const totalProfit = services.reduce((a, s) => a + (s.profit || 0), 0);
    const rows = services.map(s => `
      <tr>
        <td>${esc(s.product || '?')}</td>
        <td class="num">${s.units || 0}</td>
        <td class="num">£${fmtNum(Math.round(s.revenue_net || 0))}</td>
        <td class="num">£${fmtNum(Math.round(s.profit || 0))}</td>
        <td class="num">${(s.margin_pct || 0)}%</td>
      </tr>`).join('');
    return `
      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 4px;">Digital &amp; Services <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;">· 100%-profit non-inventory (warranty / PDF / plans), kept out of product margins above</span></h3>
      <table class="ag-table"><thead><tr><th>Service</th><th class="num">Units</th><th class="num">Revenue</th><th class="num">Profit</th><th class="num">Margin</th></tr></thead>
      <tbody>${rows}
        <tr style="font-weight:600;border-top:2px solid var(--ag-border,#ddd);"><td>Total</td><td class="num"></td><td class="num">£${fmtNum(Math.round(totalRev))}</td><td class="num">£${fmtNum(Math.round(totalProfit))}</td><td class="num">${totalRev > 0 ? Math.round((totalProfit / totalRev) * 1000) / 10 : 0}%</td></tr>
      </tbody></table>`;
  }

  function renderProfit(root, p) {
    const rows = state.profitMode === 'sku' ? (p.per_sku || []) : (p.finished || []);
    const hero = p.hero || {};
    /* Headline = the product-level (Finished) total in BOTH views, so it doesn't
       jump when you toggle Per-SKU/Finished. (Per-SKU rows can differ by a small
       bundle-attribution amount; the product total is the business figure.) */
    const total = hero.total_profit_annual_finished;
    /* Window + vs-previous-period now come from the GLOBAL date range
       (header picker). p.window = {from,to,days}; p.compare = prior-period
       totals over the immediately-preceding equal-length window. */
    const win = p.window || {};
    const periodLabel = p.since_cutover && p.cutover_date
      ? 'since cutover (' + p.cutover_date + ')'
      : (win.from ? win.from + ' → ' + win.to : 'last 365 days');
    const periodProfitLabel = 'Total profit · ' + periodLabel;
    const cmp = p.compare || null;
    const prevTotal = cmp ? (state.profitMode === 'sku' ? cmp.total_profit_per_sku : cmp.total_profit_finished) : null;
    const totalDeltaPct = (prevTotal != null && prevTotal !== 0) ? Math.round(((total - prevTotal) / Math.abs(prevTotal)) * 100) : null;
    const totalDeltaHtml = (cmp && totalDeltaPct != null)
      ? `<div style="margin-top:4px;"><span class="ag-pill ${totalDeltaPct >= 0 ? 'ok' : 'bad'}" style="font-size:10px;">${totalDeltaPct >= 0 ? '+' : ''}${totalDeltaPct}% vs previous period</span></div>`
      : '';
    root.innerHTML = `
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:6px;">
        <h2 class="ag-h2" style="margin:0 16px 0 0;">Profitability <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${esc(periodLabel)}</span></h2>
      </div>
      <div class="ag-toolbar" style="gap:6px;">
        <button class="ag-btn ${state.profitMode === 'finished' ? '' : 'secondary'}" data-mode="finished">Finished products</button>
        <button class="ag-btn ${state.profitMode === 'sku' ? '' : 'secondary'}" data-mode="sku">Per-SKU</button>
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));margin-bottom:24px;">
        <div class="ag-stat ok"><div class="label">${esc(periodProfitLabel)}</div><div class="value">£${fmtNum(Math.round(total))}</div>${totalDeltaHtml}</div>
        ${(() => { const hb = state.profitMode === 'finished' ? (hero.best_finished || hero.best_sku) : hero.best_sku; return hb ? statCard('Best earner', heroSkuDisplay(hb) + ' (£' + fmtNum(Math.round(hb.gross_profit_annual)) + ')', 'ok') : ''; })()}
        ${(() => { const hw = state.profitMode === 'finished' ? (hero.worst_finished || hero.worst_sku) : hero.worst_sku; return hw ? statCard('Worst earner', heroSkuDisplay(hw) + ' (£' + fmtNum(Math.round(hw.gross_profit_annual)) + ')', 'bad') : ''; })()}
      </div>

      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 8px;">Margin waterfall <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;">· % of revenue, for the selected range</span></h3>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:24px;">
        <div class="ag-stat ok"><div class="label">Gross margin</div><div class="value">${(hero.overall_margin_pct || 0)}%</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">revenue − COGS − fees</div></div>
        <div class="ag-stat"><div class="label">After returns</div><div class="value">${(hero.margin_after_returns_pct != null ? hero.margin_after_returns_pct : hero.overall_margin_pct || 0)}%</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">−£${fmtNum(Math.round(hero.returns_net_cost_range || 0))} net (sale cancelled, fee kept)</div></div>
        <div class="ag-stat"><div class="label">After shipping</div><div class="value">${(hero.margin_after_shipping_pct || 0)}%</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">${(() => { const s = Math.round(hero.shipping_cost_range || 0); return s < 0 ? `+£${fmtNum(-s)} shipping credit` : `−£${fmtNum(s)} outbound`; })()}</div></div>
        <div class="ag-stat ${(hero.margin_after_both_pct || 0) >= 0 ? 'ok' : 'bad'}"><div class="label">After marketing</div><div class="value">${(hero.margin_after_both_pct || 0)}%</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">−£${fmtNum(Math.round(hero.marketing_cost_range || 0))} ad + mkt (cumulative)</div></div>
        <div class="ag-stat ${(hero.margin_after_all_pct || 0) >= 0 ? 'ok' : 'bad'}"><div class="label">After ops · TRUE NET</div><div class="value">${(hero.margin_after_all_pct || 0)}%</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">−£${fmtNum(Math.round(hero.operating_cost_range || 0))} ops · bottom line</div></div>
      </div>
      ${renderMarginTimeSeriesChart(p.margin_series)}
      ${renderCategoryRollup(p.by_category)}
      ${renderServicesSection(p.services)}
      ${renderProfitInsights(p.outliers)}
      <table class="ag-table"><thead><tr>
        <th>${state.profitMode === 'sku' ? 'Product' : 'Finished product'}</th>
        <th class="num">Units</th>
        <th class="num">Revenue</th>
        <th class="num">Cost/unit</th>
        <th class="num">Cost</th>
        <th class="num">Gross profit</th>
        <th class="num">Gross margin</th>
        ${state.profitMode === 'finished' ? '<th>Cost basis</th>' : ''}
        <th class="num">Return rate</th>
        <th class="num">Net profit</th>
        <th class="num">Net margin</th>
      </tr></thead><tbody>${rows.map(r => {
        const netProfit = r.net_profit_after_returns_annual != null ? r.net_profit_after_returns_annual : r.gross_profit_annual;
        const netMargin = r.net_margin_after_returns_pct != null ? r.net_margin_after_returns_pct : r.gross_margin_pct;
        const returnRate = r.return_rate_pct || 0;
        const returnCls = returnRate > 10 ? 'bad' : returnRate > 5 ? 'warn' : 'mute';
        const costAnnual = r.cost_annual != null ? r.cost_annual : (r.cogs_per_unit || 0) * (r.units_sold_annual || 0);
        return `<tr>
          <td>${state.profitMode === 'sku'
            ? productCell(r, { compact: true })
            : (() => {
                /* "SKU · Title" (joinSkuTitle_ in the inventory script; rows stored before the
                   2026-09-11 sweep carry the old em-dash separator) → Title bold on top, SKU grey underneath.
                   Never split on '-': nearly every SKU is hyphenated. */
                const fp = String(r.finished_product || '');
                const SEP = ' \u00b7 ', SEP_LEGACY = ' \u2014 '; // em-dash-ok: STORED legacy labels
                const sep = fp.indexOf(SEP) >= 0 ? SEP : (fp.indexOf(SEP_LEGACY) >= 0 ? SEP_LEGACY : '');
                const i = sep ? fp.indexOf(sep) : -1;
                const sku = i >= 0 ? fp.slice(0, i) : '';
                const name = i >= 0 ? fp.slice(i + sep.length) : fp;
                return `<strong>${esc(name)}</strong>${sku ? `<div class="ag-muted" style="font-size:11px;margin-top:2px;">${esc(sku)}</div>` : ''}`;
              })()}</td>
          <td class="num">${r.units_sold_annual || 0}</td>
          <td class="num">£${fmtNum(r.revenue_net_annual)}</td>
          <td class="num">£${fmtNum(r.cogs_per_unit)}</td>
          <td class="num">£${fmtNum(costAnnual)}</td>
          <td class="num">£${fmtNum(r.gross_profit_annual)}</td>
          <td class="num"><span class="ag-pill ${marginClass(r.gross_margin_pct)}">${fmtNum(r.gross_margin_pct)}%</span></td>
          ${state.profitMode === 'finished' ? renderCostBasisCell(r) : ''}
          <td class="num">${returnRate ? `<span class="ag-pill ${returnCls}">${fmtNum(returnRate)}%</span>` : '<span class="ag-muted">-</span>'}</td>
          <td class="num"><strong>£${fmtNum(netProfit)}</strong></td>
          <td class="num"><span class="ag-pill ${marginClass(netMargin)}">${fmtNum(netMargin)}%</span></td>
        </tr>`;
      }).join('')}</tbody></table>
    `;
    root.querySelectorAll('[data-mode]').forEach(b => b.addEventListener('click', () => {
      state.profitMode = b.dataset.mode;
      renderProfit(root, p);
    }));
    /* Period is now driven by the global date-range picker in the header. */
  }
  function marginClass(m) {
    m = parseFloat(m) || 0;
    if (m < 0) return 'margin-loss';
    if (m < 15) return 'margin-low';
    if (m < 35) return 'margin-mid';
    return 'margin-high';
  }

  /* ─── Inline cost editor ──────────────────────────────────────
     Click any cost cell on the Stock tab (or any table using the
     inlineCostCell helper) → swap to an input → save on Enter/blur.
     Pushes to SKU_Settings.unit_cost via ui_save_sku_settings.

     UX notes:
       - Cell shows a subtle dotted underline as a "this is editable"
         hint. Pencil shows on hover.
       - Click stops propagation so it doesn't open the SKU drawer
         (which it would otherwise, rows are clickable).
       - On save, the cell value updates immediately and the row's
         data is patched in-place. No re-fetch needed for instant UX.
       - Esc cancels without saving. */
  function inlineCostCell(r) {
    const cost = parseFloat(r.unit_cost) || 0;
    const display = cost > 0 ? '£' + fmtNum(cost) : '<span class="ag-muted">-</span>';
    /* Visible pencil ✎ + dashed underline so the click target is
       obvious without hovering. Previously rendered as plain text
       with only a hover hint, operators kept missing it. */
    return `<td class="num cost-edit-cell" data-sku="${esc(r.sku)}"
      style="cursor:pointer;border-bottom:1px dashed rgba(0,0,0,0.25);white-space:nowrap;"
      title="Click to edit unit cost (SKU_Settings.unit_cost)">
      <span class="cost-value">${display}</span>
      <span class="ag-muted" style="font-size:10px;margin-left:6px;opacity:0.6;" aria-hidden="true">✎</span>
    </td>`;
  }

  /* Inline on-hand editor, same pattern as the cost cell but writes to
     Shopify via inventoryAdjustQuantities. The cell shows the current
     count + a ✎ glyph; click → input → Enter to save.  Server computes
     the delta from the stored SKU_Live value, so a stale display
     (someone else updated in the meantime) still produces a correct
     adjustment based on whatever Shopify last saw. */
  function inlineOnHandCell(r) {
    return `<td class="num onhand-edit-cell" data-sku="${esc(r.sku)}"
      style="cursor:pointer;border-bottom:1px dashed rgba(0,0,0,0.25);white-space:nowrap;"
      title="Click to adjust on-hand qty (writes to Shopify inventoryAdjustQuantities)">
      <span class="onhand-value">${r.on_hand}</span>
      <span class="ag-muted" style="font-size:10px;margin-left:6px;opacity:0.6;" aria-hidden="true">✎</span>
    </td>`;
  }
  function bindInlineOnHandEditors(container, getRow) {
    if (container._onhandEditorBound) return;
    container._onhandEditorBound = true;
    container.addEventListener('click', e => {
      const cell = e.target.closest('.onhand-edit-cell');
      if (!cell || cell.querySelector('input')) return;
      e.stopPropagation();
      const sku = cell.dataset.sku;
      const valueSpan = cell.querySelector('.onhand-value');
      const originalHtml = cell.innerHTML;
      const currentNum = parseInt((valueSpan ? valueSpan.textContent : '').replace(/[,\s]/g, ''), 10) || 0;
      cell.innerHTML = `<input type="number" step="1" min="0" value="${currentNum}"
        class="ag-input onhand-edit-input"
        style="width:80px;text-align:right;font-size:13px;padding:2px 4px;" />`;
      const input = cell.querySelector('input');
      input.focus();
      input.select();
      let resolved = false;
      const finish = async (commit) => {
        if (resolved) return;
        resolved = true;
        if (!commit) { cell.innerHTML = originalHtml; return; }
        const raw = input.value.trim();
        if (raw === '') { cell.innerHTML = originalHtml; return; }
        const newQty = parseInt(raw, 10);
        if (isNaN(newQty) || newQty < 0) {
          AG_ADMIN.toast('Invalid quantity. Must be a non-negative whole number.', true);
          cell.innerHTML = originalHtml;
          return;
        }
        if (newQty === currentNum) { cell.innerHTML = originalHtml; return; }
        cell.innerHTML = '<span class="ag-spinner"></span>';
        try {
          const r = await AG_ADMIN.api('ui_adjust_on_hand', { sku, new_on_hand: newQty });
          const row = getRow && getRow(sku);
          if (row) row.on_hand = newQty;
          cell.innerHTML = `<span class="onhand-value">${newQty}</span><span class="ag-muted" style="font-size:10px;margin-left:6px;opacity:0.6;">✎</span>`;
          const sign = r.delta > 0 ? '+' : '';
          AG_ADMIN.toast(`${sku} on-hand → ${newQty} (${sign}${r.delta})`);
        } catch (err) {
          const reason = (err && err.message) ? err.message : String(err);
          AG_ADMIN.toast('Save failed: ' + reason, true);
          /* Leave a persistent, hoverable failure marker on the cell instead of
             silently reverting, so a failed stock edit cannot be missed. Keeps
             .onhand-value (the true current count) so a retry-click still reads
             the right number; cleared on the next successful edit or re-render. */
          cell.innerHTML =
            `<span class="onhand-value">${currentNum}</span>` +
            `<span class="onhand-save-failed" title="${esc('Save failed: ' + reason)}" ` +
            `style="color:var(--ag-accent,#c0261c);font-size:11px;font-weight:700;margin-left:6px;white-space:nowrap;cursor:help;">&#9888; not saved</span>`;
        }
      };
      input.addEventListener('blur', () => finish(true));
      input.addEventListener('keydown', ev => {
        if (ev.key === 'Enter') { ev.preventDefault(); finish(true); }
        else if (ev.key === 'Escape') { ev.preventDefault(); finish(false); }
      });
      input.addEventListener('click', ev => ev.stopPropagation());
    });
  }
  /* ─── Stock-row kebab menu ────────────────────────────────────
     Row-level actions: open in Shopify admin / PDP, copy SKU, set
     lifecycle, mark inactive, archive, delete. Destructive ops
     prompt; archive/delete write to Shopify Admin API.

     Implementation: one floating menu node attached to body, repositioned
     under the active kebab button. Click outside / Esc closes. */
  let _activeStockMenu = null;
  function closeStockMenu() {
    if (_activeStockMenu) { _activeStockMenu.remove(); _activeStockMenu = null; }
  }
  function bindStockKebabMenu(container, getRow, opts) {
    if (container._kebabBound) return;
    container._kebabBound = true;
    container.addEventListener('click', e => {
      const btn = e.target.closest('.stock-kebab-btn');
      if (!btn) return;
      e.stopPropagation();
      const sku = btn.dataset.sku;
      const r = getRow(sku);
      if (!r) return;
      /* Toggle if same button clicked twice. */
      if (_activeStockMenu && _activeStockMenu.dataset.sku === sku) { closeStockMenu(); return; }
      closeStockMenu();
      openStockMenu(btn, r, opts);
    });
  }
  function openStockMenu(btn, r, opts) {
    const productNum = String(r.product_id || '').split('/').pop();
    const adminUrl = (opts.shopDomain && productNum)
      ? `https://${opts.shopDomain}/admin/products/${productNum}` : '';
    const pdpUrl = (r.handle && opts.primaryDomain)
      ? `https://${opts.primaryDomain}/products/${r.handle}` : '';
    const lifecycle = String(r.lifecycle_status || '').toLowerCase();
    const menu = document.createElement('div');
    menu.className = 'ag-stock-kebab-menu';
    menu.dataset.sku = r.sku;
    menu.style.cssText = [
      'position:absolute', 'z-index:9999', 'background:#fff',
      'border:1px solid #d0d0d0', 'border-radius:6px',
      'box-shadow:0 6px 24px rgba(0,0,0,0.15)',
      'min-width:220px', 'padding:4px 0', 'font-size:13px'
    ].join(';');
    const itemStyle = 'display:block;width:100%;text-align:left;padding:8px 14px;border:none;background:none;cursor:pointer;font-size:13px;color:#222;';
    const sepStyle = 'height:1px;background:#eee;margin:4px 0;';
    function item(label, handler, opts2) {
      const o = opts2 || {};
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.style.cssText = itemStyle + (o.color ? `color:${o.color};` : '');
      btn.textContent = label;
      btn.addEventListener('mouseenter', () => btn.style.background = o.color ? '#fff5f5' : '#f4f4f4');
      btn.addEventListener('mouseleave', () => btn.style.background = 'transparent');
      btn.addEventListener('click', async () => { closeStockMenu(); await handler(); });
      menu.appendChild(btn);
    }
    function sep() { const d = document.createElement('div'); d.style.cssText = sepStyle; menu.appendChild(d); }
    function link(label, href) {
      if (!href) return;
      const a = document.createElement('a');
      a.href = href; a.target = '_blank'; a.rel = 'noopener';
      a.style.cssText = itemStyle + 'text-decoration:none;';
      a.textContent = label;
      a.addEventListener('mouseenter', () => a.style.background = '#f4f4f4');
      a.addEventListener('mouseleave', () => a.style.background = 'transparent');
      a.addEventListener('click', () => closeStockMenu());
      menu.appendChild(a);
    }

    link('🛠 Open in Shopify admin', adminUrl);
    link('🛒 Open PDP on storefront', pdpUrl);

    item('📋 Copy SKU', async () => {
      try { await navigator.clipboard.writeText(r.sku); AG_ADMIN.toast('Copied: ' + r.sku); }
      catch (e) { AG_ADMIN.toast('Clipboard blocked', true); }
    });

    sep();

    item(`Lifecycle: Active${lifecycle === '' ? ' ✓' : ''}`, async () => {
      await AG_ADMIN.api('ui_save_sku_settings', { sku: r.sku, fields: { lifecycle_status: '' } });
      r.lifecycle_status = '';
      AG_ADMIN.toast(`${r.sku} → Active`);
      if (opts.rerender) opts.rerender();
    });
    item(`Lifecycle: On Hold${lifecycle === 'on hold' ? ' ✓' : ''}`, async () => {
      await AG_ADMIN.api('ui_save_sku_settings', { sku: r.sku, fields: { lifecycle_status: 'On Hold' } });
      r.lifecycle_status = 'On Hold';
      AG_ADMIN.toast(`${r.sku} → On Hold`);
      if (opts.rerender) opts.rerender();
    });
    item(`Lifecycle: Discontinued${lifecycle === 'discontinued' ? ' ✓' : ''}`, async () => {
      await AG_ADMIN.api('ui_save_sku_settings', { sku: r.sku, fields: { lifecycle_status: 'Discontinued' } });
      r.lifecycle_status = 'Discontinued';
      AG_ADMIN.toast(`${r.sku} → Discontinued`);
      if (opts.rerender) opts.rerender();
    });

    item(`Mark inactive in inventory${r.active === false ? ' ✓' : ''}`, async () => {
      const next = !(r.active === false);
      await AG_ADMIN.api('ui_save_sku_settings', { sku: r.sku, fields: { active: !next } });
      r.active = !next;
      AG_ADMIN.toast(`${r.sku} → ${r.active ? 'active' : 'inactive'} in inventory`);
      if (opts.rerender) opts.rerender();
    });

    /* Drop-ship / external: keeps its cost (counts in profit) but is excluded
       from our stock value (we don't hold the stock). */
    item(`Drop-ship / external${r.bucket === 'dropship' ? ' ✓' : ''}`, async () => {
      const on = r.bucket !== 'dropship';
      await AG_ADMIN.api('ui_save_sku_settings', { sku: r.sku, fields: { dropship: on ? true : '' } });
      AG_ADMIN.toast(`${r.sku} → ${on ? 'drop-ship (excluded from stock value)' : 'owned'}`);
      renderTab('stock'); // re-fetch so bucket + value + stock-value KPI all update
    });

    sep();

    item('📦 Archive product (Shopify)', async () => {
      if (!confirm(`Archive product for ${r.sku}?\n\n"${r.product_title || ''}" will be hidden from the storefront. Reversible from Shopify admin.`)) return;
      try {
        await AG_ADMIN.api('ui_archive_product', { sku: r.sku });
        AG_ADMIN.toast('Archived. Will drop off after next sync.');
      } catch (err) {
        AG_ADMIN.toast('Archive failed: ' + (err.message || err), true);
      }
    }, { color: '#b86d00' });

    item('🗑 Delete product (Shopify)', async () => {
      const expected = r.sku;
      const typed = prompt(`PERMANENTLY DELETE this product?\n\n"${r.product_title || ''}"\n\nThis is IRREVERSIBLE. To confirm, type the SKU exactly:\n\n${expected}`);
      if (typed !== expected) { AG_ADMIN.toast('Delete cancelled.'); return; }
      /* APP-BLOCK-03: second factor beyond the shared admin password, the
         operator must also enter the DELETE_CONFIRM_TOKEN (a Script Property,
         not the dashboard login). The server validates it constant-time and
         rejects deletion without it, so a leaked password alone can't delete. */
      const delTok = prompt('FINAL STEP. Enter the delete confirmation token to authorise this deletion:');
      if (!delTok) { AG_ADMIN.toast('Delete cancelled.'); return; }
      try {
        const res = await AG_ADMIN.api('ui_delete_product', { sku: r.sku, confirm: true, delete_token: delTok.trim() });
        AG_ADMIN.toast(`Deleted (${res.removed_rows || 0} rows cleared).`);
        if (opts.rerender) opts.rerender();
      } catch (err) {
        AG_ADMIN.toast('Delete failed: ' + (err.message || err), true);
      }
    }, { color: '#c8102e' });

    document.body.appendChild(menu);
    const rect = btn.getBoundingClientRect();
    const mw = menu.offsetWidth || 220;
    const mh = menu.offsetHeight || 200;
    /* Position: try below-right of the button; flip up if it overflows. */
    let top = window.scrollY + rect.bottom + 4;
    let left = window.scrollX + rect.right - mw;
    if (top + mh > window.scrollY + window.innerHeight - 8) {
      top = window.scrollY + rect.top - mh - 4;
    }
    if (left < 8) left = 8;
    menu.style.top = top + 'px';
    menu.style.left = left + 'px';
    _activeStockMenu = menu;

    /* Close on outside click + Esc. */
    setTimeout(() => {
      const onDocClick = e => { if (!menu.contains(e.target)) { closeStockMenu(); document.removeEventListener('mousedown', onDocClick); } };
      document.addEventListener('mousedown', onDocClick);
      const onEsc = e => { if (e.key === 'Escape') { closeStockMenu(); document.removeEventListener('keydown', onEsc); } };
      document.addEventListener('keydown', onEsc);
    }, 0);
  }

  /* Inline vendor editor, vendor is a Shopify product-level field, so
     a change affects EVERY variant of that product. We patch all rows
     sharing the same product_id locally + bust caches server-side. */
  function inlineVendorCell(r) {
    return `<td class="vendor-edit-cell ag-muted" data-sku="${esc(r.sku)}" data-vendor="${esc(r.vendor || '')}"
      style="font-size:11px;cursor:pointer;border-bottom:1px dashed rgba(0,0,0,0.2);white-space:nowrap;"
      title="Click to edit vendor (writes to Shopify product.vendor for ALL variants of this product)">
      <span class="vendor-value">${esc(r.vendor || '-')}</span>
      <span class="ag-muted" style="font-size:10px;margin-left:6px;opacity:0.6;" aria-hidden="true">✎</span>
    </td>`;
  }
  function bindInlineVendorEditors(container, getRow, getAllRows) {
    if (container._vendorEditorBound) return;
    container._vendorEditorBound = true;
    container.addEventListener('click', e => {
      const cell = e.target.closest('.vendor-edit-cell');
      if (!cell || cell.querySelector('input')) return;
      e.stopPropagation();
      const sku = cell.dataset.sku;
      const originalHtml = cell.innerHTML;
      const currentVendor = cell.dataset.vendor || '';
      /* Suggest the vendors already in use (most used first) so a pick from
         the list cannot create a near-duplicate like "Am/Eb" next to "AM/EB".
         Typing a new vendor still works. */
      const vendorCounts = new Map();
      ((getAllRows && getAllRows()) || []).forEach(r => {
        const v = String(r.vendor || '').trim();
        if (v) vendorCounts.set(v, (vendorCounts.get(v) || 0) + 1);
      });
      const vendorOptions = Array.from(vendorCounts.keys())
        .sort((a, b) => (vendorCounts.get(b) - vendorCounts.get(a)) || a.localeCompare(b))
        .map(v => `<option value="${esc(v)}"></option>`).join('');
      cell.innerHTML = `<input type="text" value="${esc(currentVendor)}"
        class="ag-input vendor-edit-input" list="ag-vendor-options" autocomplete="off"
        style="width:140px;font-size:12px;padding:2px 4px;" />
        <datalist id="ag-vendor-options">${vendorOptions}</datalist>`;
      const input = cell.querySelector('input');
      input.focus();
      input.select();
      let resolved = false;
      const finish = async (commit) => {
        if (resolved) return;
        resolved = true;
        if (!commit) { cell.innerHTML = originalHtml; return; }
        const newVendor = input.value.trim();
        if (!newVendor) { cell.innerHTML = originalHtml; return; }
        if (newVendor === currentVendor) { cell.innerHTML = originalHtml; return; }
        cell.innerHTML = '<span class="ag-spinner"></span>';
        try {
          const res = await AG_ADMIN.api('ui_set_vendor', { sku, vendor: newVendor });
          /* Vendor is product-level → the write affects EVERY variant of this
             product. Patch the canonical row data for all affected SKUs AND
             repaint every rendered vendor cell for them (not just the clicked
             one), so sibling variant rows in the list update immediately
             instead of keeping the old vendor until the next re-render. */
          const allRows = (getAllRows && getAllRows()) || [];
          const affectedSkus = (res.affected_skus && res.affected_skus.length) ? res.affected_skus : [sku];
          const affectedSet = new Set(affectedSkus);
          affectedSkus.forEach(s => {
            const row = allRows.find(r => r.sku === s);
            if (row) row.vendor = newVendor;
          });
          const vendorCellInner = `<span class="vendor-value">${esc(newVendor)}</span><span class="ag-muted" style="font-size:10px;margin-left:6px;opacity:0.6;" aria-hidden="true">✎</span>`;
          container.querySelectorAll('.vendor-edit-cell').forEach(c => {
            if (!affectedSet.has(c.dataset.sku)) return;
            c.dataset.vendor = newVendor;
            c.innerHTML = vendorCellInner;
          });
          const n = affectedSkus.length;
          AG_ADMIN.toast(`Vendor → ${newVendor}${n > 1 ? ` (${n} SKUs)` : ''}`);
        } catch (err) {
          AG_ADMIN.toast('Save failed: ' + (err.message || err), true);
          cell.innerHTML = originalHtml;
        }
      };
      input.addEventListener('blur', () => finish(true));
      input.addEventListener('keydown', ev => {
        if (ev.key === 'Enter') { ev.preventDefault(); finish(true); }
        else if (ev.key === 'Escape') { ev.preventDefault(); finish(false); }
      });
      input.addEventListener('click', ev => ev.stopPropagation());
    });
  }

  function bindInlineCostEditors(container, getRow) {
    if (container._costEditorBound) return;
    container._costEditorBound = true;
    container.addEventListener('click', e => {
      const cell = e.target.closest('.cost-edit-cell');
      if (!cell || cell.querySelector('input')) return;
      e.stopPropagation();
      const sku = cell.dataset.sku;
      const valueSpan = cell.querySelector('.cost-value');
      const originalHtml = cell.innerHTML;
      const currentText = (valueSpan ? valueSpan.textContent : '').replace(/[£,\s]/g, '');
      const currentNum = parseFloat(currentText) || 0;
      cell.innerHTML = `<input type="number" step="0.01" min="0" value="${currentNum > 0 ? currentNum : ''}"
        class="ag-input cost-edit-input"
        style="width:80px;text-align:right;font-size:13px;padding:2px 4px;" />`;
      const input = cell.querySelector('input');
      input.focus();
      input.select();
      let resolved = false;
      const finish = async (commit) => {
        if (resolved) return;
        resolved = true;
        if (!commit) { cell.innerHTML = originalHtml; return; }
        const raw = input.value.trim();
        if (raw === '') { cell.innerHTML = originalHtml; return; }
        const newCost = parseFloat(raw);
        if (isNaN(newCost) || newCost < 0) {
          AG_ADMIN.toast('Invalid cost. Must be a positive number.', true);
          cell.innerHTML = originalHtml;
          return;
        }
        /* Deliberate £0: the backend blocks accidental zero-cost writes (auto
           reconcile / PO sign-off) to protect real costs. A manual £0 typed here
           is intentional, so confirm + force it through with allow_zero. */
        let allowZero = false;
        if (newCost === 0) {
          if (!confirm('Set cost to £0 on Shopify for ' + sku + '? This overwrites any existing cost.')) {
            cell.innerHTML = originalHtml; return;
          }
          allowZero = true;
        }
        cell.innerHTML = '<span class="ag-spinner"></span>';
        try {
          await AG_ADMIN.api('ui_set_unit_cost', { sku, unit_cost: newCost, allow_zero: allowZero }); // writes to Shopify (inventory item cost)
          /* Patch the cached payload so the next paint stays correct
             without a refetch. */
          const row = getRow && getRow(sku);
          if (row) row.unit_cost = newCost;
          cell.innerHTML = `<span class="cost-value">£${fmtNum(newCost)}</span>`;
          AG_ADMIN.toast('Cost updated: £' + fmtNum(newCost));
        } catch (err) {
          AG_ADMIN.toast('Save failed: ' + (err.message || err), true);
          cell.innerHTML = originalHtml;
        }
      };
      input.addEventListener('blur', () => finish(true));
      input.addEventListener('keydown', ev => {
        if (ev.key === 'Enter') { ev.preventDefault(); finish(true); }
        else if (ev.key === 'Escape') { ev.preventDefault(); finish(false); }
      });
      /* Prevent the input's own click from bubbling and re-triggering. */
      input.addEventListener('click', ev => ev.stopPropagation());
    });
  }
  /* Best/worst earner display. Prefer product_title (readable),
     fall back to bare sku. [no-sku]56583653097859 alone is hostile UX. */
  function heroSkuDisplay(r) {
    if (!r) return '';
    const title = String(r.product_title || '').trim();
    return title || String(r.sku || '');
  }
  /* Cost-basis pill for the Finished products view. Surfaces the
     Policy-1 outcome per bundle so the operator can spot
     under-counted margins at a glance.
       complete: Derived from fully-costed components (best)
       direct: Simple product's own unit_cost (also good)
       parent_fallback . Derived missing/partial, parent cost rescued it
       partial: Some components costed, others not, NO parent
                          fallback available. Margin is understated
       incomplete: No cost basis at all, margin is meaningless */
  function renderCostBasisCell(r) {
    const basis = String(r.cost_basis || 'incomplete');
    const source = String(r.cost_source || '');
    let tone, label;
    if (basis === 'complete' && source === 'parent_fallback') {
      tone = 'mute'; label = 'parent fallback';
    } else if (basis === 'complete') {
      tone = 'ok'; label = 'complete';
    } else if (basis === 'direct') {
      tone = 'ok'; label = 'direct';
    } else if (basis === 'partial') {
      tone = 'warn'; label = 'partial';
    } else {
      tone = 'err'; label = 'no cost';
    }
    const tooltip = `cost_source=${source || 'unknown'} · cost_basis=${basis}`;
    return `<td><span class="ag-pill ${tone}" title="${esc(tooltip)}">${esc(label)}</span></td>`;
  }

  /* Tolerance for the header-vs-Σlines USD reconciliation. Splitting a PO total
     across 2-decimal per-unit costs (esp. shared box SKUs allocated different split
     prices, then qty-weighted-merged) leaves unavoidable penny/dollar rounding that
     can't reconcile exactly. Allow ~$1 of drift per line so that rounding never
     flags, while real data-entry errors (tens–thousands of $) always do. Floored at
     $1 for line-less/edge cases. */
  function usdReconTol(linesCount) { return Math.max(1, (parseInt(linesCount, 10) || 0)); }

  /* ─── Tab 5: POs ─────────────────────────────────────────── */
  /* Safe inline PO date save. The backend REPLACES all lines from payload.lines
     (an omitted/empty lines array wipes them), so we can't send a header-only
     save. Instead: load the full PO for its lines, patch the ONE header date
     (the backend preserves every other header column), and resend the lines
     unchanged. ui_get_po returns lines in exactly the shape ui_save_po expects. */
  async function savePoDateField(poNumber, field, value) {
    const po = await AG_ADMIN.api('ui_get_po', { po_number: poNumber });
    if (!po || typeof po !== 'object') throw new Error('could not load PO');
    /* Never save without the PO's lines: uiSavePOWriter_ replaces all lines from
       the payload, so an absent/empty array wipes them. If ui_get_po didn't
       return a lines ARRAY (backend shape change, partial response), refuse
       rather than risk a silent wipe. An empty array is a genuine 0-line PO and
       is safe to pass through. */
    if (!Array.isArray(po.lines)) throw new Error('PO lines did not load. Not saving, to avoid wiping this PO\'s lines. Try again.');
    const lines = po.lines.map(l => ({
      sku: String(l.sku || '').trim(),
      qty_ordered: parseInt(l.qty_ordered, 10) || 0,
      qty_received: parseInt(l.qty_received, 10) || 0,
      unit_cost_usd: parseFloat(l.unit_cost_usd) || 0,
      landed_unit_cost_gbp: parseFloat(l.landed_unit_cost_gbp) || 0,
      hs_code: String(l.hs_code || '').trim()
    })).filter(l => l.sku);
    const header = { po_number: poNumber };
    header[field] = value; // '' clears the date (backend treats explicit '' as a clear)
    await savePoWithBusyRetry({ header, lines });
  }
  /* ui_save_po with automatic busy-retry. Unlike writes in general, re-sending
     an IDENTICAL PO-save payload is idempotent server-side (same header merge,
     same atomic lines rewrite), so retrying after a timeout or a lock-busy
     error cannot double-anything. Without this, a save that landed while the
     hourly pipeline held the script just sat until the 300 s write deadline
     and died: "clicked Save and it just sat for ages, then didn't save"
     (2026-08-17). The drawer keeps the typed values throughout, and each wait
     is announced so the operator knows it is retrying, not stuck. */
  async function savePoWithBusyRetry(payload) {
    const RETRYABLE = /timed out after|another stock operation is in progress|failed to fetch|networkerror/i;
    const DELAYS_MS = [15000, 45000];
    for (let attempt = 0; ; attempt++) {
      try {
        return await AG_ADMIN.api('ui_save_po', payload);
      } catch (e) {
        const msg = String(e && e.message || e);
        if (attempt >= DELAYS_MS.length || !RETRYABLE.test(msg)) throw e;
        AG_ADMIN.toast('Backend busy. Retrying the save in ' + Math.round(DELAYS_MS[attempt] / 1000) +
          's (attempt ' + (attempt + 2) + ' of ' + (DELAYS_MS.length + 1) + '). Your values are kept.', true);
        await new Promise(r => setTimeout(r, DELAYS_MS[attempt]));
      }
    }
  }

  function onPoDateCellClick(e) {
    const cell = e.currentTarget;
    e.stopPropagation(); // don't let the row-click open the drawer
    if (cell.querySelector('input')) return;
    const poNumber = cell.dataset.po, field = cell.dataset.field, label = cell.dataset.label || 'date';
    const cur = cell.dataset.value || '';
    const original = cell.innerHTML;
    cell.innerHTML = `<input type="date" value="${cur}" class="ag-input" style="font-size:11px;padding:1px 3px;width:140px;">`;
    const input = cell.querySelector('input');
    input.focus();
    let done = false;
    const finish = async (commit) => {
      if (done) return; done = true;
      const val = input.value; // '' or YYYY-MM-DD
      if (!commit || val === cur) { cell.innerHTML = original; return; }
      cell.innerHTML = '<span class="ag-spinner" style="width:11px;height:11px;border-width:1.5px;"></span>';
      try {
        await savePoDateField(poNumber, field, val);
        const rec = (state.posByNumber || {})[poNumber]; if (rec) rec[field] = val;
        cell.dataset.value = val;
        cell.innerHTML = val ? esc(fmtFriendly(val)) : '<span class="ag-muted" style="opacity:.45;">＋</span>';
        AG_ADMIN.toast(`${poNumber}: ${label} → ${val || 'cleared'}`);
      } catch (err) {
        AG_ADMIN.toast('Save failed: ' + (err.message || err), true);
        cell.innerHTML = original;
      }
    };
    input.addEventListener('change', () => finish(true));
    input.addEventListener('blur', () => finish(true));
    input.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); finish(true); } else if (ev.key === 'Escape') { ev.preventDefault(); finish(false); } });
    input.addEventListener('click', ev => ev.stopPropagation());
  }
  function renderPosList(root, p) {
    const allPos = (p && p.pos) || [];
    /* Cache the full-header list rows so opening a PO renders instantly. */
    state.posByNumber = {}; allPos.forEach(po => { if (po.po_number) state.posByNumber[po.po_number] = po; });
    const showArchived = !!state.poShowArchived;
    const visiblePos = showArchived ? allPos : allPos.filter(po => !po.archived);
    const today = new Date().toISOString().slice(0, 10);
    /* "Open" = money still in flight (not arrived/signed-off, not cancelled). Once
       a PO is signed off its cost is attributed to the product, so it drops out
       of the PO commitment here. */
    const open = visiblePos.filter(po => po.status && ['arrived', 'cancelled'].indexOf(poStageKey(po.status)) === -1);
    const overdue = open.filter(po => po.eta && String(po.eta).slice(0, 10) < today);
    /* Money cards. Three currencies per the audit: USD (invoice value), ACTUAL £
       (only where a GBP figure was entered), and ESTIMATED £ (converted from USD
       where no GBP was entered yet).
         rate = USD per GBP. Same convention as the PO form's usd_gbp_rate and the
         sign-off costing (GBP = USD ÷ rate). Each PO carries its own rate; where
         blank we prefer the effective rate implied by a fully-known paid leg
         (USD + £ both entered), then the average of the rates operators HAVE
         entered across open POs, then a sane constant. */
    const money = n => fmtNum(Math.round(n));
    const FX_USD_PER_GBP_FALLBACK = 1.30;
    const enteredRates = open.map(po => parseFloat(po.usd_gbp_rate) || 0).filter(r => r > 0);
    const blendRate = enteredRates.length ? enteredRates.reduce((a, b) => a + b, 0) / enteredRates.length : FX_USD_PER_GBP_FALLBACK;
    const rateFor = po => {
      const r = parseFloat(po.usd_gbp_rate) || 0;
      if (r > 0) return r;
      const dU = parseFloat(po.deposit_usd) || 0, dG = parseFloat(po.deposit_gbp_paid) || 0;
      const bU = parseFloat(po.balance_usd) || 0, bG = parseFloat(po.balance_gbp_paid) || 0;
      if (dU > 0 && dG > 0) return dU / dG;
      if (bU > 0 && bG > 0) return bU / bG;
      return blendRate;
    };
    const toGbp = (usd, po) => { const r = rateFor(po); return r > 0 ? usd / r : 0; };
    /* "Paid" uses the SAME rule as the ● Dep / ● Bal pills below: a leg counts as
       settled if its USD amount, its £ paid, OR a paid-date is recorded. (The old
       card only counted a leg when deposit_gbp_paid was entered, so deposits paid
       in USD / by date silently vanished: undercounting Paid, overcounting Due.) */
    let commitUsd = 0, commitGbpEst = 0;
    let paidUsd = 0, paidGbpActual = 0, paidGbpEst = 0;
    let dueUsd = 0, dueGbpEst = 0;
    open.forEach(po => {
      const goods = parseFloat(po.goods_value_usd) || 0;
      commitUsd += goods;
      commitGbpEst += toGbp(goods, po);
      let poPaidUsd = 0;
      [['deposit_usd', 'deposit_gbp_paid', 'deposit_paid_date'],
       ['balance_usd', 'balance_gbp_paid', 'balance_paid_date']].forEach(leg => {
        const u = parseFloat(po[leg[0]]) || 0, g = parseFloat(po[leg[1]]) || 0;
        const paid = u > 0 || g > 0 || !!String(po[leg[2]] || '').trim();
        if (!paid) return;
        const legUsd = u > 0 ? u : (g > 0 ? g * rateFor(po) : 0); // fill USD from £ if only £ is known
        poPaidUsd += legUsd;
        paidUsd += legUsd;
        paidGbpActual += g;                                       // real £ where entered
        if (g === 0 && legUsd > 0) paidGbpEst += toGbp(legUsd, po); // estimate £ for a paid leg missing its £
      });
      /* Instalments beyond deposit + balance (PO_Payments, stamped on each PO by
         the list read). Each carries its real USD and £, so no estimate needed. */
      const xU = parseFloat(po.extra_paid_usd) || 0, xG = parseFloat(po.extra_paid_gbp) || 0;
      if (xU > 0) { poPaidUsd += xU; paidUsd += xU; paidGbpActual += xG; }
      const poDueUsd = Math.max(0, goods - poPaidUsd);
      dueUsd += poDueUsd;
      dueGbpEst += toGbp(poDueUsd, po);
    });
    const sub = v => `<div class="delta" style="font-size:13px;font-weight:600;color:var(--ag-muted,#888);margin-top:2px;">${v}</div>`;
    /* Sortable columns. Click a header to sort; click again to flip direction. */
    const sortArrow = k => (state.poSort && state.poSort.key === k) ? (state.poSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
    const poTh = (label, k, cls) => `<th class="po-sort${cls ? ' ' + cls : ''}" data-sort="${k}" style="cursor:pointer;user-select:none;white-space:nowrap;" title="Sort by ${esc(label)}">${esc(label)}<span class="ag-muted" style="font-size:10px;">${sortArrow(k)}</span></th>`;
    /* Each lifecycle milestone gets its own sortable column so dates line up. */
    const PO_MILESTONES = [
      { k: 'date_ordered', l: 'Ordered' },
      { k: 'production_date', l: 'Production' },
      { k: 'est_completion_date', l: 'Est. done' },
      { k: 'date_shipped', l: 'Shipped' },
      { k: 'eta_port', l: 'ETA port' },
      { k: 'eta', l: 'ETA whse' },
      { k: 'date_received', l: 'Arrived' }
    ];
    /* "Arrival order": order each PO by the first date it actually has, most-
       progressed milestone first, so the list reads top-to-bottom as the order
       things land with us. Arrived > ETA whse > ETA port > Shipped > Est done >
       Production > Ordered. Used as the default sort and the toolbar button. */
    const ARRIVAL_CASCADE = ['date_received', 'eta', 'eta_port', 'date_shipped', 'est_completion_date', 'production_date', 'date_ordered'];
    const arrivalKey = po => {
      for (let i = 0; i < ARRIVAL_CASCADE.length; i++) {
        const v = String(po[ARRIVAL_CASCADE[i]] || '').slice(0, 10);
        if (v) return v;
      }
      return '';
    };
    /* USD header-vs-lines mismatch (needs backend lines_usd). One helper, reused by
       the banner + the row tint + the per-row ⚠ so a PO that doesn't reconcile is
       impossible to miss. Counted over the visible (non-archived) set so the banner
       is a stable store-wide signal regardless of the stage filter. */
    const poUsdMismatch = po => { const h = parseFloat(po.goods_value_usd) || 0, l = parseFloat(po.lines_usd) || 0; return l > 0 && Math.abs(h - l) > usdReconTol(po.lines_count); };
    const mismatchPos = visiblePos.filter(poUsdMismatch);
    const mismatchBanner = mismatchPos.length ? `
      <div style="background:rgba(180,83,9,0.1);border:1px solid rgba(180,83,9,0.35);color:#8a4408;border-radius:8px;padding:10px 14px;margin-bottom:16px;font-size:13px;display:flex;gap:8px;align-items:baseline;flex-wrap:wrap;">
        <strong>⚠ ${mismatchPos.length} PO${mismatchPos.length === 1 ? '' : 's'} with a USD header ≠ line-total mismatch</strong>
        <span>, ${mismatchPos.map(po => esc(po.po_number || '')).join(', ')}. Open each to reconcile (set header = Σ lines, or fix the line).</span>
      </div>` : '';
    root.innerHTML = `
      <h2 class="ag-h2">Purchase Orders</h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:24px;">
        ${statCardCmp('Open commitment', '$' + money(commitUsd), '', sub('≈ £' + money(commitGbpEst) + ' est'))}
        ${statCardCmp('Paid', '$' + money(paidUsd), '', sub('£' + money(paidGbpActual) + ' paid' + (paidGbpEst > 0 ? ' · ≈£' + money(paidGbpEst) + ' est' : '')))}
        ${statCardCmp('Due', '$' + money(dueUsd), dueUsd > 0 ? 'warn' : 'ok', sub('≈ £' + money(dueGbpEst) + ' due'))}
        ${statCardCmp('Open POs', open.length, '', '')}
        ${statCardCmp('Overdue', overdue.length, overdue.length ? 'bad' : 'ok', '')}
      </div>
      ${mismatchBanner}
      <div class="ag-toolbar">
        <select class="ag-select" id="po-filter" style="width:auto;">
          <option value="">All stages</option>
          ${PO_STAGES.map(s => `<option value="${s.key}" ${state.poFilter === s.key ? 'selected' : ''}>${esc(s.label)}</option>`).join('')}
          <option value="cancelled" ${state.poFilter === 'cancelled' ? 'selected' : ''}>Cancelled</option>
        </select>
        <label style="font-size:12px;display:inline-flex;align-items:center;gap:5px;cursor:pointer;color:var(--ag-muted,#888);"><input type="checkbox" id="po-show-archived" ${showArchived ? 'checked' : ''}> Show archived</label>
        <button class="ag-btn" onclick="AGInv.newPo()">+ New PO</button>
        <button class="ag-btn secondary" id="po-sort-arrival" title="Order by soonest expected arrival: Arrived, then ETA whse, ETA port, Shipped, Est done, Production, Ordered">⇅ Arrival order</button>
        <button class="ag-btn secondary" onclick="AGInv.refreshList(this)" title="Reload the list with the latest data">↻ Refresh</button>
      </div>
      <div style="overflow-x:auto;">
      <table class="ag-table"><thead><tr>
        ${poTh('PO Number', 'po_number')}${poTh('Supplier', 'supplier')}${poTh('Status', 'status')}
        ${PO_MILESTONES.map(m => poTh(m.l, m.k)).join('')}
        ${poTh('Lines', 'lines', 'num')}<th>Payment</th><th>Invoice</th>
      </tr></thead><tbody id="po-tbody"></tbody></table>
      </div>
      <div id="po-arrived-section" style="margin-top:10px;"></div>`;
    $('#po-filter').addEventListener('change', e => { state.poFilter = e.target.value; renderPosList(root, p); });
    const saChk = $('#po-show-archived'); if (saChk) saChk.addEventListener('change', e => { state.poShowArchived = e.target.checked; renderPosList(root, p); });
    if (state.poSort === undefined) state.poSort = { key: 'arrival', dir: 'asc' }; // default: soonest expected arrival first (coalesced cascade)
    const arrBtn = $('#po-sort-arrival');
    if (arrBtn) {
      if (state.poSort && state.poSort.key === 'arrival') { arrBtn.style.fontWeight = '700'; arrBtn.style.borderColor = 'var(--ag-accent,#c0261c)'; }
      arrBtn.addEventListener('click', () => { state.poSort = { key: 'arrival', dir: 'asc' }; renderPosList(root, p); });
    }
    let filtered = state.poFilter ? visiblePos.filter(x => poStageKey(x.status) === state.poFilter) : visiblePos;
    /* Apply the active column sort. Empty dates/strings sort last on ascending
       so undated POs don't crowd the top. */
    if (state.poSort && state.poSort.key) {
      const STAGE_ORDER = ['pre_purchase', 'in_production', 'completed', 'arrived', 'cancelled'];
      const val = (po, k) => {
        switch (k) {
          case 'supplier': return (po.supplier || '').toLowerCase();
          case 'status': return STAGE_ORDER.indexOf(poStageKey(po.status));
          case 'lines': return po.lines_count || 0;
          case 'po_number': return (po.po_number || '').toLowerCase();
          case 'arrival': return arrivalKey(po); // coalesced soonest-expected-arrival date
          default: return String(po[k] || '').slice(0, 10); // any milestone date column
        }
      };
      const dir = state.poSort.dir === 'asc' ? 1 : -1;
      filtered = filtered.slice().sort((a, b) => {
        const va = val(a, state.poSort.key), vb = val(b, state.poSort.key);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        const ea = va === '', eb = vb === '';
        if (ea !== eb) return ea ? 1 : -1;                 // empties always last
        return String(va).localeCompare(String(vb)) * dir;
      });
    }
    const poRowHtml = po => {
      /* "Paid" = the USD payment amount entered (operator records the payment in
         USD when it's made), OR a GBP amount paid, OR a paid-DATE recorded. */
      const depPaid = (parseFloat(po.deposit_usd) || 0) > 0 || (parseFloat(po.deposit_gbp_paid) || 0) > 0 || !!String(po.deposit_paid_date || '').trim();
      const balPaid = (parseFloat(po.balance_usd) || 0) > 0 || (parseFloat(po.balance_gbp_paid) || 0) > 0 || !!String(po.balance_paid_date || '').trim();
      const poOpen = po.status && ['arrived', 'cancelled'].indexOf(poStageKey(po.status)) === -1;
      /* Editable date cell. Click to pick a date; saves immediately (see
         onPoDateCellClick). Empty shows a faint ＋ affordance. The dashed
         underline signals it's editable, matching the stock inline editor. */
      const cell = m => {
        const raw = po[m.k];
        const d = raw ? String(raw).slice(0, 10) : '';
        const overdue = m.k === 'eta' && poOpen && d && d < today;
        const inner = d ? `${esc(fmtFriendly(d))}${overdue ? ' ⚠' : ''}` : '<span class="ag-muted" style="opacity:.45;">＋</span>';
        return `<td class="po-date-cell" data-po="${esc(po.po_number || '')}" data-field="${esc(m.k)}" data-value="${esc(d)}" data-label="${esc(m.l)}" title="Click to set ${esc(m.l)}" style="font-size:11px;white-space:nowrap;cursor:pointer;border-bottom:1px dashed rgba(0,0,0,0.18);${overdue ? 'color:#dc2626;font-weight:600;' : ''}">${inner}</td>`;
      };
      const poPill = po.preorder === 'live' ? '<span class="ag-pill ok" style="font-size:9px;margin-left:6px;">PRE-ORDER LIVE</span>'
        : po.preorder === 'armed' ? '<span class="ag-pill info" style="font-size:9px;margin-left:6px;">PRE-ORDER armed</span>'
        : po.preorder === 'bundle' ? '<span class="ag-pill mute" style="font-size:9px;margin-left:6px;">bundle</span>' : '';
      /* USD integrity flag: header goods_value_usd should equal Σ(line qty × unit USD)
         (lines_usd, from the backend). Mismatched rows get a ⚠ + an amber tint. */
      const _usdMism = poUsdMismatch(po);
      const usdFlag = _usdMism
        ? `<span title="Header $${fmtNum(Math.round(parseFloat(po.goods_value_usd) || 0))} ≠ Σ lines $${fmtNum(Math.round(parseFloat(po.lines_usd) || 0))}. Open to reconcile" style="margin-left:6px;color:#b45309;font-weight:700;cursor:help;">⚠</span>`
        : '';
      return `<tr class="clickable" data-po="${esc(po.po_number || '')}"${_usdMism ? ' style="background:rgba(180,83,9,0.09);"' : ''}>
        <td><strong>${esc(po.po_number || '')}</strong>${usdFlag}${poPill}</td>
        <td>${esc(po.supplier || '')}</td>
        <td><span class="ag-pill ${poPillClass(po.status)}">${esc(poStageLabel(po.status))}</span></td>
        ${PO_MILESTONES.map(cell).join('')}
        <td class="num">${po.lines_count || 0}</td>
        <td style="font-size:11px;white-space:nowrap;"><span style="color:${depPaid ? '#16a34a' : '#ccc'}" title="Deposit ${depPaid ? 'paid' : 'unpaid'}">● Dep</span> <span style="color:${balPaid ? '#16a34a' : '#ccc'}" title="Balance ${balPaid ? 'paid' : 'unpaid'}">● Bal</span>${(parseInt(po.extra_payments_count, 10) || 0) > 0 ? ` <span style="color:#16a34a" title="${parseInt(po.extra_payments_count, 10)} further payment${parseInt(po.extra_payments_count, 10) === 1 ? '' : 's'} recorded">+${parseInt(po.extra_payments_count, 10)}</span>` : ''}</td>
        <td class="ag-inv-drop" data-po="${esc(po.po_number)}"
            ondragover="event.preventDefault();event.stopPropagation();this.style.outline='2px dashed var(--ag-info,#2563eb)';this.style.outlineOffset='-2px';"
            ondragleave="this.style.outline='';"
            ondrop="AGInv.dropInvoice(event, this.dataset.po)"
            title="Drag an invoice (PDF/image) here to upload">${po.invoice_url
          ? `<a href="${esc(po.invoice_url)}" target="_blank" rel="noopener" onclick="event.stopPropagation();" title="Open invoice, drag a new file to replace">📄 link</a>`
          : `<span class="ag-muted" style="font-size:11px;opacity:.55;">⬍ drop</span>`}</td>
      </tr>`;
    };
    /* Arrived / received POs go into their own collapsible section below the active
       list so a "done" PO (stock is in) doesn't sit confusingly among the live ones. */
    const arrivedRows = filtered.filter(po => poStageKey(po.status) === 'arrived');
    const activeRows  = filtered.filter(po => poStageKey(po.status) !== 'arrived');
    $('#po-tbody').innerHTML = activeRows.map(poRowHtml).join('');
    const arrSec = $('#po-arrived-section');
    if (arrSec) {
      arrSec.innerHTML = arrivedRows.length ? `
        <details class="ag-po-arrived"${state.poArrivedOpen ? ' open' : ''}>
          <summary style="cursor:pointer;user-select:none;font-size:13px;font-weight:600;padding:8px 4px;">
            Arrived / Received <span class="ag-pill ok" style="font-size:10px;margin-left:4px;">${arrivedRows.length}</span>
          </summary>
          <div style="overflow-x:auto;">
          <table class="ag-table"><thead><tr>
            ${poTh('PO Number', 'po_number')}${poTh('Supplier', 'supplier')}${poTh('Status', 'status')}
            ${PO_MILESTONES.map(m => poTh(m.l, m.k)).join('')}
            ${poTh('Lines', 'lines', 'num')}<th>Payment</th><th>Invoice</th>
          </tr></thead><tbody id="po-arrived-tbody">${arrivedRows.map(poRowHtml).join('')}</tbody></table>
          </div>
        </details>` : '';
      const det = arrSec.querySelector('details');
      if (det) det.addEventListener('toggle', () => { state.poArrivedOpen = det.open; });
    }
    root.querySelectorAll('#po-tbody tr[data-po], #po-arrived-tbody tr[data-po]').forEach(tr => tr.addEventListener('click', () => editPo(tr.dataset.po)));
    /* Inline date editing: each date cell stops the row-click (so the drawer
       doesn't open) and turns into a date picker that saves on its own. */
    root.querySelectorAll('.po-date-cell').forEach(c => c.addEventListener('click', onPoDateCellClick));
    root.querySelectorAll('th.po-sort').forEach(th => th.addEventListener('click', () => {
      const k = th.dataset.sort, cur = state.poSort || {};
      state.poSort = { key: k, dir: (cur.key === k && cur.dir === 'asc') ? 'desc' : 'asc' };
      renderPosList(root, p);
    }));
  }
  function poPillClass(s) {
    const k = poStageKey(s);
    if (k === 'arrived') return 'ok';
    if (k === 'cancelled') return 'mute';
    if (k === 'pre_purchase') return 'info';
    return 'warn'; // in_production / completed
  }
  /* Friendly stage label for the list pill (normalises legacy statuses). */
  function poStageLabel(status) {
    const k = poStageKey(status);
    if (k === 'cancelled') return 'Cancelled';
    const s = PO_STAGES.find(x => x.key === k);
    return s ? s.label : (status || '');
  }
  /* Compact lifecycle timeline for the list. Shows only the milestones that
     have a date, in order, with the warehouse ETA flagged red when overdue. */
  function renderPoTimeline(po, today) {
    const ms = [
      { k: 'date_ordered', l: 'Ordered' },
      { k: 'production_date', l: 'Production' },
      { k: 'est_completion_date', l: 'Est. done' },
      { k: 'date_shipped', l: 'Shipped' },
      { k: 'eta_port', l: 'ETA port' },
      { k: 'eta', l: 'ETA whse' },
      { k: 'date_received', l: 'Arrived' }
    ];
    const isOpen = po.status && ['arrived', 'cancelled'].indexOf(poStageKey(po.status)) === -1;
    const set = ms.filter(m => po[m.k]);
    if (!set.length) return '<span class="ag-muted">-</span>';
    return `<div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">${set.map((m, i) => {
      const d = String(po[m.k]).slice(0, 10);
      const overdue = m.k === 'eta' && isOpen && d < today;
      return `${i ? '<span style="color:#d4d4d4;">›</span>' : ''}<span style="white-space:nowrap;${overdue ? 'color:#dc2626;font-weight:600;' : ''}"><span style="font-size:8px;text-transform:uppercase;letter-spacing:.3px;color:var(--ag-text-dim,#999);">${m.l}</span> ${esc(fmtFriendly(d))}${overdue ? ' ⚠' : ''}</span>`;
    }).join('')}</div>`;
  }
  /* Consistent section header across the PO drawer (form + receiving + docs). */
  function poSectionTitle(t) {
    return `<div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:var(--ag-text-dim,#888);margin:22px 0 8px;border-bottom:1px solid var(--ag-border,#e5e5e5);padding-bottom:4px;">${t}</div>`;
  }

  /* ─── Per-product pre-order override control (Manual / Auto / Force ON / Force OFF) ──
     'auto' (shown as Manual) = only via Set pre-orders LIVE or On. 'allow' (shown
     as Auto) = goes on pre-order by itself when it sells out and an open PO with
     an ETA is bringing more, with the PO date (Michael's option B, 13 Oct 2026).
     Shared by the Pre-orders tab rows AND the PO drawer's per-line panel. A
     bundle BOX row resolves server-side to its parent bundle(s). That's what
     the customer actually pre-orders. The whole-PO "Set pre-orders LIVE" button
     still works; these overrides win over it per product. */
  function ensurePreorderSegCss() {
    if (document.getElementById('ag-poseg-css')) return;
    const st = document.createElement('style');
    st.id = 'ag-poseg-css';
    st.textContent = `
      .ag-poseg{display:inline-flex;border:1px solid var(--ag-border,#ccc);border-radius:6px;overflow:hidden;vertical-align:middle;}
      .ag-poseg-btn{border:0;background:var(--ag-panel-2,#f4f4f4);color:var(--ag-text,#333);font:inherit;font-size:11px;padding:3px 9px;cursor:pointer;border-left:1px solid var(--ag-border,#ccc);}
      .ag-poseg-btn:first-child{border-left:0;}
      .ag-poseg-btn:hover:not(.active){background:var(--ag-panel,#fff);}
      .ag-poseg-btn.active{color:#fff;}
      .ag-poseg-btn[data-po-state="auto"].active{background:var(--ag-muted,#777);border-color:var(--ag-muted,#777);}
      .ag-poseg-btn[data-po-state="allow"].active{background:var(--ag-info,#2563eb);border-color:var(--ag-info,#2563eb);}
      .ag-poseg-btn[data-po-state="on"].active{background:var(--ag-ok,#1f7a37);border-color:var(--ag-ok,#1f7a37);}
      .ag-poseg-btn[data-po-state="off"].active{background:var(--ag-bad,#a30d26);border-color:var(--ag-bad,#a30d26);}
      .ag-poseg-btn:disabled{opacity:.5;cursor:default;}`;
    document.head.appendChild(st);
  }
  function preorderSeg(sku, override) {
    const s = override || 'auto';
    const mk = (st, lbl, tip) => `<button type="button" class="ag-poseg-btn${s === st ? ' active' : ''}" data-po-seg="${esc(sku)}" data-po-state="${st}" title="${tip}">${lbl}</button>`;
    return `<span class="ag-poseg">${mk('auto', 'Manual', 'Only goes on pre-order when you press Set pre-orders LIVE on the PO, or On')}${mk('allow', 'Auto', 'Goes on pre-order by itself when it sells out and an open PO with an ETA is bringing more, with the PO date. Back to normal stock when it lands')}${mk('on', 'On', 'Force pre-order ON, sell past 0 stock')}${mk('off', 'Off', 'Force pre-order OFF, stop selling at 0')}</span>` +
      (s === 'mixed' ? '<span class="ag-muted" style="font-size:10px;margin-left:6px;" title="This box feeds bundles that are in different states">mixed</span>' : '');
  }
  /* Attach click handlers to every seg control inside `container`. afterFn(result)
     runs after a successful change (e.g. reload the tab). */
  function wirePreorderSegs(container, afterFn) {
    container.querySelectorAll('button[data-po-seg]').forEach(btn => {
      btn.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        if (btn.classList.contains('active')) return; // already this state
        const sku = btn.dataset.poSeg, wanted = btn.dataset.poState;
        const seg = btn.closest('.ag-poseg');
        seg.querySelectorAll('button').forEach(b => { b.disabled = true; });
        try {
          const r = await AG_ADMIN.api('ui_preorder_set_product', { sku, state: wanted });
          if (!r || r.ok === false) throw new Error((r && r.error) || 'failed');
          const n = (r.keys || []).length;
          const kindMsg = r.kind === 'box' ? ` · ${n} bundle${n === 1 ? '' : 's'}` : '';
          AG_ADMIN.toast(wanted === 'auto' ? `Pre-order: Manual${kindMsg}` : wanted === 'allow' ? `Pre-order: Auto, follows the PO${kindMsg}` : `Pre-order forced ${wanted.toUpperCase()}${kindMsg}`);
          if (r.warning) AG_ADMIN.toast(r.warning, true);
          seg.querySelectorAll('button').forEach(b => b.classList.toggle('active', b.dataset.poState === wanted));
          if (afterFn) afterFn(r);
        } catch (e) {
          AG_ADMIN.toast('Pre-order toggle failed: ' + (e.message || e), true);
        } finally {
          seg.querySelectorAll('button').forEach(b => { b.disabled = false; });
        }
      });
    });
  }
  /* PO drawer: per-line pre-order controls. Fetches each line's current override
     (boxes resolved to parents) then renders a compact toggle per line. */
  async function hydratePoPreorderPanel(po) {
    const wrap = document.getElementById('po-preorder-lines');
    if (!wrap) return;
    const skus = [...new Set(((po && po.lines) || []).map(l => String(l.sku || '').trim()).filter(Boolean))];
    if (!skus.length) { wrap.innerHTML = ''; return; }
    wrap.innerHTML = '<div class="ag-muted" style="font-size:11px;"><span class="ag-spinner" style="width:10px;height:10px;"></span> loading per-product pre-order controls…</div>';
    let states = {};
    try { const r = await AG_ADMIN.api('ui_preorder_get_states', { skus }); states = (r && r.states) || {}; }
    catch (e) { wrap.innerHTML = `<div class="ag-muted" style="font-size:11px;color:#a30d26;">Couldn't load pre-order controls: ${esc(e.message || e)}</div>`; return; }
    ensurePreorderSegCss();
    wrap.innerHTML = `
      <div style="border:1px solid var(--ag-border);border-radius:6px;padding:10px 12px;">
        <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:var(--ag-muted,#666);margin-bottom:8px;">Per-product pre-order
          <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;">· pick which lines are on/off, overrides the whole-PO button above</span></div>
        <table class="ag-table" style="background:transparent;"><tbody>
          ${skus.map(sku => {
            const st = states[sku] || { override: 'auto', kind: 'simple', controls: [] };
            const line = ((po && po.lines) || []).find(l => String(l.sku || '').trim() === sku) || { sku };
            return `<tr>
              <td>${productCell({ product_title: line.title || line.product_title || sku, sku }, { compact: true })}
                ${st.kind === 'box' && st.controls && st.controls.length ? `<div class="ag-muted" style="font-size:10px;" title="${esc(st.controls.join(', '))}">↳ controls ${esc(st.controls.length === 1 ? st.controls[0] : st.controls.length + ' bundles')}</div>` : ''}</td>
              <td style="text-align:right;white-space:nowrap;">${preorderSeg(sku, st.override)}</td>
            </tr>`;
          }).join('')}
        </tbody></table>
      </div>`;
    wirePreorderSegs(wrap, null);
  }

  /* ─── Tab 6: Pre-orders / backlog ─────────────────────────── */
  /* ─── Pre-orders tab: search, sort and the "All products" view ──
     Michael, 13 Oct 2026: find any product by title and set its pre-order
     choice (Manual / Auto / On / Off) before it sells out, not only the ones
     that already have open orders. The view, search text and sort are kept per
     browser; "All products" reads ui_preorder_catalog once per page load. */
  const PO_VIEW_KEY = 'agPoView';
  let poCatalogCache = null;
  function poViewState() {
    const d = { mode: 'open', q: '', sort: 'title' };
    try { return Object.assign(d, JSON.parse(localStorage.getItem(PO_VIEW_KEY) || '{}') || {}); } catch (_) { return d; }
  }
  function savePoView(v) { try { localStorage.setItem(PO_VIEW_KEY, JSON.stringify(v)); } catch (_) {} }
  function poRowAttrs(i) {
    const name = ((i.product_title || i.title || '') + ' ' + (i.variant_title || '')).trim().toLowerCase();
    return `data-po-name="${esc(name)}" data-po-find="${esc((name + ' ' + (i.sku || '')).toLowerCase())}" data-po-open="${Number(i.open) || 0}"`;
  }
  /* "15 Oct · KY012ST-2026", flagged late when the ETA has passed and the PO
     is still open (goods not received). Michael, 13 Oct 2026. */
  function nextDeliveryCell(i) {
    if (!i.next_po) return '<span class="ag-muted">none</span>';
    const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(i.next_po_eta || ''));
    let when = esc(i.next_po_label || ''), late = false;
    if (m) {
      when = `${Number(m[3])} ${MON[Number(m[2]) - 1]}`;
      const t = new Date(); const todayIso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
      late = i.next_po_eta < todayIso;
    }
    return `<span title="${esc(i.next_po_label ? 'Customers see: ' + i.next_po_label : '')}">${when}</span>` +
      (late ? ' <span class="ag-pill bad" style="font-size:9px;">late</span>' : '') +
      `<div class="ag-muted" style="font-size:10px;">${esc(i.next_po)}</div>`;
  }
  function preorderToolbar(v) {
    const modeBtn = (m, lbl) => `<button type="button" class="ag-poview-btn${v.mode === m ? ' active' : ''}" data-po-mode="${m}">${lbl}</button>`;
    return `<div class="ag-po-toolbar" style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;margin:0 0 16px;">
        <span class="ag-poview">${modeBtn('open', 'Open orders')}${modeBtn('all', 'All products')}</span>
        <input type="search" id="po-q" value="${esc(v.q)}" placeholder="Filter by title or SKU" autocomplete="off"
          style="flex:1 1 220px;max-width:340px;padding:6px 10px;border:1px solid var(--ag-border,#ccc);border-radius:6px;font:inherit;font-size:13px;background:var(--ag-panel,#fff);color:var(--ag-text,#333);">
        <select id="po-sort" style="padding:6px 8px;border:1px solid var(--ag-border,#ccc);border-radius:6px;font:inherit;font-size:13px;background:var(--ag-panel,#fff);color:var(--ag-text,#333);">
          <option value="title"${v.sort === 'title' ? ' selected' : ''}>Title A to Z</option>
          <option value="title-desc"${v.sort === 'title-desc' ? ' selected' : ''}>Title Z to A</option>
          <option value="open"${v.sort === 'open' ? ' selected' : ''}>Most open orders</option>
        </select>
        <span id="po-count" class="ag-muted" style="font-size:12px;"></span>
      </div>`;
  }
  function ensurePoViewCss() {
    if (document.getElementById('ag-poview-css')) return;
    const st = document.createElement('style');
    st.id = 'ag-poview-css';
    st.textContent = `
      .ag-poview{display:inline-flex;border:1px solid var(--ag-border,#ccc);border-radius:6px;overflow:hidden;}
      .ag-poview-btn{border:0;background:var(--ag-panel-2,#f4f4f4);color:var(--ag-text,#333);font:inherit;font-size:12px;padding:6px 12px;cursor:pointer;border-left:1px solid var(--ag-border,#ccc);}
      .ag-poview-btn:first-child{border-left:0;}
      .ag-poview-btn.active{background:var(--ag-text,#1a1d21);color:#fff;}`;
    document.head.appendChild(st);
  }
  /* Show/hide rows by the search text and re-order each table by the chosen
     sort, inside whichever view is showing. A PO card with no matching rows hides. */
  function applyPreorderFilter(root) {
    const v = poViewState();
    const view = root.querySelector(v.mode === 'all' ? '#po-all-view' : '#po-open-view');
    if (!view) return;
    const tokens = String(v.q || '').toLowerCase().split(/\s+/).filter(Boolean);
    let shown = 0, total = 0;
    view.querySelectorAll('tbody').forEach(tb => {
      const rows = Array.from(tb.querySelectorAll('tr[data-po-name]'));
      if (!rows.length) return;
      rows.sort((a, b) => {
        if (v.sort === 'open') return (Number(b.dataset.poOpen) - Number(a.dataset.poOpen)) || a.dataset.poName.localeCompare(b.dataset.poName);
        const c = a.dataset.poName.localeCompare(b.dataset.poName);
        return v.sort === 'title-desc' ? -c : c;
      });
      rows.forEach(tr => {
        const hit = tokens.every(t => tr.dataset.poFind.indexOf(t) >= 0);
        tr.style.display = hit ? '' : 'none';
        total++; if (hit) shown++;
        tb.appendChild(tr);
      });
      const card = tb.closest('[data-po-card]');
      if (card) card.style.display = rows.some(tr => tr.style.display !== 'none') ? '' : 'none';
    });
    const cnt = root.querySelector('#po-count');
    if (cnt) cnt.textContent = total ? (shown === total ? `${total} products` : `${shown} of ${total} products`) : '';
  }
  async function renderPreorderCatalog(root) {
    const box = root.querySelector('#po-all-view');
    if (!box) return;
    if (!poCatalogCache) {
      box.innerHTML = '<div class="ag-muted" style="padding:16px;">Loading every product…</div>';
      try {
        const r = await AG_ADMIN.api('ui_preorder_catalog', {});
        poCatalogCache = (r && r.items) || [];
      } catch (e) {
        box.innerHTML = `<div class="ag-empty">Could not load the product list: ${esc(e.message || e)}</div>`;
        return;
      }
    }
    const items = poCatalogCache;
    /* The pre-order tag is product-level, so every size of a pre-order product
       reads is_preorder; a size with stock on hand is in stock, not on pre-order. */
    const status = i => i.on_hand > 0 ? '<span class="ag-pill ok">In stock</span>'
      : i.is_preorder ? '<span class="ag-pill info">Pre-order</span>' : '<span class="ag-pill warn">Sold out</span>';
    box.innerHTML = `<div style="background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:4px;padding:16px;">
        <table class="ag-table"><thead><tr>
          <th>Product</th><th class="num">Stock</th><th>Status</th><th>Next delivery</th><th>Pre-order</th>
        </tr></thead><tbody>${items.map(i => `<tr ${poRowAttrs(i)}>
          <td>${productCell({ product_title: i.product_title, variant_title: i.variant_title, sku: i.kind === 'parent' ? '' : i.sku }, { compact: true })}${i.kind === 'parent' ? '<div class="ag-muted" style="font-size:10px;">bundle</div>' : ''}</td>
          <td class="num">${Number(i.on_hand) || 0}</td>
          <td>${status(i)}</td>
          <td>${nextDeliveryCell(i)}</td>
          <td style="min-width:190px;white-space:nowrap;">${preorderSeg(i.sku, i.override)}</td>
        </tr>`).join('')}</tbody></table>
      </div>`;
    ensurePreorderSegCss();
    wirePreorderSegs(box, r => {
      const want = r && r.state;
      (r && r.keys || []).forEach(k => { const it = poCatalogCache.find(x => x.sku === k); if (it && want) it.override = want; });
    });
    applyPreorderFilter(root);
  }
  function wirePreorderToolbar(root) {
    ensurePoViewCss();
    const show = () => {
      const v = poViewState();
      const open = root.querySelector('#po-open-view'), all = root.querySelector('#po-all-view');
      if (open) open.hidden = v.mode === 'all';
      if (all) all.hidden = v.mode !== 'all';
      root.querySelectorAll('button[data-po-mode]').forEach(b => b.classList.toggle('active', b.dataset.poMode === v.mode));
      if (v.mode === 'all') renderPreorderCatalog(root); else applyPreorderFilter(root);
    };
    root.querySelectorAll('button[data-po-mode]').forEach(b => b.addEventListener('click', () => {
      savePoView(Object.assign(poViewState(), { mode: b.dataset.poMode })); show();
    }));
    const q = root.querySelector('#po-q');
    if (q) q.addEventListener('input', () => { savePoView(Object.assign(poViewState(), { q: q.value })); applyPreorderFilter(root); });
    const so = root.querySelector('#po-sort');
    if (so) so.addEventListener('change', () => { savePoView(Object.assign(poViewState(), { sort: so.value })); applyPreorderFilter(root); });
    show();
  }

  function renderPreorders(root, p) {
    const items = (p && p.items) || [];
    const byPo = (p && p.by_po) || {};
    const poView = poViewState();
    if (!items.length) {
      root.innerHTML = `<h2 class="ag-h2">Pre-orders &amp; backlog</h2>
        ${preorderToolbar(poView)}
        <div id="po-open-view"><div class="ag-empty">
          No unfulfilled orders right now.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;">
            This view aggregates Shopify's open (unfulfilled) backlog. If you expected items here, click <strong>Pull now</strong> in the tab bar to refresh. Use <strong>All products</strong> to set a product's pre-order choice ahead of time.
          </div>
        </div></div>
        <div id="po-all-view" hidden></div>`;
      wirePreorderToolbar(root);
      return;
    }
    const preorderItems = items.filter(i => i.is_preorder);
    const backorderItems = items.filter(i => !i.is_preorder);
    const totalUnits = items.reduce((a, i) => a + i.open, 0);
    const atCap = (p && p.at_cap) || [];
    const needsAttention = (p && p.needs_attention) || [];
    const capRun = (p && p.cap_run) || null;
    const capErrors = (capRun && capRun.errors) || [];
    root.innerHTML = `
      <h2 class="ag-h2">Pre-orders &amp; backlog</h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(200px,1fr));margin-bottom:24px;">
        ${statCard('Pre-orders', preorderItems.length + ' SKUs', '')}
        ${statCard('Backorders', backorderItems.length + ' SKUs', '')}
        ${statCard('Total units', totalUnits, '')}
        ${statCard('Linked POs', Object.keys(byPo).filter(k => k !== '(no linked PO)').length, '')}
        ${statCard('At cap', atCap.length + ' SKUs', atCap.length > 0 ? 'warn' : 'ok')}
        ${(needsAttention.length || capErrors.length) ? statCard('Needs attention', (needsAttention.length + capErrors.length), 'bad') : ''}
      </div>
      ${preorderToolbar(poView)}
      <div id="po-all-view" hidden></div>
      <div id="po-open-view">
      ${(needsAttention.length || capErrors.length) ? `
        <div style="background:#fff8ec;border:1px solid #f0d9a8;border-radius:6px;padding:14px 16px;margin-bottom:20px;">
          <strong style="color:#8a5a12;">⚠ Needs attention</strong>
          ${needsAttention.length ? `<div style="margin-top:6px;font-size:12px;line-height:1.5;">${needsAttention.length} capped SKU${needsAttention.length === 1 ? '' : 's'} with <strong>no linked PO</strong>. These can't auto-reopen when the cap clears. Set a linked PO on: ${needsAttention.map(i => esc(i.sku)).join(', ')}</div>` : ''}
          ${capErrors.length ? `<div style="margin-top:6px;font-size:12px;line-height:1.5;">${capErrors.length} cap-enforcement error${capErrors.length === 1 ? '' : 's'} on the last run${capRun && capRun.at ? ' (' + esc(fmtFriendly(capRun.at)) + ')' : ''}:
            <ul style="margin:4px 0 0 16px;">${capErrors.slice(0, 8).map(e => `<li>${esc(e.title || e.product_id || '')}: ${esc(e.error || '')}</li>`).join('')}</ul></div>` : ''}
        </div>` : ''}
      ${atCap.length ? `
        <div style="background:#fff5f5;border:1px solid #f0bdbd;border-radius:6px;padding:14px 16px;margin-bottom:20px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <strong style="color:#9a1f1f;">${atCap.length} SKU${atCap.length === 1 ? '' : 's'} currently at cap</strong>
            <span class="ag-muted" style="font-size:11px;">sales blocked at 0 stock + pre-order tag stripped · reopens automatically when cap clears</span>
          </div>
          <table class="ag-table" style="background:transparent;">
            <thead><tr><th>Product</th><th>Linked PO</th><th class="num">Open</th><th class="num">Max</th></tr></thead>
            <tbody>${atCap.map(i => `<tr class="clickable" data-sku="${esc(i.sku)}">
              <td>${productCell({ product_title: i.product_title || i.title, variant_title: i.variant_title, sku: i.sku }, { compact: true })}</td>
              <td><span class="ag-muted">${esc(i.linked_po || '-')}</span></td>
              <td class="num"><strong>${i.open}</strong></td>
              <td class="num">${i.max}</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>` : ''}
      <h3 style="margin-top:24px;">By linked PO</h3>
      ${Object.keys(byPo).sort().map(po => `
        <div data-po-card style="background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:4px;padding:16px;margin-bottom:12px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <strong>${esc(po)}</strong>
            ${byPo[po][0].po_eta ? `<span class="ag-muted">ETA ${esc(fmtFriendly(byPo[po][0].po_eta))}${byPo[po][0].po_label ? ' · ' + esc(byPo[po][0].po_label) : ''}</span>` : ''}
          </div>
          <table class="ag-table"><thead><tr>
            <th>Product</th><th>Type</th><th class="num">Open</th><th>Next delivery</th><th class="num">Max</th><th>Cap usage</th><th>Pre-order</th>
          </tr></thead><tbody>${byPo[po].map(i => `<tr class="clickable" data-sku="${esc(i.sku)}" ${poRowAttrs(i)}>
            <td>${productCell({ product_title: i.product_title || i.title, variant_title: i.variant_title, sku: i.sku }, { compact: true })}</td>
            <td>${i.is_preorder
              ? `<span class="ag-pill info">Pre-order${i.dispatch_label ? ' · ' + esc(i.dispatch_label) : ''}</span>`
              : '<span class="ag-pill warn">Backorder</span>'}</td>
            <td class="num">${i.open}</td>
            <td>${nextDeliveryCell(i)}</td>
            <td class="num">${i.max || '∞'}</td>
            <td style="min-width:120px;">
              ${i.max ? `<div class="ag-slot-bar"><div class="fill ${i.pct >= 95 ? 'critical' : i.pct >= 70 ? '' : 'healthy'}" style="width:${Math.min(i.pct, 100)}%"></div></div>
                  <div class="ag-muted" style="font-size:11px;">${i.pct}%</div>` : '<span class="ag-muted">no cap set</span>'}
            </td>
            <td onclick="event.stopPropagation()" style="min-width:150px;white-space:nowrap;">
              ${preorderSeg(i.sku, i.override)}
              ${i.kind === 'box' && i.controls && i.controls.length ? `<div class="ag-muted" style="font-size:10px;margin-top:2px;" title="${esc(i.controls.join(', '))}">↳ ${esc(i.controls.length === 1 ? i.controls[0] : i.controls.length + ' bundles')}</div>` : ''}
            </td>
          </tr>`).join('')}</tbody></table>
        </div>`).join('')}
      </div>`;
    root.querySelectorAll('#po-open-view tr[data-sku]').forEach(tr => tr.addEventListener('click', () => openSkuDrawer(tr.dataset.sku)));
    ensurePreorderSegCss();
    wirePreorderSegs(root.querySelector('#po-open-view'), () => { poCatalogCache = null; refreshTab('preorders', true); });
    wirePreorderToolbar(root);
  }

  /* ─── Tab: Stock (full breakdown) ─────────────────────────── */
  /* ─── Stock search query language ─────────────────────────────
     A small DSL the operator can type into the Stock filter input.
     Designed for fast triage of 600+ SKUs without leaving the keyboard.

     Plain text (multi-token AND): `chrome 41` matches rows whose
     sku/title/variant/vendor/collection/type/notes contain BOTH tokens.

     Quoted phrase: `"adjustable dumbbell"` exact substring match.

     Negation: `-black` excludes rows matching `black`. Works on any
     token type (`-vendor:acme`, `-oos`, etc).

     Field prefixes. Narrow a token to one column:
       sku:KY008        product:dumbbell    title:dumbbell
       vendor:acme      variant:chrome      collection:dumbbells
       type:bars        notes:fragile       source:settings
       bucket:owned     lifecycle:hold      supplier:cdt

     Numeric comparisons (>, <, >=, <=, =, bare number = equals):
       onhand:>0   onhand:0   onhand:<=5   stock:>10
       cost:0      cost:>50   value:>500   value:<=100

     Flag keywords (no colon: bare):
       oos / out                on_hand == 0
       lowstock / low           1..5 on hand
       nocost / hascost         unit cost zero / non-zero
       hasnote / nonote         operator notes set / not
       active / inactive        SKU_Settings.active flag
       discontinued / onhold    lifecycle status
       preorder                 pre-order configured
       owned / collective       bucket shortcut
       tagged / untracked       bucket shortcut
       bundle                   bundle_parent bucket
       tracked                  Shopify tracked inventory
  */
  function tokeniseStockQuery(q) {
    const out = [];
    let i = 0;
    const n = q.length;
    while (i < n) {
      while (i < n && /\s/.test(q[i])) i++;
      if (i >= n) break;
      let neg = false;
      if (q[i] === '-' && i + 1 < n && !/\s/.test(q[i + 1]) && q[i + 1] !== '-') {
        neg = true; i++;
      }
      /* Field-prefix: word followed by ':'. */
      let field = '';
      const fStart = i;
      while (i < n && /[a-zA-Z_]/.test(q[i])) i++;
      if (i < n && q[i] === ':' && i > fStart) {
        field = q.slice(fStart, i).toLowerCase();
        i++;
      } else {
        i = fStart;
      }
      /* Value. Quoted or bare. */
      let value = '';
      if (q[i] === '"') {
        i++;
        const e = q.indexOf('"', i);
        if (e < 0) { value = q.slice(i); i = n; }
        else { value = q.slice(i, e); i = e + 1; }
      } else {
        const vStart = i;
        while (i < n && !/\s/.test(q[i])) i++;
        value = q.slice(vStart, i);
      }
      if (value) out.push({ neg, field, value });
    }
    return out;
  }

  const STOCK_TEXT_FIELDS = ['sku', 'product_title', 'variant_title', 'vendor',
    'primary_collection', 'product_type', 'notes', 'supplier', 'lifecycle_status'];

  /* Field weights for fuzzy scoring. Higher = stronger match signal.
     SKU + product title dominate; notes/lifecycle are tie-breakers. */
  const STOCK_FIELD_WEIGHTS = {
    sku: 12, product_title: 8, variant_title: 6,
    vendor: 4, primary_collection: 4, product_type: 4,
    supplier: 4, lifecycle_status: 3, notes: 5
  };

  /* Normalise a token for variant-tolerant comparison:
     - lowercase, & → and, drop trailing 's'/'es'/'ies' (simple stemmer)
     - SKU forms: also try a hyphen/underscore-stripped variant */
  function normaliseStockToken(t) {
    let s = String(t).toLowerCase().replace(/&/g, 'and');
    if (s.length > 4) {
      if (s.endsWith('ies')) s = s.slice(0, -3) + 'y';
      else if (s.endsWith('ses') || s.endsWith('xes') || s.endsWith('zes')) s = s.slice(0, -2);
      else if (s.endsWith('s') && !s.endsWith('ss')) s = s.slice(0, -1);
    }
    return s;
  }
  function stripPunct(s) { return String(s).toLowerCase().replace(/[-_/\\.\s]+/g, ''); }

  /* Bounded Levenshtein. Bails out as soon as the running minimum
     exceeds the threshold. Cheap for the comparisons we run (< 600 rows
     × a few tokens × short words). */
  function boundedEditDistance(a, b, threshold) {
    const al = a.length, bl = b.length;
    if (Math.abs(al - bl) > threshold) return threshold + 1;
    if (al === 0) return bl;
    if (bl === 0) return al;
    let prev = new Array(bl + 1);
    for (let j = 0; j <= bl; j++) prev[j] = j;
    for (let i = 1; i <= al; i++) {
      const curr = new Array(bl + 1);
      curr[0] = i;
      let rowMin = i;
      const ac = a.charCodeAt(i - 1);
      for (let j = 1; j <= bl; j++) {
        const cost = ac === b.charCodeAt(j - 1) ? 0 : 1;
        const v = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        curr[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > threshold) return threshold + 1;
      prev = curr;
    }
    return prev[bl];
  }

  /* Build a per-row haystack (cached on the row object once per render
     pass). Cheap because rows aren't reused across renders. Paint()
     rebuilds the filtered list each keystroke. We attach the cache
     under a non-enumerable property so it doesn't show up anywhere. */
  function ensureStockHay(row) {
    if (row._hay) return row._hay;
    const fieldsRaw = {};
    const fieldsNorm = {};
    const fieldsStripped = {};
    const fieldsWords = {};
    STOCK_TEXT_FIELDS.forEach(f => {
      const v = String(row[f] || '').toLowerCase();
      fieldsRaw[f] = v;
      fieldsStripped[f] = stripPunct(v);
      const words = v.split(/[^a-z0-9]+/).filter(Boolean);
      fieldsWords[f] = words;
      fieldsNorm[f] = words.map(normaliseStockToken).join(' ');
    });
    row._hay = { fieldsRaw, fieldsNorm, fieldsStripped, fieldsWords };
    return row._hay;
  }

  /* Score one plain-text token against a row.
     Tiers (best first):
       exact substring               weight × 10  (× 1.5 if whole-field equality)
       hyphen/punct-stripped match    weight × 9   (helps SKUs: "KY-008" ≈ "ky008")
       plural/singular stem match     weight × 8
       edit-distance 1 token match    weight × 5
       edit-distance 2 (len ≥ 6)      weight × 3   (lower bar: riskier)
     Returns 0 if nothing matched. */
  function scoreStockToken(token, row) {
    const lt = token.toLowerCase();
    if (!lt) return 0;
    const hay = ensureStockHay(row);
    const stripped = stripPunct(lt);
    const norm = normaliseStockToken(lt);
    let best = 0;
    for (const field of STOCK_TEXT_FIELDS) {
      const w = STOCK_FIELD_WEIGHTS[field] || 2;
      const raw = hay.fieldsRaw[field];
      if (!raw) continue;
      /* Tier 1: exact substring. */
      if (raw.includes(lt)) {
        const tier = raw === lt ? 15 : 10;
        if (w * tier > best) best = w * tier;
        continue;
      }
      /* Tier 2: punctuation-stripped substring (SKU friendliness). */
      if (stripped.length >= 3 && hay.fieldsStripped[field].includes(stripped)) {
        if (w * 9 > best) best = w * 9;
        continue;
      }
      /* Tier 3: plural/singular normalised substring. */
      if (norm.length >= 3 && hay.fieldsNorm[field].indexOf(norm) >= 0) {
        if (w * 8 > best) best = w * 8;
        continue;
      }
      /* Tiers 4–5: fuzzy edit-distance per word. Skip for very short
         tokens (false positives explode). */
      if (lt.length < 4) continue;
      const words = hay.fieldsWords[field];
      let localBest = 0;
      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        if (!word) continue;
        const lenDelta = Math.abs(word.length - lt.length);
        if (lenDelta > 2) continue;
        const d = boundedEditDistance(lt, word, 2);
        if (d === 1) localBest = Math.max(localBest, w * 5);
        else if (d === 2 && lt.length >= 6) localBest = Math.max(localBest, w * 3);
      }
      if (localBest > best) best = localBest;
    }
    return best;
  }

  function makeStockTextPred(field) {
    return v => {
      const lv = String(v).toLowerCase();
      return r => String(r[field] || '').toLowerCase().includes(lv);
    };
  }
  function makeStockNumPred(field) {
    return v => {
      const m = String(v).match(/^\s*(>=|<=|>|<|=)?\s*(-?\d+(?:\.\d+)?)\s*$/);
      if (!m) return () => false;
      const op = m[1] || '=';
      const num = parseFloat(m[2]);
      return r => {
        const x = parseFloat(r[field]) || 0;
        if (op === '>') return x > num;
        if (op === '<') return x < num;
        if (op === '>=') return x >= num;
        if (op === '<=') return x <= num;
        return x === num;
      };
    };
  }

  const STOCK_FIELD_PREDS = {
    sku:        makeStockTextPred('sku'),
    product:    makeStockTextPred('product_title'),
    title:      makeStockTextPred('product_title'),
    variant:    makeStockTextPred('variant_title'),
    vendor:     makeStockTextPred('vendor'),
    collection: makeStockTextPred('primary_collection'),
    type:       makeStockTextPred('product_type'),
    notes:      makeStockTextPred('notes'),
    note:       makeStockTextPred('notes'),
    source:     makeStockTextPred('cost_source'),
    lifecycle:  makeStockTextPred('lifecycle_status'),
    supplier:   makeStockTextPred('supplier'),
    bucket:     v => { const lv = String(v).toLowerCase();
                       return r => String(r.bucket || '').toLowerCase() === lv; },
    onhand:     makeStockNumPred('on_hand'),
    stock:      makeStockNumPred('on_hand'),
    cost:       makeStockNumPred('unit_cost'),
    value:      makeStockNumPred('value')
  };

  const STOCK_FLAG_PREDS = {
    oos:          r => r.on_hand === 0,
    out:          r => r.on_hand === 0,
    lowstock:     r => r.on_hand > 0 && r.on_hand <= 5,
    low:          r => r.on_hand > 0 && r.on_hand <= 5,
    nocost:       r => !(r.unit_cost > 0),
    hascost:      r => r.unit_cost > 0,
    hasnote:      r => !!(r.notes && r.notes.length),
    nonote:       r => !(r.notes && r.notes.length),
    active:       r => !!r.active,
    inactive:     r => !r.active,
    discontinued: r => /discontinued/i.test(r.lifecycle_status || ''),
    onhold:       r => /on\s*hold/i.test(r.lifecycle_status || ''),
    preorder:     r => !!r.is_preorder,
    owned:        r => r.bucket === 'owned',
    collective:   r => r.bucket === 'collective',
    tagged:       r => r.bucket === 'tagged',
    untracked:    r => r.bucket === 'untracked',
    bundle:       r => r.bucket === 'bundle_parent',
    dropship:     r => r.bucket === 'dropship',
    tracked:      r => r.tracked !== false
  };

  /* Build matchers that carry both a boolean test and a fuzzy score.
     Only plain-text bare tokens contribute non-trivial scores; field-
     prefixed / flag / numeric tokens stay precise (score 1 when they
     match, else 0). Negation flips test() and zeroes the score. */
  function buildStockPredicates(query) {
    const tokens = tokeniseStockQuery(query);
    return tokens.map(tok => {
      const { neg, field, value } = tok;
      let test, scoreFn;
      if (field && STOCK_FIELD_PREDS[field]) {
        const fn = STOCK_FIELD_PREDS[field](value);
        test = fn; scoreFn = r => (fn(r) ? 1 : 0);
      } else if (!field) {
        const flag = STOCK_FLAG_PREDS[value.toLowerCase()];
        if (flag) {
          test = flag; scoreFn = r => (flag(r) ? 1 : 0);
        } else {
          /* Plain-text bare token: fuzzy + ranked. */
          scoreFn = r => scoreStockToken(value, r);
          test = r => scoreFn(r) > 0;
        }
      } else {
        /* Unknown field prefix: treat as plain text. */
        scoreFn = r => scoreStockToken(value, r);
        test = r => scoreFn(r) > 0;
      }
      if (neg) {
        return { test: r => !test(r), score: r => (test(r) ? 0 : 1) };
      }
      return { test, score: scoreFn };
    });
  }

  /* Return total score across all predicates if every predicate passes,
     otherwise 0. Used both to filter (>0 = include) and to sort
     (higher = more relevant). */
  function scoreStockQuery(row, predicates) {
    let total = 0;
    for (let i = 0; i < predicates.length; i++) {
      const p = predicates[i];
      if (!p.test(row)) return 0;
      total += p.score(row);
    }
    return total;
  }

  function openStockSearchHelp() {
    /* Reuse the standard drawer overlay machinery for consistency
       (closes on ESC + backdrop click, animation, focus trap). */
    function onEsc(e) { if (e.key === 'Escape') close(); }
    const close = () => {
      const ov = document.getElementById('stock-help-overlay');
      if (ov) ov.remove();
      document.removeEventListener('keydown', onEsc); // remove on EVERY close, not only ESC
    };
    /* Toggle behaviour. Second click closes. */
    if (document.getElementById('stock-help-overlay')) { close(); return; }
    function ex(s) { return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
    const examples = [
      ['oos', 'every out-of-stock SKU'],
      ['oos vendor:cdt', 'OOS items from the CDT supplier'],
      ['nocost owned', 'owned SKUs with no unit cost set, fix these'],
      ['onhand:>0 cost:0', 'stocked items missing a cost (silent margin killers)'],
      ['lowstock -preorder', 'low-stock items that aren\'t pre-order placeholders'],
      ['value:>500', 'highest-£-value lines'],
      ['"adjustable dumbbell" chrome', 'exact phrase + extra token (AND)'],
      ['collection:dumbbells -bundle', 'dumbbell collection, exclude bundle parents'],
      ['lifecycle:hold', 'parked SKUs awaiting a decision'],
      ['hasnote', 'rows with operator notes, context flagged'],
      ['source:none oos', 'OOS items with neither Shopify nor settings cost']
    ];
    const fields = [
      ['sku:KY008',        'SKU contains KY008'],
      ['product:dumbbell', 'product title contains dumbbell'],
      ['title:dumbbell',   'same as product:'],
      ['variant:chrome',   'variant title contains chrome'],
      ['vendor:acme',      'vendor name contains acme'],
      ['collection:dumbbells', 'primary collection match'],
      ['type:bars',        'Shopify product_type match'],
      ['notes:fragile',    'operator notes contain fragile'],
      ['source:settings',  'cost source = settings (operator override)'],
      ['bucket:owned',     'exact bucket match (owned/collective/tagged/untracked/bundle_parent)'],
      ['lifecycle:hold',   'On Hold / Discontinued lifecycle'],
      ['supplier:cdt',     'supplier from SKU_Settings'],
      ['onhand:>0',        'numeric: >, <, >=, <=, = (or bare number for ==)'],
      ['cost:0',           'unit_cost equals 0'],
      ['value:>500',       'stock value > £500']
    ];
    const flags = [
      'oos / out', 'lowstock / low', 'nocost', 'hascost',
      'hasnote', 'nonote', 'active', 'inactive',
      'discontinued', 'onhold', 'preorder',
      'owned', 'collective', 'tagged', 'untracked', 'bundle', 'tracked'
    ];
    const html = `
      <div id="stock-help-overlay" style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:48px 16px;overflow:auto;">
        <div style="background:#fff;max-width:760px;width:100%;border-radius:8px;box-shadow:0 24px 60px rgba(0,0,0,0.25);padding:24px;">
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">
            <h2 style="margin:0;font-size:18px;">Stock search</h2>
            <span style="font-size:12px;color:#666;">multi-token AND match · case-insensitive</span>
            <span class="spacer" style="flex:1;"></span>
            <button id="stock-help-close" class="ag-btn secondary" style="padding:6px 10px;">Close</button>
          </div>

          <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.5px;margin:16px 0 6px;">Quick examples</h3>
          <ul style="margin:0;padding-left:18px;font-size:13px;line-height:1.7;">
            ${examples.map(([q, d]) => `<li><code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">${ex(q)}</code> · ${ex(d)}</li>`).join('')}
          </ul>

          <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.5px;margin:20px 0 6px;">Field prefixes</h3>
          <table style="width:100%;font-size:12px;border-collapse:collapse;">
            ${fields.map(([q, d]) => `<tr><td style="padding:3px 8px 3px 0;vertical-align:top;"><code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">${ex(q)}</code></td><td style="padding:3px 0;color:#555;">${ex(d)}</td></tr>`).join('')}
          </table>

          <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.5px;margin:20px 0 6px;">Flag keywords</h3>
          <div style="font-size:12px;line-height:1.9;">
            ${flags.map(f => `<code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;margin-right:6px;">${ex(f)}</code>`).join('')}
          </div>

          <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.5px;margin:20px 0 6px;">Fuzzy behaviour</h3>
          <ul style="margin:0;padding-left:18px;font-size:13px;line-height:1.7;">
            <li>Word order doesn't matter, <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">dumbbell adjustable</code> matches "Adjustable Dumbbell"</li>
            <li>Plurals/singulars are equivalent, <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">dumbbells</code> finds <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">dumbbell</code></li>
            <li>One-letter typos OK on tokens 4+ chars, <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">dumbel</code>, <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">adjusable</code>, <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">varient</code> all work</li>
            <li>Two-letter typos OK on tokens 6+ chars (weaker score)</li>
            <li>Hyphens and slashes in SKUs are ignored, <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">KY-008</code> finds <code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">KY008</code></li>
            <li>Results rank by relevance, exact matches first, fuzzy matches after</li>
            <li>Field-prefixed terms (<code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">vendor:cdt</code>) stay exact. Use plain text for fuzzy</li>
          </ul>

          <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:0.5px;margin:20px 0 6px;">Other syntax</h3>
          <ul style="margin:0;padding-left:18px;font-size:13px;line-height:1.7;">
            <li><code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">"exact phrase"</code>, wrap multi-word terms in quotes (skips fuzzy)</li>
            <li><code style="background:#f4f4f4;padding:1px 6px;border-radius:3px;">-term</code>, minus prefix excludes (works on text, fields, and flags)</li>
            <li>Plain text matches across SKU, product title, variant title, vendor, collection, type, supplier, lifecycle, notes</li>
          </ul>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', html);
    const ov = document.getElementById('stock-help-overlay');
    ov.addEventListener('click', e => { if (e.target === ov) close(); });
    document.getElementById('stock-help-close').addEventListener('click', close);
    document.addEventListener('keydown', onEsc);
  }

  function renderStock(root, p) {
    const h = (p && p.hero) || {};
    const rows = (p && p.rows) || [];
    const shopDomain = (p && p.shop_domain) || '';
    const primaryDomain = (p && p.primary_domain) || 'assassingoods.com';
    const fullValueHist = (p && (p.value_history || p.value_history_90d)) || [];
    const cutoverDate = (p && p.cutover_date) || '';
    if (!state.stockChartRange) state.stockChartRange = cutoverDate ? 'cutover' : '90d';
    /* Slice the full stock-value history to the operator-selected range. The
       backend returns the wider series (up to 400d) + the cutover date. */
    const stockHistFor = (range) => {
      if (range === 'all') return { data: fullValueHist, label: 'all time' };
      if (range === 'cutover' && cutoverDate) return { data: fullValueHist.filter(pt => String(pt.date) >= cutoverDate), label: 'since cutover' };
      const days = range === '30d' ? 30 : 90;
      const c = new Date(); c.setDate(c.getDate() - days);
      const cStr = c.toISOString().slice(0, 10);
      return { data: fullValueHist.filter(pt => String(pt.date) >= cStr), label: 'last ' + days + ' days' };
    };
    const valueHist = fullValueHist; // chart-block visibility guard below uses .length
    /* Default filter state. Owned only, mirror the headline KPI. */
    if (!state.stockBucket) state.stockBucket = 'owned';
    if (state.stockSearch == null) state.stockSearch = '';
    /* Bulk-selection state. A Set so toggle/has/clear are O(1). Lives
       on `state` so it survives re-paints when filters change. Cleared
       when the tab is unmounted (handled by setTab). */
    if (!state.stockSelected) state.stockSelected = new Set();
    root.innerHTML = `
      <h2 class="ag-h2">Stock</h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(200px,1fr));margin-bottom:16px;">
        ${statCard('Stock value (owned)', '£' + fmtNum(h.value_owned || 0), '')}
        ${statCard('Collective value', '£' + fmtNum(h.value_collective || 0), '')}
        ${(h.value_non_own_brand || 0) > 0 ? statCard('Non-AG vendor value', '£' + fmtNum(h.value_non_own_brand || 0) + ' (' + (h.non_own_brand_skus || 0) + ' SKUs)', '') : ''}
        ${(h.value_tagged || 0) > 0 ? statCard('Tagged excluded', '£' + fmtNum(h.value_tagged || 0), '') : ''}
        ${statCard('Total units (all)', h.total_units || 0, '')}
        ${statCard('Owned units', h.owned_units || 0, '')}
        ${(() => { const n = h.owned_no_cost_skus != null ? h.owned_no_cost_skus : (h.no_cost_skus || 0); return statCard('Owned · no cost', n, n > 0 ? 'warn' : 'ok'); })()}
      </div>

      ${valueHist.length ? `
        <div class="ag-chart" style="height:290px;margin-bottom:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
            <div class="label" id="stock-chart-label">Owned stock value · ${esc(stockHistFor(state.stockChartRange).label)}</div>
            <div id="stock-range-picker" style="display:flex;gap:4px;">
              ${cutoverDate ? `<button class="ag-btn secondary" data-range="cutover" style="font-size:11px;padding:2px 8px;">Since cutover</button>` : ''}
              <button class="ag-btn secondary" data-range="90d" style="font-size:11px;padding:2px 8px;">90d</button>
              <button class="ag-btn secondary" data-range="30d" style="font-size:11px;padding:2px 8px;">30d</button>
              <button class="ag-btn secondary" data-range="all" style="font-size:11px;padding:2px 8px;">All</button>
            </div>
          </div>
          <canvas id="ch-stock-value"></canvas>
        </div>` : ''}

      <div class="ag-toolbar" style="flex-wrap:wrap;">
        <input class="ag-input" id="stock-search" placeholder='Search: text, sku:, vendor:, onhand:>0, cost:0, oos, nocost, hasnote, lifecycle:hold, -black, "exact phrase"…' style="max-width:520px;flex:1;min-width:240px;" value="${esc(state.stockSearch)}">
        <button class="ag-btn secondary" id="stock-search-help" type="button" title="Search syntax help" aria-label="Search syntax help" style="padding:6px 10px;font-size:12px;">?</button>
        <select class="ag-select" id="stock-bucket" style="width:auto;">
          <option value="owned"         ${state.stockBucket === 'owned' ? 'selected' : ''}>Owned (counts toward stock value)</option>
          <option value="collective"    ${state.stockBucket === 'collective' ? 'selected' : ''}>Collective (${h.collective_skus || 0})</option>
          <option value="non_own_brand" ${state.stockBucket === 'non_own_brand' ? 'selected' : ''}>Non-AG vendor (${h.non_own_brand_skus || 0})</option>
          <option value="tagged"        ${state.stockBucket === 'tagged' ? 'selected' : ''}>Tagged excluded (${h.tagged_skus || 0})</option>
          <option value="untracked"  ${state.stockBucket === 'untracked' ? 'selected' : ''}>Untracked (${h.untracked_skus || 0})</option>
          <option value="dropship"   ${state.stockBucket === 'dropship' ? 'selected' : ''}>Drop-ship / external (${h.dropship_skus || 0})</option>
          <option value="no_cost"    ${state.stockBucket === 'no_cost' ? 'selected' : ''}>Owned · no cost set (${h.owned_no_cost_skus != null ? h.owned_no_cost_skus : (h.no_cost_skus || 0)})</option>
          <option value="all"        ${state.stockBucket === 'all' ? 'selected' : ''}>All</option>
        </select>
        <span class="spacer" style="flex:1;"></span>
        <span class="ag-muted" id="stock-count"></span>
      </div>
      <table class="ag-table"><thead><tr>
        <th style="width:32px;"><input type="checkbox" id="stock-select-all" title="Select all visible" aria-label="Select all visible"></th>
        <th>Product</th>
        <th>Vendor</th>
        <th class="num">On hand</th>
        <th class="num">Unit cost</th>
        <th>Cost src</th>
        <th class="num">Value</th>
        <th>Bucket</th>
        <th style="width:32px;" aria-label="Actions"></th>
      </tr></thead><tbody id="stock-tbody"></tbody></table>
      <div id="stock-bulk-bar" class="ag-bulk-bar" hidden>
        <span id="stock-bulk-count" class="ag-bulk-count"></span>
        <button class="ag-btn" onclick="AGInv.bulkStockEditCost()">Set cost…</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetSupplier()">Set supplier…</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetNotes()">Set notes…</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetLifecycle()">Lifecycle…</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetActive(true)">Mark active</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetActive(false)">Mark inactive</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetDropship(true)">Mark not-stocked</button>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockSetDropship(false)">Clear not-stocked</button>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn secondary" onclick="AGInv.bulkStockClear()">Clear</button>
      </div>
    `;

    const tbody = $('#stock-tbody');
    const countEl = $('#stock-count');
    const search = $('#stock-search');
    const bucketSel = $('#stock-bucket');
    function bucketLabel(r) {
      if (r.bucket === 'owned')         return `<span class="ag-pill ${r.unit_cost > 0 ? 'ok' : 'warn'}">${r.unit_cost > 0 ? 'Owned' : 'Owned · no cost'}</span>`;
      if (r.bucket === 'collective')    return '<span class="ag-pill info">Collective</span>';
      if (r.bucket === 'non_own_brand') return '<span class="ag-pill info">Non-AG</span>';
      if (r.bucket === 'resell')        return '<span class="ag-pill info">Resell</span>';
      if (r.bucket === 'tagged')        return '<span class="ag-pill warn">Tagged</span>';
      if (r.bucket === 'untracked')     return '<span class="ag-pill mute">Untracked</span>';
      if (r.bucket === 'dropship')      return '<span class="ag-pill info">Drop-ship</span>';
      return '<span class="ag-pill mute">' + esc(r.bucket) + '</span>';
    }
    /* Last filtered set. Captured for the bulk-action handlers + the
       select-all checkbox so they only affect what's currently visible. */
    let lastFiltered = [];
    function paint() {
      const qRaw = (search.value || '').trim();
      const b = bucketSel.value;
      const preds = qRaw ? buildStockPredicates(qRaw) : [];
      let filtered;
      if (preds.length) {
        /* Score + bucket filter in one pass. Then sort by score desc so
           the most relevant matches sit at the top, replaces the
           default owned-no-cost-first sort while a query is active. */
        const scored = [];
        for (const r of rows) {
          if (b === 'no_cost') { if (r.bucket !== 'owned' || r.unit_cost > 0) continue; }
          else if (b !== 'all' && r.bucket !== b) continue;
          const s = scoreStockQuery(r, preds);
          if (s > 0) scored.push({ r, s });
        }
        scored.sort((a, b) => b.s - a.s || (b.r.value || 0) - (a.r.value || 0));
        filtered = scored.map(x => x.r);
      } else {
        filtered = rows.filter(r => {
          if (b === 'no_cost') { if (r.bucket !== 'owned' || r.unit_cost > 0) return false; }
          else if (b !== 'all' && r.bucket !== b) return false;
          return true;
        });
      }
      lastFiltered = filtered;
      const totalValue = filtered.reduce((a, r) => a + (r.value || 0), 0);
      countEl.textContent = filtered.length + ' SKUs · £' + fmtNum(totalValue);
      progressiveRows(tbody, filtered, r => {
        const checked = state.stockSelected.has(r.sku);
        return `<tr class="clickable ${checked ? 'selected' : ''}" data-sku="${esc(r.sku)}">
        <td class="stock-select-cell"><input type="checkbox" class="stock-row-check" data-sku="${esc(r.sku)}" ${checked ? 'checked' : ''} aria-label="Select ${esc(r.sku)}"></td>
        <td>${productCell(r, { compact: true })}</td>
        ${inlineVendorCell(r)}
        ${inlineOnHandCell(r)}
        ${inlineCostCell(r)}
        <td class="ag-muted" style="font-size:11px;">${esc(r.cost_source || '')}</td>
        <td class="num"><strong>£${fmtNum(r.value)}</strong></td>
        <td>${bucketLabel(r)}</td>
        <td class="stock-kebab-cell" style="width:32px;text-align:center;">
          <button class="ag-btn-icon stock-kebab-btn" data-sku="${esc(r.sku)}"
            title="Row actions"
            style="background:none;border:none;cursor:pointer;font-size:16px;padding:2px 6px;color:#666;line-height:1;">⋮</button>
        </td>
      </tr>`;
      });
      /* Row click + checkbox change are handled by delegated listeners on
         tbody (attached once below), not per-row, so big tables stay cheap. */
      syncSelectAllCheckbox(filtered);
      refreshBulkBar();
      /* Wire inline editors. Event delegation on tbody so paints
         don't need to re-attach. */
      const getRow = sku => filtered.find(r => r.sku === sku);
      const getAllRows = () => rows;
      bindInlineCostEditors(tbody, getRow);
      bindInlineOnHandEditors(tbody, getRow);
      bindInlineVendorEditors(tbody, getRow, getAllRows);
      bindStockKebabMenu(tbody, getRow, { shopDomain, primaryDomain, rerender: paint });
    }
    function refreshBulkBar() {
      const bar = $('#stock-bulk-bar');
      const cnt = $('#stock-bulk-count');
      const n = state.stockSelected.size;
      if (!bar) return;
      bar.hidden = n === 0;
      if (cnt) cnt.textContent = n + ' SKU' + (n === 1 ? '' : 's') + ' selected';
    }
    function syncSelectAllCheckbox(filtered) {
      const all = $('#stock-select-all');
      if (!all) return;
      const visibleSelected = filtered.filter(r => state.stockSelected.has(r.sku)).length;
      if (visibleSelected === 0) { all.checked = false; all.indeterminate = false; }
      else if (visibleSelected === filtered.length) { all.checked = true; all.indeterminate = false; }
      else { all.checked = false; all.indeterminate = true; }
    }
    /* Select-all toggles every currently-visible row. Hidden rows are
       left alone so a bucket-filter switch doesn't surprise the operator. */
    $('#stock-select-all').addEventListener('change', e => {
      if (e.target.checked) lastFiltered.forEach(r => state.stockSelected.add(r.sku));
      else lastFiltered.forEach(r => state.stockSelected.delete(r.sku));
      paint();
    });
    /* Expose bulk-action handlers on AGInv so the action bar onclick
       hooks resolve. Defined inline so they close over `paint` + the
       Stock-tab payload. */
    AGInv.bulkStockClear = function() { state.stockSelected.clear(); paint(); };
    AGInv.bulkStockEditCost = async function() {
      const v = prompt('Set unit cost (£, ex-VAT) for ' + state.stockSelected.size + ' SKU(s):', '');
      if (v == null) return;
      const num = parseFloat(String(v).replace(/[£$€,\s]/g, ''));
      if (!isFinite(num) || num < 0) { AG_ADMIN.toast('Invalid cost', true); return; }
      const skus = Array.from(state.stockSelected);
      try {
        const r = await AG_ADMIN.api('ui_bulk_save_sku_settings', { skus, fields: { unit_cost: num } });
        AG_ADMIN.toast('Updated ' + (r.updated + r.appended) + ' SKU(s)');
        delete state.cache.stock; delete state.cache.today; delete state.cache.profit;
        renderTab('stock');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    };
    AGInv.bulkStockSetActive = async function(active) {
      const skus = Array.from(state.stockSelected);
      if (!skus.length) return;
      if (!confirm('Mark ' + skus.length + ' SKU(s) as ' + (active ? 'active' : 'inactive') + '?')) return;
      try {
        const r = await AG_ADMIN.api('ui_bulk_save_sku_settings', { skus, fields: { active: active ? 'TRUE' : 'FALSE' } });
        AG_ADMIN.toast('Updated ' + (r.updated + r.appended) + ' SKU(s)');
        delete state.cache.stock; delete state.cache.today;
        renderTab('stock');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    };
    /* Mark selected as drop-ship / external (we don't hold the stock) → excluded
       from stock value but cost kept for profit. For batches we don't stock. */
    AGInv.bulkStockSetDropship = async function(on) {
      const skus = Array.from(state.stockSelected);
      if (!skus.length) return;
      if (!confirm((on ? 'Mark' : 'Clear') + ' drop-ship / external (excluded from stock value) for ' + skus.length + ' SKU(s)?')) return;
      try {
        const r = await AG_ADMIN.api('ui_bulk_save_sku_settings', { skus, fields: { dropship: on ? 'TRUE' : '' } });
        AG_ADMIN.toast('Updated ' + (r.updated + r.appended) + ' SKU(s)');
        delete state.cache.stock; delete state.cache.today; delete state.cache.profit;
        renderTab('stock');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    };
    AGInv.bulkStockSetLifecycle = async function() {
      const options = ['', 'Discontinued', 'On Hold'];
      const v = prompt('Set lifecycle for ' + state.stockSelected.size + ' SKU(s).\nOptions: ' + options.filter(Boolean).join(', ') + ', or blank to clear.', '');
      if (v == null) return;
      const value = String(v).trim();
      if (value && options.indexOf(value) < 0) { AG_ADMIN.toast('Unknown lifecycle status', true); return; }
      const skus = Array.from(state.stockSelected);
      try {
        const r = await AG_ADMIN.api('ui_bulk_save_sku_settings', { skus, fields: { lifecycle_status: value } });
        AG_ADMIN.toast('Updated ' + (r.updated + r.appended) + ' SKU(s)');
        delete state.cache.stock;
        renderTab('stock');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    };
    AGInv.bulkStockSetSupplier = async function() {
      /* Suppliers come from the bootstrap call (ui_bootstrap → state.bootstrap.suppliers).
         Show as a hint in the prompt so the operator knows the canonical names. */
      const suppliers = (state.bootstrap && state.bootstrap.suppliers) || [];
      const names = suppliers.map(s => s.name || s).filter(Boolean);
      const hint = names.length ? '\nKnown suppliers: ' + names.join(', ') : '';
      const v = prompt('Set supplier for ' + state.stockSelected.size + ' SKU(s) (blank to clear):' + hint, '');
      if (v == null) return;
      const value = String(v).trim();
      const skus = Array.from(state.stockSelected);
      try {
        const r = await AG_ADMIN.api('ui_bulk_save_sku_settings', { skus, fields: { supplier: value } });
        AG_ADMIN.toast('Updated ' + (r.updated + r.appended) + ' SKU(s)');
        delete state.cache.stock; delete state.cache.reorder;
        renderTab('stock');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    };
    AGInv.bulkStockSetNotes = async function() {
      const v = prompt('Set notes for ' + state.stockSelected.size + ' SKU(s).\n(Overwrites existing notes. Use blank to clear.)', '');
      if (v == null) return;
      const value = String(v);
      const skus = Array.from(state.stockSelected);
      try {
        const r = await AG_ADMIN.api('ui_bulk_save_sku_settings', { skus, fields: { notes: value } });
        AG_ADMIN.toast('Updated ' + (r.updated + r.appended) + ' SKU(s)');
        delete state.cache.stock;
        renderTab('stock');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    };

    search.addEventListener('input', debounce(e => { state.stockSearch = e.target.value; paint(); }));
    bucketSel.addEventListener('change', e => { state.stockBucket = e.target.value; paint(); });
    const helpBtn = $('#stock-search-help');
    if (helpBtn) helpBtn.addEventListener('click', openStockSearchHelp);
    /* Delegated row-click + checkbox-change, attached ONCE on tbody (not
       per-row, not per-paint) so large tables don't pay a listener per row
       on every re-render. Use lastFiltered for the current visible set. */
    tbody.addEventListener('click', e => {
      if (e.target.closest('.cost-edit-cell, .onhand-edit-cell, .vendor-edit-cell, .stock-select-cell, .stock-kebab-cell')) return;
      const tr = e.target.closest('tr[data-sku]');
      if (tr) openSkuDrawer(tr.dataset.sku);
    });
    tbody.addEventListener('change', e => {
      const cb = e.target.closest('.stock-row-check');
      if (!cb) return;
      const sku = cb.dataset.sku;
      if (cb.checked) state.stockSelected.add(sku); else state.stockSelected.delete(sku);
      const tr = cb.closest('tr'); if (tr) tr.classList.toggle('selected', cb.checked);
      refreshBulkBar();
      syncSelectAllCheckbox(lastFiltered);
    });
    paint();
    /* Stock-value chart with an operator-selectable date range (default: since
       cutover). Redraws in place on range change; the full series comes from the
       backend and is sliced client-side by stockHistFor. */
    function drawStockChart() {
      const sel = stockHistFor(state.stockChartRange);
      const lbl = document.getElementById('stock-chart-label');
      if (lbl) lbl.textContent = 'Owned stock value · ' + sel.label;
      const picker = document.getElementById('stock-range-picker');
      if (picker) picker.querySelectorAll('[data-range]').forEach(b => {
        const on = b.dataset.range === state.stockChartRange;
        b.style.fontWeight = on ? '700' : '';
        b.style.borderColor = on ? 'var(--ag-accent,#c0261c)' : '';
      });
      chartLine('ch-stock-value', sel.data, 'Owned stock value (£)', { decimals: 0 });
    }
    if (valueHist.length) {
      drawStockChart();
      const picker = document.getElementById('stock-range-picker');
      if (picker) picker.addEventListener('click', e => {
        const btn = e.target.closest('[data-range]');
        if (!btn) return;
        state.stockChartRange = btn.dataset.range;
        drawStockChart();
      });
    }
  }

  /* ─── Cohort retention grid (lives inside Customers tab) ────────
     Heatmap-style table: rows = first-purchase month, columns =
     elapsed months since first purchase, cells = % of cohort with
     any order that elapsed month. Future cells (e.g. m6 for a
     cohort that's only 3 months old) render as ·-empty. */
  /* RFM segment pill, colour-coded by recommended action.
     Champions / Loyal = green (cherish), At Risk / Cant Lose = red
     (urgent), Hibernating / Lost = grey (low-priority winback). */
  function rfmSegmentPill(segment) {
    if (!segment) return '<span class="ag-pill mute">-</span>';
    const toneMap = {
      'Champions': 'ok',
      'Loyal': 'ok',
      'Potential Loyalists': 'ok',
      'New Customers': 'info',
      'Promising': 'info',
      'Need Attention': 'warn',
      'Cant Lose Them': 'err',
      'At Risk': 'err',
      'About to Sleep': 'warn',
      'Hibernating': 'mute',
      'Lost': 'mute'
    };
    return `<span class="ag-pill ${toneMap[segment] || 'mute'}">${esc(segment)}</span>`;
  }
  /* RFM segments panel, counts + revenue per segment.
     Sits between the customer hero stats and the customer table. */
  function renderRfmSegments(segments) {
    if (!segments || !segments.length) return '';
    return `
      <div style="margin-bottom:24px;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 6px 0;">RFM segments</h3>
        <p class="ag-muted" style="font-size:11px;margin:0 0 8px 0;max-width:720px;">Quintile-based scoring across Recency, Frequency, Monetary. Champions/Loyal = nurture. At Risk / Cant Lose = win-back priority. Lost = low-cost reactivation.</p>
        <table class="ag-table" style="font-size:12px;">
          <thead><tr>
            <th>Segment</th>
            <th class="num">Customers</th>
            <th class="num">% of base</th>
            <th class="num">Revenue (365d)</th>
            <th class="num">£ per customer</th>
          </tr></thead>
          <tbody>
            ${segments.map(s => {
              const perCust = s.count > 0 ? s.revenue_gbp / s.count : 0;
              return `<tr>
                <td>${rfmSegmentPill(s.segment)}</td>
                <td class="num">${s.count}</td>
                <td class="num">${s.pct_of_customers}%</td>
                <td class="num"><strong>£${fmtNum(s.revenue_gbp)}</strong></td>
                <td class="num">£${fmtNum(perCust)}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  function renderCohortGrid(mount, payload) {
    if (!mount) return;
    if (!payload || !payload.rows || !payload.rows.length) {
      mount.innerHTML = `
        <h3 class="ag-h2" style="font-size:14px;">Cohort retention</h3>
        <div class="ag-empty">
          No cohort data yet.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;">
            Cohort grid builds in the same pass as the Customers table.
            Click <strong>Pull this tab</strong> to populate.
          </div>
        </div>`;
      return;
    }
    const rows = payload.rows;
    const hero = payload.hero || {};
    const avgRetention = payload.avg_retention || [];
    const elapsedCols = 12;
    /* Heat-cell colour: 5 bands keyed off retention %.
       Tuned for a low-repeat-rate brand. Anything double-digit is
       brand-red, single-digit fades to grey. Adjust for context. */
    function cellStyle(pct) {
      if (pct == null) return 'background:transparent;color:var(--ag-muted, #888);';
      if (pct >= 25) return 'background:#7a1414;color:#fff;font-weight:600;';
      if (pct >= 15) return 'background:#c8102e;color:#fff;font-weight:600;';
      if (pct >= 8)  return 'background:#e85b5c;color:#fff;';
      if (pct >= 3)  return 'background:#f6c1c1;color:#400;';
      return 'background:#fbe8e8;color:#400;';
    }
    const fmtPct = v => v == null ? '·' : (v >= 10 ? Math.round(v) : v.toFixed(1)) + '%';
    const headerCols = Array.from({ length: elapsedCols }, (_, i) => `<th class="num" style="width:42px;">m${i}</th>`).join('');
    const bodyRows = rows.map(r => {
      const cells = r.retention.map(v => `<td class="num" style="${cellStyle(v)}">${fmtPct(v)}</td>`).join('');
      return `<tr>
        <td><strong>${esc(r.cohort_month)}</strong></td>
        <td class="num"><span class="ag-muted">${r.cohort_size}</span></td>
        ${cells}
      </tr>`;
    }).join('');
    /* Footer row = average retention per elapsed month (across cohorts that have data there). */
    const avgCells = avgRetention.map(v => `<td class="num" style="${cellStyle(v)};border-top:2px solid var(--ag-border, #444);">${fmtPct(v)}</td>`).join('');
    mount.innerHTML = `
      <h3 class="ag-h2" style="font-size:14px;margin-bottom:6px;">Cohort retention <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· last ${rows.length} months · ${hero.total_customers || 0} customers</span></h3>
      <p class="ag-muted" style="font-size:11px;margin:0 0 12px 0;max-width:720px;">
        Each row = customers whose FIRST purchase fell in that month. Cell mN = % of that cohort with any order N months later. m0 is always 100%. Read columns vertically to see if retention is improving (newer cohorts higher at the same elapsed month).
      </p>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(140px,1fr));margin-bottom:12px;">
        ${statCard('Avg m1 retention',  (hero.avg_m1_retention_pct  != null ? hero.avg_m1_retention_pct  + '%' : '-'), '')}
        ${statCard('Avg m3 retention',  (hero.avg_m3_retention_pct  != null ? hero.avg_m3_retention_pct  + '%' : '-'), '')}
        ${statCard('Avg m6 retention',  (hero.avg_m6_retention_pct  != null ? hero.avg_m6_retention_pct  + '%' : '-'), '')}
        ${statCard('Avg m12 retention', (hero.avg_m12_retention_pct != null ? hero.avg_m12_retention_pct + '%' : '-'), '')}
      </div>
      <div style="overflow-x:auto;">
        <table class="ag-table" style="font-size:11px;min-width:760px;">
          <thead><tr>
            <th>Cohort</th>
            <th class="num">Size</th>
            ${headerCols}
          </tr></thead>
          <tbody>${bodyRows}</tbody>
          <tfoot><tr style="font-weight:600;">
            <td>Average</td>
            <td class="num"><span class="ag-muted">-</span></td>
            ${avgCells}
          </tr></tfoot>
        </table>
      </div>
      ${renderCohortLTVTable(rows, payload.avg_ltv || [], hero, elapsedCols)}
    `;
  }

  /* Cohort LTV table. Same shape as the retention heatmap but
     showing cumulative £ revenue per cohort per elapsed month.
     Colour bands tuned to £ scale; quartile-relative against the
     observed max in the dataset so the gradient adapts. */
  function renderCohortLTVTable(rows, avgLtv, hero, elapsedCols) {
    if (!rows.length) return '';
    /* Find max LTV cell across all rows for relative shading. */
    let maxLtv = 0;
    rows.forEach(r => (r.ltv || []).forEach(v => { if (v != null && v > maxLtv) maxLtv = v; }));
    function ltvStyle(v) {
      if (v == null) return 'background:transparent;color:var(--ag-muted, #888);';
      if (maxLtv <= 0) return '';
      const ratio = Math.min(1, v / maxLtv);
      if (ratio >= 0.75) return 'background:#0a5d3a;color:#fff;font-weight:600;';
      if (ratio >= 0.5)  return 'background:#1a8a5a;color:#fff;font-weight:600;';
      if (ratio >= 0.25) return 'background:#4cb37c;color:#063;';
      if (ratio >= 0.1)  return 'background:#cfe9d7;color:#063;';
      return 'background:#eaf6ed;color:#063;';
    }
    const fmtLtv = v => v == null ? '·' : '£' + Math.round(v).toLocaleString();
    const headerCols = Array.from({ length: elapsedCols }, (_, i) => `<th class="num" style="width:64px;">m${i}</th>`).join('');
    const bodyRows = rows.map(r => {
      const cells = (r.ltv || []).map(v => `<td class="num" style="${ltvStyle(v)}">${fmtLtv(v)}</td>`).join('');
      return `<tr>
        <td><strong>${esc(r.cohort_month)}</strong></td>
        <td class="num"><span class="ag-muted">${r.cohort_size}</span></td>
        ${cells}
      </tr>`;
    }).join('');
    const avgCells = avgLtv.map(v => `<td class="num" style="${ltvStyle(v)};border-top:2px solid var(--ag-border, #444);">${fmtLtv(v)}</td>`).join('');
    return `
      <h3 class="ag-h2" style="font-size:14px;margin:24px 0 6px 0;">Cohort lifetime value <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· cumulative £ spend per customer</span></h3>
      <p class="ag-muted" style="font-size:11px;margin:0 0 12px 0;max-width:720px;">
        Cell mN = average cumulative spend through elapsed month N for each cohort. m0 is first-month spend (effectively AOV × orders that month). Watch the column trend, newer cohorts higher at the same elapsed month means LTV is growing.
      </p>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(140px,1fr));margin-bottom:12px;">
        ${statCard('Avg m1 LTV',  (hero.avg_m1_ltv_gbp  != null ? '£' + Math.round(hero.avg_m1_ltv_gbp)  : '-'), '')}
        ${statCard('Avg m6 LTV',  (hero.avg_m6_ltv_gbp  != null ? '£' + Math.round(hero.avg_m6_ltv_gbp)  : '-'), '')}
        ${statCard('Avg m12 LTV', (hero.avg_m12_ltv_gbp != null ? '£' + Math.round(hero.avg_m12_ltv_gbp) : '-'), '')}
      </div>
      <div style="overflow-x:auto;">
        <table class="ag-table" style="font-size:11px;min-width:760px;">
          <thead><tr>
            <th>Cohort</th>
            <th class="num">Size</th>
            ${headerCols}
          </tr></thead>
          <tbody>${bodyRows}</tbody>
          <tfoot><tr style="font-weight:600;">
            <td>Average</td>
            <td class="num"><span class="ag-muted">-</span></td>
            ${avgCells}
          </tr></tfoot>
        </table>
      </div>
    `;
  }

  /* ─── Tab: Customers ──────────────────────────────────────── */
  function renderCustomers(root, p) {
    const rows = (p && p.rows) || [];
    const hero = (p && p.hero) || {};
    if (!rows.length) {
      root.innerHTML = `<h2 class="ag-h2">Customers</h2>
        <div class="ag-empty">
          No customer aggregates yet.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;">
            Click <strong>Pull this tab</strong> to build the rollup (walks last 365d of orders).
          </div>
        </div>`;
      return;
    }
    /* Search + sort state. Default sort by total revenue desc. Biggest spenders. */
    if (!state.customersSort) state.customersSort = 'revenue';
    if (state.customersSearch == null) state.customersSearch = '';
    if (state.customersFilter == null) state.customersFilter = 'all';
    if (state.customersSegment == null) state.customersSegment = '';
    /* Bestseller cap mirrors the Reorder Top-N pattern. */
    if (state.customersTopN == null) state.customersTopN = 0;

    root.innerHTML = `
      <h2 class="ag-h2">Customers <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· last 365 days (fixed, ignores the date range above)</span></h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px;">
        ${statCard('Total customers', hero.total_customers || 0, '')}
        ${statCard('Repeat rate', (hero.repeat_rate_pct || 0) + '%', (hero.repeat_rate_pct || 0) >= 20 ? 'ok' : 'warn')}
        ${statCard('Avg LTV (365d)', '£' + fmtNum(hero.avg_ltv_365d || 0), '')}
        ${statCard('Total revenue', '£' + fmtNum(hero.total_revenue_365d || 0), 'ok')}
        ${statCard('New customers (30d)', hero.new_customers_30d || 0, '')}
        ${hero.top_spender ? statCard('Top spender', '£' + fmtNum(hero.top_spender.total_revenue_gbp), 'ok') : ''}
      </div>
      ${renderRfmSegments(p && p.segments)}
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:8px;">
        <input class="ag-input" id="cust-search" placeholder="Search email or name…" style="max-width:300px;" value="${esc(state.customersSearch)}">
        <select class="ag-select" id="cust-filter" style="width:auto;">
          <option value="all"      ${state.customersFilter === 'all' ? 'selected' : ''}>All customers</option>
          <option value="repeat"   ${state.customersFilter === 'repeat' ? 'selected' : ''}>Repeat customers (${hero.repeat_customers || 0})</option>
          <option value="onetime"  ${state.customersFilter === 'onetime' ? 'selected' : ''}>One-time (${hero.one_time_customers || 0})</option>
          <option value="new30"    ${state.customersFilter === 'new30' ? 'selected' : ''}>New in last 30d (${hero.new_customers_30d || 0})</option>
        </select>
        <select class="ag-select" id="cust-segment" style="width:auto;">
          <option value="">All segments</option>
          ${((p && p.segments) || []).map(s =>
            `<option value="${esc(s.segment)}" ${state.customersSegment === s.segment ? 'selected' : ''}>${esc(s.segment)} (${s.count})</option>`
          ).join('')}
        </select>
        <select class="ag-select" id="cust-sort" style="width:auto;">
          <option value="revenue" ${state.customersSort === 'revenue' ? 'selected' : ''}>Sort: Total spend</option>
          <option value="orders"  ${state.customersSort === 'orders'  ? 'selected' : ''}>Sort: Orders count</option>
          <option value="aov"     ${state.customersSort === 'aov'     ? 'selected' : ''}>Sort: AOV</option>
          <option value="recent"  ${state.customersSort === 'recent'  ? 'selected' : ''}>Sort: Most recent purchase</option>
        </select>
        <select class="ag-select" id="cust-topn" style="width:auto;">
          <option value="0"   ${!state.customersTopN ? 'selected' : ''}>All</option>
          <option value="10"  ${state.customersTopN === 10 ? 'selected' : ''}>Top 10</option>
          <option value="50"  ${state.customersTopN === 50 ? 'selected' : ''}>Top 50</option>
          <option value="100" ${state.customersTopN === 100 ? 'selected' : ''}>Top 100</option>
        </select>
        <span class="spacer" style="flex:1;"></span>
        <span class="ag-muted" id="cust-count"></span>
      </div>
      <table class="ag-table"><thead><tr>
        <th>Customer</th>
        <th class="num">Orders</th>
        <th class="num">Total spent</th>
        <th class="num">AOV</th>
        <th class="num">Units</th>
        <th>First → last</th>
        <th class="num">Days since</th>
        <th>Segment</th>
        <th>Status</th>
      </tr></thead><tbody id="cust-tbody"></tbody></table>
      <div id="cohort-grid-mount" style="margin-top:32px;"></div>
    `;
    /* Cohort retention grid below the customer table. Independent
       data source, painted once (doesn't react to search/filter
       changes). Renders nothing when the cohorts payload is empty. */
    renderCohortGrid($('#cohort-grid-mount'), (p && p.cohorts) || null);
    const search = $('#cust-search');
    const filterSel = $('#cust-filter');
    const sortSel = $('#cust-sort');
    const topNSel = $('#cust-topn');
    const tbody = $('#cust-tbody');
    const countLbl = $('#cust-count');
    function paint() {
      const q = (state.customersSearch || '').toLowerCase();
      let filtered = rows.filter(r => {
        if (state.customersSegment && r.rfm_segment !== state.customersSegment) return false;
        if (state.customersFilter === 'repeat' && !r.is_repeat) return false;
        if (state.customersFilter === 'onetime' && r.is_repeat) return false;
        if (state.customersFilter === 'new30') {
          /* The backend's own window (30 complete London days ending yesterday) and each row's
             London first day, so the list matches the count. The fallback covers a cached payload
             from before those fields existed. */
          if (hero.new_customers_30d_from && r.first_day != null) {
            const d = String(r.first_day || '');
            if (d < hero.new_customers_30d_from || d > hero.new_customers_30d_to) return false;
          } else {
            const fp = String(r.first_purchase || '');
            const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
            if (fp < cutoff.toISOString().slice(0, 10)) return false;
          }
        }
        if (q && !(
          String(r.email || '').toLowerCase().includes(q) ||
          String(r.first_name || '').toLowerCase().includes(q) ||
          String(r.last_name || '').toLowerCase().includes(q)
        )) return false;
        return true;
      });
      const sortKey = state.customersSort;
      filtered.sort((a, b) => {
        if (sortKey === 'orders') return b.orders_count - a.orders_count;
        if (sortKey === 'aov')    return b.avg_order_value_gbp - a.avg_order_value_gbp;
        if (sortKey === 'recent') return (a.days_since_last_purchase || 0) - (b.days_since_last_purchase || 0);
        return b.total_revenue_gbp - a.total_revenue_gbp;
      });
      if (state.customersTopN) filtered = filtered.slice(0, state.customersTopN);
      countLbl.textContent = filtered.length + ' of ' + rows.length + ' customers' + (rows.length >= 500 ? ' · top 500 by spend (search covers these only)' : '');
      progressiveRows(tbody, filtered, r => {
        const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
        const label = name || r.email || r.customer_id;
        const statusPill = r.is_repeat
          ? `<span class="ag-pill ok">Repeat · ${r.orders_count}×</span>`
          : '<span class="ag-pill mute">One-time</span>';
        return `<tr class="clickable" data-customer="${esc(r.customer_id)}">
          <td>
            <div style="font-weight:600;">${esc(label)}</div>
            ${name && r.email ? `<div class="ag-muted" style="font-size:11px;">${esc(r.email)}</div>` : ''}
          </td>
          <td class="num">${r.orders_count}</td>
          <td class="num"><strong>£${fmtNum(r.total_revenue_gbp)}</strong></td>
          <td class="num">£${fmtNum(r.avg_order_value_gbp)}</td>
          <td class="num">${r.units_count}</td>
          <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.first_purchase))} → ${esc(fmtFriendly(r.last_purchase))}</td>
          <td class="num">${r.days_since_last_purchase}d</td>
          <td>${rfmSegmentPill(r.rfm_segment)}</td>
          <td>${statusPill}</td>
        </tr>`;
      });
    }
    const segmentSel = $('#cust-segment');
    /* Delegated row click. Attached once, not per-row. */
    tbody.addEventListener('click', e => {
      const tr = e.target.closest('tr[data-customer]');
      if (tr) openCustomerDrawer(tr.dataset.customer, rows.find(x => String(x.customer_id) === tr.dataset.customer));
    });
    search.addEventListener('input', debounce(e => { state.customersSearch = e.target.value; paint(); }));
    filterSel.addEventListener('change', e => { state.customersFilter = e.target.value; paint(); });
    sortSel.addEventListener('change', e => { state.customersSort = e.target.value; paint(); });
    topNSel.addEventListener('change', e => { state.customersTopN = parseInt(e.target.value, 10) || 0; paint(); });
    if (segmentSel) segmentSel.addEventListener('change', e => { state.customersSegment = e.target.value; paint(); });
    paint();
  }

  /* ─── Tab: Channels ───────────────────────────────────────── */
  function renderChannels(root, p) {
    const rows = (p && p.rows) || [];
    const hero = (p && p.hero) || {};
    if (!rows.length) {
      root.innerHTML = `<h2 class="ag-h2">Channels</h2>
        <div class="ag-empty">
          No channel aggregates yet.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;">
            Click <strong>Pull this tab</strong> to build the rollup (walks last 365d of orders).
          </div>
        </div>`;
      return;
    }
    root.innerHTML = `
      <h2 class="ag-h2">Channels <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${esc(rangeLabelText())} · ${rows.length} channels</span></h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px;">
        ${statCardCmp('Total revenue', '£' + fmtNum(hero.total_revenue_365d || 0), 'ok', p.compare ? rangeDeltaPill(hero.total_revenue_365d, p.compare.total_revenue) : '')}
        ${statCardCmp('Orders', hero.total_orders_365d || 0, '', p.compare ? rangeDeltaPill(hero.total_orders_365d, p.compare.total_orders) : '')}
        ${statCardCmp('Units', hero.total_units_365d || 0, '', p.compare ? rangeDeltaPill(hero.total_units_365d, p.compare.total_units) : '')}
        ${statCard('Avg order value', '£' + fmtNum(hero.avg_order_value_gbp || 0), '')}
        ${statCard('Refund rate', (hero.refund_rate_pct || 0) + '%', (hero.refund_rate_pct || 0) >= 5 ? 'warn' : ((hero.refund_rate_pct || 0) >= 3 ? '' : 'ok'))}
        ${statCard('Cancelled orders', hero.cancelled_orders || 0, (hero.cancelled_orders || 0) > 50 ? 'warn' : '')}
        ${hero.dominant_channel ? statCard('Dominant channel', hero.dominant_channel + ' · ' + (hero.dominant_share_pct || 0) + '%', '') : ''}
      </div>
      <table class="ag-table"><thead><tr>
        <th>Channel</th>
        <th class="num">Orders</th>
        <th class="num">Cancelled</th>
        <th class="num">Units</th>
        <th class="num">Revenue<span style="font-weight:400;color:#999;"> (inc-VAT)</span></th>
        <th class="num">Share</th>
        <th class="num">AOV</th>
        <th class="num">Refunded</th>
        <th class="num">Refund rate</th>
        <th>Last order</th>
      </tr></thead><tbody>${rows.map(r => {
        const refundClass = r.refund_rate_pct >= 5 ? 'warn' : (r.refund_rate_pct >= 3 ? '' : 'ok');
        const sharePct = r.revenue_share_pct || 0;
        return `<tr>
          <td><strong>${esc(r.channel_label)}</strong>${r.channel_key !== r.channel_label ? `<div class="ag-muted" style="font-size:11px;">${esc(r.channel_key)}</div>` : ''}</td>
          <td class="num">${r.orders_count}</td>
          <td class="num">${r.cancelled_orders > 0 ? `<span class="ag-pill warn">${r.cancelled_orders}</span>` : '<span class="ag-muted">0</span>'}</td>
          <td class="num">${r.units_count}</td>
          <td class="num"><strong>£${fmtNum(r.total_revenue_gbp)}</strong></td>
          <td class="num">
            <div style="display:flex;align-items:center;gap:6px;justify-content:flex-end;">
              <div style="flex:0 0 60px;height:6px;background:var(--ag-bg-2);border-radius:3px;overflow:hidden;">
                <div style="width:${Math.min(100, sharePct)}%;height:100%;background:var(--ag-info);"></div>
              </div>
              <span style="min-width:42px;">${sharePct}%</span>
            </div>
          </td>
          <td class="num">£${fmtNum(r.avg_order_value_gbp)}</td>
          <td class="num">£${fmtNum(r.refunded_amount_gbp)}</td>
          <td class="num"><span class="ag-pill ${refundClass}">${r.refund_rate_pct}%</span></td>
          <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.last_order_date))}</td>
        </tr>`;
      }).join('')}</tbody></table>
    `;
  }

  /* ─── Tab: Discount Codes ─────────────────────────────────── */
  function renderDiscounts(root, p) {
    const rows = (p && p.rows) || [];
    const hero = (p && p.hero) || {};
    if (!state.discountsDays) state.discountsDays = 30;
    if (!state.discountsSort) state.discountsSort = 'revenue';
    if (state.discountsTypeFilter == null) state.discountsTypeFilter = 'all';
    if (state.discountsSearch == null) state.discountsSearch = '';

    const periodLabel = rangeLabelText();       // global date-range picker
    const periodButtonsHtml = '';                // period is driven by the header picker now

    if (!rows.length) {
      root.innerHTML = `
        <div class="ag-toolbar" style="flex-wrap:wrap;gap:6px;">
          <h2 class="ag-h2" style="margin:0 16px 0 0;">Discount Codes <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${esc(periodLabel)}</span></h2>
          ${periodButtonsHtml}
        </div>
        <div class="ag-empty">
          No discount codes used in this window.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;">
            Try a longer period or click <strong>Pull this tab</strong> to refresh.
          </div>
        </div>`;
      root.querySelectorAll('[data-period]').forEach(b => b.addEventListener('click', () => {
        const d = parseInt(b.dataset.period, 10) || 365;
        if (d === state.discountsDays) return;
        state.discountsDays = d;
        delete state.cache.discounts;
        saveCache();
        renderTab('discounts');
      }));
      return;
    }

    /* Aggregate ROI colouring matches per-row scheme: >5 = ok, 2-5 = warn, <2 = err. */
    const aggRoiClass = hero.aggregate_roi > 5 ? 'ok' : (hero.aggregate_roi >= 2 ? 'warn' : 'err');
    const roiMinOrders = hero.min_orders_for_roi || 5;
    root.innerHTML = `
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:6px;">
        <h2 class="ag-h2" style="margin:0 16px 0 0;">Discount Codes <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${esc(periodLabel)} · ${rows.length} codes used</span></h2>
        ${periodButtonsHtml}
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px;">
        ${statCard('Aggregate ROI', (hero.aggregate_roi || 0) + 'x', aggRoiClass)}
        ${statCardCmp('Discount-driven revenue', '£' + fmtNum(hero.discount_driven_revenue || 0), 'ok', p.compare ? rangeDeltaPill(hero.discount_driven_revenue, p.compare.discount_driven_revenue) : '')}
        ${statCardCmp('Discount given', '£' + fmtNum(hero.total_discount_given || 0), '', p.compare ? rangeDeltaPill(hero.total_discount_given, p.compare.total_discount_given) : '')}
        ${statCardCmp('Orders with code', hero.total_orders_with_code || 0, '', p.compare ? rangeDeltaPill(hero.total_orders_with_code, p.compare.total_orders_with_code) : '')}
        ${hero.top_revenue_code ? statCard('Top revenue code', hero.top_revenue_code, '') : ''}
        ${hero.top_roi_code ? statCard('Best ROI code (' + roiMinOrders + '+ orders)', hero.top_roi_code + ' · ' + hero.top_roi_value + 'x', 'ok') : ''}
      </div>
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:8px;">
        <input class="ag-input" id="disc-search" placeholder="Search code…" style="max-width:280px;" value="${esc(state.discountsSearch)}">
        <select class="ag-select" id="disc-type" style="width:auto;">
          <option value="all" ${state.discountsTypeFilter === 'all' ? 'selected' : ''}>All types</option>
          <option value="percentage" ${state.discountsTypeFilter === 'percentage' ? 'selected' : ''}>Percentage</option>
          <option value="fixed" ${state.discountsTypeFilter === 'fixed' ? 'selected' : ''}>Fixed £</option>
          <option value="shipping" ${state.discountsTypeFilter === 'shipping' ? 'selected' : ''}>Free shipping</option>
          <option value="other" ${state.discountsTypeFilter === 'other' ? 'selected' : ''}>Other</option>
        </select>
        <select class="ag-select" id="disc-sort" style="width:auto;">
          <option value="revenue"   ${state.discountsSort === 'revenue' ? 'selected' : ''}>Sort: Gross revenue</option>
          <option value="discount"  ${state.discountsSort === 'discount' ? 'selected' : ''}>Sort: Discount given</option>
          <option value="orders"    ${state.discountsSort === 'orders' ? 'selected' : ''}>Sort: Orders</option>
          <option value="roi"       ${state.discountsSort === 'roi' ? 'selected' : ''}>Sort: ROI</option>
          <option value="recent"    ${state.discountsSort === 'recent' ? 'selected' : ''}>Sort: Most recent</option>
        </select>
        <span class="spacer" style="flex:1;"></span>
        <span class="ag-muted" id="disc-count"></span>
      </div>
      <table class="ag-table"><thead><tr>
        <th>Code</th>
        <th>Type</th>
        <th class="num">Orders</th>
        <th class="num">Gross<span style="font-weight:400;color:#999;"> (inc-VAT)</span></th>
        <th class="num">Discount</th>
        <th class="num">Net</th>
        <th class="num">AOV</th>
        <th class="num">ROI</th>
        <th class="num">Refunded</th>
        <th>Last used</th>
      </tr></thead><tbody id="disc-tbody"></tbody></table>
    `;
    const search = $('#disc-search');
    const typeSel = $('#disc-type');
    const sortSel = $('#disc-sort');
    const tbody = $('#disc-tbody');
    const countLbl = $('#disc-count');

    function paint() {
      const q = (state.discountsSearch || '').toLowerCase();
      let filtered = rows.filter(r => {
        if (state.discountsTypeFilter !== 'all' && r.code_type !== state.discountsTypeFilter) return false;
        if (q && !String(r.code || '').toLowerCase().includes(q)) return false;
        return true;
      });
      const sortKey = state.discountsSort;
      filtered.sort((a, b) => {
        if (sortKey === 'discount') return b.discount_amount_gbp - a.discount_amount_gbp;
        if (sortKey === 'orders')   return b.orders_count - a.orders_count;
        if (sortKey === 'roi') {
          /* Codes with no discount given (ratio null) sort to bottom. */
          const ar = a.roi_ratio == null ? -Infinity : a.roi_ratio;
          const br = b.roi_ratio == null ? -Infinity : b.roi_ratio;
          return br - ar;
        }
        if (sortKey === 'recent') return String(b.last_used_date).localeCompare(String(a.last_used_date));
        return b.gross_revenue_gbp - a.gross_revenue_gbp;
      });
      countLbl.textContent = filtered.length + ' of ' + rows.length + ' codes';
      tbody.innerHTML = filtered.map(r => {
        const typeLabel = ({percentage:'%', fixed:'£', shipping:'shipping', other:'other'})[r.code_type] || r.code_type;
        const typeClass = ({percentage:'mute', fixed:'mute', shipping:'mute', other:'mute'})[r.code_type] || 'mute';
        let roiCell = '<span class="ag-muted">-</span>';
        if (r.roi_ratio != null) {
          const roiClass = r.roi_ratio > 5 ? 'ok' : (r.roi_ratio >= 2 ? 'warn' : 'err');
          roiCell = `<span class="ag-pill ${roiClass}">${r.roi_ratio}x</span>`;
        }
        return `<tr>
          <td><strong>${esc(r.code)}</strong></td>
          <td><span class="ag-pill ${typeClass}">${typeLabel}</span></td>
          <td class="num">${r.orders_count}${r.cancelled_orders > 0 ? `<div class="ag-muted" style="font-size:10px;">${r.cancelled_orders} cancelled</div>` : ''}</td>
          <td class="num"><strong>£${fmtNum(r.gross_revenue_gbp)}</strong></td>
          <td class="num">£${fmtNum(r.discount_amount_gbp)}</td>
          <td class="num">£${fmtNum(r.net_revenue_gbp)}</td>
          <td class="num">£${fmtNum(r.avg_order_value_gbp)}</td>
          <td class="num">${roiCell}</td>
          <td class="num">${r.refunded_amount_gbp > 0 ? '£' + fmtNum(r.refunded_amount_gbp) : '<span class="ag-muted">-</span>'}</td>
          <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.last_used_date))}</td>
        </tr>`;
      }).join('');
    }
    search.addEventListener('input', debounce(e => { state.discountsSearch = e.target.value; paint(); }));
    typeSel.addEventListener('change', e => { state.discountsTypeFilter = e.target.value; paint(); });
    sortSel.addEventListener('change', e => { state.discountsSort = e.target.value; paint(); });
    /* Period buttons. Refetch with the new window. */
    root.querySelectorAll('[data-period]').forEach(b => b.addEventListener('click', () => {
      const d = parseInt(b.dataset.period, 10) || 365;
      if (d === state.discountsDays) return;
      state.discountsDays = d;
      delete state.cache.discounts;
      saveCache();
      renderTab('discounts');
    }));
    paint();
  }

  /* ─── Tab: Subscriptions ─────────────────────────────────── */
  function renderSubscriptions(root, p) {
    const h = (p && p.hero) || {};
    const rows = (p && p.rows) || [];
    const series = (p && p.series) || [];
    const topCustomers = (p && p.top_customers) || [];
    const planMix = (p && p.plan_mix) || [];

    if (!state.subsStatusFilter) state.subsStatusFilter = 'active';
    if (!state.subsSort) state.subsSort = 'mrr';
    if (state.subsSearch == null) state.subsSearch = '';

    /* Empty-state UX. Covers both pre-launch (scope not granted +
       no contracts) and post-launch with 0 active. */
    if (!rows.length) {
      root.innerHTML = `
        <div class="ag-toolbar" style="margin-bottom:8px;">
          <h2 class="ag-h2" style="margin:0;">Subscriptions</h2>
          <span class="spacer" style="flex:1;"></span>
          <button class="ag-btn secondary" id="subs-sync-now-empty" style="padding:6px 12px;font-size:12px;">Sync now</button>
        </div>
        <div class="ag-empty">
          No subscription contracts found yet.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;max-width:560px;">
            Subscriptions haven't generated any contracts yet, or the bundles app token doesn't
            have <code>read_own_subscription_contracts</code> + <code>read_customers</code>.
            The dashboard auto-fills once the first contract is created. Click <b>Sync now</b>
            to force a refresh.
          </div>
        </div>`;
      const sb = $('#subs-sync-now-empty');
      if (sb) sb.addEventListener('click', async () => {
        sb.disabled = true; const orig = sb.textContent; sb.textContent = 'Syncing…';
        try {
          const r = await AG_ADMIN.api('ui_sync_subscriptions', {});
          if (r.scope_error) AG_ADMIN.toast('Scope not granted, sync skipped', true);
          else {
            AG_ADMIN.toast(`${r.contracts || 0} contracts found`);
            delete state.cache.subscriptions;
            saveCache();
            renderTab('subscriptions');
          }
        } catch (e) { AG_ADMIN.toast(e.message || e, true); }
        finally { sb.disabled = false; sb.textContent = orig; }
      });
      return;
    }

    /* MRR delta. Last snapshot vs 7 days prior. */
    let mrrDeltaPill = '<span class="ag-muted">-</span>';
    if (series.length >= 8) {
      const recent = series[series.length - 1].mrr_gbp;
      const prior = series[series.length - 8].mrr_gbp;
      if (prior > 0) {
        const pct = Math.round(((recent - prior) / prior) * 1000) / 10;
        const cls = pct >= 0 ? 'ok' : 'err';
        const sign = pct >= 0 ? '+' : '';
        mrrDeltaPill = `<span class="ag-pill ${cls}" style="font-size:10px;">${sign}${pct}% vs 7d</span>`;
      }
    }

    root.innerHTML = `
      <div class="ag-toolbar" style="margin-bottom:8px;">
        <h2 class="ag-h2" style="margin:0;">Subscriptions <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· ${h.total_contracts || 0} contracts</span></h2>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn secondary" id="subs-sync-now" style="padding:6px 12px;font-size:12px;">Sync now</button>
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(170px,1fr));margin-bottom:16px;">
        <div class="ag-stat ok">
          <div class="label">MRR</div>
          <div class="value">£${fmtNum(h.mrr_gbp || 0)}</div>
          <div style="margin-top:4px;">${mrrDeltaPill}</div>
        </div>
        ${statCard('Active', h.active || 0, (h.active || 0) > 0 ? 'ok' : '')}
        ${statCard('Paused', h.paused || 0, (h.paused || 0) > 0 ? 'warn' : '')}
        ${statCard('Cancelled', h.cancelled || 0, '')}
        ${h.churn_pct_30d != null ? statCard('Churn · 30d', (h.churn_pct_30d || 0) + '%', (h.churn_pct_30d || 0) > 5 ? 'warn' : '') : ''}
        ${h.failed > 0 ? statCard('Failed payment', h.failed || 0, 'err') : ''}
      </div>

      ${series.length > 1 ? `
        <div class="ag-chart" style="height:240px;margin-bottom:16px;">
          <div class="label">MRR, last ${series.length} days</div>
          <canvas id="ch-subs-mrr"></canvas>
        </div>` : ''}

      ${planMix.length ? `
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px 0;">Plan mix · active MRR</h3>
        <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));margin-bottom:16px;">
          ${planMix.map(pm => `
            <div class="ag-stat">
              <div class="label">${esc(pm.plan_name || '(unnamed plan)')}</div>
              <div class="value">£${fmtNum(pm.monthly_value_gbp)}</div>
            </div>`).join('')}
        </div>` : ''}

      ${topCustomers.length ? `
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px 0;">Top customers · MRR</h3>
        <table class="ag-table"><thead><tr><th>Email</th><th class="num">Monthly</th></tr></thead>
          <tbody>${topCustomers.map(c => `<tr><td>${esc(c.email)}</td><td class="num"><strong>£${fmtNum(c.monthly_value_gbp)}</strong></td></tr>`).join('')}</tbody>
        </table>` : ''}

      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px 0;">Contracts</h3>
      <div class="ag-toolbar" style="flex-wrap:wrap;gap:8px;">
        <input class="ag-input" id="subs-search" placeholder="Search email or plan…" style="max-width:280px;" value="${esc(state.subsSearch)}">
        <select class="ag-select" id="subs-status" style="width:auto;">
          <option value="all"       ${state.subsStatusFilter === 'all' ? 'selected' : ''}>All statuses</option>
          <option value="active"    ${state.subsStatusFilter === 'active' ? 'selected' : ''}>Active</option>
          <option value="paused"    ${state.subsStatusFilter === 'paused' ? 'selected' : ''}>Paused</option>
          <option value="cancelled" ${state.subsStatusFilter === 'cancelled' ? 'selected' : ''}>Cancelled</option>
          <option value="expired"   ${state.subsStatusFilter === 'expired' ? 'selected' : ''}>Expired</option>
          <option value="failed"    ${state.subsStatusFilter === 'failed' ? 'selected' : ''}>Failed</option>
        </select>
        <select class="ag-select" id="subs-sort" style="width:auto;">
          <option value="mrr"     ${state.subsSort === 'mrr' ? 'selected' : ''}>Sort: Monthly value</option>
          <option value="created" ${state.subsSort === 'created' ? 'selected' : ''}>Sort: Created</option>
          <option value="next"    ${state.subsSort === 'next' ? 'selected' : ''}>Sort: Next billing</option>
        </select>
        <span class="spacer" style="flex:1;"></span>
        <span class="ag-muted" id="subs-count"></span>
      </div>
      <table class="ag-table"><thead><tr>
        <th>Status</th>
        <th>Customer</th>
        <th>Plan</th>
        <th class="num">Cycle £</th>
        <th class="num">Monthly £</th>
        <th>Next billing</th>
        <th>Created</th>
      </tr></thead><tbody id="subs-tbody"></tbody></table>
    `;

    const search = $('#subs-search');
    const statusSel = $('#subs-status');
    const sortSel = $('#subs-sort');
    const tbody = $('#subs-tbody');
    const countLbl = $('#subs-count');

    function paint() {
      const q = (state.subsSearch || '').toLowerCase();
      const sf = state.subsStatusFilter;
      let filtered = rows.filter(r => {
        if (sf !== 'all' && String(r.status || '').toLowerCase() !== sf) return false;
        if (q) {
          const hay = (String(r.customer_email || '') + ' ' +
            String(r.customer_name || '') + ' ' +
            String(r.plan_name || '') + ' ' +
            String(r.skus || '')).toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
      filtered.sort((a, b) => {
        if (state.subsSort === 'created') return String(b.created_at).localeCompare(String(a.created_at));
        if (state.subsSort === 'next') return String(a.next_billing_date || '9').localeCompare(String(b.next_billing_date || '9'));
        return b.monthly_value_gbp - a.monthly_value_gbp;
      });
      countLbl.textContent = filtered.length + ' of ' + rows.length;
      tbody.innerHTML = filtered.map(r => {
        const status = String(r.status || '').toUpperCase();
        const cls = status === 'ACTIVE' ? 'ok' : status === 'PAUSED' ? 'warn' : status === 'FAILED' ? 'err' : 'mute';
        const name = r.customer_name && r.customer_name.trim();
        const customerCell = `<div>${esc(r.customer_email || '-')}</div>${name ? `<div class="ag-muted" style="font-size:11px;">${esc(name)}</div>` : ''}`;
        return `<tr>
          <td><span class="ag-pill ${cls}">${esc(status || '-')}</span></td>
          <td>${customerCell}</td>
          <td>${esc(r.plan_name || '-')}</td>
          <td class="num">£${fmtNum(r.cycle_price_gbp)}</td>
          <td class="num"><strong>£${fmtNum(r.monthly_value_gbp)}</strong></td>
          <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.next_billing_date))}</td>
          <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.created_at))}</td>
        </tr>`;
      }).join('');
    }
    search.addEventListener('input', debounce(e => { state.subsSearch = e.target.value; paint(); }));
    statusSel.addEventListener('change', e => { state.subsStatusFilter = e.target.value; paint(); });
    sortSel.addEventListener('change', e => { state.subsSort = e.target.value; paint(); });
    const syncBtn = $('#subs-sync-now');
    if (syncBtn) syncBtn.addEventListener('click', async () => {
      syncBtn.disabled = true;
      const original = syncBtn.textContent;
      syncBtn.textContent = 'Syncing…';
      try {
        const r = await AG_ADMIN.api('ui_sync_subscriptions', {});
        if (r.scope_error) {
          AG_ADMIN.toast('Scope not granted, sync skipped', true);
        } else {
          AG_ADMIN.toast(`${r.contracts || 0} contracts · ${r.active || 0} active · MRR £${fmtNum(r.mrr_gbp || 0)}`);
          delete state.cache.subscriptions;
          saveCache();
          renderTab('subscriptions');
        }
      } catch (e) {
        AG_ADMIN.toast(e.message || e, true);
      } finally {
        syncBtn.disabled = false;
        syncBtn.textContent = original;
      }
    });
    paint();

    if (series.length > 1) {
      chartLine('ch-subs-mrr', series.map(s => ({ date: s.date, value: s.mrr_gbp })), 'MRR (£)', { decimals: 0 });
    }
  }

  /* ─── Tab: Marketing spend ──────────────────────────────────
     Phase 1. Operator-entered recurring + ad-hoc fees. Meta/Google
     ad-spend columns are placeholders until Phase 2/3 sync wires up. */
  function renderMarketing(root, p) {
    const h = (p && p.hero) || {};
    const recurring = (p && p.recurring) || [];
    const adhoc = (p && p.adhoc) || [];
    const mix = (p && p.category_mix) || [];
    const series = (p && p.series) || [];
    const cats = (p && p.categories) || { recurring: [], adhoc: [] };
    const uploads = ((p && p.uploads && p.uploads.uploads) || []);
    const coverage = ((p && p.uploads && p.uploads.coverage) || {});
    const coverageEstimated = ((p && p.uploads && p.uploads.coverage_estimated) || {});
    const dailyAvg = ((p && p.uploads && p.uploads.daily_avg) || {});

    root.innerHTML = `
      <div class="ag-toolbar" style="margin-bottom:8px;">
        <h2 class="ag-h2" style="margin:0;">Marketing spend <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· month-to-date (day ${h.days_elapsed || 0} of ${h.days_in_month || 30})</span></h2>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn ag-btn-primary" id="mkt-paste-spend" style="padding:6px 12px;font-size:12px;">Upload ad spend</button>
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:16px;">
        ${statCard('Total MTD', '£' + fmtNum(h.total_mtd_gbp || 0), 'ok')}
        ${statCard('Recurring MTD', '£' + fmtNum(h.recurring_mtd_gbp || 0), '')}
        ${statCard('Ad-hoc MTD', '£' + fmtNum(h.adhoc_mtd_gbp || 0), '')}
        <div class="ag-stat">
          <div class="label">Ad spend MTD</div>
          <div class="value">£${fmtNum(h.ad_mtd_gbp || 0)}</div>
          ${(h.ad_mtd_estimated_gbp || 0) > 0 ? `<div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(h.ad_mtd_real_gbp || 0)} real · £${fmtNum(h.ad_mtd_estimated_gbp)} est</div>` : ''}
        </div>
        ${h.mer_mtd != null ? `<div class="ag-stat ${h.mer_mtd >= 1 ? 'ok' : 'bad'}"><div class="label">MER (blended)</div><div class="value">${h.mer_mtd.toFixed(1)}×</div><div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(h.revenue_net_mtd_gbp || 0)} net rev ÷ £${fmtNum(h.ad_mtd_gbp || 0)} ad</div></div>` : ''}
        ${statCard('MRC (committed)', '£' + fmtNum(h.mrc_gbp || 0), '')}
        ${(h.ad_meta_mtd_gbp || 0) > 0 ? `<div class="ag-stat"><div class="label">Meta</div><div class="value">£${fmtNum(h.ad_meta_mtd_gbp)}</div>${(h.ad_meta_mtd_estimated_gbp || 0) > 0 ? `<div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(h.ad_meta_mtd_real_gbp || 0)} real · £${fmtNum(h.ad_meta_mtd_estimated_gbp)} est</div>` : ''}</div>` : ''}
        ${(h.ad_google_mtd_gbp || 0) > 0 ? `<div class="ag-stat"><div class="label">Google</div><div class="value">£${fmtNum(h.ad_google_mtd_gbp)}</div>${(h.ad_google_mtd_estimated_gbp || 0) > 0 ? `<div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(h.ad_google_mtd_real_gbp || 0)} real · £${fmtNum(h.ad_google_mtd_estimated_gbp)} est</div>` : ''}</div>` : ''}
      </div>

      ${series.length ? `
        <div class="ag-chart" style="height:240px;margin-bottom:16px;">
          <div class="label">Daily spend, last 60 days</div>
          <canvas id="ch-marketing-daily"></canvas>
        </div>` : ''}

      ${renderMarketingUploadHistory(uploads, coverage, coverageEstimated, dailyAvg, (p && p.uploads && p.uploads.today_fraction) || 0)}

      ${mix.length ? `
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px;">Active recurring · monthly mix</h3>
        <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px;">
          ${mix.map(m => `
            <div class="ag-stat">
              <div class="label">${esc(m.category)}</div>
              <div class="value">£${fmtNum(m.monthly_gbp)}</div>
            </div>`).join('')}
        </div>` : ''}

      <div class="ag-toolbar" style="margin-top:24px;">
        <h3 style="margin:0;font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Recurring fees</h3>
        <span class="ag-muted" style="font-size:11px;">${recurring.length} entries</span>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn ag-btn-primary" id="mkt-add-recurring" type="button">+ Add recurring</button>
      </div>
      <table class="ag-table" style="margin-bottom:24px;"><thead><tr>
        <th>Vendor</th><th>Category</th><th class="num">£/cycle</th><th class="num">£/month</th><th class="num">£/day</th><th>Start</th><th>End</th><th>Notes</th><th></th>
      </tr></thead><tbody id="mkt-recurring-body">
        ${recurring.length ? recurring.map(r => marketingRecurringRow(r)).join('') :
          `<tr><td colspan="9" class="ag-empty" style="padding:10px;">No recurring fees yet. Add agency retainers, SaaS subscriptions and any other monthly commitments here.</td></tr>`}
      </tbody></table>

      <div class="ag-toolbar">
        <h3 style="margin:0;font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Ad-hoc fees</h3>
        <span class="ag-muted" style="font-size:11px;">${adhoc.length} entries</span>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn ag-btn-primary" id="mkt-add-adhoc" type="button">+ Add ad-hoc</button>
      </div>
      <table class="ag-table"><thead><tr>
        <th>Date</th><th>Vendor</th><th>Category</th><th class="num">£</th><th>Notes</th><th></th>
      </tr></thead><tbody id="mkt-adhoc-body">
        ${adhoc.length ? adhoc.map(r => marketingAdhocRow(r)).join('') :
          `<tr><td colspan="6" class="ag-empty" style="padding:10px;">No one-off fees yet. Add ad creative, photoshoot bills, sponsorships etc here.</td></tr>`}
      </tbody></table>

      <p class="ag-muted" style="font-size:11px;margin-top:16px;">Ad spend entered via CSV paste from Meta + Google Ads exports, gap-filled and pro-rated by the daily-average helper. Recurring + ad-hoc fees below feed the same MTD totals.</p>
    `;

    $('#mkt-add-recurring').addEventListener('click', () => openMarketingRecurringForm(null, cats));
    $('#mkt-add-adhoc').addEventListener('click', () => openMarketingAdhocForm(null, cats));
    bindMarketingRowActions(cats);
    const pasteBtn = $('#mkt-paste-spend');
    if (pasteBtn) pasteBtn.addEventListener('click', () => openPasteAdSpendForm());

    if (series.length) {
      /* Stacked daily chart, recurring + adhoc + meta + google.
         chartLine supports a single series so we layer by summing
         for v1 (cleaner) and split via tooltip extras. */
      const totalSeries = series.map(s => ({
        date: s.date,
        value: (s.recurring || 0) + (s.adhoc || 0) + (s.meta || 0) + (s.google || 0) + (s.other_ad || 0)
      }));
      chartLine('ch-marketing-daily', totalSeries, 'Daily spend (£)', { decimals: 0, beginAtZero: true });
    }
  }

  function marketingRecurringRow(r) {
    // Lifecycle vs today, mirroring the backend isActive gate (started AND not ended) that decides
    // what counts toward current spend, surfaced as a status pill so the table matches the maths.
    const todayISO = _iso(new Date());
    const ended = !!(r.end_date && r.end_date < todayISO);
    const upcoming = !ended && !!(r.start_date && r.start_date > todayISO); // starts in the future
    const statusPill = ended
      ? '<span class="ag-pill mute" style="font-size:9px;">ENDED</span>'
      : upcoming
        ? '<span class="ag-pill warn" style="font-size:9px;">UPCOMING</span>'
        : '<span class="ag-pill ok" style="font-size:9px;">ACTIVE</span>';
    const endLabel = r.end_date ? esc(fmtFriendly(r.end_date)) : '<span class="ag-muted">ongoing</span>';
    const freq = r.frequency || 'monthly';
    const perPeriod = r.amount_gbp_per_period != null ? r.amount_gbp_per_period : r.amount_gbp_monthly;
    const daily = (r.amount_gbp_monthly || 0) / 30.4375;
    // Dim ended rows so it's obvious at a glance they no longer count toward current spend.
    const rowStyle = ended ? ' style="opacity:0.5;"' : '';
    return `<tr data-mkt-rec="${esc(r.id)}"${rowStyle}>
      <td><strong>${esc(r.vendor || '-')}</strong> ${statusPill}</td>
      <td><span class="ag-pill mute">${esc(r.category || '-')}</span></td>
      <td class="num"><strong>£${fmtNum(perPeriod)}</strong><div class="ag-muted" style="font-size:10px;">/ ${esc(freq)}</div></td>
      <td class="num">£${fmtNum(r.amount_gbp_monthly || 0)}</td>
      <td class="num"><span class="ag-muted">£${fmtNum(daily)}/day</span></td>
      <td class="ag-muted" style="font-size:11px;">${r.start_date ? esc(fmtFriendly(r.start_date)) : '<span class="ag-muted">-</span>'}</td>
      <td class="ag-muted" style="font-size:11px;">${endLabel}</td>
      <td class="ag-muted" style="font-size:11px;">${esc((r.notes || '').slice(0, 60))}</td>
      <td><button class="ag-btn secondary" data-mkt-edit-rec="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Edit</button> <button class="ag-btn secondary" data-mkt-del-rec="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Delete</button></td>
    </tr>`;
  }

  function marketingAdhocRow(r) {
    return `<tr data-mkt-adhoc="${esc(r.id)}">
      <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.date))}</td>
      <td><strong>${esc(r.vendor || '-')}</strong></td>
      <td><span class="ag-pill mute">${esc(r.category || '-')}</span></td>
      <td class="num"><strong>£${fmtNum(r.amount_gbp)}</strong></td>
      <td class="ag-muted" style="font-size:11px;">${esc((r.notes || '').slice(0, 60))}</td>
      <td><button class="ag-btn secondary" data-mkt-edit-adhoc="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Edit</button> <button class="ag-btn secondary" data-mkt-del-adhoc="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Delete</button></td>
    </tr>`;
  }

  function bindMarketingRowActions(cats) {
    document.querySelectorAll('[data-mkt-edit-rec]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.mktEditRec;
      const row = (state.cache.marketing && state.cache.marketing.payload.recurring || []).find(r => r.id === id);
      if (row) openMarketingRecurringForm(row, cats);
    }));
    document.querySelectorAll('[data-mkt-del-rec]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.mktDelRec;
      if (!confirm('Delete this recurring fee?')) return;
      try {
        await AG_ADMIN.api('ui_delete_marketing_fee', { tab: 'recurring', id });
        AG_ADMIN.toast('Deleted');
        delete state.cache.marketing; renderTab('marketing');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    }));
    document.querySelectorAll('[data-mkt-edit-adhoc]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.mktEditAdhoc;
      const row = (state.cache.marketing && state.cache.marketing.payload.adhoc || []).find(r => r.id === id);
      if (row) openMarketingAdhocForm(row, cats);
    }));
    document.querySelectorAll('[data-mkt-del-adhoc]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.mktDelAdhoc;
      if (!confirm('Delete this ad-hoc fee?')) return;
      try {
        await AG_ADMIN.api('ui_delete_marketing_fee', { tab: 'adhoc', id });
        AG_ADMIN.toast('Deleted');
        delete state.cache.marketing; renderTab('marketing');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    }));
  }

  function openMarketingRecurringForm(existing, cats) {
    const isEdit = !!existing;
    const today = new Date().toISOString().slice(0, 10);
    const r = existing || { vendor: '', category: 'agency', amount_gbp_per_period: '', amount_gbp_monthly: '', frequency: 'monthly', start_date: today, end_date: '', notes: '' };
    const opts = (cats.recurring || []).map(c => `<option value="${esc(c)}" ${r.category === c ? 'selected' : ''}>${esc(c)}</option>`).join('');
    const freqs = ['weekly', 'monthly', 'quarterly', 'annually'];
    const currentFreq = r.frequency || 'monthly';
    const freqOpts = freqs.map(f => `<option value="${f}" ${currentFreq === f ? 'selected' : ''}>${f}</option>`).join('');
    const initialAmount = r.amount_gbp_per_period != null ? r.amount_gbp_per_period : (r.amount_gbp_monthly || '');
    showMarketingForm({
      title: isEdit ? 'Edit recurring fee' : 'New recurring fee',
      body: `
        <label class="ag-fld"><span>Vendor</span><input id="mkt-rec-vendor" type="text" value="${esc(r.vendor)}" placeholder="e.g. Sociable Pickle"></label>
        <label class="ag-fld"><span>Category</span><select id="mkt-rec-category">${opts}</select></label>
        <div class="ag-bnd-row">
          <label class="ag-fld"><span>Frequency</span><select id="mkt-rec-frequency" onchange="(function(s){var l=document.getElementById('mkt-rec-period-label');var m={weekly:'week',monthly:'month',quarterly:'quarter',annually:'year'};if(l)l.textContent=m[s.value]||'month';})(this)">${freqOpts}</select></label>
          <label class="ag-fld"><span>Amount per <span id="mkt-rec-period-label">${esc(currentFreq === 'monthly' ? 'month' : currentFreq === 'weekly' ? 'week' : currentFreq === 'quarterly' ? 'quarter' : 'year')}</span> (£, ex-VAT)</span><input id="mkt-rec-amount" type="number" step="0.01" min="0" value="${esc(initialAmount)}" placeholder="e.g. 1500"></label>
        </div>
        <div class="ag-bnd-row"><label class="ag-fld"><span>Start date</span><input id="mkt-rec-start" type="date" value="${esc(r.start_date)}"></label>
          <label class="ag-fld"><span>End date (blank = ongoing)</span><input id="mkt-rec-end" type="date" value="${esc(r.end_date)}"></label></div>
        <label class="ag-fld"><span>Notes</span><textarea id="mkt-rec-notes" rows="2">${esc(r.notes)}</textarea></label>
      `,
      onSave: async () => {
        const frequency = document.getElementById('mkt-rec-frequency').value || 'monthly';
        const amountPerPeriod = parseFloat(document.getElementById('mkt-rec-amount').value) || 0;
        const fields = {
          vendor: document.getElementById('mkt-rec-vendor').value.trim(),
          category: document.getElementById('mkt-rec-category').value,
          frequency: frequency,
          amount_gbp_per_period: amountPerPeriod,
          start_date: document.getElementById('mkt-rec-start').value,
          end_date: document.getElementById('mkt-rec-end').value,
          notes: document.getElementById('mkt-rec-notes').value
        };
        if (!fields.vendor) { AG_ADMIN.toast('Vendor required', true); return false; }
        if (!(amountPerPeriod > 0)) { AG_ADMIN.toast('Amount must be > 0', true); return false; }
        await AG_ADMIN.api('ui_save_marketing_recurring', { id: existing ? existing.id : '', fields });
        AG_ADMIN.toast(isEdit ? 'Updated' : 'Added');
        delete state.cache.marketing; renderTab('marketing');
        return true;
      }
    });
  }

  function renderMarketingUploadHistory(uploads, coverage, coverageEstimated, dailyAvg, todayFraction) {
    coverageEstimated = coverageEstimated || {};
    dailyAvg = dailyAvg || {};
    todayFraction = todayFraction || 0;
    if (!uploads.length && (!coverage || !Object.keys(coverage).length)) return '';
    /* "Weeks covered", build the last 8 ISO weeks (Mon–Sun) and tick
       each platform that has spend data for ≥1 day of that week. Gives
       a quick "have I done this week's upload?" signal. */
    function isoWeekStart(d) {
      const dt = new Date(d);
      const day = dt.getUTCDay() || 7;
      dt.setUTCDate(dt.getUTCDate() - (day - 1));
      dt.setUTCHours(0, 0, 0, 0);
      return dt;
    }
    function fmtYmd(dt) { return dt.toISOString().slice(0, 10); }
    function fmtShort(dt) {
      return dt.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
    }
    const today = new Date();
    const thisWeekStart = isoWeekStart(today);
    const platforms = ['meta', 'google', 'other'].filter(p => Object.keys(coverage[p] || {}).length || uploads.some(u => u.platform === p));
    const weeks = [];
    for (let i = 0; i < 8; i++) {
      const start = new Date(thisWeekStart);
      start.setUTCDate(start.getUTCDate() - i * 7);
      const end = new Date(start);
      end.setUTCDate(end.getUTCDate() + 6);
      weeks.push({ start, end, key: fmtYmd(start) });
    }
    function weekStatus(platform, week) {
      const cov = coverage[platform] || {};
      const covEst = coverageEstimated[platform] || {};
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(week.start);
        d.setUTCDate(d.getUTCDate() + i);
        const k = fmtYmd(d);
        const realVal = cov[k];
        const estVal = covEst[k];
        days.push({
          date: k,
          has_real: realVal != null,
          has_est: realVal == null && estVal != null,
          value: realVal != null ? realVal : (estVal || 0)
        });
      }
      const realCount = days.filter(d => d.has_real).length;
      const estCount  = days.filter(d => d.has_est).length;
      const total = days.reduce((a, d) => a + d.value, 0);
      const isThisWeek = week.key === fmtYmd(thisWeekStart);
      const expected = isThisWeek ? Math.min(7, Math.floor((today - week.start) / 86400000) + 1) : 7;
      return { realCount, estCount, expected, total, days, isThisWeek };
    }
    const weeksRows = weeks.map(w => {
      const cells = platforms.map(pf => {
        const st = weekStatus(pf, w);
        const covered = st.realCount + st.estCount;
        let cls, label;
        if (covered === 0) {
          cls = 'mute'; label = '-';
        } else if (st.realCount >= st.expected) {
          /* All days have real data → green tick */
          cls = 'ok'; label = '✓ £' + fmtNum(st.total);
        } else if (st.realCount === 0) {
          /* All filled with estimates → orange "est" */
          cls = 'warn'; label = `est · £${fmtNum(st.total)}`;
        } else {
          /* Mixed: real + estimated → orange with mix breakdown */
          cls = 'warn';
          label = `${st.realCount}r+${st.estCount}e · £${fmtNum(st.total)}`;
        }
        const title = `${st.realCount} real day${st.realCount===1?'':'s'} + ${st.estCount} estimated · expected ${st.expected}`;
        return `<td class="num"><span class="ag-pill ${cls}" style="font-size:10px;" title="${esc(title)}">${label}</span></td>`;
      }).join('');
      return `<tr>
        <td><strong>${fmtShort(w.start)} – ${fmtShort(w.end)}</strong>${w.isThisWeek ? ' <span class="ag-muted" style="font-size:10px;">(this week)</span>' : ''}</td>
        ${cells}
      </tr>`;
    }).join('');

    const recent = uploads.slice(0, 8).map(u => {
      const when = u.uploaded_at ? new Date(u.uploaded_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-';
      const flags = [];
      if (u.aggregated) flags.push('<span class="ag-muted" style="font-size:10px;">spread evenly</span>');
      if (u.skipped_today) flags.push('<span class="ag-muted" style="font-size:10px;">skip today</span>');
      return `<tr>
        <td><strong>${esc(u.platform)}</strong></td>
        <td>${esc(u.from)} → ${esc(u.to)}</td>
        <td class="num">${u.rows}</td>
        <td class="num">£${fmtNum(u.spend_gbp)}</td>
        <td>${esc(u.filename || '-')}</td>
        <td><span class="ag-muted" style="font-size:11px;">${esc(when)}</span> ${flags.join(' ')}</td>
      </tr>`;
    }).join('');

    const todayPct = Math.round(todayFraction * 100);
    const avgLine = (dailyAvg && (dailyAvg.meta || dailyAvg.google))
      ? `<p class="ag-muted" style="font-size:11px;margin:0 0 8px;">Days without a real upload are filled with the trailing-30-day average (£${fmtNum(dailyAvg.meta || 0)}/day Meta · £${fmtNum(dailyAvg.google || 0)}/day Google) and shown in <span class="ag-pill warn" style="font-size:9px;">orange</span>. Today is pro-rated to ${todayPct}% (${todayPct}% of the day elapsed). Green ✓ = all-real, gray - = no data yet.</p>`
      : '';
    return `
      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px;">Weeks covered</h3>
      ${avgLine}
      <table class="ag-table" style="margin-bottom:16px;">
        <thead><tr>
          <th>Week</th>
          ${platforms.map(pf => `<th class="num">${esc(pf)}</th>`).join('')}
        </tr></thead>
        <tbody>${weeksRows}</tbody>
      </table>
      ${recent ? `
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px;">Recent uploads</h3>
        <table class="ag-table" style="margin-bottom:16px;">
          <thead><tr><th>Platform</th><th>Window</th><th class="num">Rows</th><th class="num">Spend</th><th>File</th><th>When</th></tr></thead>
          <tbody>${recent}</tbody>
        </table>` : ''}
    `;
  }

  function openPasteAdSpendForm() {
    const platforms = [
      { v: 'auto',   label: 'Auto-detect (recommended)' },
      { v: 'meta',   label: 'Meta (Facebook + Instagram)' },
      { v: 'google', label: 'Google Ads' },
      { v: 'other',  label: 'Other (Pinterest, TikTok, etc.)' }
    ];
    /* Always start on Auto. The "one upload box" flow. The dropdown stays as a
       manual override for the rare case detection can't tell (platform_unclear). */
    const lastPlatform = 'auto';
    const opts = platforms.map(p => `<option value="${p.v}" ${p.v === lastPlatform ? 'selected' : ''}>${esc(p.label)}</option>`).join('');
    const state_ = { text: '', filename: '' };
    showMarketingForm({
      title: 'Upload ad spend',
      body: `
        <div style="font-size:11px;color:var(--ag-muted,#666);margin-bottom:10px;line-height:1.5;">
          Drop any CSV you exported from Meta Ads Manager or Google Ads. The platform is <b>auto-detected</b> from the file. Default Ad-sets / Campaigns export works (one row per ad set covering the range). Adding <b>Day</b> as a breakdown gives true per-day accuracy.
        </div>
        <div class="ag-bnd-row">
          <label class="ag-fld"><span>Platform</span><select id="paste-platform">${opts}</select></label>
          <label class="ag-fld"><span>Account label (optional)</span><input id="paste-account" type="text" placeholder="e.g. AG-Main" value=""></label>
        </div>
        <div id="ad-drop-zone" style="border:2px dashed var(--c-border,#d0d0d0);border-radius:8px;padding:24px;text-align:center;cursor:pointer;background:#fafafa;transition:all 0.15s;">
          <div style="font-size:13px;font-weight:600;margin-bottom:4px;">Drag &amp; drop CSV here</div>
          <div class="ag-muted" style="font-size:11px;">or click to choose a file</div>
          <input type="file" id="ad-file-picker" accept=".csv,.tsv,.txt" style="display:none;">
          <div id="ad-drop-status" class="ag-muted" style="font-size:11px;margin-top:10px;"></div>
        </div>
        <label style="display:flex;align-items:center;gap:8px;margin-top:10px;font-size:12px;">
          <input type="checkbox" id="paste-skip-today" checked>
          <span>Skip today's incomplete day (recommended, re-upload next week to fill it in)</span>
        </label>
        <details style="margin-top:10px;">
          <summary style="font-size:11px;color:var(--ag-muted,#666);cursor:pointer;">Or paste raw text instead</summary>
          <textarea id="paste-text" rows="6" style="font-family:monospace;font-size:11px;margin-top:6px;width:100%;" placeholder="Reporting starts,Reporting ends,Ad set name,…,Amount spent (GBP),…&#10;2026-06-08,2026-06-08,Advantage+ Ad Set,…,179.45,…"></textarea>
        </details>
      `,
      onMount: () => {
        const zone = document.getElementById('ad-drop-zone');
        const picker = document.getElementById('ad-file-picker');
        const status = document.getElementById('ad-drop-status');
        function readFile(file) {
          if (!file) return;
          state_.filename = file.name;
          const reader = new FileReader();
          reader.onload = (e) => {
            state_.text = String(e.target.result || '');
            const lines = state_.text.split(/\r?\n/).filter(l => l.trim()).length;
            status.innerHTML = `<b>${esc(file.name)}</b> · ${lines - 1} data rows ready to upload`;
            status.style.color = 'var(--ag-success,#0a7)';
          };
          reader.readAsText(file);
        }
        zone.addEventListener('click', () => picker.click());
        picker.addEventListener('change', (e) => readFile(e.target.files[0]));
        zone.addEventListener('dragover', (e) => { e.preventDefault(); zone.style.background = '#eef'; });
        zone.addEventListener('dragleave', () => { zone.style.background = '#fafafa'; });
        zone.addEventListener('drop', (e) => {
          e.preventDefault(); zone.style.background = '#fafafa';
          readFile(e.dataTransfer.files[0]);
        });
      },
      onSave: async () => {
        const platform = document.getElementById('paste-platform').value;
        const account  = document.getElementById('paste-account').value.trim();
        const skipToday = document.getElementById('paste-skip-today').checked;
        const pasted = document.getElementById('paste-text').value.trim();
        const text = pasted || state_.text;
        if (!text) { AG_ADMIN.toast('Drop a file or paste data first', true); return false; }
        localStorage.setItem('ag_paste_ad_platform', platform);
        const r = await AG_ADMIN.api('ui_paste_ad_spend', { platform, account, text, skip_today: skipToday, filename: state_.filename });
        if (!r.ok) {
          console.log('Upload ad spend response:', r);
          if (r.error === 'platform_unclear') {
            /* Detection couldn't tell, nudge the operator to the override
               dropdown (which is right there) instead of a dead-end error. */
            AG_ADMIN.toast("Couldn't auto-detect the platform. Pick Meta or Google above, then upload again.", true);
            const sel = document.getElementById('paste-platform');
            if (sel) { sel.focus(); sel.style.outline = '2px solid var(--ag-warn,#f59e0b)'; }
            return false;
          }
          AG_ADMIN.toast('Upload failed: ' + (r.detail || r.error || 'unknown') + ' (see console)', true);
          return false;
        }
        /* Show the RESOLVED platform (r.platform), with a tick when it was
           auto-detected, so the operator can confirm it filed correctly. */
        const label = (r.platform || platform) + (r.detected ? ' (auto-detected)' : '');
        const parts = [label, `${r.rows} rows`, `£${fmtNum(r.spend_gbp)}`, `(${r.from} → ${r.to})`];
        if (r.dropped_today) parts.push(`skipped today (${r.dropped_today} partial rows)`);
        if (r.aggregated) parts.push('spread evenly');
        AG_ADMIN.toast(parts.join(' · '));
        delete state.cache.marketing; saveCache(); renderTab('marketing');
        return true;
      }
    });
  }

  function openMarketingAdhocForm(existing, cats) {
    const isEdit = !!existing;
    const today = new Date().toISOString().slice(0, 10);
    const r = existing || { date: today, vendor: '', category: 'creative', amount_gbp: '', notes: '' };
    const opts = (cats.adhoc || []).map(c => `<option value="${esc(c)}" ${r.category === c ? 'selected' : ''}>${esc(c)}</option>`).join('');
    showMarketingForm({
      title: isEdit ? 'Edit ad-hoc fee' : 'New ad-hoc fee',
      body: `
        <label class="ag-fld"><span>Date</span><input id="mkt-ah-date" type="date" value="${esc(r.date)}"></label>
        <label class="ag-fld"><span>Vendor</span><input id="mkt-ah-vendor" type="text" value="${esc(r.vendor)}" placeholder="e.g. Studio X"></label>
        <label class="ag-fld"><span>Category</span><select id="mkt-ah-category">${opts}</select></label>
        <label class="ag-fld"><span>Amount (£, ex-VAT)</span><input id="mkt-ah-amount" type="number" step="0.01" min="0" value="${esc(r.amount_gbp)}" placeholder="e.g. 800"></label>
        <label class="ag-fld"><span>Notes</span><textarea id="mkt-ah-notes" rows="2">${esc(r.notes)}</textarea></label>
      `,
      onSave: async () => {
        const fields = {
          date: document.getElementById('mkt-ah-date').value,
          vendor: document.getElementById('mkt-ah-vendor').value.trim(),
          category: document.getElementById('mkt-ah-category').value,
          amount_gbp: parseFloat(document.getElementById('mkt-ah-amount').value) || 0,
          notes: document.getElementById('mkt-ah-notes').value
        };
        if (!fields.date) { AG_ADMIN.toast('Date required', true); return false; }
        if (!fields.vendor) { AG_ADMIN.toast('Vendor required', true); return false; }
        if (!(fields.amount_gbp > 0)) { AG_ADMIN.toast('Amount must be > 0', true); return false; }
        await AG_ADMIN.api('ui_save_marketing_adhoc', { id: existing ? existing.id : '', fields });
        AG_ADMIN.toast(isEdit ? 'Updated' : 'Added');
        delete state.cache.marketing; renderTab('marketing');
        return true;
      }
    });
  }

  /* Lightweight modal, mirrors the bundle-help-overlay shape but
     editable. Closes on ESC + backdrop click + Cancel + successful save. */
  function showMarketingForm({ title, body, onSave, onMount }) {
    function onEsc(e) { if (e.key === 'Escape') close(); }
    const close = () => { const ov = document.getElementById('mkt-form-overlay'); if (ov) ov.remove(); document.removeEventListener('keydown', onEsc); };
    if (document.getElementById('mkt-form-overlay')) close();
    document.body.insertAdjacentHTML('beforeend', `
      <div id="mkt-form-overlay" style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:48px 16px;overflow:auto;">
        <div style="background:#fff;max-width:520px;width:100%;border-radius:8px;box-shadow:0 24px 60px rgba(0,0,0,0.25);padding:20px;">
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;">
            <h2 style="margin:0;font-size:17px;">${esc(title)}</h2>
            <span class="spacer" style="flex:1;"></span>
            <button id="mkt-form-cancel" class="ag-btn secondary" style="padding:6px 10px;">Cancel</button>
          </div>
          <div style="display:flex;flex-direction:column;gap:10px;">${body}</div>
          <div style="display:flex;justify-content:flex-end;margin-top:16px;">
            <button id="mkt-form-save" class="ag-btn ag-btn-primary">Save</button>
          </div>
        </div>
      </div>`);
    const ov = document.getElementById('mkt-form-overlay');
    ov.addEventListener('click', e => { if (e.target === ov) close(); });
    document.getElementById('mkt-form-cancel').addEventListener('click', close);
    document.addEventListener('keydown', onEsc);
    const saveBtn = document.getElementById('mkt-form-save');
    saveBtn.addEventListener('click', async () => {
      saveBtn.disabled = true; const orig = saveBtn.textContent; saveBtn.textContent = 'Saving…';
      try {
        const ok = await onSave();
        if (ok !== false) close();
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
      finally { saveBtn.disabled = false; saveBtn.textContent = orig; }
    });
    if (typeof onMount === 'function') {
      try { onMount(); } catch (e) { console.error('onMount error:', e); }
    }
  }

  /* ─── Tab: Operating Costs ───────────────────────────────────
     Parallel to Marketing, covers wages / rent / utilities / etc.
     Same editor pattern, no Meta/Google sync layer. */
  function renderCosts(root, p) {
    const h = (p && p.hero) || {};
    const recurring = (p && p.recurring) || [];
    const adhoc = (p && p.adhoc) || [];
    const mix = (p && p.category_mix) || [];
    const series = (p && p.series) || [];
    const cats = (p && p.categories) || { recurring: [], adhoc: [] };

    root.innerHTML = `
      <h2 class="ag-h2">Operating costs <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· month-to-date (day ${h.days_elapsed || 0} of ${h.days_in_month || 30}) · non-marketing overheads (Marketing tab covers ad spend)</span></h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:16px;">
        ${statCard('Total MTD', '£' + fmtNum(h.total_mtd_gbp || 0), 'ok')}
        ${statCard('Recurring MTD', '£' + fmtNum(h.recurring_mtd_gbp || 0), '')}
        ${statCard('Ad-hoc MTD', '£' + fmtNum(h.adhoc_mtd_gbp || 0), '')}
        ${statCard('MRC (committed)', '£' + fmtNum(h.mrc_gbp || 0), '')}
        ${statCard('Active commitments', h.active_recurring_count || 0, '')}
      </div>

      ${series.length ? `
        <div class="ag-chart" style="height:240px;margin-bottom:16px;">
          <div class="label">Daily operating spend, last 60 days</div>
          <canvas id="ch-costs-daily"></canvas>
        </div>` : ''}

      ${mix.length ? `
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px;">Active recurring · monthly mix</h3>
        <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:16px;">
          ${mix.map(m => `
            <div class="ag-stat">
              <div class="label">${esc(m.category)}</div>
              <div class="value">£${fmtNum(m.monthly_gbp)}</div>
            </div>`).join('')}
        </div>` : ''}

      <div class="ag-toolbar" style="margin-top:24px;">
        <h3 style="margin:0;font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Recurring costs</h3>
        <span class="ag-muted" style="font-size:11px;">${recurring.length} entries</span>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn ag-btn-primary" id="costs-add-recurring" type="button">+ Add recurring</button>
      </div>
      <table class="ag-table" style="margin-bottom:24px;"><thead><tr>
        <th>Vendor</th><th>Category</th><th class="num">£/cycle</th><th class="num">£/month</th><th class="num">£/day</th><th>Start</th><th>End</th><th>Notes</th><th></th>
      </tr></thead><tbody id="costs-recurring-body">
        ${recurring.length ? recurring.map(r => costsRecurringRow(r)).join('') :
          `<tr><td colspan="9" class="ag-empty" style="padding:10px;">No recurring costs yet. Add wages, rent, utilities and other monthly commitments here.</td></tr>`}
      </tbody></table>

      <div class="ag-toolbar">
        <h3 style="margin:0;font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Ad-hoc costs</h3>
        <span class="ag-muted" style="font-size:11px;">${adhoc.length} entries</span>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn ag-btn-primary" id="costs-add-adhoc" type="button">+ Add ad-hoc</button>
      </div>
      <table class="ag-table"><thead><tr>
        <th>Date</th><th>Vendor</th><th>Category</th><th class="num">£</th><th>Notes</th><th></th>
      </tr></thead><tbody id="costs-adhoc-body">
        ${adhoc.length ? adhoc.map(r => costsAdhocRow(r)).join('') :
          `<tr><td colspan="6" class="ag-empty" style="padding:10px;">No one-off costs yet. Add equipment purchases, repairs, travel, legal/consulting fees here.</td></tr>`}
      </tbody></table>

      <p class="ag-muted" style="font-size:11px;margin-top:16px;">Marketing spend is tracked separately on the <b>Marketing</b> tab (ad spend + agency fees). This tab captures everything else: wages, rent, utilities, software, etc.</p>
    `;

    $('#costs-add-recurring').addEventListener('click', () => openCostsRecurringForm(null, cats));
    $('#costs-add-adhoc').addEventListener('click', () => openCostsAdhocForm(null, cats));
    bindCostsRowActions(cats);

    if (series.length) {
      const totalSeries = series.map(s => ({ date: s.date, value: (s.recurring || 0) + (s.adhoc || 0) }));
      chartLine('ch-costs-daily', totalSeries, 'Daily operating cost (£)', { decimals: 0, beginAtZero: true });
    }
  }

  function renderTotalCosts(root, p) {
    const mkt = (p && p.marketing) || {};
    const ops = (p && p.operating) || {};
    const cogsP = (p && p.cogs) || {};
    const shipP = (p && p.shipping) || {};
    const vat = (p && p.vat) || null;
    const mh  = mkt.hero || {};
    const oh  = ops.hero || {};
    const ms  = mkt.series || [];
    const os  = ops.series || [];

    const cogs_mtd = cogsP.cogs_mtd_gbp || 0;
    const shipping_mtd = shipP.shipping_mtd_gbp || 0;
    const ship_first = shipP.first_box_gbp || 0;
    const ship_extra = shipP.extra_box_gbp || 0;
    const ship_orders = shipP.orders_counted || 0;
    const ship_boxes = shipP.boxes_total || 0;
    const ship_avg_boxes = shipP.avg_boxes_per_order || 0;
    const ship_zones = shipP.zone_totals_mtd || {};
    const ship_zone_orders = shipP.zone_orders_mtd || {};
    const ship_missing_pc = shipP.orders_missing_postcode_mtd || 0;
    /* NET PROFIT MTD comes from the Pulse's month-so-far window (audit 2026-10-01). It used to be
       ex-VAT goods revenue BEFORE refunds minus gross costs, so it left out refunds, payment fees,
       the cost of restocked returns, CS replacement parts and the shipping customers paid for,
       and could read thousands higher than the Pulse days added up. The cost table below is still
       the gross spend by bucket; the profit line is the Pulse's. Falls back to the old sum only if
       the window could not be fetched, and says so. */
    const month = (p && p.month) || null;
    const monthSpend = month && month.spend ? month.spend : null;
    const fromPulse = !!(month && monthSpend);
    const revenue_mtd = fromPulse ? (month.revenue_net || 0) : (cogsP.revenue_net_mtd_gbp || 0);
    const overhead_mtd  = (mh.total_mtd_gbp || 0) + (oh.total_mtd_gbp || 0);
    const total_mtd     = overhead_mtd + cogs_mtd + shipping_mtd;
    const recurring_mtd = (mh.recurring_mtd_gbp || 0) + (oh.recurring_mtd_gbp || 0);
    const adhoc_mtd     = (mh.adhoc_mtd_gbp || 0) + (oh.adhoc_mtd_gbp || 0);
    const ad_spend_mtd  = (mh.ad_mtd_gbp || 0);
    const mrc_total     = (mh.mrc_gbp || 0) + (oh.mrc_gbp || 0);
    const active_count  = (mh.active_recurring_count || 0) + (oh.active_recurring_count || 0);
    const days_in_month = mh.days_in_month || oh.days_in_month || cogsP.days_in_month || 30;
    const days_elapsed  = mh.days_elapsed || oh.days_elapsed || cogsP.days_elapsed || 0;
    const projected_mtd_run_rate = days_elapsed > 0 ? (total_mtd / days_elapsed) * days_in_month : 0;
    const net_profit_mtd = fromPulse ? (month.profit || 0) - (monthSpend.total || 0) : revenue_mtd - total_mtd;
    const net_margin_pct = revenue_mtd > 0 ? Math.round((net_profit_mtd / revenue_mtd) * 100) : null;
    const net_basis = fromPulse
      ? 'same as the Pulse: ex-VAT sales after refunds, minus cost of goods, fees, returns and all spend (shipping net of what customers paid)'
      : 'Pulse figure unavailable: revenue before refunds minus total cost below';

    /* Merge daily series by date. Operating + Marketing both end at today.
       COGS is intentionally NOT in the chart: we only know it MTD, not as
       a per-day series. Spreading it across the 60-day window would smear
       this-month COGS over pre-month dates which is misleading. The KPI
       cards + breakdown table cover COGS instead. */
    const opsBy = {}; os.forEach(s => { opsBy[s.date] = s; });
    const allDates = new Set();
    ms.forEach(s => allDates.add(s.date));
    os.forEach(s => allDates.add(s.date));
    const merged = Array.from(allDates).sort().map(date => {
      const m = ms.find(x => x.date === date) || {};
      const o = opsBy[date] || {};
      const mktDaily = (m.recurring || 0) + (m.adhoc || 0) + (m.meta || 0) + (m.google || 0) + (m.other_ad || 0);
      const opsDaily = (o.recurring || 0) + (o.adhoc || 0);
      return { date, marketing: mktDaily, operating: opsDaily, total: mktDaily + opsDaily };
    });

    root.innerHTML = `
      <h2 class="ag-h2">Total costs <span class="ag-muted" style="font-size:11px;font-weight:400;text-transform:none;letter-spacing:0;">· month-to-date (day ${days_elapsed} of ${days_in_month}) · COGS + marketing + operating</span></h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:16px;">
        ${statCard('Total cost MTD', '£' + fmtNum(total_mtd), '')}
        ${statCard('COGS MTD', '£' + fmtNum(cogs_mtd), '')}
        ${statCard('Marketing MTD', '£' + fmtNum(mh.total_mtd_gbp || 0), '')}
        ${statCard('Operating MTD', '£' + fmtNum(oh.total_mtd_gbp || 0), '')}
        <div class="ag-stat">
          <div class="label">Shipping MTD</div>
          <div class="value">£${fmtNum(shipping_mtd)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">${ship_orders} orders · ${ship_avg_boxes} boxes/order avg${(ship_zone_orders.highlands || ship_zone_orders.ni || ship_zone_orders.iom) ? ` · <span title="Mainland £${fmtNum(ship_zones.mainland || 0)} · Highlands £${fmtNum(ship_zones.highlands || 0)} (${ship_zone_orders.highlands || 0}) · NI £${fmtNum(ship_zones.ni || 0)} (${ship_zone_orders.ni || 0}) · IOM £${fmtNum(ship_zones.iom || 0)} (${ship_zone_orders.iom || 0})">includes ${(ship_zone_orders.highlands || 0) + (ship_zone_orders.ni || 0) + (ship_zone_orders.iom || 0)} offshore</span>` : ''} · <button type="button" id="shipping-settings-open" class="ag-link-btn" style="background:none;border:none;padding:0;color:inherit;text-decoration:underline;cursor:pointer;font-size:inherit;font-family:inherit;">edit rates</button></div>
        </div>
      </div>

      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:16px;">
        <div class="ag-stat">
          <div class="label">Revenue MTD</div>
          <div class="value">£${fmtNum(revenue_mtd)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">${fromPulse ? 'ex-VAT sales after refunds' : 'ex-VAT, before refunds'}</div>
        </div>
        <div class="ag-stat ${net_profit_mtd >= 0 ? 'ok' : 'bad'}">
          <div class="label">Net profit MTD${net_margin_pct != null ? ' · ' + net_margin_pct + '%' : ''}</div>
          <div class="value">£${fmtNum(net_profit_mtd)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">${esc(net_basis)}</div>
        </div>
        <div class="ag-stat">
          <div class="label">Ad spend MTD</div>
          <div class="value">£${fmtNum(ad_spend_mtd)}</div>
          ${(mh.ad_mtd_estimated_gbp || 0) > 0 ? `<div class="ag-muted" style="font-size:10px;margin-top:2px;">£${fmtNum(mh.ad_mtd_real_gbp || 0)} real · £${fmtNum(mh.ad_mtd_estimated_gbp)} est</div>` : ''}
        </div>
        ${statCard('MRC (committed)', '£' + fmtNum(mrc_total), '')}
        ${statCard('Avg £/day', '£' + fmtNum(days_elapsed > 0 ? total_mtd / days_elapsed : 0), '')}
      </div>

      ${merged.length ? `
        <div class="ag-chart" style="height:260px;margin-bottom:24px;">
          <div class="label">Daily total spend, marketing + operating, last 60 days</div>
          <canvas id="ch-totalcosts-stacked"></canvas>
        </div>` : ''}

      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 8px;">MTD split</h3>
      <table class="ag-table" style="margin-bottom:24px;">
        <thead><tr><th>Bucket</th><th class="num">MTD</th><th class="num">% of total</th><th class="num">Avg £/day</th></tr></thead>
        <tbody>
          ${totalCostsRow('COGS (cost of goods sold)', cogs_mtd, total_mtd, days_elapsed)}
          ${totalCostsRow('Outbound shipping (courier)', shipping_mtd, total_mtd, days_elapsed)}
          ${totalCostsRow('Marketing: recurring', mh.recurring_mtd_gbp || 0, total_mtd, days_elapsed)}
          ${totalCostsRow('Marketing: ad-hoc',    mh.adhoc_mtd_gbp || 0,     total_mtd, days_elapsed)}
          ${totalCostsRow('Marketing: ad spend',  ad_spend_mtd,              total_mtd, days_elapsed)}
          ${totalCostsRow('Operating: recurring', oh.recurring_mtd_gbp || 0, total_mtd, days_elapsed)}
          ${totalCostsRow('Operating: ad-hoc',    oh.adhoc_mtd_gbp || 0,     total_mtd, days_elapsed)}
          <tr style="border-top:2px solid var(--c-border);"><td><strong>Total cost</strong></td><td class="num"><strong>£${fmtNum(total_mtd)}</strong></td><td class="num">100%</td><td class="num"><strong>£${fmtNum(days_elapsed > 0 ? total_mtd / days_elapsed : 0)}</strong></td></tr>
          <tr><td><span class="ag-muted">Revenue MTD</span></td><td class="num"><span class="ag-muted">£${fmtNum(revenue_mtd)}</span></td><td class="num"></td><td class="num"><span class="ag-muted">£${fmtNum(days_elapsed > 0 ? revenue_mtd / days_elapsed : 0)}/day</span></td></tr>
          <tr style="border-top:2px solid var(--c-border);"><td><strong>Net profit MTD${net_margin_pct != null ? ' · ' + net_margin_pct + '%' : ''}</strong>${fromPulse ? ' <span class="ag-muted" style="font-size:11px;">Pulse basis: after refunds, fees and shipping customers paid, so not revenue minus the costs above</span>' : ''}</td><td class="num"><strong style="color:${net_profit_mtd >= 0 ? 'var(--ag-success,#0a7)' : 'var(--ag-danger,#c00)'};">£${fmtNum(net_profit_mtd)}</strong></td><td class="num"></td><td class="num"><strong>£${fmtNum(days_elapsed > 0 ? net_profit_mtd / days_elapsed : 0)}/day</strong></td></tr>
        </tbody>
      </table>

      ${vat ? `
      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:28px 0 8px;">VAT estimate · quarter ${vat.quarter_from} → ${vat.quarter_to} <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;">(rough)</span></h3>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(170px,1fr));margin-bottom:8px;">
        <div class="ag-stat">
          <div class="label">VAT payable ≈</div>
          <div class="value">£${fmtNum(vat.vat_payable_gbp)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">output − reclaimable input</div>
        </div>
        <div class="ag-stat">
          <div class="label">Output VAT (on sales)</div>
          <div class="value">£${fmtNum(vat.output_vat_gbp)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">sales £${fmtNum(vat.sales_vat_gbp)} − refunds £${fmtNum(vat.refund_vat_gbp)} · ${vat.orders_counted} orders</div>
        </div>
        <div class="ag-stat">
          <div class="label">Input VAT (reclaim)</div>
          <div class="value">−£${fmtNum(vat.input_vat_gbp)}</div>
          <div class="ag-muted" style="font-size:10px;margin-top:2px;">shipping £${fmtNum(vat.input_vat_shipping_gbp)} + overheads £${fmtNum(vat.input_vat_operating_gbp)}</div>
        </div>
      </div>
      <p class="ag-muted" style="font-size:11px;margin-bottom:24px;">${vat.note}</p>` : ''}

      <p class="ag-muted" style="font-size:11px;margin-top:16px;"><strong>All amounts shown ex-VAT.</strong> COGS pulled live from Daily_Aggregates (revenue × cost basis from Profitability tab). Outbound shipping = the real per-order carrier cost (ex-VAT) stamped by the shipping calculator, with the courier-rate estimate (£${fmtNum(ship_first)} first box + £${fmtNum(ship_extra)} × extra) as fallback for orders not yet stamped. Marketing + Operating overheads from the respective sub-tabs. Net profit = Revenue MTD − Total cost MTD (true bottom-line including COGS, courier, agency fees, ad spend, wages, rent, everything). Add or edit overhead entries on the sub-tabs; totals refresh on the next view.</p>
    `;

    /* Bind the "edit rates" link explicitly. Inline onclick handlers
       were being silently swallowed in some embedded contexts. */
    const editBtn = root.querySelector('#shipping-settings-open');
    if (editBtn) editBtn.addEventListener('click', e => {
      e.preventDefault();
      try { openShippingConfigModal(); }
      catch (err) { console.error('openShippingConfigModal:', err); AG_ADMIN.toast(err.message || err, true); }
    });

    if (merged.length) ensureChartJs(function () {
      const ctx = document.getElementById('ch-totalcosts-stacked');
      if (ctx) {
        if (ctx._chart) ctx._chart.destroy();
        const tick = chartTickColor(), grid = chartGridColor();
        const rawDates = merged.map(p => p.date);
        ctx._chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: merged.map(p => fmtChartDate(p.date)),
            datasets: [
              { label: 'Operating', data: merged.map(p => p.operating), backgroundColor: 'rgba(59,130,246,0.85)', stack: 's' },
              { label: 'Marketing', data: merged.map(p => p.marketing), backgroundColor: 'rgba(200, 16, 46,0.85)', stack: 's' }
            ]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: true, labels: { color: chartTextColor() } },
              tooltip: {
                mode: 'index', intersect: false,
                callbacks: {
                  title: items => items.length ? fmtFriendly(rawDates[items[0].dataIndex]) : '',
                  label: item => (item.dataset.label || '') + ': £' + fmtNum(item.parsed.y),
                  footer: items => 'Total: £' + fmtNum(items.reduce((a, i) => a + (i.parsed.y || 0), 0))
                }
              }
            },
            scales: {
              x: { stacked: true, ticks: { color: tick, maxTicksLimit: 8, autoSkip: true, maxRotation: 0 }, grid: { color: grid } },
              y: { stacked: true, beginAtZero: true, ticks: { color: tick, callback: v => '£' + Math.round(v) }, grid: { color: grid } }
            }
          }
        });
      }
    });
  }

  function openShippingConfigModal() {
    const p = (state.cache.totalcosts && state.cache.totalcosts.payload) || {};
    const sh = (p && p.shipping) || {};
    /* When the operator opens the modal cold, pre-fill the inputs
       with AG's known courier rates so the typical case is one click
       to save. Operator can override before saving. */
    /* `!= null` ONLY, never `> 0`. A deliberately ZEROED rate is a real setting (e.g. a
       surcharge the courier stopped charging), but `> 0` treated it as absent and showed the
       hardcoded default instead. Saving then re-persisted that default over the operator's 0,
       so the value could not be set to zero at all. That is the 2026-09-01 EU incident. */
    const dFirst = sh.first_box_gbp != null ? sh.first_box_gbp : 4.00;
    const dExtra = sh.extra_box_gbp != null ? sh.extra_box_gbp : 3.60;
    const dFuel  = sh.fuel_pct != null ? sh.fuel_pct : 1.4;
    const dFixed = sh.consignment_fixed_gbp != null ? sh.consignment_fixed_gbp : 1.48;
    const dHigh  = sh.highlands_per_parcel_gbp != null ? sh.highlands_per_parcel_gbp : 11.24;
    const dNi    = sh.ni_per_parcel_gbp != null ? sh.ni_per_parcel_gbp : 9.78;
    const dIom   = sh.iom_per_parcel_gbp != null ? sh.iom_per_parcel_gbp : 11.83;
    const missing = sh.orders_missing_postcode_mtd || 0;
    const overlay = document.createElement('div');
    overlay.id = 'ag-shipping-rates-modal';
    overlay.setAttribute('style', 'position:fixed!important;inset:0!important;background:rgba(0,0,0,0.5)!important;z-index:99999!important;display:flex!important;align-items:flex-start!important;justify-content:center!important;padding:48px 16px!important;overflow:auto!important;visibility:visible!important;opacity:1!important;');
    /* Inline styles throughout. The admin-shell context strips theme
       CSS classes so we can't rely on .ag-fld / .ag-bnd-row layout. */
    const fldStyle = 'display:flex;flex-direction:column;gap:4px;flex:1;min-width:0;';
    const labelStyle = 'font-size:11px;font-weight:600;color:#555;text-transform:uppercase;letter-spacing:0.3px;';
    const inputStyle = 'padding:8px 10px;font-size:14px;border:1px solid #ccc;border-radius:4px;width:100%;box-sizing:border-box;font-family:inherit;';
    const rowStyle = 'display:flex;gap:12px;margin-bottom:10px;';
    const sectionHeadStyle = 'font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin:16px 0 8px;color:#777;border-top:1px solid #eee;padding-top:14px;';
    overlay.innerHTML = `
      <div style="background:#fff;max-width:560px;width:100%;border-radius:8px;box-shadow:0 24px 60px rgba(0,0,0,0.25);padding:24px;font-family:system-ui,-apple-system,sans-serif;">
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;">
          <h2 style="margin:0;font-size:18px;font-weight:700;">Courier rates</h2>
          <span style="flex:1;"></span>
          <button id="ship-cancel" style="padding:6px 12px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer;font-size:13px;">Cancel</button>
        </div>
        <p style="font-size:11px;margin:0 0 12px;line-height:1.5;color:#666;">All figures ex-VAT. Mainland: <code>(first + (boxes − 1) × extra) × (1 + fuel%) + fixed</code>. Offshore zones: <code>per_parcel × boxes × (1 + fuel%) + fixed</code>. Each Shopify line unit = one box.</p>

        <div style="${sectionHeadStyle}border-top:none;padding-top:0;">Mainland UK</div>
        <div style="${rowStyle}">
          <label style="${fldStyle}"><span style="${labelStyle}">First parcel (£)</span><input id="ship-first" type="number" step="0.01" min="0" value="${esc(dFirst)}" style="${inputStyle}"></label>
          <label style="${fldStyle}"><span style="${labelStyle}">Every extra parcel (£)</span><input id="ship-extra" type="number" step="0.01" min="0" value="${esc(dExtra)}" style="${inputStyle}"></label>
        </div>

        <div style="${sectionHeadStyle}">Offshore zones (per parcel, flat)</div>
        <div style="${rowStyle}">
          <label style="${fldStyle}"><span style="${labelStyle}">Scottish Highlands (£/parcel)</span><input id="ship-highlands" type="number" step="0.01" min="0" value="${esc(dHigh)}" style="${inputStyle}"></label>
          <label style="${fldStyle}"><span style="${labelStyle}">Northern Ireland (£/parcel)</span><input id="ship-ni" type="number" step="0.01" min="0" value="${esc(dNi)}" style="${inputStyle}"></label>
        </div>
        <div style="${rowStyle}">
          <label style="${fldStyle}"><span style="${labelStyle}">Isle of Man (£/parcel)</span><input id="ship-iom" type="number" step="0.01" min="0" value="${esc(dIom)}" style="${inputStyle}"></label>
          <div style="flex:1;"></div>
        </div>

        <div style="${sectionHeadStyle}">Shared (apply to all zones)</div>
        <div style="${rowStyle}">
          <label style="${fldStyle}"><span style="${labelStyle}">Fuel surcharge (%)</span><input id="ship-fuel" type="number" step="0.01" min="0" value="${esc(dFuel)}" style="${inputStyle}"></label>
          <label style="${fldStyle}"><span style="${labelStyle}">Per-consignment fixed fees (£)</span><input id="ship-fixed" type="number" step="0.01" min="0" value="${esc(dFixed)}" style="${inputStyle}"></label>
        </div>

        <div id="ship-preview" style="font-size:11px;margin-top:14px;padding:10px 12px;background:#f6f8fa;border-radius:4px;line-height:1.6;color:#555;"></div>

        ${missing > 0 ? `
        <div style="margin-top:12px;padding:10px 12px;background:#fff5e6;border:1px solid #f5d99a;border-radius:4px;font-size:11px;line-height:1.5;">
          <b>${missing} this-month order${missing === 1 ? '' : 's'} have no postcode yet</b>. They're treated as mainland.
          <button id="ship-backfill" style="padding:4px 10px;font-size:11px;margin-left:8px;border:1px solid #ccc;background:#fff;border-radius:3px;cursor:pointer;">Backfill 60d</button>
        </div>` : ''}

        <div style="display:flex;justify-content:flex-end;margin-top:20px;">
          <button id="ship-save" style="padding:10px 20px;background:#c8102e;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:14px;font-weight:600;">Save</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
    document.getElementById('ship-cancel').addEventListener('click', () => overlay.remove());

    function updatePreview() {
      const first = parseFloat(document.getElementById('ship-first').value) || 0;
      const extra = parseFloat(document.getElementById('ship-extra').value) || 0;
      const fuel  = parseFloat(document.getElementById('ship-fuel').value)  || 0;
      const fixed = parseFloat(document.getElementById('ship-fixed').value) || 0;
      const high  = parseFloat(document.getElementById('ship-highlands').value) || 0;
      const ni    = parseFloat(document.getElementById('ship-ni').value) || 0;
      const iom   = parseFloat(document.getElementById('ship-iom').value) || 0;
      function calc(parcels) { return parcels + parcels * (fuel / 100) + fixed; }
      function mainland(n) { return calc(first + Math.max(0, n - 1) * extra); }
      function zone(rate, n) { return calc(rate * n); }
      document.getElementById('ship-preview').innerHTML = `
        <b>Live preview, 2-box order:</b><br>
        Mainland → £${mainland(2).toFixed(2)} · Highlands → £${zone(high, 2).toFixed(2)} · NI → £${zone(ni, 2).toFixed(2)} · IOM → £${zone(iom, 2).toFixed(2)}<br>
        <b>4-box order:</b><br>
        Mainland → £${mainland(4).toFixed(2)} · Highlands → £${zone(high, 4).toFixed(2)} · NI → £${zone(ni, 4).toFixed(2)} · IOM → £${zone(iom, 4).toFixed(2)}
      `;
    }
    ['ship-first', 'ship-extra', 'ship-fuel', 'ship-fixed', 'ship-highlands', 'ship-ni', 'ship-iom'].forEach(id => {
      document.getElementById(id).addEventListener('input', updatePreview);
    });
    updatePreview();

    const backfillBtn = document.getElementById('ship-backfill');
    if (backfillBtn) backfillBtn.addEventListener('click', async () => {
      backfillBtn.disabled = true; const orig = backfillBtn.textContent; backfillBtn.textContent = 'Pulling…';
      try {
        const r = await AG_ADMIN.api('ui_backfill_shipping_addresses', { days: 60 });
        if (r && r.ok === false) { AG_ADMIN.toast('Backfill failed: ' + (r.error || 'unknown'), true); return; }
        AG_ADMIN.toast(`Backfilled ${r && (r.upserts || r.pages_count) || ''} orders. Re-open the modal to see new totals`);
        overlay.remove();
        delete state.cache.totalcosts; delete state.cache.pulse;
        saveCache();
        renderTab('totalcosts');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
      finally { backfillBtn.disabled = false; backfillBtn.textContent = orig; }
    });

    document.getElementById('ship-save').addEventListener('click', async () => {
      const first = parseFloat(document.getElementById('ship-first').value);
      const extra = parseFloat(document.getElementById('ship-extra').value);
      const fuel  = parseFloat(document.getElementById('ship-fuel').value);
      const fixed = parseFloat(document.getElementById('ship-fixed').value);
      const high  = parseFloat(document.getElementById('ship-highlands').value);
      const ni    = parseFloat(document.getElementById('ship-ni').value);
      const iom   = parseFloat(document.getElementById('ship-iom').value);
      if (isNaN(first) || first < 0) { AG_ADMIN.toast('First parcel must be ≥ 0', true); return; }
      if (isNaN(extra) || extra < 0) { AG_ADMIN.toast('Extra parcel must be ≥ 0', true); return; }
      try {
        const r = await AG_ADMIN.api('ui_save_shipping_config', {
          first_box_gbp: first, extra_box_gbp: extra,
          fuel_pct: fuel || 0, consignment_fixed_gbp: fixed || 0,
          highlands_per_parcel_gbp: high || 0,
          ni_per_parcel_gbp: ni || 0,
          iom_per_parcel_gbp: iom || 0
        });
        if (!r.ok) { AG_ADMIN.toast(r.error || 'Save failed', true); return; }
        AG_ADMIN.toast(`Rates saved · mainland £${fmtNum(first)}+£${fmtNum(extra)} · zones £${fmtNum(high)}/£${fmtNum(ni)}/£${fmtNum(iom)}`);
        overlay.remove();
        delete state.cache.totalcosts; delete state.cache.pulse;
        saveCache();
        renderTab('totalcosts');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    });
  }

  function totalCostsRow(label, amount, total, days_elapsed) {
    const pct = total > 0 ? ((amount / total) * 100).toFixed(1) : '0.0';
    const perDay = days_elapsed > 0 ? amount / days_elapsed : 0;
    return `<tr>
      <td>${esc(label)}</td>
      <td class="num">£${fmtNum(amount)}</td>
      <td class="num"><span class="ag-muted">${pct}%</span></td>
      <td class="num"><span class="ag-muted">£${fmtNum(perDay)}</span></td>
    </tr>`;
  }

  function costsRecurringRow(r) {
    const endLabel = r.end_date ? esc(fmtFriendly(r.end_date)) : '<span class="ag-muted">ongoing</span>';
    const freq = r.frequency || 'monthly';
    const perPeriod = r.amount_gbp_per_period != null ? r.amount_gbp_per_period : r.amount_gbp_monthly;
    const daily = (r.amount_gbp_monthly || 0) / 30.4375;
    return `<tr data-cost-rec="${esc(r.id)}">
      <td><strong>${esc(r.vendor || '-')}</strong></td>
      <td><span class="ag-pill mute">${esc(r.category || '-')}</span></td>
      <td class="num"><strong>£${fmtNum(perPeriod)}</strong><div class="ag-muted" style="font-size:10px;">/ ${esc(freq)}</div></td>
      <td class="num">£${fmtNum(r.amount_gbp_monthly || 0)}</td>
      <td class="num"><span class="ag-muted">£${fmtNum(daily)}/day</span></td>
      <td class="ag-muted" style="font-size:11px;">${r.start_date ? esc(fmtFriendly(r.start_date)) : '<span class="ag-muted">-</span>'}</td>
      <td class="ag-muted" style="font-size:11px;">${endLabel}</td>
      <td class="ag-muted" style="font-size:11px;">${esc((r.notes || '').slice(0, 60))}</td>
      <td><button class="ag-btn secondary" data-cost-edit-rec="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Edit</button> <button class="ag-btn secondary" data-cost-del-rec="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Delete</button></td>
    </tr>`;
  }

  function costsAdhocRow(r) {
    return `<tr data-cost-adhoc="${esc(r.id)}">
      <td class="ag-muted" style="font-size:11px;">${esc(fmtFriendly(r.date))}</td>
      <td><strong>${esc(r.vendor || '-')}</strong></td>
      <td><span class="ag-pill mute">${esc(r.category || '-')}</span></td>
      <td class="num"><strong>£${fmtNum(r.amount_gbp)}</strong></td>
      <td class="ag-muted" style="font-size:11px;">${esc((r.notes || '').slice(0, 60))}</td>
      <td><button class="ag-btn secondary" data-cost-edit-adhoc="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Edit</button> <button class="ag-btn secondary" data-cost-del-adhoc="${esc(r.id)}" style="padding:3px 8px;font-size:11px;">Delete</button></td>
    </tr>`;
  }

  function bindCostsRowActions(cats) {
    document.querySelectorAll('[data-cost-edit-rec]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.costEditRec;
      const row = (state.cache.costs && state.cache.costs.payload.recurring || []).find(r => r.id === id);
      if (row) openCostsRecurringForm(row, cats);
    }));
    document.querySelectorAll('[data-cost-del-rec]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.costDelRec;
      if (!confirm('Delete this recurring cost?')) return;
      try {
        await AG_ADMIN.api('ui_delete_operating_cost', { tab: 'recurring', id });
        AG_ADMIN.toast('Deleted');
        delete state.cache.costs; renderTab('costs');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    }));
    document.querySelectorAll('[data-cost-edit-adhoc]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.costEditAdhoc;
      const row = (state.cache.costs && state.cache.costs.payload.adhoc || []).find(r => r.id === id);
      if (row) openCostsAdhocForm(row, cats);
    }));
    document.querySelectorAll('[data-cost-del-adhoc]').forEach(b => b.addEventListener('click', async () => {
      const id = b.dataset.costDelAdhoc;
      if (!confirm('Delete this ad-hoc cost?')) return;
      try {
        await AG_ADMIN.api('ui_delete_operating_cost', { tab: 'adhoc', id });
        AG_ADMIN.toast('Deleted');
        delete state.cache.costs; renderTab('costs');
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    }));
  }

  function openCostsRecurringForm(existing, cats) {
    const isEdit = !!existing;
    const today = new Date().toISOString().slice(0, 10);
    const r = existing || { vendor: '', category: 'personnel', amount_gbp_per_period: '', amount_gbp_monthly: '', frequency: 'monthly', start_date: today, end_date: '', notes: '' };
    const opts = (cats.recurring || []).map(c => `<option value="${esc(c)}" ${r.category === c ? 'selected' : ''}>${esc(c)}</option>`).join('');
    const freqs = ['weekly', 'monthly', 'quarterly', 'annually'];
    const currentFreq = r.frequency || 'monthly';
    const freqOpts = freqs.map(f => `<option value="${f}" ${currentFreq === f ? 'selected' : ''}>${f}</option>`).join('');
    const initialAmount = r.amount_gbp_per_period != null ? r.amount_gbp_per_period : (r.amount_gbp_monthly || '');
    /* VAT on this cost. Defaults by category (rent/wages/insurance/finance carry
       no reclaimable input VAT) but the operator sets it explicitly. */
    const NONVAT_CATS = ['personnel', 'premises', 'insurance', 'finance'];
    const recVat = (r.vat_rate != null && r.vat_rate !== '') ? Number(r.vat_rate) : (NONVAT_CATS.indexOf(r.category) >= 0 ? 0 : 20);
    const recVatOpts = [[20, 'Standard 20%'], [0, 'None / exempt'], [5, 'Reduced 5%']].map(([v, l]) => `<option value="${v}" ${recVat === v ? 'selected' : ''}>${l}</option>`).join('');
    showMarketingForm({
      title: isEdit ? 'Edit recurring cost' : 'New recurring cost',
      body: `
        <label class="ag-fld"><span>Vendor / payee</span><input id="cost-rec-vendor" type="text" value="${esc(r.vendor)}" placeholder="e.g. Cumbria Property Ltd"></label>
        <label class="ag-fld"><span>Category</span><select id="cost-rec-category">${opts}</select></label>
        <div class="ag-bnd-row">
          <label class="ag-fld"><span>Frequency</span><select id="cost-rec-frequency" onchange="(function(s){var l=document.getElementById('cost-rec-period-label');var m={weekly:'week',monthly:'month',quarterly:'quarter',annually:'year'};if(l)l.textContent=m[s.value]||'month';})(this)">${freqOpts}</select></label>
          <label class="ag-fld"><span>Amount per <span id="cost-rec-period-label">${esc(currentFreq === 'monthly' ? 'month' : currentFreq === 'weekly' ? 'week' : currentFreq === 'quarterly' ? 'quarter' : 'year')}</span> (£, ex-VAT)</span><input id="cost-rec-amount" type="number" step="0.01" min="0" value="${esc(initialAmount)}" placeholder="e.g. 3500"></label>
        </div>
        <label class="ag-fld"><span>VAT on this cost <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;">, is VAT charged (reclaimable input VAT)?</span></span><select id="cost-rec-vat">${recVatOpts}</select></label>
        <div class="ag-bnd-row"><label class="ag-fld"><span>Start date</span><input id="cost-rec-start" type="date" value="${esc(r.start_date)}"></label>
          <label class="ag-fld"><span>End date (blank = ongoing)</span><input id="cost-rec-end" type="date" value="${esc(r.end_date)}"></label></div>
        <label class="ag-fld"><span>Notes</span><textarea id="cost-rec-notes" rows="2">${esc(r.notes)}</textarea></label>
      `,
      onSave: async () => {
        const frequency = document.getElementById('cost-rec-frequency').value || 'monthly';
        const amountPerPeriod = parseFloat(document.getElementById('cost-rec-amount').value) || 0;
        const fields = {
          vendor: document.getElementById('cost-rec-vendor').value.trim(),
          category: document.getElementById('cost-rec-category').value,
          frequency: frequency,
          amount_gbp_per_period: amountPerPeriod,
          vat_rate: parseFloat(document.getElementById('cost-rec-vat').value) || 0,
          start_date: document.getElementById('cost-rec-start').value,
          end_date: document.getElementById('cost-rec-end').value,
          notes: document.getElementById('cost-rec-notes').value
        };
        if (!fields.vendor) { AG_ADMIN.toast('Vendor required', true); return false; }
        if (!(amountPerPeriod > 0)) { AG_ADMIN.toast('Amount must be > 0', true); return false; }
        await AG_ADMIN.api('ui_save_operating_recurring', { id: existing ? existing.id : '', fields });
        AG_ADMIN.toast(isEdit ? 'Updated' : 'Added');
        delete state.cache.costs; renderTab('costs');
        return true;
      }
    });
  }

  function openCostsAdhocForm(existing, cats) {
    const isEdit = !!existing;
    const today = new Date().toISOString().slice(0, 10);
    const r = existing || { date: today, vendor: '', category: 'equipment', amount_gbp: '', notes: '' };
    const opts = (cats.adhoc || []).map(c => `<option value="${esc(c)}" ${r.category === c ? 'selected' : ''}>${esc(c)}</option>`).join('');
    const AH_NONVAT = ['personnel', 'premises', 'insurance', 'finance'];
    const ahVat = (r.vat_rate != null && r.vat_rate !== '') ? Number(r.vat_rate) : (AH_NONVAT.indexOf(r.category) >= 0 ? 0 : 20);
    const ahVatOpts = [[20, 'Standard 20%'], [0, 'None / exempt'], [5, 'Reduced 5%']].map(([v, l]) => `<option value="${v}" ${ahVat === v ? 'selected' : ''}>${l}</option>`).join('');
    showMarketingForm({
      title: isEdit ? 'Edit ad-hoc cost' : 'New ad-hoc cost',
      body: `
        <label class="ag-fld"><span>Date</span><input id="cost-ah-date" type="date" value="${esc(r.date)}"></label>
        <label class="ag-fld"><span>Vendor / payee</span><input id="cost-ah-vendor" type="text" value="${esc(r.vendor)}" placeholder="e.g. Howdens Joinery"></label>
        <label class="ag-fld"><span>Category</span><select id="cost-ah-category">${opts}</select></label>
        <label class="ag-fld"><span>Amount (£, ex-VAT)</span><input id="cost-ah-amount" type="number" step="0.01" min="0" value="${esc(r.amount_gbp)}" placeholder="e.g. 450"></label>
        <label class="ag-fld"><span>VAT on this cost <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;">, is VAT charged (reclaimable input VAT)?</span></span><select id="cost-ah-vat">${ahVatOpts}</select></label>
        <label class="ag-fld"><span>Notes</span><textarea id="cost-ah-notes" rows="2">${esc(r.notes)}</textarea></label>
      `,
      onSave: async () => {
        const fields = {
          date: document.getElementById('cost-ah-date').value,
          vendor: document.getElementById('cost-ah-vendor').value.trim(),
          category: document.getElementById('cost-ah-category').value,
          amount_gbp: parseFloat(document.getElementById('cost-ah-amount').value) || 0,
          vat_rate: parseFloat(document.getElementById('cost-ah-vat').value) || 0,
          notes: document.getElementById('cost-ah-notes').value
        };
        if (!fields.date) { AG_ADMIN.toast('Date required', true); return false; }
        if (!fields.vendor) { AG_ADMIN.toast('Vendor required', true); return false; }
        if (!(fields.amount_gbp > 0)) { AG_ADMIN.toast('Amount must be > 0', true); return false; }
        await AG_ADMIN.api('ui_save_operating_adhoc', { id: existing ? existing.id : '', fields });
        AG_ADMIN.toast(isEdit ? 'Updated' : 'Added');
        delete state.cache.costs; renderTab('costs');
        return true;
      }
    });
  }

  /* ─── Tab: BIS (Back in stock) ────────────────────────────── */
  function renderBis(root, p) {
    const rows = (p && p.rows) || [];
    if (!rows.length) {
      root.innerHTML = `<h2 class="ag-h2">Back-in-stock signups</h2>
        <div class="ag-empty">
          No back-in-stock signups in the last 30 days.
          <div class="ag-muted" style="margin-top:6px;font-size:12px;">
            Click <strong>Pull this tab</strong> to fetch the latest from Klaviyo.
          </div>
        </div>`;
      return;
    }
    /* Split rows by notification status into two distinct lists.
       Pending = still awaiting restock (or in-stock pre-signup edge).
       Notified = stock has returned and the email has fired. */
    const pendingRows  = rows.filter(r => r.notified_status !== 'notified');
    const notifiedRows = rows.filter(r => r.notified_status === 'notified');
    /* Sort notified by notified_at desc, pending by signups desc. */
    pendingRows.sort((a, b) => (b.signups || 0) - (a.signups || 0));
    notifiedRows.sort((a, b) => String(b.notified_at || '').localeCompare(String(a.notified_at || '')));

    function bisRow(r, includeStatus) {
      const statusPill = r.notified_status === 'notified'
        ? `<span class="ag-pill ok" title="Stock returned on ${esc(r.notified_at)}. Email fired">Notified ${esc(fmtFriendly(r.notified_at))}</span>`
        : r.notified_status === 'in_stock_pre_signup'
          ? '<span class="ag-pill info">In stock pre-signup</span>'
          : '<span class="ag-pill warn">Pending</span>';
      const stockCell = r.current_stock != null ? r.current_stock : '<span class="ag-muted">-</span>';
      return `<tr class="${r.sku ? 'clickable' : ''}" ${r.sku ? `data-sku="${esc(r.sku)}"` : ''}>
        <td>${productCell(r, { compact: true })}</td>
        <td class="num"><strong style="color:var(--ag-accent);">${r.signups || 0}</strong></td>
        <td class="num">${stockCell}</td>
        ${includeStatus ? `<td>${statusPill}</td>` : ''}
        <td class="ag-muted">${esc(fmtFriendly(r.last_signup))}</td>
      </tr>`;
    }

    root.innerHTML = `
      <h2 class="ag-h2">Back-in-stock signups</h2>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:8px;">
        ${statCard('Total signups (30d)', p.total_signups || 0, '')}
        ${statCard('Distinct products', p.distinct_products || 0, '')}
        ${statCard('Pending', pendingRows.length, pendingRows.length > 0 ? 'warn' : '')}
        ${statCard('Notified', notifiedRows.length, notifiedRows.length > 0 ? 'ok' : '')}
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:24px;">
        ${statCard('Notified · last 1d',  p.customers_notified_1d  || 0, (p.customers_notified_1d || 0) > 0 ? 'ok' : '')}
        ${statCard('Notified · last 7d',  p.customers_notified_7d  || 0, '')}
        ${statCard('Notified · last 30d', p.customers_notified_30d || 0, '')}
        ${statCard('Total notified', p.customers_notified_total || 0, '')}
      </div>

      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:24px 0 6px 0;">
        Pending notifications
        <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;font-size:11px;">
          ${pendingRows.length === 0 ? 'all cleared 🎉' : pendingRows.length + ' awaiting restock'}
        </span>
      </h3>
      ${pendingRows.length === 0 ? `<div class="ag-empty" style="padding:12px;">No pending signups. Every BIS request currently has stock available or has been notified.</div>` : `
      <table class="ag-table"><thead><tr>
        <th>Product</th>
        <th class="num">Signups</th>
        <th class="num">On hand</th>
        <th>Last signup</th>
      </tr></thead><tbody>${pendingRows.map(r => bisRow(r, false)).join('')}</tbody></table>
      `}

      <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:32px 0 6px 0;">
        Notified, email sent
        <span class="ag-muted" style="font-weight:400;text-transform:none;letter-spacing:0;font-size:11px;">
          ${notifiedRows.length === 0 ? 'none yet in the last 30 days' : notifiedRows.length + ' notified · most recent first'}
        </span>
      </h3>
      ${notifiedRows.length === 0 ? `<div class="ag-empty" style="padding:12px;">No notifications fired in the last 30 days. Klaviyo sends these when stock returns from 0 → positive.</div>` : `
      <table class="ag-table"><thead><tr>
        <th>Product</th>
        <th class="num">Signups notified</th>
        <th class="num">On hand</th>
        <th>Status</th>
        <th>Last signup</th>
      </tr></thead><tbody>${notifiedRows.map(r => bisRow(r, true)).join('')}</tbody></table>
      `}
    `;
    root.querySelectorAll('tr.clickable[data-sku]').forEach(tr => tr.addEventListener('click', () => openSkuDrawer(tr.dataset.sku)));
  }

  /* ─── Tab 7: System ──────────────────────────────────────── */
  /* Data Quality panel. Surfaces structural contaminants that
     distort dashboard numbers so the operator can see them at a
     glance instead of running an audit when something looks off. */
  /* Bundle cutover audit. Verifies native-bundles regime is intact:
     BOM source, drift, legacy-duplicate Phase-4 leftovers. */
  function renderSeasonality(s) {
    if (!s) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sept','Oct','Nov','Dec'];
    if (!s.active) {
      return `<details style="margin:16px 0;background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:6px;padding:0;">
        <summary style="padding:10px 14px;cursor:pointer;font-weight:600;font-size:13px;">Seasonality, <span class="ag-muted">${esc(s.note || 'inactive')}</span></summary>
        <div style="padding:0 14px 14px 14px;font-size:12px;color:#666;">
          Reorder engine is treating every month as flat 1.0× velocity. Set <code>Config.seasonality_factors</code> to a comma-separated list of 12 multipliers (Jan→Dec) to enable.
        </div>
      </details>`;
    }
    const cur = s.current_month_idx;
    return `<details open style="margin:16px 0;background:var(--ag-panel);border:1px solid var(--ag-border);border-radius:6px;padding:0;">
      <summary style="padding:10px 14px;cursor:pointer;font-weight:600;font-size:13px;">Seasonality factors <span class="ag-muted" style="font-weight:400;">· current month ${esc(months[cur])} · ${(s.current_month_factor || 0).toFixed(2)}× baseline</span></summary>
      <div style="padding:8px 14px 14px 14px;">
        <p class="ag-muted" style="font-size:11px;margin:0 0 8px 0;">Each future simulated day's velocity is multiplied by <code>factor[month] / factor[${esc(months[cur])}]</code>. So forecasts ramp up in Nov/Dec/Jan and down in Jun/Oct relative to the velocity baseline measured today.</p>
        <table class="ag-table" style="font-size:12px;"><thead><tr>
          <th>Month</th><th class="num">Factor</th><th class="num">Ratio vs ${esc(months[cur])}</th>
        </tr></thead><tbody>${s.factors.map((f, idx) => {
          const ratio = f / (s.current_month_factor || 1);
          const isCurrent = idx === cur;
          return `<tr${isCurrent ? ' style="background:rgba(200, 16, 46,0.05);"' : ''}>
            <td><strong>${esc(months[idx])}</strong>${isCurrent ? ' <span class="ag-pill info" style="font-size:10px;">now</span>' : ''}</td>
            <td class="num">${f.toFixed(2)}</td>
            <td class="num">${ratio > 1.05 ? '<span style="color:#9a1f1f;">' : (ratio < 0.95 ? '<span style="color:#666;">' : '<span>')}${ratio.toFixed(2)}×</span></td>
          </tr>`;
        }).join('')}</tbody></table>
        <p class="ag-muted" style="font-size:11px;margin:8px 0 0 0;">Edit <code>Config.seasonality_factors</code> in the sheet to override. Re-run the engine via System → Run health check, or wait for the next daily pipeline.</p>
      </div>
    </details>`;
  }

  function renderBundleCutover(b) {
    if (!b) return '';
    const ok = b.ok;
    const stampedSince = b.cutover_date_stamped
      ? `<span class="ag-pill ok" style="font-size:11px;">Native-bundles regime since ${esc(b.cutover_date_stamped)}</span>`
      : `<span class="ag-pill warn" style="font-size:11px;">Not yet stamped. Run Verify &amp; stamp</span>`;
    const stat = (label, value, tone) => `
      <div style="background:#fafafa;border:1px solid #eaeaea;border-radius:6px;padding:10px;">
        <div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.5px;">${esc(label)}</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px;${tone === 'warn' ? 'color:#d97706;' : tone === 'err' ? 'color:#c8102e;' : tone === 'ok' ? 'color:#16a34a;' : ''}">${value}</div>
      </div>`;
    const drift = (b.drift || []).slice(0, 8);
    const dupes = (b.legacy_duplicates || []).slice(0, 8);
    return `
      <div style="margin:16px 0 24px 0;">
        <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:8px;">
          <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0;">Bundle cutover</h3>
          ${stampedSince}
          <span class="spacer" style="flex:1;"></span>
          <button class="ag-btn secondary" id="bundle-cutover-verify" style="padding:4px 10px;font-size:11px;">Verify &amp; stamp</button>
        </div>
        <p class="ag-muted" style="font-size:11px;margin:0 0 12px 0;max-width:720px;">Stock + profitability are correct when every Shopify native-bundle parent has a BOM entry (sourced from <code>native_bundle</code>) and there's no drift. Phase-4 duplicates ("Bundle - X" / "Exclusive Deals -") are tracked separately as cleanup work, they don't break analytics, just clutter the catalogue.</p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:8px;">
          ${stat('Native parents', b.native_parents || 0, 'ok')}
          ${stat('Cost-on-components tag', b.bundle_parent_tagged || 0)}
          ${stat('BOM rows · native', b.bom_rows_native || 0)}
          ${stat('BOM rows · legacy', b.bom_rows_legacy || 0, (b.bom_rows_legacy || 0) > 0 ? 'err' : 'ok')}
          ${stat('BOM rows · manual / inferred', b.bom_rows_other || 0)}
          ${stat('Drift', (b.drift || []).length, (b.drift || []).length > 0 ? 'warn' : 'ok')}
          ${stat('Legacy duplicates', b.legacy_duplicates_total || 0, (b.legacy_duplicates_total || 0) > 0 ? 'warn' : 'ok')}
          ${stat('Overall', ok ? 'OK ✓' : 'Needs attention', ok ? 'ok' : 'warn')}
        </div>
        ${drift.length ? `
          <div style="margin-top:10px;padding:10px 12px;background:#fff8e6;border:1px solid #fde68a;border-radius:6px;">
            <div style="font-size:12px;font-weight:600;color:#92400e;margin-bottom:4px;">Drift details (${drift.length})</div>
            <ul style="margin:0 0 0 18px;font-size:12px;line-height:1.6;color:#92400e;">
              ${drift.map(d => `<li>${esc(d)}</li>`).join('')}
            </ul>
          </div>` : ''}
        ${dupes.length ? `
          <details style="margin-top:10px;">
            <summary style="font-size:12px;font-weight:600;cursor:pointer;">Phase 4 cleanup queue, ${b.legacy_duplicates_total} duplicate product${b.legacy_duplicates_total === 1 ? '' : 's'} (top ${dupes.length})</summary>
            <table class="ag-table" style="margin-top:6px;font-size:12px;">
              <thead><tr><th>SKU</th><th>Product / variant</th><th class="num">On hand</th><th class="num">Unit £</th><th>Excluded</th></tr></thead>
              <tbody>
                ${dupes.map(d => `<tr><td>${esc(d.sku)}</td><td>${esc(d.product_title)}${d.variant_title ? ' · ' + esc(d.variant_title) : ''}</td><td class="num">${d.on_hand}</td><td class="num">£${fmtNum(d.unit_cost)}</td><td>${d.excluded ? '<span class="ag-pill ok">yes</span>' : '<span class="ag-pill warn">no</span>'}</td></tr>`).join('')}
              </tbody>
            </table>
            <p class="ag-muted" style="font-size:11px;margin:6px 0 0;">These are AGPB mirror SKUs from before native bundles. Phase 4 (AGPB rewire) retires them. Most should already be excluded from stock value via tag/vendor, ones marked "no" deserve a closer look.</p>
          </details>` : ''}
      </div>`;
  }

  function renderDataQuality(dq) {
    if (!dq) return '';
    const stat = (label, n, pct, tone) => `
      <div style="background:#fafafa;border:1px solid #eaeaea;border-radius:6px;padding:10px;">
        <div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.5px;">${esc(label)}</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px;${tone === 'warn' ? 'color:#d97706;' : tone === 'err' ? 'color:#c8102e;' : ''}">${n}</div>
        ${pct != null ? `<div class="ag-muted" style="font-size:11px;margin-top:2px;">${pct}% of catalogue</div>` : ''}
      </div>
    `;
    const issues = (dq.known_issues || []).map(s =>
      `<li style="font-size:12px;line-height:1.5;">${esc(s)}</li>`
    ).join('');
    return `
      <div style="margin:16px 0 24px 0;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 4px 0;">Data Quality</h3>
        <p class="ag-muted" style="font-size:11px;margin:0 0 12px 0;max-width:720px;">Structural contaminants and missing data. Most don't break the dashboard, read-time filters handle them. But seeing the counts helps diagnose "the numbers look wrong" moments without a code audit.</p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:8px;">
          ${stat('Total SKUs', dq.sku_total)}
          ${stat('Missing unit cost', dq.sku_missing_cost, dq.sku_missing_cost_pct, dq.sku_missing_cost_pct > 20 ? 'warn' : '')}
          ${stat('Missing collection', dq.sku_missing_collection, dq.sku_missing_collection_pct, dq.sku_missing_collection_pct > 30 ? 'warn' : '')}
          ${stat('Bundle children', dq.sku_bundle_children, dq.sku_bundle_children_pct)}
          ${stat('Bundles.app parents', dq.sku_bundles_app_parents)}
          ${stat('Synthetic [no-sku]', dq.sku_synthetic, dq.sku_synthetic_pct)}
          ${stat('Excluded SKUs', dq.sku_excluded_total)}
          ${stat('£0-revenue lines (recent)', dq.sales_history_zero_rev_rows, dq.sales_history_zero_rev_pct)}
        </div>
        ${issues ? `<div style="margin-top:12px;background:#fff8e6;border:1px solid #fde68a;border-radius:6px;padding:10px 14px;">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:#92400e;margin-bottom:4px;">Known issues</div>
          <ul style="margin:0;padding-left:18px;">${issues}</ul>
        </div>` : ''}
      </div>
    `;
  }

  /* Pipeline Heartbeat panel. Per-step waterfall for the most recent
     run. Big red banner when a step is hung (start without end). Lets
     us see exactly which step a 30-min timeout hit, without digging
     into Apps Script logs. */
  function renderD1SalesShadow(d1) {
    if (!d1) return '';
    const head = `<h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 6px 0;">Shopify-verified sales <span class="ag-muted" style="font-weight:400;font-size:11px;letter-spacing:0;text-transform:none;">· analytics datastore (D1) · last ${(d1.days || 30)}d</span></h3>`;
    if (!d1.ok) {
      return `<div style="margin-bottom:24px;">${head}
        <div class="ag-muted" style="font-size:12px;">Not available, ${esc(d1.error || 'unknown')}</div></div>`;
    }
    const fmt = n => '£' + (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `<div style="margin-bottom:24px;">${head}
      <div style="display:flex;gap:28px;flex-wrap:wrap;border:1px solid #e5e7eb;border-radius:6px;padding:12px 16px;">
        <div><div class="ag-muted" style="font-size:11px;">Total sales · inc VAT · ties to Shopify</div>
          <div style="font-size:22px;font-weight:700;color:#c8102e;">${fmt(d1.total_sales)}</div></div>
        <div><div class="ag-muted" style="font-size:11px;">Product revenue · ex VAT · = this dashboard</div>
          <div style="font-size:22px;font-weight:700;">${fmt(d1.dashboard_revenue)}</div></div>
        <div style="display:flex;align-items:flex-end;"><div class="ag-muted" style="font-size:11px;">${d1.orders} orders · ${esc(d1.from)} → ${esc(d1.to)}</div></div>
      </div>
      <div class="ag-muted" style="font-size:11px;margin-top:4px;">Live millisecond query from the analytics datastore, reconciled to Shopify (0.04%).</div>
    </div>`;
  }

  function renderPipelineHeartbeat(hb) {
    if (!hb || !hb.runs || !hb.runs.length) return '';
    const latest = hb.runs[0];
    const hung = latest.hung_steps || [];
    /* A start-marker-without-end is only a HANG once the step is genuinely
       overdue. Every pipeline step writes its end marker within a single Apps
       Script execution (~6 min wall), so under 12 minutes it is almost
       certainly still RUNNING and a red HUNG banner is a false alarm. This
       tab used to scream HUNG at runs that were 2 minutes in, which read as
       "the tool is broken" several times a day. Amber running state below the
       threshold; red only when the end marker is truly overdue. */
    let hungAgeMin = null;
    if (hung.length && hung[0].started_at) {
      const t = new Date(hung[0].started_at).getTime();
      if (!isNaN(t)) hungAgeMin = Math.max(0, Math.round((Date.now() - t) / 60000));
    }
    const stillRunning = hung.length && hungAgeMin !== null && hungAgeMin < 12;
    const banner = !hung.length ? '' : stillRunning ? `
      <div style="background:#eff6ff;border:1px solid #bfdbfe;color:#1e40af;padding:10px 14px;border-radius:6px;margin-bottom:12px;font-size:13px;">
        <strong>⏳ Run in progress:</strong> ${hung.map(h => esc(h.step)).join(', ')}
        <div class="ag-muted" style="font-size:11px;margin-top:2px;color:#1e40af;">Started ${esc(fmtFriendly(hung[0].started_at))} · running for ${hungAgeMin}m. Steps report here when they finish; this becomes a warning if no end marker lands within 12 minutes.</div>
      </div>` : `
      <div style="background:#fee;border:1px solid #fcc;color:#900;padding:10px 14px;border-radius:6px;margin-bottom:12px;font-size:13px;">
        <strong>⚠ Last run hung on:</strong> ${hung.map(h => esc(h.step)).join(', ')}
        <div class="ag-muted" style="font-size:11px;margin-top:2px;color:#900;">Started ${esc(fmtFriendly(hung[0].started_at))}${hungAgeMin !== null ? ' (' + hungAgeMin + 'm ago)' : ''}. No end marker recorded, so the step died or was killed at the execution wall. This is the step to investigate.</div>
      </div>`;
    const errorBanner = (!hung.length && latest.has_error) ? `
      <div style="background:#fff8e6;border:1px solid #fde68a;color:#92400e;padding:10px 14px;border-radius:6px;margin-bottom:12px;font-size:13px;">
        <strong>⚠ Last run had errors</strong>: see the highlighted steps below.
      </div>` : '';
    /* Render waterfall. Bars sized proportionally to slowest step. */
    const maxMs = Math.max(1, ...latest.steps.map(s => s.duration_ms));
    const totalSec = Math.round((latest.total_ms || 0) / 100) / 10;
    return `
      <div style="margin-bottom:24px;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 6px 0;">Pipeline Heartbeat <span class="ag-muted" style="font-weight:400;font-size:11px;letter-spacing:0;text-transform:none;">· last run: ${esc(fmtFriendlyDateTime(latest.started_at))} · ${totalSec}s total · ${latest.step_count} steps</span></h3>
        ${banner}
        ${errorBanner}
        <table class="ag-table" style="font-size:12px;">
          <thead><tr>
            <th>Step</th>
            <th class="num" style="width:90px;">Duration</th>
            <th>Relative</th>
          </tr></thead>
          <tbody>
            ${latest.steps.map(s => {
              const isErr = s.phase === 'error';
              const pct = (s.duration_ms / maxMs) * 100;
              const barColor = isErr ? 'var(--ag-bad,#c8102e)' : (s.duration_ms > 5000 ? 'var(--ag-warn,#d97706)' : 'var(--ag-info,#3b82f6)');
              return `<tr ${isErr ? 'style="background:#fef2f2;"' : ''}>
                <td><strong>${esc(s.step)}</strong>${isErr ? `<div class="ag-muted" style="font-size:11px;color:#c8102e;">${esc(s.error)}</div>` : ''}</td>
                <td class="num" style="font-variant-numeric:tabular-nums;">${(s.duration_ms / 1000).toFixed(1)}s</td>
                <td>
                  <div style="background:#f3f4f6;border-radius:3px;height:14px;overflow:hidden;">
                    <div style="background:${barColor};width:${pct}%;height:100%;"></div>
                  </div>
                </td>
              </tr>`;
            }).join('')}
            ${hung.map(h => `<tr style="background:#fef2f2;">
              <td><strong>${esc(h.step)}</strong> <span class="ag-pill err" style="font-size:10px;">HUNG</span></td>
              <td class="num ag-muted">-</td>
              <td><div style="background:#fef2f2;border:1px dashed #fca5a5;border-radius:3px;height:14px;"></div></td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  /* Sheet Sizes panel. Current row counts of the big data sheets plus
     day-over-day delta. Surfaces unexpected growth (e.g. Refunds_Cache
     5k → 50k overnight) before it manifests as a hung pipeline. */
  function renderSheetSizes(sizes) {
    if (!sizes || !sizes.latest || !sizes.latest.length) return '';
    /* All rows in `latest` share the same snapshot_at (snapshot taken
       at pipeline start), so pick the first one to label the header. */
    const snappedAt = sizes.latest[0].snapshot_at;
    return `
      <div style="margin-bottom:24px;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 6px 0;">Sheet sizes <span class="ag-muted" style="font-weight:400;font-size:11px;letter-spacing:0;text-transform:none;">· snapshot: ${esc(fmtFriendlyDateTime(snappedAt))} · vs previous run</span></h3>
        <table class="ag-table" style="font-size:12px;">
          <thead><tr>
            <th>Sheet</th>
            <th class="num">Rows</th>
            <th class="num">Δ vs last run</th>
          </tr></thead>
          <tbody>
            ${sizes.latest.map(s => {
              const delta = s.change_vs_yesterday || 0;
              const deltaCls = delta > 1000 ? 'warn' : (delta > 0 ? '' : 'mute');
              const deltaSign = delta > 0 ? '+' : '';
              return `<tr>
                <td><strong>${esc(s.sheet_name)}</strong></td>
                <td class="num"><strong>${fmtNum(s.row_count)}</strong></td>
                <td class="num"><span class="ag-pill ${deltaCls}" style="font-size:10px;">${delta === 0 ? '-' : deltaSign + fmtNum(delta)}</span></td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  /* Pipeline Health panel. Which engine is authoritative (Sheets vs D1),
     data freshness, COGS coverage, and the nightly automated parity +
     Shopify-reconciliation results with a cutover-readiness verdict. Fed by
     ui_system.pipeline (see 87_parity_check.js / 87_sales_reconciliation.js).
     Pure inline styles to match the Data Quality panel. */
  function renderPipelineHealth(pl) {
    if (!pl) return '';
    const tones = { ok: '#16a34a', warn: '#d97706', bad: '#c8102e', '': '#1a1a1a' };
    const card = (label, value, sub, tone) => `
      <div style="background:#fafafa;border:1px solid #eaeaea;border-radius:6px;padding:10px;">
        <div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.5px;">${esc(label)}</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px;color:${tones[tone || ''] || tones['']};">${value}</div>
        ${sub != null ? `<div class="ag-muted" style="font-size:11px;margin-top:2px;">${sub}</div>` : ''}
      </div>`;

    /* Source of truth. */
    const srcTone = pl.on_d1 ? 'ok' : '';
    const srcCard = card('Source of truth', pl.on_d1 ? 'D1' : 'Sheets',
      pl.on_d1 ? 'analytics datastore' : 'spreadsheet engine', srcTone);

    /* Freshness. */
    let freshVal = '-', freshSub = 'no runs logged', freshTone = 'warn';
    if (typeof pl.freshness_min === 'number') {
      const m = pl.freshness_min;
      freshVal = m < 60 ? `${m} min` : `${Math.round(m / 60 * 10) / 10} h`;
      freshSub = `last run ${esc(pl.last_run_status || '')}`.trim();
      freshTone = m <= 120 ? 'ok' : (m <= 720 ? 'warn' : 'bad');
    }
    const freshCard = card('Data freshness', freshVal, freshSub, freshTone);

    /* COGS coverage. */
    let covVal = '-', covTone = 'warn';
    if (typeof pl.cogs_coverage_pct === 'number') {
      covVal = `${pl.cogs_coverage_pct}%`;
      covTone = pl.cogs_coverage_pct >= 90 ? 'ok' : (pl.cogs_coverage_pct >= 70 ? 'warn' : 'bad');
    }
    const covCard = card('COGS coverage', covVal, 'SKUs with a cost', covTone);

    /* Parity. */
    const par = pl.parity;
    let parVal = 'pending', parSub = 'not run yet', parTone = 'warn';
    if (par && par.ok === false) { parVal = 'error'; parSub = esc(par.error || 'check failed'); parTone = 'bad'; }
    else if (par && par.ok) {
      parVal = `${par.max_drift_pct}%`;
      parSub = `worst: ${esc(par.worst_metric || '')} · streak ${par.green_streak || 0}/${par.streak_required || '?'}`;
      parTone = par.within_tolerance ? 'ok' : 'bad';
    }
    const parCard = card('D1 parity (max drift)', parVal, parSub, parTone);

    /* Reconciliation. */
    const rec = pl.reconcile;
    let recVal = 'pending', recSub = 'not run yet', recTone = 'warn';
    const money = n => '£' + (Number(n) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });
    if (rec && rec.ok === false) { recVal = 'n/a'; recSub = esc(rec.error || 'unavailable'); recTone = 'warn'; }
    else if (rec && rec.ok) {
      recVal = `${rec.gap_pct}%`;
      recSub = `ours ${money(rec.ours)} vs Shopify ${money(rec.shopify)} · ${esc(rec.reference_source || '')}`;
      recTone = rec.within_tolerance ? 'ok' : 'bad';
    }
    const recCard = card('Shopify reconciliation gap', recVal, recSub, recTone);

    /* Last full audit (weekly comprehensive pass). */
    const fa = pl.full_audit;
    let faVal = 'pending', faSub = 'no weekly audit yet', faTone = 'warn';
    if (fa && fa.verdict) {
      faVal = String(fa.verdict).toUpperCase();
      faSub = `${fa.issue_count != null ? fa.issue_count + ' issue(s) · ' : ''}${fa.generated_at ? String(fa.generated_at).slice(0, 10) : ''}`;
      faTone = fa.verdict === 'pass' ? 'ok' : (fa.verdict === 'warn' ? 'warn' : 'bad');
    }
    const faCard = card('Last full audit', faVal, faSub, faTone);

    /* Cutover verdict banner. */
    const ready = !!pl.cutover_ready;
    const blockers = (pl.cutover_blockers || []).map(esc).join(' · ');
    const banner = `
      <div style="margin-top:12px;background:${ready ? '#ecfdf5' : '#fff8e6'};border:1px solid ${ready ? '#a7f3d0' : '#fde68a'};border-radius:6px;padding:10px 14px;">
        <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;color:${ready ? '#065f46' : '#92400e'};">
          ${ready ? '✓ D1 cutover ready' : '⏳ D1 cutover not ready yet'}
        </div>
        ${!ready && blockers ? `<div class="ag-muted" style="font-size:11px;margin-top:4px;">Blockers: ${blockers}</div>` : ''}
        ${ready ? `<div class="ag-muted" style="font-size:11px;margin-top:4px;">Parity has held green for the required streak and reconciliation is in tolerance. Flip Config.daily_aggregates_source to 'd1' when ready.</div>` : ''}
      </div>`;

    return `
      <div style="margin:16px 0 24px 0;">
        <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 4px 0;">Pipeline Health</h3>
        <p class="ag-muted" style="font-size:11px;margin:0 0 12px 0;max-width:720px;">Which engine feeds the numbers, how fresh they are, and whether the D1 analytics datastore matches the spreadsheet (parity) and Shopify's own totals (reconciliation). The nightly checks email on drift.</p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:8px;">
          ${srcCard}${freshCard}${covCard}${parCard}${recCard}${faCard}
        </div>
        ${banner}
      </div>`;
  }

  function renderSystem(root, p) {
    const runs = (p && p.runs) || [];
    const triggers = (p && p.triggers) || [];
    const dq = (p && p.data_quality) || null;
    const hb = (p && p.pipeline_heartbeat) || null;
    const sizes = (p && p.sheet_sizes) || null;
    root.innerHTML = `
      <div class="ag-toolbar">
        <h2 class="ag-h2" style="margin:0;">System</h2>
        <span class="spacer" style="flex:1;"></span>
        <button class="ag-btn secondary" id="run-health-check">Run data health check</button>
      </div>
      ${renderPipelineHeartbeat(hb)}
      <div id="sys-watermarks"></div>
      ${renderD1SalesShadow((p && p.d1_sales) || null)}
      ${renderSeasonality((p && p.seasonality) || null)}
      ${renderBundleCutover((p && p.bundle_cutover) || null)}
      ${renderSheetSizes(sizes)}
      ${renderPipelineHealth((p && p.pipeline) || null)}
      ${renderDataQuality(dq)}
      <div id="health-panel"></div>
      <div class="ag-chart" style="height:280px;">
        <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
          <div class="label" style="margin:0;">FX rate (GBP/USD)</div>
          <div style="display:flex;gap:4px;">
            ${['1h','6h','1d','1w','1m'].map(p => `<button class="ag-btn ${(state.fxPeriod || '1d') === p ? '' : 'secondary'}" data-fx="${p}" style="padding:4px 10px;font-size:11px;">${p}</button>`).join('')}
          </div>
          <div class="ag-muted" id="ch-fx-summary" style="font-size:11px;flex-basis:100%;text-align:right;"></div>
        </div>
        <canvas id="ch-fx"></canvas>
      </div>
      <h3>Recent runs</h3>
      <table class="ag-table"><thead><tr>
        <th>Started</th><th>Duration</th><th>Status</th><th>Message</th>
      </tr></thead><tbody>${runs.slice(0, 20).map(r => `<tr>
        <td>${esc(String(r.started_at).slice(0, 19))}</td>
        <td class="num">${Math.round((r.duration_ms || 0) / 1000)}s</td>
        <td><span class="ag-run-status ${esc(r.status)}"></span>${esc(r.status || '')}</td>
        <td class="ag-muted" style="font-size:11px;">${esc(String(r.message || '').slice(0, 200))}</td>
      </tr>`).join('')}</tbody></table>
      <h3 style="margin-top:24px;">Installed triggers</h3>
      <table class="ag-table"><thead><tr><th>Handler</th><th>Event</th></tr></thead><tbody>${
        triggers.map(t => `<tr><td>${esc(t.handler)}</td><td class="ag-muted">${esc(t.event)}</td></tr>`).join('')
      }</tbody></table>`;
    /* FX period filter. Slice the 31-day series client-side by
       timestamp (snapshots arrive every 30 min). Period values are
       hours so we filter precisely instead of just slicing the tail. */
    if (!state.fxPeriod) state.fxPeriod = '1d';
    const FX_HOURS = { '1h': 1, '6h': 6, '1d': 24, '1w': 168, '1m': 24 * 31 };
    function renderFxChart() {
      const full = (p.fx_history || []).map(r => ({ date: r.date, value: r.value }));
      if (!full.length) return;
      const hours = FX_HOURS[state.fxPeriod] || 24;
      const cutoff = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      const sliced = full.filter(r => String(r.date) >= cutoff);
      if (!sliced.length) return;
      /* Update summary line. */
      const sumEl = $('#ch-fx-summary');
      if (sumEl) {
        const vals = sliced.map(r => parseFloat(r.value) || 0);
        const avg = vals.reduce((a, v) => a + v, 0) / vals.length;
        const last = vals[vals.length - 1];
        const min = Math.min(...vals);
        const max = Math.max(...vals);
        sumEl.innerHTML = `avg <strong>${avg.toFixed(5)}</strong> · last ${last.toFixed(5)} · range ${min.toFixed(5)}–${max.toFixed(5)}`;
      }
      /* FX snapshots are intraday (every 30 min), so show the TIME. Not just the date.
         Short periods (1h/6h/1d) label the x-axis with HH:MM; longer ones (1w/1m) label
         with the date. The tooltip always shows date + time so you can read the exact moment. */
      const intraday = ['1h', '6h', '1d'].indexOf(state.fxPeriod) !== -1;
      const xFmt = d => {
        const dt = new Date(d); if (isNaN(dt)) return String(d);
        return intraday
          ? dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
          : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      };
      const titleFmt = d => {
        const dt = new Date(d); if (isNaN(dt)) return String(d);
        return dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
      };
      chartLine('ch-fx', sliced, 'GBP/USD', { decimals: 5, meanLine: true, xFmt, titleFmt });
    }
    root.querySelectorAll('[data-fx]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.fxPeriod = btn.dataset.fx;
        root.querySelectorAll('[data-fx]').forEach(b => {
          b.classList.toggle('secondary', b.dataset.fx !== state.fxPeriod);
        });
        renderFxChart();
      });
    });
    renderFxChart();
    loadWatermarks();
    $('#run-health-check').addEventListener('click', runHealthCheck);
    const bv = $('#bundle-cutover-verify');
    if (bv) bv.addEventListener('click', verifyBundleCutover);
  }
  /* Sync watermarks + the deferred-days queue (backend ui_watermarks, read
     only). Diagnosing the orders sync used to mean inferring the watermark from
     code and symptoms, and deferred days (older than the 7-day rebuild
     lookback) sat invisible in a script property until a console backfill.
     Fetched after the System tab paints so it never delays it. The auth
     Worker only lets an account holding BOTH the stock and analytics sections
     call it; for anyone else the call is refused and the card stays hidden. */
  async function loadWatermarks() {
    const slot = $('#sys-watermarks');
    if (!slot) return;
    let r = null;
    try { r = await AG_ADMIN.api('ui_watermarks', {}); } catch (_) { return; }
    const now = $('#sys-watermarks');
    if (!now || !r || !r.ok) return;
    now.innerHTML = renderWatermarks(r);
  }
  function renderWatermarks(r) {
    const rows = Array.isArray(r.watermarks) ? r.watermarks : [];
    const deferred = Array.isArray(r.deferred_days) ? r.deferred_days.map(String) : [];
    const ageH = iso => {
      const t = new Date(iso).getTime();
      return isNaN(t) ? null : Math.max(0, Math.round((Date.now() - t) / 3600000));
    };
    const deferredBlock = deferred.length ? `
      <div style="background:#fff8e6;border:1px solid #fde68a;color:#92400e;padding:10px 14px;border-radius:6px;margin:8px 0 12px;font-size:13px;">
        <strong>⚠ ${deferred.length} deferred day${deferred.length === 1 ? '' : 's'}</strong>: changed orders on ${deferred.length === 1 ? 'this day are' : 'these days are'} older than the ${esc(r.max_lookback_days || 7)}-day rebuild window, so the nightly sync will not repair ${deferred.length === 1 ? 'it' : 'them'}. Run a deliberate orders backfill from the earliest one.
        <div style="font-family:ui-monospace,monospace;font-size:12px;margin-top:6px;">${deferred.map(d => esc(d)).join(' · ')}</div>
      </div>` : `
      <div class="ag-muted" style="font-size:12px;margin:6px 0 12px;">No deferred days: every changed order sits inside the ${esc(r.max_lookback_days || 7)}-day rebuild window.</div>`;
    return `
      <h3 style="margin-top:8px;">Sync watermarks</h3>
      ${rows.length ? `<table class="ag-table"><thead><tr>
        <th>Sync</th><th>Synced up to</th><th>Last run</th><th class="num">Orders last run</th><th>Notes</th>
      </tr></thead><tbody>${rows.map(w => {
        const h = ageH(w.last_synced_at);
        return `<tr>
          <td>${esc(w.sync_name || '')}</td>
          <td>${esc(String(w.last_synced_at || '').slice(0, 19))}${h !== null ? ` <span class="ag-muted" style="font-size:11px;">(${h}h ago)</span>` : ''}</td>
          <td>${esc(String(w.last_run_at || '').slice(0, 19))}</td>
          <td class="num">${esc(w.orders_processed_last_run == null ? '' : w.orders_processed_last_run)}</td>
          <td class="ag-muted" style="font-size:11px;">${esc(String(w.notes || '').slice(0, 200))}</td>
        </tr>`;
      }).join('')}</tbody></table>` : '<div class="ag-muted" style="font-size:12px;">No watermark rows.</div>'}
      ${deferredBlock}`;
  }
  async function verifyBundleCutover() {
    const btn = $('#bundle-cutover-verify');
    if (!btn) return;
    btn.disabled = true; btn.textContent = 'Verifying…';
    try {
      const r = await AG_ADMIN.api('ui_bundle_cutover_verify_and_stamp', {});
      const msg = r.cutover_just_stamped
        ? `Stamped ${r.cutover_date_stamped} · ${r.ok ? 'OK' : 'drift ' + (r.drift || []).length}`
        : `${r.ok ? 'OK' : 'Drift ' + (r.drift || []).length} · since ${r.cutover_date_stamped || '-'}`;
      AG_ADMIN.toast(msg);
      delete state.cache.system;
      saveCache();
      renderTab('system');
    } catch (e) {
      AG_ADMIN.toast(e.message || e, true);
      btn.disabled = false; btn.textContent = 'Verify & stamp';
    }
  }
  async function runHealthCheck() {
    const btn = $('#run-health-check');
    const panel = $('#health-panel');
    btn.disabled = true; btn.innerHTML = '<span class="ag-spinner"></span> Auditing…';
    panel.innerHTML = '';
    try {
      const r = await AG_ADMIN.api('ui_data_health', {});
      panel.innerHTML = renderHealthReport(r);
    } catch (e) {
      panel.innerHTML = `<div class="ag-empty">Health check failed: ${esc(e.message || e)}</div>`;
    } finally {
      btn.disabled = false; btn.textContent = 'Run data health check';
    }
  }
  function renderHealthReport(r) {
    const sv = r.stock_value || {};
    const s30 = r.sales_30d || {};
    const o30 = r.orders_30d || {};
    const inv = r.inventory_snapshots || {};
    const bkl = r.backlog_snapshots || {};
    const po = r.pre_orders || {};
    const bis = r.bis || {};
    const pulls = r.pulls || {};
    const sh = r.sheet_health || {};
    const rr = r.restocked_refunds || {};
    const dup = r.duplicate_skus || {};
    const covCls = pct => pct >= 90 ? 'ok' : pct >= 70 ? 'warn' : 'bad';
    /* PO USD integrity: count POs whose header goods_value_usd differs from the sum
       of their line items (lines_usd). Computed from the cached PO list. Only shown
       when that list is loaded, so it never falsely reports 0. */
    const _hpPos = (state.bootstrap && state.bootstrap.pos) || [];
    let _poUsdMismatch = 0;
    _hpPos.forEach(p => {
      const hv = parseFloat(p.goods_value_usd) || 0, lv = parseFloat(p.lines_usd) || 0;
      if (lv > 0 && Math.abs(hv - lv) > usdReconTol(p.lines_count)) _poUsdMismatch++;
    });
    return `
      <div class="ag-h2" style="margin-top:8px;">Data Health</div>
      <div class="ag-muted" style="margin-bottom:12px;font-size:12px;">Generated ${esc(r.generated_at)}</div>

      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(220px,1fr));margin-bottom:16px;">
        ${statCard('Stock value (owned)', '£' + fmtNum(sv.stock_value_owned_gbp != null ? sv.stock_value_owned_gbp : sv.stock_value_gbp), '')}
        ${statCard('Collective stock value', '£' + fmtNum(sv.stock_value_collective_gbp || 0) + ' (' + (sv.collective_skus || 0) + ' SKUs)', '')}
        ${(sv.tagged_excluded_skus || 0) > 0 ? statCard('Tagged excluded', '£' + fmtNum(sv.stock_value_excluded_tagged_gbp || 0) + ' (' + sv.tagged_excluded_skus + ' SKUs)', '') : ''}
        ${statCard('Cost coverage (owned SKUs)', sv.coverage_pct + '% (' + ((sv.owned_skus || 0) - (sv.skus_with_no_cost || 0)) + '/' + (sv.owned_skus || 0) + ')', covCls(sv.coverage_pct))}
        ${statCard('Units coverage', sv.units_coverage_pct + '%', covCls(sv.units_coverage_pct))}
        ${statCard('Units at £0 cost', sv.units_at_zero_cost, sv.units_at_zero_cost > 0 ? 'warn' : 'ok')}
        ${_hpPos.length ? statCard('PO USD mismatches', String(_poUsdMismatch), _poUsdMismatch > 0 ? 'warn' : 'ok') : ''}
        ${statCard('Duplicate SKUs', String(dup.count || 0), (dup.count || 0) > 0 ? 'bad' : 'ok')}
      </div>

      ${(dup.count || 0) > 0 ? `
      <div class="ag-issues" style="border-color:var(--ag-accent);background:rgba(192,38,28,0.06);margin-bottom:16px;">
        <strong style="color:var(--ag-accent);">&#9888; ${dup.count} duplicate SKU${dup.count === 1 ? '' : 's'}. Stock edits are blocked for these.</strong>
        <div class="ag-muted" style="font-size:12px;margin:4px 0 8px;">${esc(dup.note || '')}</div>
        <table class="ag-table"><thead><tr><th>SKU</th><th>Variants sharing it</th></tr></thead><tbody>
        ${(dup.duplicates || []).map(d => `<tr>
          <td><span class="sku" style="font-weight:700;">${esc(d.sku)}</span></td>
          <td>${(d.variants || []).map(v => esc((v.product_title || '') + (v.variant_title ? ' · ' + v.variant_title : '') + ' (on hand: ' + (v.on_hand != null ? v.on_hand : '?') + ')')).join('<br>')}</td>
        </tr>`).join('')}
        </tbody></table>
      </div>` : ''}

      <h3>Sales, last 30 days (cross-check vs Shopify)</h3>
      <table class="ag-table" style="margin-bottom:16px;"><tbody>
        <tr><td>Window</td><td>${esc(s30.window || '')}</td></tr>
        <tr><td>Units sold (Σ Sales_History.units_sold)</td><td class="num">${s30.units_sold || 0}</td></tr>
        <tr><td>Net revenue (ex-VAT, Σ Sales_History.net_revenue)</td><td class="num">£${fmtNum(s30.net_revenue_ex_vat_gbp)}</td></tr>
        <tr><td>Orders (Orders_Log, cancelled excluded)</td><td class="num">${o30.orders || 0}</td></tr>
        <tr><td>Cancelled orders</td><td class="num">${o30.cancelled_orders || 0}</td></tr>
        <tr><td>Cancelled gross £</td><td class="num">£${fmtNum(o30.cancelled_gross_gbp)}</td></tr>
        <tr><td>Refunded amount (Σ Orders_Log.refunded_amount)</td><td class="num">£${fmtNum(o30.refunded_amount_gbp)}</td></tr>
        <tr><td>Gross revenue (Σ Orders_Log.gross_revenue)</td><td class="num">£${fmtNum(o30.gross_revenue_gbp)}</td></tr>
        <tr><td>Net revenue from Orders_Log</td><td class="num">£${fmtNum(o30.net_revenue_ex_vat_gbp)}</td></tr>
        <tr><td>Average order value (net)</td><td class="num">£${fmtNum(o30.avg_order_value_net)}</td></tr>
        <tr><td>Monetary line items / zero-revenue lines</td><td class="num">${s30.monetary_line_items} / ${s30.zero_revenue_line_items}</td></tr>
      </tbody></table>

      <h3>Coverage &amp; freshness</h3>
      <table class="ag-table" style="margin-bottom:16px;"><tbody>
        <tr><td>Sheet tabs present</td><td>${sh.tabs_present} (missing: ${sh.tabs_missing.length ? esc(sh.tabs_missing.join(', ')) : 'none'})</td></tr>
        <tr><td>Sales_History date range</td><td>${esc(s30.earliest_sale)} → ${esc(s30.latest_sale)}</td></tr>
        <tr><td>Orders_Log date range</td><td>${esc(o30.earliest_order)} → ${esc(o30.latest_order)}</td></tr>
        <tr><td>Latest inventory sync</td><td>${esc(pulls.latest_inventory_sync || '')}</td></tr>
        <tr><td>Inventory_Snapshots</td><td>${inv.distinct_dates || 0} days · latest ${esc(inv.latest_snapshot)} (${inv.days_since_last_snapshot}d ago)</td></tr>
        <tr><td>Backlog_Snapshots</td><td>${bkl.exists ? `${bkl.distinct_dates || 0} days · latest ${esc(bkl.latest)}` : '<span class="ag-muted">not yet populated</span>'}</td></tr>
        <tr><td>FX_History latest</td><td>${esc(pulls.latest_fx || '')}</td></tr>
        <tr><td>Last engine run</td><td>${pulls.last_run ? `${esc(pulls.last_run.started_at.slice(0,19))} · ${esc(pulls.last_run.status)} · ${Math.round(pulls.last_run.duration_ms/1000)}s` : '<span class="ag-muted">n/a</span>'}</td></tr>
      </tbody></table>

      <h3>Pre-order vs backorder split</h3>
      <table class="ag-table" style="margin-bottom:16px;"><tbody>
        <tr><td>Pre-order SKUs</td><td class="num">${po.preorder_skus || 0}</td></tr>
        <tr><td>Pre-order units</td><td class="num">${po.preorder_units || 0}</td></tr>
        <tr><td>Backorder SKUs</td><td class="num">${po.backorder_skus || 0}</td></tr>
        <tr><td>Backorder units</td><td class="num">${po.backorder_units || 0}</td></tr>
        <tr><td>Pre-order dispatch labels</td><td>${esc(JSON.stringify(po.preorder_dispatch_labels || {}))}</td></tr>
      </tbody></table>

      <h3>Back-in-stock (Klaviyo)</h3>
      <table class="ag-table" style="margin-bottom:16px;"><tbody>
        <tr><td>BIS_Signups exists</td><td>${bis.exists ? 'yes' : '<span class="ag-bad">no. Set KLAVIYO_PRIVATE_KEY + run pullBisSignups()</span>'}</td></tr>
        <tr><td>Distinct products</td><td class="num">${bis.distinct_products || 0}</td></tr>
        <tr><td>Distinct product-variants</td><td class="num">${bis.distinct_product_variants || 0}</td></tr>
        <tr><td>Total signups (30d)</td><td class="num">${bis.total_signups_30d || 0}</td></tr>
        <tr><td>Latest snapshot date</td><td>${esc(bis.latest_snapshot || '')}</td></tr>
      </tbody></table>

      <h3>Restocked refunds <span class="ag-muted" style="font-weight:400;font-size:11px;">, units returned to stock (folded into stock value + removed from velocity demand)</span></h3>
      <table class="ag-table" style="margin-bottom:16px;"><tbody>
        <tr><td>Restocked, last 30d</td><td class="num">${(rr.restocked_30d||{}).lines||0} lines · ${(rr.restocked_30d||{}).units||0} units · £${fmtNum((rr.restocked_30d||{}).amount||0)}</td></tr>
        <tr><td>Restocked, last 365d</td><td class="num">${(rr.restocked_365d||{}).lines||0} lines · ${(rr.restocked_365d||{}).units||0} units · £${fmtNum((rr.restocked_365d||{}).amount||0)}</td></tr>
        <tr><td>No-restock refunds (365d, kept as demand)</td><td class="num">${(rr.no_restock_365d||{}).lines||0} lines · ${(rr.no_restock_365d||{}).units||0} units</td></tr>
        <tr><td>Unknown restock type (365d)</td><td class="num"><span class="${(rr.unknown_365d||{}).lines>0?'ag-pill warn':''}">${(rr.unknown_365d||{}).lines||0} lines · ${(rr.unknown_365d||{}).units||0} units</span></td></tr>
        <tr><td>Status</td><td>${esc(rr.note||'')}</td></tr>
      </tbody></table>
      ${rr.examples && rr.examples.length ? `
        <table class="ag-table" style="margin-bottom:16px;"><thead><tr>
          <th>SKU</th><th class="num">Qty</th><th class="num">£</th><th>Restock type</th><th>Refunded</th>
        </tr></thead><tbody>${rr.examples.map(x => `<tr class="clickable" data-sku="${esc(x.sku)}">
          <td><span class="sku" style="font-weight:600;">${esc(x.sku)}</span></td>
          <td class="num">${x.qty}</td><td class="num">£${fmtNum(x.amount)}</td>
          <td>${esc(x.restock_type)}</td><td>${esc(x.refunded_at)}</td>
        </tr>`).join('')}</tbody></table>` : ''}

      ${sv.top_missing_cost_by_units && sv.top_missing_cost_by_units.length ? `
        <h3>Top stock holdings with no cost set</h3>
        <table class="ag-table" style="margin-bottom:16px;"><thead><tr>
          <th>SKU</th><th>Product</th><th class="num">On hand</th>
        </tr></thead><tbody>${sv.top_missing_cost_by_units.map(x => `<tr class="clickable" data-sku="${esc(x.sku)}">
          <td><span class="sku" style="font-weight:600;">${esc(x.sku)}</span></td>
          <td>${esc(x.title || '')}</td>
          <td class="num">${x.on_hand}</td>
        </tr>`).join('')}</tbody></table>` : ''}
    `;
  }

  /* ─── SKU drawer ─────────────────────────────────────────── */
  /* ─── Customer drawer ─────────────────────────────────────────
     Reuses the same #inv-drawer scaffold as the SKU drawer. Shows
     aggregates, monthly LTV curve, full orders list, Klaviyo link. */
  async function openCustomerDrawer(customerId, agg) {
    const drawer = $('#inv-drawer');
    const label = (agg && [agg.first_name, agg.last_name].filter(Boolean).join(' ')) || (agg && agg.email) || customerId;
    $('#inv-drawer-title').textContent = label;
    $('#inv-drawer-body').innerHTML = '<div class="ag-empty"><span class="ag-spinner"></span> Loading…</div>';
    drawer.classList.remove('hidden');
    try {
      const d = await AG_ADMIN.api('ui_customer_detail', { customer_id: customerId });
      paintCustomerDrawer(d, agg);
    } catch (e) {
      $('#inv-drawer-body').innerHTML = `<div class="ag-empty">Error: ${esc(e.message || e)}</div>`;
    }
  }
  function paintCustomerDrawer(d, fallbackAgg) {
    const a = d.aggregate || fallbackAgg || {};
    const orders = d.orders || [];
    const monthly = d.monthly || [];
    const name = [a.first_name, a.last_name].filter(Boolean).join(' ');
    const label = name || a.email || a.customer_id || d.customer_id;
    $('#inv-drawer-title').textContent = label;
    const klaviyoUrl = a.email
      ? `https://www.klaviyo.com/profiles?q=${encodeURIComponent(a.email)}`
      : '';
    const shopifyUrl = a.customer_id
      ? `https://${ (window.AG_ADMIN && AG_ADMIN.shopDomain) || 'assassin-goods2.myshopify.com' }/admin/customers/${String(a.customer_id).replace(/[^0-9]/g, '')}`
      : '';
    /* Loyalty arc: months from first to last purchase. */
    const totalMonths = monthly.length;
    const orderCount = a.orders_count || orders.length;
    const refunded = a.refunded_amount_gbp || orders.reduce((s, o) => s + (o.refunded_amount_gbp || 0), 0);
    $('#inv-drawer-body').innerHTML = `
      <div class="ag-prod" style="margin:-4px 0 12px;">
        <span class="name" style="font-size:14px;">${esc(label)}</span>
        ${a.email && name ? `<span class="variant">${esc(a.email)}</span>` : ''}
        <span class="sku">${esc(a.customer_id || d.customer_id || '')}</span>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:14px;">
        ${klaviyoUrl ? `<a class="ag-btn secondary" href="${klaviyoUrl}" target="_blank" rel="noopener" style="padding:4px 10px;font-size:11px;">Klaviyo profile →</a>` : ''}
        ${shopifyUrl ? `<a class="ag-btn secondary" href="${shopifyUrl}" target="_blank" rel="noopener" style="padding:4px 10px;font-size:11px;">Shopify admin →</a>` : ''}
        ${a.email ? `<a class="ag-btn secondary" href="mailto:${esc(a.email)}" style="padding:4px 10px;font-size:11px;">Email</a>` : ''}
      </div>
      <div class="ag-grid" style="grid-template-columns:1fr 1fr;margin-bottom:14px;">
        ${statCard('Total revenue', '£' + fmtNum(a.total_revenue_gbp || 0), '')}
        ${statCard('Orders', orderCount, '')}
        ${statCard('AOV', '£' + fmtNum(a.avg_order_value_gbp || 0), '')}
        ${statCard('Units', a.units_count || 0, '')}
        ${statCard('Last purchase', fmtFriendly(a.last_purchase) || '-', '')}
        ${statCard('Days since', (a.days_since_last_purchase || 0) + 'd', '')}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <span style="font-size:12px;">RFM ${a.rfm_segment ? '· ' + rfmSegmentPill(a.rfm_segment) : ''}</span>
        <span class="ag-muted" style="font-size:11px;">R${a.r_score || '?'} F${a.f_score || '?'} M${a.m_score || '?'} · ${totalMonths}-month lifespan</span>
      </div>
      ${refunded > 0 ? `<p class="ag-muted" style="font-size:11px;color:#a33;margin:0 0 12px 0;">£${fmtNum(refunded)} refunded across orders.</p>` : ''}
      ${monthly.length ? `
        <h3 style="margin-top:18px;">Monthly LTV</h3>
        <div class="ag-chart" style="height:160px;"><canvas id="ch-cust-ltv"></canvas></div>` : ''}
      <h3 style="margin-top:18px;">Orders <span class="ag-muted" style="font-weight:400;">· ${orders.length}${d.orders_total > orders.length ? ' of ' + d.orders_total : ''}</span></h3>
      <table class="ag-table" style="font-size:12px;"><thead><tr>
        <th>Date</th><th>Order</th><th>Channel</th><th>Discount</th><th class="num">£</th><th class="num">Units</th><th></th>
      </tr></thead><tbody>${orders.map(o => {
        const isCancelled = !!o.cancelled_at;
        const hasRefund = (o.refunded_amount_gbp || 0) > 0;
        return `<tr${isCancelled ? ' style="opacity:0.5;"' : ''}>
          <td>${esc(String(o.created_at).slice(0, 10))}</td>
          <td class="ag-muted">${esc(String(o.order_id).split('/').pop())}</td>
          <td>${esc(o.channel || o.source_name || '-')}</td>
          <td class="ag-muted">${o.discount_code ? esc(o.discount_code) : '-'}</td>
          <td class="num"><strong>£${fmtNum(o.gross_revenue_gbp)}</strong>${hasRefund ? `<div class="ag-muted" style="font-size:10px;color:#a33;">−£${fmtNum(o.refunded_amount_gbp)}</div>` : ''}</td>
          <td class="num">${o.units_count}</td>
          <td>${isCancelled ? '<span class="ag-pill mute">cancelled</span>' : (hasRefund ? '<span class="ag-pill warn">refunded</span>' : '')}</td>
        </tr>`;
      }).join('')}</tbody></table>`;
    if (monthly.length) {
      chartLine('ch-cust-ltv', monthly.map(m => ({ date: m.month + '-01', value: m.gross })), 'Gross £/month', { decimals: 0, beginAtZero: true });
    }
  }

  async function openSkuDrawer(sku) {
    const drawer = $('#inv-drawer');
    $('#inv-drawer-title').textContent = sku;
    $('#inv-drawer-body').innerHTML = '<div class="ag-empty"><span class="ag-spinner"></span> Loading…</div>';
    drawer.classList.remove('hidden');
    try {
      const d = await AG_ADMIN.api('ui_sku_detail', { sku });
      paintSkuDrawer(d);
    } catch (e) { $('#inv-drawer-body').innerHTML = `<div class="ag-empty">Error: ${esc(e.message || e)}</div>`; }
  }
  function paintSkuDrawer(d) {
    const live = d.live || {};
    const s = d.settings || {};
    const e = d.engine || {};
    const bis = d.bis || { signups: 0, variants: [], last_signup: '' };
    /* Drawer title. Keep it text-only (the panel header is a plain h3) but
       use product name as the primary string, variant as secondary. */
    const titleParts = [live.product_title || d.sku];
    if (live.variant_title) titleParts.push(live.variant_title);
    $('#inv-drawer-title').textContent = titleParts.join(' · ');
    $('#inv-drawer-body').innerHTML = `
      <div class="ag-prod" style="margin:-4px 0 12px;">
        <span class="name" style="font-size:14px;">${esc(live.product_title || '-')}</span>
        ${live.variant_title ? `<span class="variant">${esc(live.variant_title)}</span>` : ''}
        <span class="sku">${esc(d.sku || '')}</span>
      </div>
      <a class="ag-btn secondary" href="#sku/${encodeURIComponent(d.sku)}" style="display:inline-block;margin-bottom:12px;font-size:11px;">Open full page →</a>
      <div class="ag-grid" style="grid-template-columns:1fr 1fr;margin-bottom:16px;">
        ${statCard('On hand', live.on_hand_available || 0, '')}
        ${statCard('Status', e.status || '-', '')}
        ${statCard('Daily velocity', fmtNum(e.daily_velocity, 3), '')}
        ${statCard('Annual units', e.annual_units_sold || 0, '')}
      </div>
      ${bis.signups ? `
        <div class="ag-alert" style="margin-bottom:16px;">
          <h3>Back-in-stock signups<span class="count">${bis.signups}</span></h3>
          <div class="ag-bis-rows">
            ${(bis.variants && bis.variants.length ? bis.variants : [{ variant_title: live.variant_title || '', signups: bis.signups, last_signup: bis.last_signup }])
              .map(v => `<div class="ag-bis-row">
                <span>${esc(v.variant_title || '(default)')}</span>
                <span><span class="qty">${v.signups || 0}</span> <span class="when">· last ${esc(fmtFriendly(v.last_signup))}</span></span>
              </div>`).join('')}
          </div>
        </div>` : ''}
      <h3>90-day stock + sales</h3>
      <div class="ag-chart" style="height:200px;"><canvas id="ch-sku-trend"></canvas></div>
      <h3 style="margin-top:24px;">Settings</h3>
      <form id="sku-settings-form" class="ag-form-grid" style="grid-template-columns:1fr;">
        ${fieldRow('supplier', 'Supplier', s.supplier)}
        ${fieldRow('lead_time_days', 'Lead time (days)', s.lead_time_days, 'number')}
        ${fieldRow('batch_size', 'Batch size', s.batch_size, 'number')}
        ${fieldRow('moq', 'MOQ', s.moq, 'number')}
        ${fieldRow('unit_cost', 'Unit cost (£, ex-VAT)', s.unit_cost, 'number')}
        ${fieldRow('shipping_cost_per_unit', 'Shipping/unit (£, ex-VAT)', s.shipping_cost_per_unit, 'number')}
        ${fieldRow('max_preorder_qty', 'Max pre-order qty', s.max_preorder_qty, 'number')}
        ${fieldRow('linked_po_number', 'Linked PO', s.linked_po_number)}
        ${selectRow('lifecycle_status', 'Lifecycle', s.lifecycle_status, ['', 'Discontinued', 'On Hold'])}
        ${fieldRow('active', 'Active (TRUE/FALSE)', s.active === true ? 'TRUE' : s.active === false ? 'FALSE' : '')}
        ${fieldRow('notes', 'Notes', s.notes)}
        <button type="submit" class="ag-btn">Save</button>
      </form>`;
    chartSkuTrend('ch-sku-trend', d.sales || [], d.snapshots || [], d.backlog || []);
    if (!isSectionAllowed('ops')) { lockSkuSettingsForm('#sku-settings-form'); return; }
    $('#sku-settings-form').addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const fields = {};
      $$('#sku-settings-form input, #sku-settings-form select').forEach(el => {
        const name = el.name; if (!name) return;
        let v = el.value;
        if (name === 'active') v = (String(v).toUpperCase() === 'TRUE');
        fields[name] = v;
      });
      try {
        await AG_ADMIN.api('ui_save_sku_settings', { sku: d.sku, fields });
        AG_ADMIN.toast('Saved');
        closeDrawer();
        delete state.cache.today;
        if (state.tab === 'today' || state.tab === 'reorder') renderTab(state.tab);
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    });
  }
  /* Format a money value for display: thousands commas + exactly 2 dp
     (0.2 → "0.20", 33734 → "33,734.00"). Empty/NaN → ''. */
  function fmtMoneyInput(v) {
    const n = parseFloat(String(v == null ? '' : v).replace(/[,\s£$€]/g, ''));
    if (!isFinite(n)) return '';
    return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function fieldRow(name, label, value, type, opts) {
    opts = opts || {};
    const ph = opts.placeholder ? ` placeholder="${esc(opts.placeholder)}"` : '';
    /* Money fields: text + inputmode decimal so we can show commas (a number
       input can't). Pre-formatted; commas stripped on save. */
    if (type === 'money') {
      const fv = (value == null || value === '') ? '' : fmtMoneyInput(value);
      return `<label><span class="label">${esc(label)}</span><input class="ag-input ag-money" name="${esc(name)}" id="f-${esc(name)}" type="text" inputmode="decimal"${ph} value="${esc(fv)}"></label>`;
    }
    /* number inputs default to step=1 (integers only) → reject decimals like
       23613.80 and any value <1. step="any" accepts any decimal. */
    const step = (type === 'number') ? ' step="any"' : '';
    return `<label><span class="label">${esc(label)}</span><input class="ag-input" name="${esc(name)}" id="f-${esc(name)}" type="${type || 'text'}"${step}${ph} value="${esc(value == null ? '' : value)}"></label>`;
  }
  /* SKU settings (supplier, lead time, costs, pre-order cap...) are a stock
     write: the auth Worker accepts ui_save_sku_settings on the stock route
     only (ag-admin-auth #8). The analytics view shows the same settings for
     reference, so lock them there instead of offering a Save that would 403. */
  function lockSkuSettingsForm(sel) {
    const f = $(sel); if (!f) return;
    f.querySelectorAll('input, select, textarea').forEach(el => { el.disabled = true; });
    f.querySelectorAll('button[type="submit"]').forEach(b => b.remove());
    f.insertAdjacentHTML('beforeend', '<p class="ag-muted" style="grid-column:1/-1;margin:4px 0 0;">Read only here. Edit these on the Stock Management page.</p>');
  }
  function selectRow(name, label, value, options) {
    return `<label><span class="label">${esc(label)}</span><select class="ag-select" name="${esc(name)}">${
      options.map(o => `<option value="${esc(o)}" ${String(value || '') === o ? 'selected' : ''}>${esc(o || '(none)')}</option>`).join('')
    }</select></label>`;
  }
  function closeDrawer() {
    /* Guard against losing unsaved PO edits. */
    if (document.querySelector('#po-form') && state.poDirty &&
        !confirm('You have unsaved changes to this PO.\n\nClose without saving?')) return;
    state.poDirty = false;
    $('#inv-drawer').classList.add('hidden');
    /* Restore default-width drawer for non-PO uses. */
    const panel = document.querySelector('#inv-drawer .ag-drawer-panel');
    if (panel) panel.classList.remove('wide');
  }

  /* ─── Settings drawer ────────────────────────────────────── */
  function openSettings() {
    const d = $('#inv-settings');
    const theme = state.settings.theme || 'light';
    $('#inv-settings-body').innerHTML = `
      <p class="ag-muted">Saved locally to this browser. Apply across browsers by exporting/importing the JSON below.</p>
      <label><span class="label">Theme</span>
        <select class="ag-select" id="set-theme">
          <option value="light" ${theme === 'light' ? 'selected' : ''}>Light</option>
          <option value="dark" ${theme === 'dark' ? 'selected' : ''}>Dark</option>
        </select></label>
      <label><span class="label">Default tab on load</span>
        <select class="ag-select" id="set-tab">${TABS.map(t => `<option value="${t.id}" ${state.settings.default_tab === t.id ? 'selected' : ''}>${esc(t.label)}</option>`).join('')}</select></label>
      <label><span class="label">Default sales period (days)</span>
        <select class="ag-select" id="set-period">${[7,30,90,365].map(n => `<option value="${n}" ${state.settings.default_period === n ? 'selected' : ''}>${n}</option>`).join('')}</select></label>
      <label><span class="label">Pre-order alert threshold (% of cap)</span>
        <input class="ag-input" id="set-pct" type="number" min="0" max="100" value="${state.settings.preorder_alert_pct}"></label>
      <label><span class="label">Hide tabs</span>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">${TABS.map(t => `<label style="display:flex;gap:4px;align-items:center;"><input type="checkbox" data-hide="${t.id}" ${state.settings.hidden_tabs.includes(t.id) ? 'checked' : ''}> ${esc(t.label)}</label>`).join('')}</div></label>
      <div style="margin-top:20px;display:flex;gap:8px;">
        <button class="ag-btn" onclick="AGInv.saveSettings()">Save</button>
        <button class="ag-btn secondary" onclick="AGInv.closeSettings()">Cancel</button>
      </div>`;
    d.classList.remove('hidden');
  }
  function closeSettings() { $('#inv-settings').classList.add('hidden'); }
  function applySettings() {
    state.settings.theme = $('#set-theme').value;
    state.settings.default_tab = $('#set-tab').value;
    state.settings.default_period = parseInt($('#set-period').value, 10);
    state.settings.preorder_alert_pct = parseInt($('#set-pct').value, 10);
    state.settings.hidden_tabs = $$('#inv-settings-body input[data-hide]:checked').map(i => i.dataset.hide);
    saveSettings();
    applyTheme();
    closeSettings();
    renderTabs();
    AG_ADMIN.toast('Settings saved');
  }

  /* ─── PO form (existing) ─────────────────────────────────── */
  async function editPo(poNumber) {
    const drawer = $('#inv-drawer');
    /* Open as the wide centred modal from the first frame (no slide-then-snap). */
    const panel = drawer.querySelector('.ag-drawer-panel'); if (panel) panel.classList.add('wide');
    drawer.classList.remove('hidden');
    /* Optimistic open: the list already carries the full header, so render the
       form instantly and only show a spinner for the lines + docs, which need a
       round-trip. Falls back to a plain spinner if the row isn't cached. */
    const cached = (state.posByNumber || {})[poNumber];
    if (cached) {
      paintPoDrawer({ header: cached, lines: [], documents: [], _loading: true });
    } else {
      $('#inv-drawer-title').textContent = 'PO ' + poNumber;
      $('#inv-drawer-body').innerHTML = '<div class="ag-empty"><span class="ag-spinner"></span> Loading…</div>';
    }
    try {
      const po = await AG_ADMIN.api('ui_get_po', { po_number: poNumber });
      /* If the operator started editing while lines loaded, keep their header
         edits and just drop in the lines/docs; otherwise re-render fresh. */
      if (cached && state.poDirty) fillPoDetail(po);
      else paintPoDrawer(po);
    } catch (e) {
      if (cached) AG_ADMIN.toast('Couldn’t load lines/docs: ' + (e.message || e), true);
      else $('#inv-drawer-body').innerHTML = `<div class="ag-empty">${esc(e.message || e)}</div>`;
    }
  }
  function newPo() {
    const drawer = $('#inv-drawer');
    const panel = drawer.querySelector('.ag-drawer-panel'); if (panel) panel.classList.add('wide');
    drawer.classList.remove('hidden');
    $('#inv-drawer-title').textContent = 'New PO';
    paintPoDrawer(null);
  }
  /* ── PO lifecycle stages ──────────────────────────────────────────
     A PO moves Pre-purchase → In Production → Completed → Arrived. The
     form reveals each stage's fields as you advance (you see the current
     + past stages, never future). Legacy/canonical statuses normalise in. */
  const PO_STAGES = [
    { key: 'pre_purchase', label: 'Pre-purchase' },
    { key: 'in_production', label: 'In Production' },
    { key: 'completed', label: 'Completed' },
    { key: 'arrived', label: 'Arrived' }
  ];
  function poStageKey(status) {
    switch (String(status || '')) {
      case 'in_production': case 'deposit_paid': case 'submitted_to_shopify': return 'in_production';
      case 'completed': case 'ready_to_ship': case 'in_transit': return 'completed';
      case 'arrived': case 'received': return 'arrived';
      case 'cancelled': return 'cancelled';
      default: return 'pre_purchase'; // draft / placed / ready_to_submit / '' / unknown
    }
  }
  function poStageIndex(status) { return PO_STAGES.findIndex(s => s.key === poStageKey(status)); }
  /* ── Payment clarity (2026-08-17) ─────────────────────────────────
     An invoice was double-paid because the form could not answer "what has
     actually been paid and what is left?": deposit/balance each spread across
     three boxes (scheduled USD amount, £ actually paid, date paid) with no
     computed state anywhere, so a filled USD box read as "paid" and an empty
     £ box read as "unpaid", both wrongly. This strip derives one unambiguous
     answer, live as the operator types. A leg counts as PAID when a paid
     date or a £-paid amount is recorded; the scheduled USD amount alone is a
     plan, not a payment. */
  function poPayState() {
    const num = id => { const el = document.querySelector('#f-' + id); return el ? (parseFloat(String(el.value).replace(/[,\s£$€]/g, '')) || 0) : 0; };
    const str = id => { const el = document.querySelector('#f-' + id); return el ? String(el.value || '').trim() : ''; };
    const goods = num('goods_value_usd');
    const leg = k => {
      const usd = num(k + '_usd'), gbp = num(k + '_gbp_paid'), date = str(k + '_paid_date');
      return { usd, gbp, date, paid: !!(gbp > 0 || date) };
    };
    const dep = leg('deposit'), bal = leg('balance');
    const extras = state.poPayments || [];
    const extraUsd = extras.reduce((t, pm) => t + (parseFloat(pm.amount_usd) || 0), 0);
    const extraGbp = extras.reduce((t, pm) => t + (parseFloat(pm.gbp_paid) || 0), 0);
    const paidUsd = (dep.paid ? dep.usd : 0) + (bal.paid ? bal.usd : 0) + extraUsd;
    const paidGbp = (dep.paid ? dep.gbp : 0) + (bal.paid ? bal.gbp : 0) + extraGbp;
    const paidCount = (dep.paid ? 1 : 0) + (bal.paid ? 1 : 0) + extras.length;
    return { goods, dep, bal, paidUsd, paidGbp, paidCount, extraUsd, extraGbp, extraCount: extras.length,
      outstanding: goods - paidUsd,
      allocated: dep.usd + bal.usd + extraUsd };
  }
  /* Rail dates timeline, derived live from the form's own date inputs. A set
     date gets a solid dot; the first unset one is the current waypoint. */
  /* Entering a £-paid amount defaults its "paid on" date to today, so a
     recorded payment can never sit dateless (the legacy POs are full of
     paid-but-dateless legs that read as DUE). Only the £-from-bank fields do
     this: the agreed-USD amount is a plan, and auto-dating it would wrongly
     flip the leg to PAID. An auto-filled date clears again if the amount is
     emptied, but a date the operator typed or edited is never touched. */
  function localTodayStr() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  /* The PO's payment LEDGER, one numbered list: the deposit leg is payment 1,
     the balance leg payment 2 (both edited in their fields above and read live
     from them), then every recorded instalment as 3, 4, ... in date order.
     Mirrors buildPoLedger_ in the inventory script. */
  const PO_PAY_METHODS = [['bank_transfer', 'Bank transfer'], ['wise', 'Wise'], ['card', 'Card'], ['paypal', 'PayPal'], ['cash', 'Cash'], ['other', 'Other']];
  const PO_PAY_CCYS = ['USD', 'GBP', 'EUR', 'CNY'];
  function poPayMethodLabel(k) { const f = PO_PAY_METHODS.find(m => m[0] === k); return f ? f[1] : (k || ''); }
  function renderPoExtraList() {
    const host = document.querySelector('#po-extra-list');
    if (!host) return;
    const money = (sym, n) => sym + (Math.round((parseFloat(n) || 0) * 100) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const st = poPayState();
    const rows = (state.poPayments || []).slice().sort((a, b) => {
      const da = String(a.paid_on || ''), db = String(b.paid_on || '');
      if (da !== db) return da < db ? -1 : 1;
      return String(a.created_at || '') < String(b.created_at || '') ? -1 : 1;
    });
    const line = (n, label, body, tail) => `
      <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;padding:6px 2px;border-bottom:1px solid #f2f4f6;font-size:12.5px;font-variant-numeric:tabular-nums;">
        <span class="ag-muted" style="min-width:22px;font-weight:700;">${n}</span>
        <span style="min-width:64px;font-weight:600;">${label}</span>
        ${body}
        ${tail || ''}
      </div>`;
    const legLine = (n, label, l) => {
      if (!l.paid && !(l.usd > 0)) return line(n, label, '<span class="ag-muted">not set</span>', '<span class="ag-muted" style="margin-left:auto;font-size:11px;">edit above</span>');
      const pill = l.paid
        ? `<span class="ag-pill" style="background:#dcfce7;color:#166534;">PAID${l.date ? ' ' + esc(fmtFriendly(l.date)) : ''}</span>`
        : '<span class="ag-pill" style="background:#fef3c7;color:#92400e;">DUE</span>';
      return line(n, label,
        `<span style="font-weight:700;">${l.usd > 0 ? money('$', l.usd) : '<span class="ag-muted">no USD</span>'}</span>
         ${l.gbp > 0 ? `<span>${money('£', l.gbp)}</span>` : ''}
         ${pill}`,
        '<span class="ag-muted" style="margin-left:auto;font-size:11px;">edit above</span>');
    };
    const instLine = (pm, i) => {
      const ccy = String(pm.currency || 'USD').toUpperCase();
      const own = ccy !== 'USD' && ccy !== 'GBP' && (parseFloat(pm.amount) || 0) > 0
        ? `<span class="ag-muted">${esc(ccy)} ${esc(money('', pm.amount))}</span>` : '';
      const rate = (parseFloat(pm.fx_rate) || 0) > 0 ? `<span class="ag-muted" title="Effective USD per £">@ ${esc(String(pm.fx_rate))}</span>` : '';
      const meta = [pm.method ? esc(poPayMethodLabel(pm.method)) : '', pm.reference ? 'ref ' + esc(String(pm.reference).slice(0, 40)) : '', pm.note ? esc(String(pm.note).slice(0, 60)) : '']
        .filter(Boolean).join(' · ');
      return line(3 + i, 'Payment',
        `<span style="font-weight:700;">${money('$', pm.amount_usd)}</span>
         <span>${money('£', pm.gbp_paid)}</span>
         ${own}${rate}
         <span class="ag-pill" style="background:#dcfce7;color:#166534;">PAID ${esc(fmtFriendly(pm.paid_on))}</span>
         ${meta ? `<span class="ag-muted">${meta}</span>` : ''}`,
        `<button type="button" class="ag-btn secondary" style="margin-left:auto;padding:2px 9px;" title="Delete this payment record" onclick="AGInv.deletePoPayment('${esc(pm.po_number)}','${esc(pm.payment_id)}')">×</button>`);
    };
    const total = st.paidCount
      ? `<div style="display:flex;gap:10px;padding:8px 2px 2px;font-size:12.5px;font-weight:700;font-variant-numeric:tabular-nums;">
           <span>Paid so far, ${st.paidCount} payment${st.paidCount === 1 ? '' : 's'}:</span>
           <span>${money('$', st.paidUsd)}</span><span>${money('£', st.paidGbp)}</span>
         </div>` : '';
    host.innerHTML = legLine(1, 'Deposit', st.dep) + legLine(2, 'Balance', st.bal) + rows.map(instLine).join('') + total;
  }
  /* Resolve the new-payment inputs to the USD it settles and the £ it cost,
     the same rules as parsePoPaymentInput_ server side: a USD payment settles
     its own amount, a GBP payment cost its own amount, and the rate (USD per £)
     may fill ONE missing side. The server re-derives and has the final say.
     A box the chosen currency HIDES is never read: a USD payment's USD is its
     amount, a GBP payment's £ is its amount, whatever a hidden box still holds
     (typing £800 under EUR then switching to GBP must not record £800). */
  function readNewPoPayment() {
    const num = id => { const el = document.querySelector('#f-' + id); return el ? (parseFloat(String(el.value).replace(/[,\s£$€]/g, '')) || 0) : 0; };
    const str = id => { const el = document.querySelector('#f-' + id); return el ? String(el.value || '').trim() : ''; };
    const currency = (str('newpay-ccy') || 'USD').toUpperCase();
    const amount = num('newpay-amount');
    let usd = currency === 'USD' ? amount : num('newpay-usd');
    let gbp = currency === 'GBP' ? amount : num('newpay-gbp');
    const fx = num('newpay-fx');
    if (fx > 0) {
      if (!(usd > 0) && gbp > 0) usd = gbp * fx;
      else if (!(gbp > 0) && usd > 0) gbp = usd / fx;
    }
    return { currency, amount, usd: Math.round(usd * 100) / 100, gbp: Math.round(gbp * 100) / 100, fx,
      date: str('newpay-date'), method: str('newpay-method'), reference: str('newpay-ref'), note: str('newpay-note') };
  }
  /* Show only the boxes the chosen currency needs: a USD payment has no
     separate USD box, a GBP payment no separate £ box. A box being hidden is
     also CLEARED, so nothing typed under another currency lingers unseen. */
  function syncNewPoPaymentFields() {
    const ccy = ((document.querySelector('#f-newpay-ccy') || {}).value || 'USD').toUpperCase();
    const show = (id, input, on) => {
      const el = document.querySelector('#' + id); if (el) el.style.display = on ? '' : 'none';
      if (!on) { const inp = document.querySelector('#f-' + input); if (inp) inp.value = ''; }
    };
    show('newpay-usd-wrap', 'newpay-usd', ccy !== 'USD');
    show('newpay-gbp-wrap', 'newpay-gbp', ccy !== 'GBP');
    const lbl = document.querySelector('#newpay-amount-lbl');
    if (lbl) lbl.textContent = 'Amount (' + ccy + ')';
  }
  async function addPoPayment(poNumber) {
    const np = readNewPoPayment();
    if (!(np.amount > 0)) { AG_ADMIN.toast('Enter the amount paid.', true); return; }
    if (!(np.usd > 0) || !(np.gbp > 0)) {
      AG_ADMIN.toast('A payment needs the USD it settles and the £ actually paid (or one of them plus the rate).', true);
      return;
    }
    /* The double-pay moment, caught at the point of recording. */
    const st = poPayState();
    if (st.goods > 0 && np.usd > st.outstanding + 0.005) {
      const over = Math.round((np.usd - Math.max(0, st.outstanding)) * 100) / 100;
      if (!confirm('This payment is $' + fmtNum(over) + ' MORE than the $' + fmtNum(Math.max(0, st.outstanding)) + ' outstanding on this PO.\n\nThis is how an invoice gets paid twice.\n\nOK = record it anyway (I am sure).\nCancel = do not record.')) return;
    }
    const btn = document.querySelector('#po-newpay-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Recording…'; }
    const payload = { po_number: poNumber, currency: np.currency, amount: np.amount, amount_usd: np.usd, gbp_paid: np.gbp,
      fx_rate: np.fx || '', paid_on: np.date, method: np.method, reference: np.reference, note: np.note };
    try {
      let r;
      try {
        r = await AG_ADMIN.api('ui_add_po_payment', payload);
      } catch (e) {
        /* AG_ADMIN.api throws on any body with an `error` field. The inventory
           script returns the duplicate refusal as `message` so it arrives here
           as a value, but a backend that still sends it as `error` (the first
           cut of this change) must not lose the confirm: recognise its text. */
        const msg = String((e && e.message) || e || '');
        if (!/^Not recorded: /.test(msg)) throw e;
        r = { ok: false, duplicate: true, message: msg };
      }
      /* The server refuses a probable repeat (same bank reference, or an
         identical row seconds ago). A real second payment is one confirm away. */
      if (r && r.ok === false && r.duplicate) {
        if (!confirm((r.message || r.error || 'This looks like a payment already recorded.') + '\n\nOK = it is a separate payment, record it.\nCancel = do not record.')) return;
        r = await AG_ADMIN.api('ui_add_po_payment', Object.assign({}, payload, { allow_duplicate: true }));
      }
      if (!r || r.ok === false) throw new Error((r && (r.message || r.error)) || 'add failed');
      state.poPayments = r.payments || [];
      ['newpay-amount', 'newpay-usd', 'newpay-gbp', 'newpay-fx', 'newpay-date', 'newpay-ref', 'newpay-note'].forEach(id => { const el = document.querySelector('#f-' + id); if (el) el.value = ''; });
      renderPoExtraList(); updatePoPaySummary();
      AG_ADMIN.toast('Payment recorded');
    } catch (e) {
      AG_ADMIN.toast('Payment NOT recorded: ' + (e.message || e), true);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = '＋ Record payment'; }
    }
  }
  async function deletePoPayment(poNumber, paymentId) {
    if (!confirm('Delete this payment record? The ledger row is removed; no bank transaction is touched.')) return;
    try {
      const r = await AG_ADMIN.api('ui_delete_po_payment', { po_number: poNumber, payment_id: paymentId });
      if (!r || r.ok === false) throw new Error((r && (r.message || r.error)) || 'delete failed');
      state.poPayments = r.payments || [];
      renderPoExtraList(); updatePoPaySummary();
      AG_ADMIN.toast('Payment record deleted');
    } catch (e) { AG_ADMIN.toast('Not deleted: ' + (e.message || e), true); }
  }

  function wirePaymentDateDefaults() {
    [['deposit_gbp_paid', 'deposit_paid_date'], ['balance_gbp_paid', 'balance_paid_date']].forEach(([amtId, dateId]) => {
      const amt = document.querySelector('#f-' + amtId), date = document.querySelector('#f-' + dateId);
      if (!amt || !date || amt._dateDefaultBound) return;
      amt._dateDefaultBound = true;
      amt.addEventListener('input', () => {
        const v = parseFloat(String(amt.value).replace(/[,\s£$€]/g, '')) || 0;
        if (v > 0 && !date.value) {
          date.value = localTodayStr();
          date.dataset.autofilled = '1';
          date.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (v <= 0 && date.dataset.autofilled === '1') {
          date.value = '';
          delete date.dataset.autofilled;
          date.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      date.addEventListener('input', () => { delete date.dataset.autofilled; });
    });
  }

  function updatePoDatesTl() {
    const host = document.querySelector('#po-dates-tl');
    if (!host) return;
    const val = id => { const el = document.querySelector('#f-' + id); return el ? String(el.value || '').trim() : ''; };
    const steps = [
      ['Ordered', val('date_ordered')], ['Production', val('production_date')],
      ['Est. done', val('est_completion_date')], ['Shipped', val('date_shipped')],
      ['ETA port', val('eta_port')], ['Warehouse', val('eta')]
    ];
    let curSeen = false;
    host.innerHTML = steps.map(([label, d]) => {
      let cls = '';
      if (d) cls = 'done';
      else if (!curSeen) { cls = 'cur'; curSeen = true; }
      return `<li class="${cls}">${esc(label)} <span class="when">${d ? esc(fmtFriendly(d)) : 'not yet'}</span></li>`;
    }).join('');
  }

  function updatePoPaySummary() {
    const host = document.querySelector('#po-pay-summary');
    if (!host) return;
    const m = (n) => '$' + (Math.round(n * 100) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const st = poPayState();
    const legChip = (label, l) => {
      const state = l.paid
        ? `<span class="ag-pill" style="background:#dcfce7;color:#166534;">PAID${l.date ? ' ' + esc(fmtFriendly(l.date)) : ''}${l.gbp > 0 ? ' · £' + (Math.round(l.gbp * 100) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 }) : ''}</span>`
        : l.usd > 0
          ? '<span class="ag-pill" style="background:#fef3c7;color:#92400e;">DUE, not yet paid</span>'
          : '<span class="ag-pill mute">not set</span>';
      return `<div style="flex:1;min-width:170px;">
        <div class="ag-muted" style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">${label}</div>
        <div style="font-size:16px;font-weight:700;">${l.usd > 0 ? m(l.usd) : '<span class="ag-muted">no amount</span>'}</div>
        <div style="margin-top:2px;">${state}</div>
      </div>`;
    };
    const over = st.outstanding < -0.005;
    const paidInFull = st.goods > 0 && Math.abs(st.outstanding) <= 0.005 && st.dep.paid && st.bal.paid;
    const outCol = over ? '#c8102e' : paidInFull ? '#166534' : '#111';
    const warnings = [];
    if (st.goods > 0 && st.allocated > st.goods + 0.005)
      warnings.push((st.extraCount ? 'Deposit, balance and the further payments add up to ' : 'Deposit + balance add up to ') + m(st.allocated) + ', which is ' + m(st.allocated - st.goods) + ' MORE than the PO total. Check the amounts before paying anything.');
    if ((st.dep.paid && st.dep.usd <= 0) || (st.bal.paid && st.bal.usd <= 0))
      warnings.push('A payment is marked paid but has no USD amount recorded, so Outstanding cannot include it. Fill in the USD amount.');
    host.innerHTML = `
      <div style="border:1px solid ${over ? '#fca5a5' : '#e3e3e3'};background:${over ? '#fef2f2' : '#fafafa'};border-radius:8px;padding:12px 14px;margin:0 0 14px;">
        <div style="display:flex;gap:18px;flex-wrap:wrap;align-items:flex-start;">
          ${legChip('Deposit', st.dep)}
          ${legChip('Balance', st.bal)}
          <div style="flex:1;min-width:170px;">
            <div class="ag-muted" style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Outstanding (of ${st.goods > 0 ? m(st.goods) : 'no PO total'})</div>
            <div style="font-size:16px;font-weight:700;color:${outCol};">${over ? 'OVERPAID by ' + m(-st.outstanding) : m(Math.max(0, st.outstanding))}</div>
            <div style="margin-top:2px;">${paidInFull ? '<span class="ag-pill" style="background:#dcfce7;color:#166534;">PAID IN FULL</span>' : ''}</div>
            ${st.extraCount ? `<div class="ag-muted" style="font-size:11px;margin-top:4px;">+ ${st.extraCount} instalment${st.extraCount === 1 ? '' : 's'} · ${m(st.extraUsd)} included</div>` : ''}
          </div>
        </div>
        ${warnings.map(w => `<div style="margin-top:8px;font-size:12px;font-weight:600;color:#c8102e;">⚠ ${esc(w)}</div>`).join('')}
      </div>`;
    /* The ledger's deposit / balance rows read the same live fields. */
    renderPoExtraList();
  }

  function poStepperHtml(status) {
    const cancelled = poStageKey(status) === 'cancelled';
    const cur = poStageIndex(status);
    return `<div id="po-stepper" class="agpo-stages">
      ${PO_STAGES.map((s, i) => {
        const done = !cancelled && i < cur, active = !cancelled && i === cur;
        return `${i > 0 ? '<span class="agpo-arrow">→</span>' : ''}<button type="button" data-stage-btn="${s.key}"
          class="agpo-stg ${active ? 'cur' : done ? 'done' : 'next'}"
          onclick="AGInv.shiftPoStage('${s.key}')" title="Set stage: ${esc(s.label)}">${done ? '✓ ' : ''}${esc(s.label)}</button>`;
      }).join('')}
    </div>${cancelled ? '<div class="ag-pill mute" style="margin:0 0 12px;">Cancelled. All fields shown for reference.</div>' : ''}`;
  }
  /* Show/hide every [data-min-stage] block for the given status (cancelled shows all). */
  function applyStageGating(status) {
    const cancelled = poStageKey(status) === 'cancelled';
    const idx = cancelled ? 99 : poStageIndex(status);
    document.querySelectorAll('#inv-drawer-body [data-min-stage]').forEach(el => {
      el.style.display = (Number(el.getAttribute('data-min-stage')) <= idx) ? '' : 'none';
    });
    const cur = poStageIndex(status);
    document.querySelectorAll('#po-stepper [data-stage-btn]').forEach((btn, i) => {
      btn.classList.remove('cur', 'done', 'next');
      const done = !cancelled && i < cur, active = !cancelled && i === cur;
      btn.classList.add(active ? 'cur' : done ? 'done' : 'next');
      const label = btn.textContent.replace(/^✓ /, '');
      btn.textContent = (done ? '✓ ' : '') + label;
    });
    /* Advance button: the next stage as a verb, not four mystery buttons. */
    const adv = document.querySelector('#po-advance-btn');
    if (adv) {
      const next = (!cancelled && cur >= 0) ? PO_STAGES[cur + 1] : null;
      adv.style.display = next ? '' : 'none';
      if (next) {
        adv.textContent = 'Mark as ' + next.label.toLowerCase() + ' →';
        adv.setAttribute('onclick', "AGInv.shiftPoStage('" + next.key + "')");
      }
    }
  }
  function shiftPoStage(stageKey) {
    const f = $('#f-status'); if (!f) return;
    f.value = stageKey; state.poDirty = true;
    applyStageGating(stageKey);
  }
  function cancelPo(poNumber) {
    if (!confirm('Cancel PO ' + poNumber + '?\n\nIt will be filed under Cancelled (no Shopify side-effects).')) return;
    const f = $('#f-status'); if (f) f.value = 'cancelled';
    savePo();
  }
  function archivePo(poNumber, isArchived) {
    const f = $('#f-archived'); if (f) f.value = isArchived ? '' : 'TRUE';
    savePo(); // save & close → list refreshes (archived rows hidden unless "Show archived")
  }
  async function deletePo(poNumber) {
    if (!confirm('Permanently DELETE PO ' + poNumber + '?\n\nRemoves the PO and its lines (receipt history is kept). This cannot be undone. Use Archive instead if you just want to hide it.')) return;
    try {
      await AG_ADMIN.api('ui_delete_po', { po_number: poNumber });
      AG_ADMIN.toast('Deleted ' + poNumber);
      state.poDirty = false; closeDrawer();
      try { ['reorder', 'pos', 'forecast'].forEach(k => delete state.cache[k]); saveCache(); } catch (_) {}
      setView('pos');
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  /* Drop the fetched lines + docs into an already-open (optimistically rendered)
     PO drawer without disturbing header fields the operator may be editing. */
  function fillPoDetail(po) {
    const tbody = $('#po-lines');
    if (tbody) { const ls = (po.lines && po.lines.length) ? po.lines : [{}]; tbody.innerHTML = ls.map(renderLineHtml).join(''); }
    const dw = document.querySelector('#po-docs-wrap'); if (dw) dw.innerHTML = renderDocsSection(po);
    const rw = document.querySelector('#po-recv-wrap'); if (rw) rw.innerHTML = renderReceiveSection(po) + poSignoffHtml(po);
    const st = $('#f-status'); applyStageGating(st ? st.value : ((po.header || {}).status || ''));
  }
  /* Landed-cost panel for a signed-off PO: what each product was costed at and
     the working behind it, read from the stored PO_Landed_Costs audit rows. This
     is the "look back later" record. Cost_Layers keeps only the final number. */
  function poLandedCostHtml(rows) {
    const current = (rows || []).filter(r => !r.superseded);
    if (!current.length) return '';
    const m = v => '£' + (Number(v) || 0).toFixed(2);
    const first = current[0];
    const superseded = (rows || []).filter(r => r.superseded).length;
    return `<div style="margin-top:10px;font-size:12px;">
      <div style="font-weight:600;margin-bottom:4px;">Landed cost: how it was calculated</div>
      <div class="ag-muted" style="font-size:11px;margin-bottom:6px;">
        Goods ${m(first.po_goods_gbp)} + shipping ${m(first.po_freight_gbp)} + duty ${m(first.po_duty_gbp)}
        = <strong>${m(first.po_total_landed_gbp)}</strong>, allocated ${esc(String(first.allocation || '').replace(/_/g, ' '))}.
        ${first.goods_source && first.goods_source !== 'gbp_paid'
          ? `Goods converted at ${esc(String(first.fx_rate_usd_per_gbp))} USD/GBP (${esc(String(first.goods_source).replace(/_/g, ' '))}).` : ''}
        Computed ${esc(String(first.computed_at || '').slice(0, 16))}.
        ${superseded ? `<em>${superseded} earlier calculation${superseded === 1 ? '' : 's'} superseded.</em>` : ''}
      </div>
      <table class="ag-table"><thead><tr>
        <th>Product</th><th class="num">Units</th><th class="num">Goods</th>
        <th class="num">Ship</th><th class="num">Duty</th><th class="num">Per unit</th>
      </tr></thead><tbody>
        ${current.map(r => `<tr>
          <td>${esc(r.sku)}${r.components ? `<div class="ag-muted" style="font-size:10px;">${esc(r.components)}</div>` : ''}</td>
          <td class="num">${r.units}</td>
          <td class="num">${m(r.per_unit_goods_gbp)}</td>
          <td class="num">${m(r.per_unit_freight_gbp)}</td>
          <td class="num">${m(r.per_unit_duty_gbp)}</td>
          <td class="num"><strong>${m(r.per_unit_gbp)}</strong></td>
        </tr>`).join('')}
      </tbody></table>
    </div>`;
  }
  function poSignoffHtml(po) {
    const h = (po && po.header) || {};
    if (h.signed_off_at) {
      /* Signed off is NOT the end of the road: freight and duty invoices routinely
         arrive after the goods, so offer a recost. Without this the only fix for a
         batch costed before the shipping bill landed was editing Cost_Layers by
         hand, which is how a wrong £153.03 stayed live on 167 units. */
      return `<div style="margin-top:14px;padding:10px 12px;background:#eaf7ee;border:1px solid #b6e0c2;border-radius:8px;font-size:12px;">
        <div>✓ <strong>Signed off &amp; accepted</strong> ${esc(String(h.signed_off_at).slice(0, 10))}. Landed cost distributed to Shopify.</div>
        <div id="po-landed-cost">${poLandedCostHtml(po && po.landed_cost)}</div>
        <div style="margin-top:8px;">
          <button type="button" class="ag-btn secondary" onclick="AGInv.signOffPo('${esc(h.po_number)}', true)"
            title="Recalculate the landed cost and REPLACE this PO's cost layers. Use when the shipping or duty invoice arrived after sign-off">↻ Recost (shipping / duty arrived late)</button>
        </div>
      </div>`;
    }
    return `<div style="margin-top:14px;">
      <button type="button" class="ag-btn" onclick="AGInv.signOffPo('${esc(h.po_number)}')"
        title="Distribute landed cost (goods + shipping + duty) across the arrived products and write per-unit cost to Shopify">✓ Signed off &amp; accepted</button>
      <div class="ag-muted" style="font-size:11px;margin-top:4px;">Distributes goods + shipping + duty across the arrived products and writes per-unit cost to Shopify. Enter <strong>Shipping cost £</strong> and <strong>Duty £</strong> above and Save first. A blank freight or duty under-costs the batch.</div>
    </div>`;
  }
  /* Render the sign-off arithmetic as text for the confirm dialog. A bare
     "£153.03/unit" is unauditable, the operator can't tell goods-only from
     goods+freight+duty, which is precisely how an under-costed batch slipped
     through unnoticed. Show the working and call out anything missing. */
  function signoffBreakdownText(r) {
    const b = r.breakdown || {};
    const money = v => '£' + (Number(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const lines = [];
    lines.push(`Goods: ${money(b.goods_gbp)}${b.goods_source && b.goods_source !== 'gbp_paid'
      ? ` (converted from $${fmtNum(Math.round(b.goods_usd_basis || 0))} at ${b.fx_rate_usd_per_gbp}. No £ paid recorded.)` : ''}`);
    lines.push(`Shipping: ${money(b.freight_gbp)}${b.missing_freight ? '   ⚠ NOT ENTERED' : ''}`);
    lines.push(`Duty: ${money(b.duty_gbp)}${b.missing_duty ? '   ⚠ NOT ENTERED' : ''}`);
    lines.push(`Total landed: ${money(b.total_landed_gbp)}`);
    lines.push(`Over ${r.total_units} unit${r.total_units === 1 ? '' : 's'} = ${money(r.per_unit_landed_gbp)} per unit`);
    const per = (r.skus_updated || []).filter(s => s.wrote_to !== 'skipped_zero');
    if (per.length > 1) {
      lines.push('');
      lines.push('Per product:');
      per.forEach(s => {
        lines.push(`• ${s.sku}: ${money(s.per_unit_gbp)} × ${s.sets}` +
          ` (goods ${money(s.per_unit_goods_gbp)} + ship ${money(s.per_unit_freight_gbp)} + duty ${money(s.per_unit_duty_gbp)})`);
      });
    }
    return lines.join('\n');
  }
  async function signOffPo(poNumber, replace) {
    if (!await ensurePoSaved(replace ? 'Recosting' : 'Signing off')) return;
    /* Preview first (no writes) so any stock-box warning surfaces in the confirm. */
    let preview;
    try { preview = await AG_ADMIN.api('ui_signoff_po', { po_number: poNumber, dry_run: true }); }
    catch (e) { AG_ADMIN.toast(e.message || e, true); return; }
    const n = (preview.skus_updated || []).length;
    const z = (preview.boxes_zeroed || []).length;
    const warnings = preview.warnings || [];
    let msg = (replace ? `RECOST ${poNumber}?\n\nThis REPLACES the existing cost layers for this PO, including for units already sold, whose COGS is recalculated from the ledger.\n\n`
                       : `Sign off ${poNumber}?\n\n`);
    msg += signoffBreakdownText(preview);
    msg += `\n\nAcross ${n} product${n === 1 ? '' : 's'}`;
    if (z) msg += `; ${z} box SKU${z === 1 ? '' : 's'} kept at £0`;
    msg += `, written to Shopify.`;
    if (preview.breakdown && (preview.breakdown.missing_freight || preview.breakdown.missing_duty)) {
      msg += `\n\n⚠ Shipping and/or duty are blank. If those invoices have arrived, Cancel, enter them, Save, then sign off. Otherwise this batch is under-costed.`;
    }
    if (warnings.length) msg += `\n\n⚠ ${warnings.length} WARNING${warnings.length === 1 ? '' : 'S'}. Review before continuing:\n` + warnings.map(w => '• ' + w).join('\n');
    if (!confirm(msg)) return;
    try {
      const r = await AG_ADMIN.api('ui_signoff_po', Object.assign({ po_number: poNumber }, replace ? { replace: true } : {}));
      const rn = (r.skus_updated || []).length, rz = (r.boxes_zeroed || []).length;
      AG_ADMIN.toast(`${replace ? 'Recosted' : 'Signed off'} · £${(r.per_unit_landed_gbp || 0).toFixed(2)}/unit on ${rn} product${rn === 1 ? '' : 's'}${rz ? ` · ${rz} box SKU${rz === 1 ? '' : 's'} kept at £0` : ''}`);
      await editPo(poNumber);
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  function paintPoDrawer(po) {
    const h = (po && po.header) || {};
    const lines = (po && po.lines) || [];
    const issues = (po && po.validation_issues) || [];
    const docs = (po && po.documents) || [];
    const isEdit = !!po;
    const loading = !!(po && po._loading); // optimistic open, header rendered, lines/docs still fetching
    const b = state.bootstrap || {};
    /* Switch the drawer panel into wide-modal mode so the PO form has
       room for the line table + details panels without horizontal
       scrolling. closeDrawer restores the default. */
    document.querySelector('#inv-drawer .ag-drawer-panel').classList.add('wide');
    /* Capture the loaded ETA + status so savePo can detect a meaningful
       change and prompt "sync pre-order tags now?" if appropriate. */
    state.poEditOriginal = isEdit
      ? { po_number: String(h.po_number || ''), eta: (h.eta || '').slice(0, 10), status: String(h.status || '') }
      : null;
    $('#inv-drawer-title').textContent = isEdit ? ('PO ' + h.po_number) : 'New PO';
    /* Sensible defaults for new POs so the form doesn't open as a wall
       of empty fields. PO number is suggested as PO-YYYYMMDD-N when
       the operator picks a supplier. Date ordered = today. Status
       defaults to 'placed' (or 'draft' if that's all that exists). */
    const today = new Date().toISOString().slice(0, 10);
    const valStatus = h.status || 'pre_purchase';
    const valArchived = (h.archived === true || String(h.archived).toUpperCase() === 'TRUE');
    const valDateOrdered = fmtDate(h.date_ordered) || (isEdit ? '' : today);
    const sec = poSectionTitle;
    state.poPayments = isEdit ? ((po && po.payments) || []) : [];
    /* Pre-order state, hoisted for the fold's summary chip + auto-open. */
    const _pre = isEdit ? ((po && po.preorder) || null) : null;
    const _preLive = !!(_pre && _pre.live);
    const _preArmed = !_preLive && !!(_pre && _pre.bundle && _pre.armed);

    $('#inv-drawer-body').innerHTML = `
      ${issues.length ? `<div class="ag-issues"><strong>${issues.length} issue${issues.length === 1 ? '' : 's'}:</strong><ul>${issues.map(i => `<li>${esc(i)}</li>`).join('')}</ul></div>` : ''}
      <style>
        #po-lines .line-recv[readonly]{background:transparent;border-color:transparent;cursor:default;}
        .agpo-stages{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:0 0 14px;}
        .agpo-stg{border:none;font:inherit;font-size:11px;font-weight:700;padding:5px 12px;border-radius:99px;cursor:pointer;}
        .agpo-stg.done{background:#e8f5ee;color:#1f7a3d;}
        .agpo-stg.cur{background:#0f172a;color:#fff;}
        .agpo-stg.next{background:#fff;color:#9aa1ab;border:1px dashed #d6d9dd;}
        .agpo-arrow{color:#c9cdd3;font-size:11px;}
        .agpo-grid{display:grid;grid-template-columns:minmax(0,1fr) 300px;gap:16px;align-items:start;}
        @media (max-width:860px){.agpo-grid{grid-template-columns:1fr;}}
        .agpo-card{border:1px solid #e8eaed;border-radius:10px;background:#fff;margin:0 0 14px;}
        .agpo-card-h{display:flex;align-items:center;gap:8px;padding:10px 14px;border-bottom:1px solid #f0f1f3;font-size:11px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;color:#3f4650;}
        .agpo-card-h .hint{margin-left:auto;font-weight:500;letter-spacing:0;text-transform:none;color:#8a919b;font-size:11.5px;}
        .agpo-card-b{padding:12px 14px;}
        .agpo-leg{padding:4px 0 10px;}
        .agpo-leg + .agpo-leg{border-top:1px solid #f0f1f3;padding-top:12px;}
        .agpo-leg-t{font-size:12px;font-weight:700;margin:0 0 6px;color:#2b3540;}
        .agpo-rail{position:sticky;top:0;}
        .agpo-rail-h{font-size:10.5px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;color:#7c828c;margin:2px 0 8px;}
        .agpo-tl{list-style:none;margin:0 0 16px;padding:0 0 0 3px;font-size:12px;}
        .agpo-tl li{position:relative;padding:0 0 11px 20px;}
        .agpo-tl li::before{content:"";position:absolute;left:5px;top:4px;width:7px;height:7px;border-radius:99px;background:#c9cdd3;}
        .agpo-tl li.done::before{background:#1f7a3d;}
        .agpo-tl li.cur::before{background:#0f172a;box-shadow:0 0 0 3px #dbe2ea;}
        .agpo-tl li::after{content:"";position:absolute;left:8px;top:13px;bottom:-2px;width:1px;background:#e5e8eb;}
        .agpo-tl li:last-child::after{display:none;}
        .agpo-tl .when{color:#8a919b;font-variant-numeric:tabular-nums;float:right;}
        .agpo-foot{position:sticky;bottom:0;z-index:8;display:flex;align-items:center;gap:8px;padding:12px 0 2px;margin-top:4px;background:linear-gradient(to top,#fff 82%,rgba(255,255,255,0));}
        .agpo-more{position:relative;margin-left:auto;}
        .agpo-more > summary{list-style:none;cursor:pointer;border:1px solid #d9dce1;border-radius:8px;background:#fff;color:#6b7280;font-weight:700;padding:8px 14px;font-size:13px;}
        .agpo-more > summary::-webkit-details-marker{display:none;}
        .agpo-more[open] > .agpo-menu{display:flex;}
        .agpo-menu{display:none;position:absolute;right:0;bottom:calc(100% + 6px);flex-direction:column;gap:2px;background:#fff;border:1px solid #e3e6ea;border-radius:10px;box-shadow:0 10px 30px -8px rgba(15,23,42,.25);padding:6px;min-width:170px;z-index:20;}
        .agpo-menu button{text-align:left;border:none;background:#fff;font:inherit;font-size:12.5px;padding:8px 10px;border-radius:7px;cursor:pointer;color:#374151;}
        .agpo-menu button:hover{background:#f4f5f7;}
        .agpo-menu button.danger{color:#a30d26;}
        details.agpo-fold > summary{list-style:none;cursor:pointer;}
        details.agpo-fold > summary::-webkit-details-marker{display:none;}
        details.agpo-fold > summary .agpo-card-h::after{content:"▸";margin-left:auto;color:#9aa1ab;}
        details.agpo-fold[open] > summary .agpo-card-h::after{content:"▾";}
        details.agpo-fold[open] > summary .agpo-card-h .hint{display:none;}
      </style>
      ${poStepperHtml(valStatus)}
      <div class="agpo-grid">
        <form id="po-form" class="agpo-main">
          <input type="hidden" id="f-status" value="${esc(valStatus)}">
          <input type="hidden" id="f-archived" value="${valArchived ? 'TRUE' : ''}">

            <div class="agpo-card">
              <div class="agpo-card-h">Order</div>
              <div class="agpo-card-b ag-form-grid" style="grid-template-columns:1fr 1fr;">
                ${fieldRow('po_number', 'PO Number', h.po_number || '', 'text', { placeholder: 'e.g. PO-' + today.replace(/-/g, '') + '-1' })}
                <label><span class="label">Supplier</span><input class="ag-input" id="f-supplier" list="po-supplier-list" required placeholder="Type to search…" value="${esc(h.supplier || '')}"></label>
                ${fieldRow('external_ref', 'External ref', h.external_ref || '', 'text', { placeholder: 'optional' })}
                ${fieldRow('date_ordered', 'Date ordered', valDateOrdered, 'date')}
                ${fieldRow('goods_value_usd', 'Goods USD (total PO value)', h.goods_value_usd || '', 'money')}
                ${fieldRow('usd_gbp_rate', 'USD→GBP rate', h.usd_gbp_rate || '', 'number', { placeholder: 'e.g. 1.32. Used to cost stock if GBP left blank' })}
              </div>
            </div>
            <datalist id="po-supplier-list">${(b.suppliers || []).map(s => `<option value="${esc(s)}">`).join('')}</datalist>

            <div class="agpo-card">
              <div class="agpo-card-h">Payments <span class="hint">every payment in one place</span></div>
              <div class="agpo-card-b">
                <div class="agpo-leg">
                  <div class="agpo-leg-t">Deposit</div>
                  <div class="ag-form-grid" style="grid-template-columns:1fr 1fr 1fr;">
                    ${fieldRow('deposit_usd', 'Deposit USD (agreed amount)', h.deposit_usd || '', 'money')}
                    ${fieldRow('deposit_gbp_paid', 'Deposit £ paid (from bank)', h.deposit_gbp_paid || '', 'money')}
                    ${fieldRow('deposit_paid_date', 'Deposit paid on', fmtDate(h.deposit_paid_date), 'date')}
                  </div>
                </div>
                <div class="agpo-leg">
                  <div class="agpo-leg-t">Balance</div>
                  <div class="ag-form-grid" style="grid-template-columns:1fr 1fr 1fr;">
                    ${fieldRow('balance_usd', 'Balance USD (agreed amount)', h.balance_usd || '', 'money')}
                    ${fieldRow('balance_gbp_paid', 'Balance £ paid (from bank)', h.balance_gbp_paid || '', 'money')}
                    ${fieldRow('balance_paid_date', 'Balance paid on', fmtDate(h.balance_paid_date), 'date')}
                  </div>
                </div>
                <div class="agpo-leg">
                  <div class="agpo-leg-t">Import costs</div>
                  <div class="ag-form-grid" style="grid-template-columns:1fr 1fr 1fr;">
                    ${fieldRow('freight_gbp_paid', 'Shipping cost £', h.freight_gbp_paid || '', 'money')}
                    ${fieldRow('duty_gbp', 'Duty £ (on import)', h.duty_gbp || '', 'money')}
                    <div></div>
                  </div>
                </div>
                ${isEdit ? `<div class="agpo-leg" id="po-extra-wrap">
                  <div class="agpo-leg-t">Payment ledger <span class="ag-muted" style="font-weight:400;font-size:11px;">every payment on this PO; deposit and balance are 1 and 2, record any further instalment below</span></div>
                  <div id="po-extra-list"></div>
                  <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;margin-top:8px;">
                    <label style="flex:0 0 84px;"><span class="label">Currency</span><select class="ag-input" id="f-newpay-ccy" onchange="AGInv.syncNewPoPaymentFields()">${PO_PAY_CCYS.map(c => `<option value="${c}">${c}</option>`).join('')}</select></label>
                    <label style="flex:1;min-width:110px;"><span class="label" id="newpay-amount-lbl">Amount (USD)</span><input class="ag-input" id="f-newpay-amount" inputmode="decimal" placeholder="0.00"></label>
                    <label style="flex:1;min-width:110px;display:none;" id="newpay-usd-wrap"><span class="label">USD it settles</span><input class="ag-input" id="f-newpay-usd" inputmode="decimal" placeholder="or use the rate"></label>
                    <label style="flex:1;min-width:110px;" id="newpay-gbp-wrap"><span class="label">£ from bank</span><input class="ag-input" id="f-newpay-gbp" inputmode="decimal" placeholder="or use the rate"></label>
                    <label style="flex:0 0 100px;"><span class="label">Rate $ per £</span><input class="ag-input" id="f-newpay-fx" inputmode="decimal" placeholder="optional"></label>
                    <label style="flex:1;min-width:130px;"><span class="label">Paid on</span><input class="ag-input" id="f-newpay-date" type="date"></label>
                  </div>
                  <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;margin-top:8px;">
                    <label style="flex:1;min-width:120px;"><span class="label">Method</span><select class="ag-input" id="f-newpay-method"><option value="">(not set)</option>${PO_PAY_METHODS.map(m => `<option value="${m[0]}">${m[1]}</option>`).join('')}</select></label>
                    <label style="flex:1;min-width:120px;"><span class="label">Reference</span><input class="ag-input" id="f-newpay-ref" placeholder="bank or invoice ref"></label>
                    <label style="flex:2;min-width:140px;"><span class="label">Note</span><input class="ag-input" id="f-newpay-note" placeholder="optional"></label>
                    <button type="button" class="ag-btn secondary" id="po-newpay-btn" onclick="AGInv.addPoPayment('${esc(h.po_number)}')">＋ Record payment</button>
                  </div>
                </div>` : ''}
              </div>
            </div>

            <div class="agpo-card">
              <div class="agpo-card-h">Production and shipping</div>
              <div class="agpo-card-b ag-form-grid" style="grid-template-columns:1fr 1fr 1fr;">
                ${fieldRow('production_date', 'Production booked (date)', fmtDate(h.production_date), 'date')}
                ${fieldRow('est_completion_date', 'Est. completion (date)', fmtDate(h.est_completion_date), 'date')}
                ${fieldRow('date_shipped', 'Date shipped', fmtDate(h.date_shipped), 'date')}
                ${fieldRow('etd', 'ETD (departs)', fmtDate(h.etd), 'date')}
                ${fieldRow('eta_port', 'ETA at port', fmtDate(h.eta_port), 'date')}
                ${fieldRow('eta', 'ETA at warehouse', fmtDate(h.eta), 'date')}
                ${fieldRow('vessel_name', 'Vessel name', h.vessel_name || '', 'text')}
                ${fieldRow('container_number', 'Container number', h.container_number || '', 'text')}
                ${fieldRow('telex_release', 'Telex release', h.telex_release || '', 'text', { placeholder: 'ref / date when received' })}
              </div>
            </div>

            <details class="agpo-fold agpo-card" data-min-stage="2"${(_preLive || _preArmed) ? ' open' : ''}>
              <summary><div class="agpo-card-h">Pre-order
                <span id="po-preorder-chip" class="ag-pill" style="${_preLive ? 'background:#e8f5ee;color:#1f7a3d;' : _preArmed ? 'background:#fef3c7;color:#92400e;' : 'background:#f0f1f3;color:#8a919b;'}font-weight:800;letter-spacing:.04em;">${_preLive ? '● LIVE' + (_pre && _pre.linked_count ? ' · ' + _pre.live_count + '/' + _pre.linked_count : '') : _preArmed ? 'ARMED' : 'OFF'}</span>
                <span class="hint">open to arm or pause</span></div></summary>
              <div class="agpo-card-b">
                <div class="ag-form-grid" style="grid-template-columns:1fr;">
                  ${fieldRow('preorder_label', 'Pre-order label (auto from ETA)', h.preorder_label || '', 'text', { placeholder: 'leave blank to derive "June 2026" from warehouse ETA' })}
                </div>
                <div class="ag-muted" style="font-size:11px;margin:6px 0 10px;">Products set to Auto on the Pre-orders tab go on pre-order by themselves when they sell out and this PO has an ETA. Others: use the button here, or On per product.</div>
                ${isEdit ? (() => {
                  const pre = (po && po.preorder) || null;
                  const isLive = !!(pre && pre.live);
                  const hint = !pre ? ''
                    : pre.bundle ? (isLive ? 'live · bundle pre-order' : (pre.armed ? 'armed · opens automatically at stock 0' : 'not armed. Click to arm'))
                    : (pre.linked_count ? (isLive ? 'live · ' : 'not live · ') + pre.live_count + '/' + pre.linked_count + ' linked' : 'no linked SKUs');
                  return `<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
                    <button type="button" id="po-preorder-live-btn" class="ag-btn ${isLive ? '' : 'secondary'}" style="${isLive ? 'background:var(--ag-ok,#1f7a37);border-color:var(--ag-ok,#1f7a37);color:#fff;' : ''}" onclick="AGInv.syncTags('${esc(h.po_number)}')" title="Push 'pre-order - <label>' tag to every product linked to this PO (opens pre-orders)">${isLive ? '● Pre-orders LIVE' : 'Set pre-orders LIVE'}</button>
                    <button type="button" class="ag-btn secondary" onclick="AGInv.pausePreorderTags('${esc(h.po_number)}')" title="Strip the pre-order tag from linked products (pauses sales) + add a 'preorder-paused' marker. Reversible.">Pause pre-orders</button>
                    <span class="ag-muted" id="po-preorder-hint" style="font-size:11px;">${esc(hint)}</span>
                  </div>
                  <div id="po-preorder-lines" style="margin-top:12px;"></div>`;
                })() : ''}
              </div>
            </details>

</form>

          <aside class="agpo-rail">
            <div class="agpo-rail-h">Money</div>
            <div id="po-pay-summary"></div>
            <div class="agpo-rail-h">Dates</div>
            <ul class="agpo-tl" id="po-dates-tl"></ul>
            <div id="po-docs-wrap" data-min-stage="0">${isEdit ? (loading ? '<div class="ag-muted"><span class="ag-spinner"></span> Loading documents…</div>' : renderDocsSection(po)) : '<div class="ag-muted" style="font-size:12px;">Save the PO first to upload documents.</div>'}</div>
          </aside>
      </div>
      <div class="agpo-card">
              <div class="agpo-card-h">Lines <span class="hint">${(b.skus || []).length} SKUs available · click an input to search</span></div>
              <div class="agpo-card-b">
                <div style="display:flex;gap:8px;margin-bottom:8px;flex-wrap:wrap;">
                  <button type="button" class="ag-btn secondary" onclick="AGInv.addLine()">+ Add line</button>
                  <button type="button" class="ag-btn secondary" onclick="AGInv.openPoLinesPasteDialog()" title="Paste rows from your invoice spreadsheet">📋 Paste from invoice</button>
                  <button type="button" class="ag-btn secondary" onclick="AGInv.openBomEditor()" title="Define a finished product = N component SKUs (e.g. 4 boxes per dumbbell pair). Then the ↓ Split button on any line auto-expands.">⚙ Manage BOM</button>
                </div>
                <table class="ag-table"><thead><tr><th style="width:24%;">SKU</th><th>Title</th><th class="num" style="width:60px;">Qty</th><th class="num" style="width:60px;white-space:nowrap;">Recv <button type="button" id="recv-unlock" onclick="AGInv.toggleRecvLock()" style="border:none;background:none;cursor:pointer;font-size:11px;padding:0 2px;vertical-align:middle;" title="Received so far is set automatically by Stock arrived below. Click to unlock the column for a manual correction.">🔒</button></th><th class="num" style="width:80px;">USD</th><th class="num" style="width:80px;">GBP</th><th style="width:100px;">HS code</th><th style="width:32px;"></th></tr></thead><tbody id="po-lines">
                  ${loading ? '<tr><td colspan="8" style="padding:14px;color:var(--ag-muted,#999);"><span class="ag-spinner"></span> Loading lines…</td></tr>' : (lines.length ? lines : [{}]).map(renderLineHtml).join('')}
                </tbody></table>
                <div id="po-usd-recon" style="font-size:12px;margin:8px 0 2px;display:flex;gap:10px;align-items:center;flex-wrap:wrap;"></div>
              </div>
            </div>
          <div class="agpo-foot">
            <button type="submit" form="po-form" class="ag-btn">${isEdit ? 'Save' : 'Create PO'}</button>
            <button type="button" class="ag-btn secondary" onclick="AGInv.closeDrawer()">Cancel</button>
            ${isEdit ? `<button type="button" id="po-advance-btn" class="ag-btn secondary" style="background:#0f172a;border-color:#0f172a;color:#fff;"></button>` : ''}
            ${isEdit ? `<details class="agpo-more">
              <summary title="More actions">⋯</summary>
              <div class="agpo-menu">
                <button type="button" onclick="AGInv.archivePo('${esc(h.po_number)}', ${valArchived})">${valArchived ? 'Unarchive' : 'Archive'}</button>
                <button type="button" class="danger" onclick="AGInv.cancelPo('${esc(h.po_number)}')">Cancel PO</button>
                <button type="button" class="danger" onclick="AGInv.deletePo('${esc(h.po_number)}')" title="Permanently delete this PO and its lines">Delete</button>
              </div>
            </details>` : ''}
          </div>
      <div id="po-recv-wrap" data-min-stage="3">${isEdit && !loading ? (renderReceiveSection(po) + poSignoffHtml(po)) : ''}</div>
      ${!isEdit ? '<div class="ag-muted" style="margin-top:24px;">Save the PO first to enable receiving + document uploads.</div>' : ''}`;
    applyStageGating(valStatus);
    updatePoPaySummary();
    updatePoDatesTl();
    renderPoExtraList();
    wirePaymentDateDefaults();
    /* Live payment maths: retyping any money/date field recomputes the strip,
       so an overpay shows the moment it is typed, not after save. */
    const _payForm = $('#po-form');
    if (_payForm && !_payForm._paySummaryBound) {
      _payForm._paySummaryBound = true;
      _payForm.addEventListener('input', () => { updatePoPaySummary(); updatePoDatesTl(); });
      _payForm.addEventListener('change', () => { updatePoPaySummary(); updatePoDatesTl(); });
    }
    if (isEdit) hydratePoPreorderPanel(po);
    $('#po-form').addEventListener('submit', e => { e.preventDefault(); savePo(); });
    /* Money inputs: show the raw number while editing (strip commas on focus),
       reformat with commas + 2dp on blur. Commas are stripped again on save. */
    $$('#po-form .ag-money').forEach(inp => {
      inp.addEventListener('focus', () => { inp.value = inp.value.replace(/[,\s£$€]/g, ''); });
      inp.addEventListener('blur', () => { inp.value = fmtMoneyInput(inp.value); });
    });
    /* Track unsaved edits for the close-drawer guard. Fresh form = clean. */
    state.poDirty = false;
    state.poRecvUnlocked = false;
    $('#po-form').addEventListener('input', () => { state.poDirty = true; });
    /* USD reconciliation wiring. poGoodsTouched gates the auto-fill so we NEVER
       silently overwrite a header that was already set (existing PO, or one the
       operator typed). Only a blank/new header tracks the line sum automatically. */
    state.poGoodsTouched = isEdit || (parseFloat(h.goods_value_usd) || 0) > 0;
    const _poLinesTbody = $('#po-lines');
    if (_poLinesTbody) _poLinesTbody.addEventListener('input', () => {
      if (!state.poGoodsTouched) {
        const g = $('#f-goods_value_usd');
        if (g) g.value = fmtMoneyInput(String(poLinesUsdTotal()));
      }
      updatePoUsdRecon();
    });
    const _poGoodsEl = $('#f-goods_value_usd');
    if (_poGoodsEl) _poGoodsEl.addEventListener('input', () => { state.poGoodsTouched = true; updatePoUsdRecon(); });
    updatePoUsdRecon();
    /* Auto-suggest the PO number once the operator picks a supplier
       (only on a fresh New PO form: never overwrites an existing one). */
    if (!isEdit) {
      const supplierEl = $('#f-supplier');
      const poEl = $('#f-po_number');
      if (supplierEl && poEl) {
        supplierEl.addEventListener('change', () => {
          if (poEl.value) return; // operator already typed something
          const sup = supplierEl.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 6).toUpperCase();
          poEl.value = `PO-${today.replace(/-/g, '')}-${sup || 'NEW'}`;
        });
      }
    }
  }
  /* Split button HTML for a line whose SKU is a BOM parent (else ''). Shared by
     the initial line render AND the SKU-picker selection (so picking a BOM parent
     after the line is drawn still surfaces the button). */
  function splitBtnHtml(sku) {
    const p = (((state.bootstrap || {}).bom || {}).parents || {})[sku];
    if (!p) return '';
    return `<button type="button" class="ag-btn secondary line-split-btn" title="Split this line into ${p.components.length} component lines (cost auto-divided)" onclick="AGInv.splitPoLineToComponents(this)" style="padding:2px 6px;font-size:10px;margin-right:3px;">↓ Split</button>`;
  }
  /* Repaint every open PO line's Split button, used after a BOM save so the
     button appears without re-rendering the rows (which would lose entered qty/cost). */
  function refreshLineSplitButtons() {
    document.querySelectorAll('#po-lines tr').forEach(tr => {
      const skuInput = tr.querySelector('.line-sku');
      const cell = tr.querySelector('td:last-child');
      if (!skuInput || !cell) return;
      const ex = cell.querySelector('.line-split-btn'); if (ex) ex.remove();
      const html = splitBtnHtml(skuInput.value.trim());
      if (html) cell.insertAdjacentHTML('afterbegin', html);
    });
  }
  /* Row tint for PO line completion: light green = fully received, light red =
     outstanding, none = empty/no-sku. Semi-transparent so it works on both themes. */
  function poLineTint(qtyO, qtyR, hasSku) {
    if (!hasSku || qtyO <= 0) return '';
    return qtyR >= qtyO ? 'rgba(31,122,55,0.12)' : 'rgba(200,16,46,0.08)';
  }
  function retintPoLine(el) {
    const tr = el.closest('tr'); if (!tr) return;
    const skuEl = tr.querySelector('.line-sku'), qEl = tr.querySelector('.line-qty'), rEl = tr.querySelector('.line-recv');
    const qtyO = parseInt(qEl && qEl.value, 10) || 0;
    const qtyR = parseInt(rEl && rEl.value, 10) || 0;
    tr.style.background = poLineTint(qtyO, qtyR, !!(skuEl && skuEl.value.trim()));
  }
  /* ── PO USD reconciliation ────────────────────────────────────────
     The header "Goods USD (total PO value)" must equal Σ(line qty × unit USD).
     We show the two side by side under the lines table, one-click sync the header
     to the line sum, and, for a fresh PO whose header is still blank, keep the
     header tracking the lines automatically. A save-time confirm catches any
     remaining drift so a stale header never silently skews Open commitment. */
  /* The Recv column is DISPLAY by default: receiving happens in Stock arrived,
     which pushes the stock to Shopify and writes the Goods_In audit row. Typing
     into Recv + Save used to record a receipt on the sheet ONLY, so the stock
     never reached Shopify and Stock arrived then saw nothing outstanding. The
     padlock is the deliberate escape hatch for correcting the RECORD (a typo,
     a pre-system PO) without moving stock. */
  function toggleRecvLock() {
    state.poRecvUnlocked = !state.poRecvUnlocked;
    document.querySelectorAll('#po-lines .line-recv').forEach(i => {
      if (state.poRecvUnlocked) i.removeAttribute('readonly'); else i.setAttribute('readonly', '');
    });
    const b = document.getElementById('recv-unlock');
    if (b) {
      b.textContent = state.poRecvUnlocked ? '\ud83d\udd13' : '\ud83d\udd12';
      b.title = state.poRecvUnlocked
        ? 'Corrections change the record only, they do not move Shopify stock. Click to lock again.'
        : 'Received so far is set automatically by Stock arrived below. Click to unlock the column for a manual correction.';
    }
    if (state.poRecvUnlocked) AG_ADMIN.toast('Received quantities unlocked for correction. Edits change the record only, they do not move Shopify stock; to receive a delivery use Stock arrived below.');
  }
  function poLineUsdCost(input) { return parseFloat(String((input && input.value) || '').replace(/[,\s£$€]/g, '')) || 0; }
  function poLinesUsdTotal() {
    let sum = 0;
    document.querySelectorAll('#po-lines tr').forEach(tr => {
      const q = tr.querySelector('.line-qty'), c = tr.querySelector('.line-cost-usd');
      if (!q || !c) return;
      sum += (parseInt(q.value, 10) || 0) * poLineUsdCost(c);
    });
    return Math.round(sum * 100) / 100;
  }
  function poHeaderUsd() {
    const el = document.getElementById('f-goods_value_usd');
    return el ? (parseFloat(String(el.value).replace(/[,\s£$€]/g, '')) || 0) : 0;
  }
  function updatePoUsdRecon() {
    const bar = document.getElementById('po-usd-recon');
    if (!bar) return;
    const anyCosted = Array.from(document.querySelectorAll('#po-lines tr')).some(tr => {
      const q = tr.querySelector('.line-qty'), c = tr.querySelector('.line-cost-usd');
      return q && c && (parseInt(q.value, 10) || 0) > 0 && poLineUsdCost(c) > 0;
    });
    if (!anyCosted) {
      bar.innerHTML = '<span class="ag-muted">Σ lines $0. Add costed lines to reconcile against the header total.</span>';
      return;
    }
    const linesUsd = poLinesUsdTotal(), hdrUsd = poHeaderUsd();
    const delta = Math.round((hdrUsd - linesUsd) * 100) / 100;
    const money = n => fmtNum(Math.round(n));
    const nLines = Array.from(document.querySelectorAll('#po-lines tr')).filter(tr => { const q = tr.querySelector('.line-qty'); return q && (parseInt(q.value, 10) || 0) > 0; }).length;
    if (Math.abs(delta) <= usdReconTol(nLines)) {
      bar.innerHTML = '<span style="color:var(--ag-ok,#1f7a37);font-weight:600;">✓ Header matches line total</span> <span class="ag-muted">Σ lines = $' + money(linesUsd) + '</span>';
    } else {
      bar.innerHTML =
        '<span style="color:#b45309;font-weight:600;">⚠ Header ≠ line total</span>' +
        '<span class="ag-muted">header $' + money(hdrUsd) + ' · Σ lines $' + money(linesUsd) + ' · Δ $' + money(delta) + '</span>' +
        '<button type="button" class="ag-btn secondary" style="padding:2px 8px;font-size:11px;" onclick="AGInv.syncGoodsUsdToLines()">Set header = $' + money(linesUsd) + '</button>';
    }
  }
  function syncGoodsUsdToLines() {
    const el = document.getElementById('f-goods_value_usd');
    if (!el) return;
    el.value = fmtMoneyInput(String(poLinesUsdTotal()));
    state.poGoodsTouched = true;
    state.poDirty = true;
    updatePoUsdRecon();
  }
  function renderLineHtml(line) {
    const sku = String(line.sku || '');
    const skuMap = {};
    ((state.bootstrap || {}).skus || []).forEach(s => { skuMap[s.sku] = s; });
    const bomParents = ((state.bootstrap || {}).bom || {}).parents || {};
    const meta = skuMap[sku] || {};
    const display = meta.product_title || meta.title || '';
    const variantPart = meta.variant_title ? `<div class="ag-muted" style="font-size:10px;">${esc(meta.variant_title)}</div>` : '';
    const splitBtn = splitBtnHtml(sku);
    const qtyO = parseInt(line.qty_ordered, 10) || 0;
    const qtyR = parseInt(line.qty_received, 10) || 0;
    return `<tr style="background:${poLineTint(qtyO, qtyR, !!sku.trim())};">
      <td style="position:relative;">
        <div class="ag-sku-picker" style="position:relative;">
          <input class="ag-input line-sku" autocomplete="off" placeholder="Search products / variants / SKUs…" value="${esc(sku)}" oninput="AGInv.onSkuPickerInput(this)" onfocus="AGInv.onSkuPickerFocus(this)" onblur="AGInv.onSkuPickerBlur(this)">
        </div>
      </td>
      <td class="line-title" style="font-size:11px;">
        <div class="line-product">${esc(display)}</div>
        <div class="line-variant">${variantPart}</div>
      </td>
      <td><input class="ag-input line-qty" type="number" min="0" value="${esc(line.qty_ordered || '')}" style="width:60px;" onchange="AGInv.retintPoLine(this)"></td>
      <td><input class="ag-input line-recv" type="number" min="0" value="${esc(line.qty_received || '')}" style="width:60px;"${state.poRecvUnlocked ? '' : ' readonly'} title="Received so far. Set by Stock arrived below; unlock the padlock in the column header to correct by hand." onchange="AGInv.retintPoLine(this)"></td>
      <td><input class="ag-input line-cost-usd" type="number" step="0.01" value="${esc(line.unit_cost_usd || '')}" style="width:80px;"></td>
      <td><input class="ag-input line-cost-gbp" type="number" step="0.01" value="${esc(line.landed_unit_cost_gbp || '')}" style="width:80px;"></td>
      <td><input class="ag-input line-hs" type="text" placeholder="9506.91" value="${esc(line.hs_code || '')}" style="width:90px;"></td>
      <td style="white-space:nowrap;">${splitBtn}<button type="button" class="ag-btn secondary" onclick="this.closest('tr').remove();AGInv.updatePoUsdRecon();">×</button></td>
    </tr>`;
  }

  /* Split a parent-SKU PO line into one row per BOM component. Splits
     the entered qty (× qty_per_unit) and the entered USD/GBP cost
     across the components proportionally to qty_per_unit weight. HS
     code, freight, supplier are inherited unchanged. Idempotent,
     re-clicking on a component line is a no-op (component has no
     BOM). */
  function splitPoLineToComponents(btn) {
    const tr = btn.closest('tr');
    const skuInput = tr.querySelector('.line-sku');
    const sku = (skuInput.value || '').trim();
    const bomParents = ((state.bootstrap || {}).bom || {}).parents || {};
    const parent = bomParents[sku];
    if (!parent || !parent.components || !parent.components.length) {
      AG_ADMIN.toast('No BOM defined for ' + sku, true);
      return;
    }
    /* Dedupe components by SKU, a product can carry BOTH a native Shopify bundle
       row AND a manual row for the same component (they declare the same thing),
       which would otherwise split into duplicate lines + double the qty. Collapse
       to one line per SKU, keeping the larger qty_per_unit. */
    const _seen = {};
    const components = [];
    parent.components.forEach(c => {
      const k = c.component_sku;
      if (_seen[k]) { _seen[k].qty_per_unit = Math.max(_seen[k].qty_per_unit || 1, c.qty_per_unit || 1); return; }
      _seen[k] = { component_sku: c.component_sku, qty_per_unit: c.qty_per_unit || 1 };
      components.push(_seen[k]);
    });
    const parentQty = parseFloat(tr.querySelector('.line-qty').value) || 0;
    const parentRecv = parseFloat(tr.querySelector('.line-recv').value) || 0;
    const parentCostUsd = parseFloat(tr.querySelector('.line-cost-usd').value) || 0;
    const parentCostGbp = parseFloat(tr.querySelector('.line-cost-gbp').value) || 0;
    const hs = tr.querySelector('.line-hs').value || '';
    /* Cost split: the parent's per-unit cost is spread evenly across every
       component UNIT, not per distinct SKU. One parent unit = totalWeight
       component units (Σ qty_per_unit), so each component unit costs
       parentCost / totalWeight. This conserves the total:
       Σ (qty_ordered × unit_cost) = parent qty × parent cost.
       e.g. A (£100) = 2×B + 2×C → 4 units → £25 each (not £50). */
    const totalWeight = components.reduce((a, c) => a + (c.qty_per_unit || 1), 0) || components.length;
    const perUnitUsd = parentCostUsd ? +(parentCostUsd / totalWeight).toFixed(2) : '';
    const perUnitGbp = parentCostGbp ? +(parentCostGbp / totalWeight).toFixed(2) : '';
    const newRows = components.map(c => {
      const wq = c.qty_per_unit || 1;
      return {
        sku: c.component_sku,
        qty_ordered: Math.round(parentQty * wq),
        qty_received: Math.round(parentRecv * wq),
        unit_cost_usd: perUnitUsd,
        landed_unit_cost_gbp: perUnitGbp,
        hs_code: hs
      };
    });
    /* Replace the parent row with the N component rows. */
    const wrap = document.createElement('tbody');
    wrap.innerHTML = newRows.map(renderLineHtml).join('');
    const parentRow = tr;
    const tbody = parentRow.parentNode;
    Array.from(wrap.children).forEach(child => tbody.insertBefore(child, parentRow));
    parentRow.remove();
    updatePoUsdRecon();
    AG_ADMIN.toast(`Split into ${newRows.length} component lines`);
  }
  function addLine() {
    const wrap = document.createElement('tbody');
    wrap.innerHTML = renderLineHtml({});
    $('#po-lines').appendChild(wrap.firstElementChild);
    updatePoUsdRecon();
  }
  /* Bulk paste from an invoice/spreadsheet. Operator pastes
     tab-separated rows (the natural form when copying from Excel /
     Sheets / Numbers). Each row is parsed as:
       SKU  Qty  USD  GBP  HS_code
     (last two are optional). Empty lines + headers are ignored. */
  function openPoLinesPasteDialog() {
    const text = prompt(
      'Paste rows from your invoice spreadsheet.\n\n' +
      'Tab-separated, one row per line:\n' +
      '  SKU<tab>Qty<tab>USD<tab>GBP<tab>HS_code\n\n' +
      'The last two columns are optional. Header rows are skipped.',
      ''
    );
    if (!text) return;
    const parsed = [];
    text.split(/\r?\n/).forEach(raw => {
      const line = raw.trim();
      if (!line) return;
      const cols = line.split('\t');
      const sku = String(cols[0] || '').trim();
      const qty = parseInt(String(cols[1] || '').replace(/[^0-9]/g, ''), 10) || 0;
      if (!sku || !qty) return; // skip header rows + invalid
      /* SKU validation: warn if not in catalogue, but accept it
         anyway, the operator might be adding a new SKU here. */
      parsed.push({
        sku,
        qty_ordered: qty,
        unit_cost_usd: parseFloat(String(cols[2] || '').replace(/[^0-9.]/g, '')) || 0,
        landed_unit_cost_gbp: parseFloat(String(cols[3] || '').replace(/[^0-9.]/g, '')) || 0,
        hs_code: String(cols[4] || '').trim()
      });
    });
    if (!parsed.length) { AG_ADMIN.toast('No valid rows found', true); return; }
    const tbody = $('#po-lines');
    /* Clear empty starter row if there's exactly one and it's blank. */
    const existing = tbody.querySelectorAll('tr');
    if (existing.length === 1 && !existing[0].querySelector('.line-sku').value.trim()) {
      tbody.innerHTML = '';
    }
    const wrap = document.createElement('tbody');
    wrap.innerHTML = parsed.map(renderLineHtml).join('');
    while (wrap.firstElementChild) tbody.appendChild(wrap.firstElementChild);
    if (!state.poGoodsTouched) { const g = $('#f-goods_value_usd'); if (g) g.value = fmtMoneyInput(String(poLinesUsdTotal())); }
    updatePoUsdRecon();
    AG_ADMIN.toast(`Added ${parsed.length} line${parsed.length === 1 ? '' : 's'}`);
  }
  /* ─── SKU picker (replaces native <datalist>) ────────────────────
     Datalist couldn't surface 522 variants well, filter behaviour
     was inconsistent and the operator never saw all variants of a
     product at a glance. Custom dropdown solves all three:
     scrolls properly, shows product + variant title separately,
     filters by SKU OR product OR variant title, hides ugly auto-
     SKU strings ([no-sku]<id> / BUNDLE-<id>) behind a "no SKU"
     badge while keeping the underlying value intact. */
  let _skuPickerEl = null;
  let _skuPickerBoundInput = null;
  /* Live-Shopify fallback state for the SKU picker (see renderSkuPickerList).
     _skuLiveDone: queries we've completed a live search for (so we don't
     re-hit Shopify and can show a settled "no matches" line).
     _skuLivePending: the query currently awaiting a network response. */
  let _skuLiveTimer = null;
  let _skuLiveDone = {};
  let _skuLivePending = null;
  function ensureSkuPickerEl() {
    if (_skuPickerEl) return _skuPickerEl;
    const el = document.createElement('div');
    el.id = 'ag-sku-picker-pop';
    el.style.cssText = 'position:fixed;z-index:10000;background:#fff;border:1px solid var(--c-border,#d0d0d0);border-radius:6px;box-shadow:0 8px 24px rgba(0,0,0,0.18);max-width:520px;width:520px;max-height:360px;overflow:auto;display:none;font-size:12px;';
    document.body.appendChild(el);
    /* mousedown (not click) so it fires before the input's blur */
    el.addEventListener('mousedown', e => {
      const row = e.target.closest('[data-sku]');
      if (!row || !_skuPickerBoundInput) return;
      e.preventDefault();
      const sku = row.dataset.sku;
      _skuPickerBoundInput.value = sku;
      paintSkuPickerRowMeta(_skuPickerBoundInput, sku);
      hideSkuPicker();
    });
    return _skuPickerEl = el;
  }
  function hideSkuPicker() {
    if (_skuPickerEl) _skuPickerEl.style.display = 'none';
    _skuPickerBoundInput = null;
  }
  function paintSkuPickerRowMeta(input, sku) {
    const tr = input.closest('tr');
    if (!tr) return;
    const skus = ((state.bootstrap || {}).skus || []);
    const meta = skus.find(s => s.sku === sku) || {};
    const productEl = tr.querySelector('.line-product');
    const variantEl = tr.querySelector('.line-variant');
    if (productEl) productEl.textContent = meta.product_title || meta.title || '';
    if (variantEl) variantEl.innerHTML = meta.variant_title ? `<div class="ag-muted" style="font-size:10px;">${esc(meta.variant_title)}</div>` : '';
    /* PO-line rows only: the row isn't re-rendered on pick, so surface/hide the
       Split button directly now the SKU (hence its BOM status) is known. */
    if (input.classList && input.classList.contains('line-sku')) {
      const cell = tr.querySelector('td:last-child');
      if (cell) {
        const existing = cell.querySelector('.line-split-btn');
        if (existing) existing.remove();
        const html = splitBtnHtml(sku);
        if (html) cell.insertAdjacentHTML('afterbegin', html);
      }
    }
  }
  function renderSkuPickerList(query) {
    const el = ensureSkuPickerEl();
    const skus = ((state.bootstrap || {}).skus || []);
    const tokens = String(query || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
    function matches(s) {
      if (!tokens.length) return true;
      const hay = ((s.sku || '') + ' ' + (s.product_title || '') + ' ' + (s.variant_title || '') + ' ' + (s.title || '')).toLowerCase();
      return tokens.every(t => hay.includes(t));
    }
    /* Group by product_title so variants of the same product cluster
       together. Empty/placeholder product_title falls back to the
       legacy combined title. Sorted alphabetically; matching rows
       only. */
    const filtered = skus.filter(matches);
    filtered.sort((a, b) => (a.product_title || a.title || '').localeCompare(b.product_title || b.title || ''));
    const SHOW_LIMIT = 200;
    const shown = filtered.slice(0, SHOW_LIMIT);
    let lastProduct = null;
    const rowsHtml = shown.map(s => {
      const product = s.product_title || s.title || '(no title)';
      const variant = s.variant_title || '';
      const skuDisplay = s.sku_is_placeholder
        ? `<span class="ag-muted" style="font-size:10px;">(no SKU set)</span>`
        : `<code style="font-size:11px;background:#f5f5f5;padding:1px 5px;border-radius:3px;">${esc(s.sku)}</code>`;
      const showProductHead = product !== lastProduct;
      lastProduct = product;
      const header = showProductHead
        ? `<div style="padding:6px 10px 2px;background:#fafafa;font-weight:600;font-size:11px;border-top:1px solid #eee;">${esc(product)}</div>`
        : '';
      const variantLabel = variant
        ? `<span style="color:#444;">${esc(variant)}</span>`
        : `<span class="ag-muted">·</span>`;
      const onHand = s.on_hand != null ? `<span class="ag-muted" style="margin-left:8px;font-size:10px;">${s.on_hand} on hand</span>` : '';
      const supplier = s.supplier ? `<span class="ag-muted" style="margin-left:8px;font-size:10px;">${esc(s.supplier)}</span>` : '';
      return `${header}<div data-sku="${esc(s.sku)}" style="padding:5px 10px;cursor:pointer;display:flex;align-items:center;gap:10px;border-top:1px solid #f3f3f3;">
        <div style="flex:1;min-width:0;">
          <div>${variantLabel}${onHand}${supplier}</div>
        </div>
        <div style="flex:0 0 auto;">${skuDisplay}</div>
      </div>`;
    }).join('');
    const moreLine = filtered.length > SHOW_LIMIT
      ? `<div class="ag-muted" style="padding:6px 10px;font-size:11px;text-align:center;border-top:1px solid #eee;">Showing ${SHOW_LIMIT} of ${filtered.length}. Refine the search to see more</div>`
      : '';
    /* No cached match, a product just added in Shopify may not be in the
       daily-synced skus list yet. Fall back to a LIVE Shopify search
       (debounced) so new products are orderable immediately, mirroring the
       Bundle Builder's picker. */
    let emptyLine = '';
    if (!filtered.length) {
      const raw = String(query || '').trim();
      if (raw.length < 2) {
        emptyLine = `<div class="ag-muted" style="padding:14px;text-align:center;">No matches.</div>`;
      } else if (_skuLiveDone[raw.toLowerCase()]) {
        emptyLine = `<div class="ag-muted" style="padding:14px;text-align:center;">No matches (checked Shopify live too).</div>`;
      } else {
        emptyLine = `<div class="ag-muted" style="padding:14px;text-align:center;">Searching Shopify…</div>`;
        scheduleSkuLiveSearch(raw);
      }
    }
    el.innerHTML = rowsHtml + moreLine + emptyLine;
  }
  /* Merge live Shopify results into state.bootstrap.skus so the picker,
     the picked-row meta (paintSkuPickerRowMeta) and the PO line renderer
     all resolve them exactly like synced rows. Skips variants with no SKU
     (can't be put on a PO) and dedupes by SKU. */
  function mergeLiveSkus(results) {
    if (!results || !results.length) return;
    state.bootstrap = state.bootstrap || {};
    const skus = state.bootstrap.skus = state.bootstrap.skus || [];
    const have = {};
    skus.forEach(s => { have[s.sku] = true; });
    results.forEach(r => {
      if (!r || !r.sku || have[r.sku]) return;
      skus.push(r);
      have[r.sku] = true;
    });
  }
  function scheduleSkuLiveSearch(raw) {
    const key = raw.toLowerCase();
    if (_skuLiveDone[key] || _skuLivePending === key) return;
    clearTimeout(_skuLiveTimer);
    _skuLiveTimer = setTimeout(() => runSkuLiveSearch(raw), 350);
  }
  function runSkuLiveSearch(raw) {
    const key = raw.toLowerCase();
    const input = _skuPickerBoundInput;
    /* Bail if the user has moved on or the search is already covered. */
    if (!input || input.value.trim().toLowerCase() !== key) return;
    if (_skuLiveDone[key] || _skuLivePending === key) return;
    _skuLivePending = key;
    AG_ADMIN.api('ui_search_products', { term: raw }).then(r => {
      _skuLivePending = null;
      _skuLiveDone[key] = true;
      mergeLiveSkus((r && r.results) || []);
      /* Re-render only if this query is still the one on screen. */
      if (_skuPickerBoundInput && _skuPickerBoundInput.value.trim().toLowerCase() === key) {
        renderSkuPickerList(_skuPickerBoundInput.value);
      }
    }).catch(() => {
      _skuLivePending = null; // leave undone so the user can retry
      if (_skuPickerBoundInput && _skuPickerBoundInput.value.trim().toLowerCase() === key) {
        ensureSkuPickerEl().innerHTML = `<div class="ag-muted" style="padding:14px;text-align:center;">Live search failed. Try again.</div>`;
      }
    });
  }
  function positionSkuPicker(input) {
    const el = ensureSkuPickerEl();
    const r = input.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const MARGIN = 8;
    const width = Math.min(Math.max(r.width, 360), 520);
    /* Viewport-aware placement. The panel is position:fixed, so any part that
       spills past the screen edge is unreachable (a low PO line used to open a
       360px list off the bottom with no way to scroll to it). Cap the height to
       the room on whichever side we open, and flip above the input when there is
       more room up than down. */
    const roomBelow = vh - r.bottom - MARGIN;
    const roomAbove = r.top - MARGIN;
    const openUp = roomBelow < 200 && roomAbove > roomBelow;
    const maxH = Math.max(140, Math.min(360, openUp ? roomAbove : roomBelow));
    el.style.maxHeight = maxH + 'px';
    el.style.width = width + 'px';
    /* Keep it fully on-screen horizontally too. */
    let left = r.left;
    if (left + width > vw - MARGIN) left = Math.max(MARGIN, vw - MARGIN - width);
    el.style.left = left + 'px';
    if (openUp) {
      el.style.top = 'auto';
      el.style.bottom = (vh - r.top + 4) + 'px';
    } else {
      el.style.bottom = 'auto';
      el.style.top = (r.bottom + 4) + 'px';
    }
    el.style.display = 'block';
  }
  function onSkuPickerFocus(input) {
    _skuPickerBoundInput = input;
    renderSkuPickerList(input.value);
    positionSkuPicker(input);
  }
  function onSkuPickerInput(input) {
    _skuPickerBoundInput = input;
    renderSkuPickerList(input.value);
    positionSkuPicker(input);
    paintSkuPickerRowMeta(input, input.value.trim());
  }
  function onSkuPickerBlur() {
    /* defer so the mousedown handler on the popup can fire first */
    setTimeout(hideSkuPicker, 120);
  }
  /* Legacy alias, kept so old inline onchange wiring still works if
     any leftover usages reference it. */
  function onSkuChange(input) {
    paintSkuPickerRowMeta(input, input.value.trim());
  }

  /* ─── BOM editor (Bill of Materials for multi-box products) ──────
     Lets the operator declare that one "finished product" SKU is
     actually shipped as N component SKUs. Native-bundle and
     auto-inferred rows are read-only (managed by the cutover script);
     manual rows are editable here. */
  function openBomEditor(seedSku) {
    const bom = (state.bootstrap && state.bootstrap.bom) || { parents: {} };
    const skus = ((state.bootstrap || {}).skus || []);
    const parentSku = seedSku || '';
    const existing = bom.parents[parentSku] || null;
    function rowHtml(c) {
      return `<tr>
        <td><input class="ag-input bom-c-sku" autocomplete="off" placeholder="Search SKU…" value="${esc(c.component_sku || '')}" oninput="AGInv.onSkuPickerInput(this)" onfocus="AGInv.onSkuPickerFocus(this)" onblur="AGInv.onSkuPickerBlur(this)" style="font-size:11px;"></td>
        <td><input class="ag-input bom-c-qty" type="number" min="0.001" step="0.001" value="${esc(c.qty_per_unit != null ? c.qty_per_unit : 1)}" style="width:80px;font-size:11px;"></td>
        <td><span class="ag-pill ${c.source && c.source !== 'manual' ? 'mute' : ''}" style="font-size:10px;">${esc(c.source || 'manual')}</span></td>
        <td><button type="button" class="ag-btn secondary" onclick="this.closest('tr').remove()" style="padding:2px 6px;font-size:10px;">×</button></td>
      </tr>`;
    }
    const seedRows = existing
      ? existing.components.map(rowHtml).join('')
      : rowHtml({ component_sku: '', qty_per_unit: 1, source: 'manual' });
    const html = `
      <div id="bom-editor-overlay" style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:48px 16px;overflow:auto;">
        <div style="background:#fff;max-width:680px;width:100%;border-radius:8px;box-shadow:0 24px 60px rgba(0,0,0,0.25);padding:20px;">
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;">
            <h2 style="margin:0;font-size:17px;">Bill of Materials</h2>
            <span class="spacer" style="flex:1;"></span>
            <button class="ag-btn secondary" onclick="document.getElementById('bom-editor-overlay').remove()" style="padding:6px 10px;">Cancel</button>
          </div>
          <p class="ag-muted" style="font-size:11px;margin:0 0 12px;line-height:1.5;">
            Declares that one "finished product" SKU ships as N component SKUs. Used by the PO drafter's <b>Split</b> button + the Reorder engine + stock value.
            Native-bundle rows (auto-managed by Shopify) and inferred rows are read-only here; manual entries below are editable.
          </p>
          <label class="ag-fld">
            <span>Finished product SKU</span>
            <input class="ag-input" id="bom-parent-sku" autocomplete="off" placeholder="Search SKU…" value="${esc(parentSku)}" oninput="AGInv.onSkuPickerInput(this)" onfocus="AGInv.onSkuPickerFocus(this)" onblur="AGInv.onSkuPickerBlur(this)">
          </label>
          <h3 style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin:14px 0 6px;">Components</h3>
          <table class="ag-table" style="font-size:11px;">
            <thead><tr><th>Component SKU</th><th class="num">Qty/unit</th><th>Source</th><th></th></tr></thead>
            <tbody id="bom-components-body">${seedRows}</tbody>
          </table>
          <button type="button" class="ag-btn secondary" id="bom-add-component" style="margin-top:8px;font-size:11px;">+ Add component</button>
          <div style="display:flex;justify-content:space-between;margin-top:16px;gap:8px;">
            ${existing ? '<button class="ag-btn secondary" id="bom-delete" style="color:#c00;">Delete BOM</button>' : '<span></span>'}
            <button class="ag-btn ag-btn-primary" id="bom-save">Save</button>
          </div>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', html);
    const overlay = document.getElementById('bom-editor-overlay');
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

    document.getElementById('bom-add-component').addEventListener('click', () => {
      const wrap = document.createElement('tbody');
      wrap.innerHTML = rowHtml({ component_sku: '', qty_per_unit: 1, source: 'manual' });
      document.getElementById('bom-components-body').appendChild(wrap.firstElementChild);
    });
    document.getElementById('bom-save').addEventListener('click', async () => {
      const finished = document.getElementById('bom-parent-sku').value.trim();
      if (!finished) { AG_ADMIN.toast('Finished product SKU required', true); return; }
      const rows = Array.from(document.querySelectorAll('#bom-components-body tr'));
      const components = rows.map(tr => ({
        component_sku: tr.querySelector('.bom-c-sku').value.trim(),
        qty_per_unit: parseFloat(tr.querySelector('.bom-c-qty').value) || 0
      })).filter(c => c.component_sku && c.qty_per_unit > 0);
      if (!components.length) { AG_ADMIN.toast('Add at least one component', true); return; }
      try {
        const r = await AG_ADMIN.api('ui_save_bom', { finished_product: finished, components });
        if (!r.ok) { AG_ADMIN.toast(r.error || 'Save failed', true); return; }
        /* Refresh bootstrap so Split button picks up the new BOM. */
        const bootstrap = await AG_ADMIN.api('ui_bootstrap', {});
        state.bootstrap = bootstrap;
        /* Drop the cached bootstrap so a later reload is also fresh, then repaint
           the open PO lines' Split buttons now the new BOM is known. */
        try { ['reorder', 'pos', 'forecast'].forEach(k => delete state.cache[k]); saveCache(); } catch (_) {}
        refreshLineSplitButtons();
        AG_ADMIN.toast(`BOM saved · ${r.saved} components`);
        overlay.remove();
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    });
    const delBtn = document.getElementById('bom-delete');
    if (delBtn) delBtn.addEventListener('click', async () => {
      if (!confirm('Delete this BOM? (Manual components only, native/inferred rows stay)')) return;
      try {
        const r = await AG_ADMIN.api('ui_delete_bom', { finished_product: parentSku });
        if (!r.ok) { AG_ADMIN.toast(r.error || 'Delete failed', true); return; }
        const bootstrap = await AG_ADMIN.api('ui_bootstrap', {});
        state.bootstrap = bootstrap;
        AG_ADMIN.toast(`BOM deleted · ${r.removed || 0} rows`);
        overlay.remove();
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    });
  }
  /* Read the PO form into the { header, lines } payload ui_save_po expects.
     Extracted from savePo so the save-before-receive guard below sends exactly
     the same shape, if these two ever diverge, a field silently stops saving on
     one path and not the other. */
  function collectPoPayload() {
    const header = {};
    /* Only the lifecycle form fields, the backend preserves any column not
       listed here (date_received, signed_off_at, invoice_url, …). */
    ['po_number', 'supplier', 'status', 'external_ref', 'date_ordered',
      'pi_url', 'goods_value_usd',
      'deposit_usd', 'deposit_gbp_paid', 'deposit_paid_date', 'production_date', 'est_completion_date',
      'balance_usd', 'balance_gbp_paid', 'balance_paid_date', 'date_shipped', 'etd', 'eta_port', 'eta',
      'vessel_name', 'container_number', 'telex_release', 'freight_gbp_paid', 'duty_gbp',
      'preorder_label', 'archived', 'usd_gbp_rate']
      .forEach(k => { const el = $('#f-' + k) || $('#po-form [name="' + k + '"]'); if (el) header[k] = el.value; });
    /* Strip display commas from money fields so the backend parses clean numbers
       ("33,734.00" → "33734.00"; parseFloat would otherwise stop at the comma). */
    ['goods_value_usd', 'deposit_usd', 'deposit_gbp_paid', 'balance_usd', 'balance_gbp_paid', 'freight_gbp_paid', 'duty_gbp']
      .forEach(k => { if (header[k] != null) header[k] = String(header[k]).replace(/[,\s£$€]/g, ''); });
    /* Send the original PO number so the backend renames (not duplicates) when
       the operator changes the PO number. */
    if (state.poEditOriginal && state.poEditOriginal.po_number) header.original_po_number = state.poEditOriginal.po_number;
    const lines = [];
    $$('#po-lines tr').forEach(tr => {
      const sku = tr.querySelector('.line-sku').value.trim();
      const qty = tr.querySelector('.line-qty').value;
      if (!sku || !qty) return;
      lines.push({ sku, qty_ordered: parseInt(qty, 10) || 0,
        qty_received: parseInt(tr.querySelector('.line-recv').value, 10) || 0,
        unit_cost_usd: parseFloat(tr.querySelector('.line-cost-usd').value) || 0,
        landed_unit_cost_gbp: parseFloat(tr.querySelector('.line-cost-gbp').value) || 0,
        hs_code: tr.querySelector('.line-hs').value.trim() });
    });
    return { header, lines };
  }
  /* Persist the PO form WITHOUT closing the drawer, used before receiving or
     signing off so the operator stays where they are. */
  async function flushPoForm() {
    const { header, lines } = collectPoPayload();
    await savePoWithBusyRetry({ header, lines });
    state.poDirty = false;
  }
  /* Receiving and signing off both read the SAVED PO from the sheet, never the
     form. So unsaved header edits are ignored by the action AND then wiped by the
     re-render that follows it. That is exactly how KY007ST-2026 got costed at
     £153.03 instead of £181.66: shipping and duty were typed, "Stock all arrived"
     was clicked, and both figures went in the bin. Save first, always.
     Returns false if the operator backs out or the save fails. */
  async function ensurePoSaved(actionLabel) {
    if (!state.poDirty) return true;
    const ok = confirm(
      'This PO has unsaved changes.\n\n' + actionLabel + ' reads the saved PO, ' +
      'so anything you have just typed (shipping £, duty £, …) would be ignored and then lost.\n\n' +
      'OK = save the changes first, then continue.\nCancel = stop. Nothing is received or costed.'
    );
    if (!ok) return false;
    try {
      await flushPoForm();
      return true;
    } catch (e) {
      AG_ADMIN.toast('Save failed. Nothing was received or costed: ' + (e.message || e), true);
      return false;
    }
  }
  async function savePo() {
    /* Loading state, the round trip is 5–10s (Apps Script cold start
       + sheet write + post-save refetch). Without feedback the button
       feels unresponsive. Flip the label to a spinner and freeze the whole
       footer: Cancel would close the drawer mid-save, and the stage button
       would race the write.
       ☠ The Save button lives in the sticky footer OUTSIDE #po-form and only
       binds via form="po-form", a form-scoped selector finds nothing, which
       is why this feedback silently never fired. Select by the form
       attribute, never by ancestry. */
    const submitBtn = document.querySelector('button[type="submit"][form="po-form"]');
    const submitOrig = submitBtn ? submitBtn.textContent : '';
    const footBtns = Array.from(document.querySelectorAll('.agpo-foot button'));
    const restoreFoot = () => {
      footBtns.forEach(b => { b.disabled = false; });
      if (submitBtn) { submitBtn.textContent = submitOrig; }
    };
    if (submitBtn) {
      submitBtn.innerHTML = '<span class="ag-spinner" style="margin-right:6px;"></span>Saving…';
    }
    footBtns.forEach(b => { b.disabled = true; });
    const { header, lines } = collectPoPayload();
    /* USD integrity: the header total must equal Σ(line qty × unit USD). If costed
       lines disagree, confirm rather than silently saving a stale header (which
       would skew Open commitment). Default OK = adopt the line total. */
    const _linesUsd = Math.round(lines.reduce((s, l) => s + (l.qty_ordered || 0) * (l.unit_cost_usd || 0), 0) * 100) / 100;
    const _hdrUsd = parseFloat(header.goods_value_usd) || 0;
    const _costed = lines.some(l => (l.unit_cost_usd || 0) > 0);
    if (_costed && Math.abs(_hdrUsd - _linesUsd) > usdReconTol(lines.length)) {
      const useLines = confirm(
        'Goods USD (header) is $' + fmtNum(Math.round(_hdrUsd)) + ' but the lines add up to $' + fmtNum(Math.round(_linesUsd)) +
        ' (Δ $' + fmtNum(Math.round(_hdrUsd - _linesUsd)) + ').\n\n' +
        'OK = set the header to the line total $' + fmtNum(Math.round(_linesUsd)) + '.\nCancel = keep the header as entered.'
      );
      if (useLines) header.goods_value_usd = String(_linesUsd);
    }
    /* A recorded £ payment must carry a date. Default a blank one to today so
       no payment is ever saved dateless (mirrors the live input default). */
    [['deposit_gbp_paid', 'deposit_paid_date'], ['balance_gbp_paid', 'balance_paid_date']].forEach(([amtK, dateK]) => {
      if ((parseFloat(header[amtK]) || 0) > 0 && !String(header[dateK] || '').trim()) {
        header[dateK] = localTodayStr();
        const el = document.querySelector('#f-' + dateK);
        if (el) el.value = header[dateK];
      }
    });
    /* No half-recorded payments. Once a leg is marked paid (a date or a £
       amount exists), BOTH the £ bank amount and the agreed USD amount must be
       filled before it can save: a dateless or amountless payment is exactly
       the shape that made paid deposits read DUE and set up the double pay.
       An agreed USD amount ALONE is still fine; that is planning, not payment. */
    for (const [leg, legLabel] of [['deposit', 'Deposit'], ['balance', 'Balance']]) {
      const usd = parseFloat(header[leg + '_usd']) || 0;
      const gbp = parseFloat(header[leg + '_gbp_paid']) || 0;
      const date = String(header[leg + '_paid_date'] || '').trim();
      const hasPayment = gbp > 0 || !!date;
      if (!hasPayment) continue;
      let missingId = null, msg = null;
      if (gbp <= 0) {
        missingId = leg + '_gbp_paid';
        msg = legLabel + ' is marked paid' + (date ? ' on ' + date : '') + ' but the £ amount from the bank is missing. Enter it, then save.';
      } else if (usd <= 0) {
        missingId = leg + '_usd';
        msg = legLabel + ' has a £ payment recorded but no agreed USD amount, so Outstanding cannot be calculated. Enter the USD amount, then save.';
      }
      if (missingId) {
        AG_ADMIN.toast(msg, true);
        const el = document.querySelector('#f-' + missingId);
        if (el) { el.focus(); el.style.outline = '2px solid #c8102e'; setTimeout(() => { el.style.outline = ''; }, 4000); }
        restoreFoot();
        return;
      }
    }
    /* Overpay guard: deposit + balance exceeding the PO total is exactly how
       an invoice gets paid twice. Loud confirm, never silent. */
    const _gDep = parseFloat(header.deposit_usd) || 0, _gBal = parseFloat(header.balance_usd) || 0;
    const _gExtra = (state.poPayments || []).reduce((t, pm) => t + (parseFloat(pm.amount_usd) || 0), 0);
    const _gGoods = parseFloat(header.goods_value_usd) || 0;
    if (_gGoods > 0 && _gDep + _gBal + _gExtra > _gGoods + 0.005) {
      const _overBy = Math.round((_gDep + _gBal + _gExtra - _gGoods) * 100) / 100;
      const okOver = confirm(
        'STOP: deposit + balance add up to MORE than this PO\'s total.\n\n' +
        'Deposit $' + fmtNum(_gDep) + ' + balance $' + fmtNum(_gBal) + ' = $' + fmtNum(_gDep + _gBal) +
        ', but the PO total is $' + fmtNum(_gGoods) + '. That is $' + fmtNum(_overBy) + ' over.\n\n' +
        'This is how an invoice gets paid twice.\n\nOK = I have checked, save anyway.\nCancel = go back and fix the amounts.'
      );
      if (!okOver) {
        restoreFoot();
        return;
      }
    }
    try {
      const res = await savePoWithBusyRetry({ header, lines });
      const mergedN = res.lines_consolidated || 0;
      AG_ADMIN.toast('Saved ' + res.po_number + (mergedN ? ` · merged ${mergedN} duplicate line${mergedN === 1 ? '' : 's'}` : ''));
      state.poDirty = false; // saved → no longer dirty
      /* ETA-change auto-prompt: if the ETA moved AND the PO is in flight
         (status implies it's still customer-visible as pre-order), ask
         whether to push the new label to linked products immediately. */
      const orig = state.poEditOriginal;
      const newEta = (header.eta || '').slice(0, 10);
      const inFlight = poStageKey(String(header.status || '')) === 'completed'; // pre-orders live in Completed
      if (orig && orig.eta && newEta && newEta !== orig.eta && inFlight) {
        if (confirm(`ETA changed: ${orig.eta} → ${newEta}\n\nSync pre-order tags on linked products now so the storefront label updates?`)) {
          try {
            const r = await AG_ADMIN.api('ui_sync_preorder_tags', { po_number: res.po_number });
            AG_ADMIN.toast(r.products_updated ? `Synced "${r.label}" to ${r.products_updated} product(s)` : 'Nothing to sync');
          } catch (e) { AG_ADMIN.toast('Tag sync failed: ' + (e.message || e), true); }
        }
      }
      delete state.cache.today; delete state.cache.reorder; delete state.cache.pos; saveCache();
      /* Save & close, drop straight back to the refreshed list. */
      state.poDirty = false;
      closeDrawer();
      setView('pos');
    } catch (e) {
      AG_ADMIN.toast(e.message || e, true);
      /* Restore the footer on error so the operator can retry. The
         success path closes the drawer, so there's no need
         to restore in that case. */
      restoreFoot();
    }
  }
  function renderReceiveSection(po) {
    const lines = (po.lines || []).filter(l => (parseInt(l.qty_ordered, 10) || 0) > (parseInt(l.qty_received, 10) || 0));
    if (!lines.length) return poSectionTitle('📦 Stock arrived') + '<div class="ag-muted">All lines fully received.</div>';
    return `${poSectionTitle('📦 Stock arrived')}
      <p class="ag-muted" style="font-size:12px;margin:0 0 8px 0;">Quantities are pre-filled with everything outstanding; adjust a line for a partial delivery, then press Receive stock. Pushes the stock to Shopify so pre-orders convert to in-stock immediately. Pre-order tags are stripped automatically when a line is fully received.</p>
      <table class="ag-table"><thead><tr><th>SKU</th><th>Outstanding</th><th>Recv now</th></tr></thead><tbody>
        ${lines.map(l => {
          const left = (parseInt(l.qty_ordered, 10) || 0) - (parseInt(l.qty_received, 10) || 0);
          return `<tr><td>${esc(l.sku)}</td><td class="num">${left}</td><td><input class="ag-input recv-qty" type="number" min="0" max="${left}" data-sku="${esc(l.sku)}" value="${left}" style="width:80px;"></td></tr>`;
        }).join('')}
      </tbody></table>
      <div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;">
        <button type="button" class="ag-btn" onclick="AGInv.receive('${esc(po.header.po_number)}')" title="Receive the quantities entered above and push the stock to Shopify">Receive stock</button>
      </div>`;
  }
  function renderDocsSection(po) {
    const docs = po.documents || [];
    const types = po.doc_types || ['proforma_invoice', 'invoice', 'packing_list', 'commercial_invoice', 'bill_of_lading', 'telex_release', 'other'];
    const labels = { proforma_invoice: 'Proforma Invoice (PI)', invoice: 'Invoice', packing_list: 'Packing List', commercial_invoice: 'Commercial Invoice', bill_of_lading: 'Bill of Lading', telex_release: 'Telex Release', other: 'Other' };
    return `${poSectionTitle('Documents')}
      ${docs.length ? `<table class="ag-table" style="margin-bottom:12px;"><tbody>${docs.map(d => `<tr>
        <td><span class="ag-pill mute">${esc(labels[d.doc_type] || d.doc_type)}</span></td>
        <td><a href="${esc(d.drive_url)}" target="_blank" style="color:var(--ag-info)">${esc(d.file_name)}</a></td>
        <td><button class="ag-btn secondary" onclick="AGInv.deleteDoc('${esc(d.drive_file_id)}','${esc(po.header.po_number)}')">×</button></td>
      </tr>`).join('')}</tbody></table>` : ''}
      <div class="ag-toolbar">
        <select class="ag-select" id="doc-type" style="width:auto;">${types.map(t => `<option value="${esc(t)}">${esc(labels[t] || t)}</option>`).join('')}</select>
        <input type="file" id="doc-file">
        <button class="ag-btn" id="doc-upload-btn" onclick="AGInv.uploadDoc('${esc(po.header.po_number)}')">Upload</button>
      </div>`;
  }
  /* Receipt ids (B629, Codex audit A03). Every Receive sends a receipt_key. The SAME quantities on
     the same PO reuse their key for as long as this page is open, so a retry after a timeout or a
     lost answer, or a second click, comes back "already received" and moves nothing. The stock
     script also refuses an identical receipt made in the last 15 minutes under any other key (a
     colleague on another screen), so a real second delivery of the same quantities is always an
     explicit OK, which sends a fresh key with confirm_repeat. */
  const receiptKeys = {};
  function newReceiptKey() {
    try { if (window.crypto && typeof crypto.randomUUID === 'function') return 'ukpage-' + crypto.randomUUID(); } catch (_) {}
    return 'ukpage-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }
  function receiptSig(poNumber, receipts) {
    const bySku = {};
    receipts.forEach(r => { bySku[r.sku] = (bySku[r.sku] || 0) + r.qty; });
    return poNumber + '|' + Object.keys(bySku).sort().map(k => k + '=' + bySku[k]).join('|');
  }
  const SECOND_DELIVERY_ASK = '\n\nOK = this is a SECOND delivery of the same quantities, receive it.\nCancel = it is the same delivery, change nothing.';
  async function receive(poNumber) {
    const receipts = $$('.recv-qty').map(i => ({ sku: i.dataset.sku, qty: parseInt(i.value, 10) || 0 })).filter(r => r.qty > 0);
    if (!receipts.length) { AG_ADMIN.toast('Enter a quantity on at least one line', true); return; }
    if (!await ensurePoSaved('Receiving')) return;
    const units = receipts.reduce((a, r) => a + r.qty, 0);
    const partial = $$('.recv-qty').some(i => (parseInt(i.value, 10) || 0) < (parseInt(i.getAttribute('max'), 10) || 0));
    if (!confirm(`Receive ${units} unit(s) across ${receipts.length} line(s) and push to Shopify?` +
      (partial ? '\n\nThis is a PARTIAL receipt; whatever you left off stays outstanding.'
               : '\n\nEverything outstanding will be received. Fully received lines flip linked products out of pre-order to in-stock.'))) return;
    const sig = receiptSig(poNumber, receipts);
    const call = (extra) => AG_ADMIN.api('ui_receive_po', Object.assign({ po_number: poNumber, receipts, receipt_key: receiptKeys[sig] }, extra || {}));
    const asNewDelivery = () => { receiptKeys[sig] = newReceiptKey(); return call({ confirm_repeat: true }); };
    if (!receiptKeys[sig]) receiptKeys[sig] = newReceiptKey();
    try {
      let res;
      try {
        res = await call();
      } catch (e) {
        /* The same quantities were received minutes ago under another key: say so in plain words
           and ask, rather than showing the script's API wording. */
        const msg = String((e && e.message) || e);
        if (!/confirm_repeat/.test(msg)) throw e;
        if (!confirm(msg.replace(/\s*If it is a second delivery[^.]*\.\s*$/, '') + SECOND_DELIVERY_ASK)) return;
        res = await asNewDelivery();
      }
      if (res && res.replayed) {
        /* This page already received exactly this. Nothing moved; a line the first receipt could not
           confirm in Shopify is named so it is checked there before anything is received again. */
        const unconfirmed = (res.skipped || []).length;
        const again = confirm('Already received as receipt ' + res.receipt_id + ', so nothing was changed.' +
          (unconfirmed ? '\n\nNot confirmed in Shopify, check there first: ' + res.skipped.join(', ') + '.' : '') + SECOND_DELIVERY_ASK);
        if (!again) { await editPo(poNumber); return; }
        res = await asNewDelivery();
      }
      AG_ADMIN.toast('Received ' + receipts.length + ' line(s)' + (res.po_fully_received ? '. PO complete' : ''));
      delete state.cache.today; delete state.cache.reorder; saveCache();
      await editPo(poNumber);
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  async function uploadDoc(poNumber) {
    const t = $('#doc-type').value;
    const f = $('#doc-file').files[0];
    if (!f) { AG_ADMIN.toast('Pick a file', true); return; }
    if (f.size > 35 * 1024 * 1024) { AG_ADMIN.toast('Too large (35MB max)', true); return; }
    /* Loading state. Without it a slow upload looked like a dead button: no
       feedback while the file transfers, and the 3s error toast was easy to
       miss (the 2026-08-06 EU "uploads don't work" report; same UI here). */
    const btn = $('#doc-upload-btn');
    const idleLabel = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Uploading…'; }
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => { const s = String(r.result); res(s.slice(s.indexOf(',') + 1)); }; r.onerror = rej; r.readAsDataURL(f);
      });
      await AG_ADMIN.api('ui_upload_po_doc', { po_number: poNumber, doc_type: t, filename: f.name, mimeType: f.type, base64 });
      AG_ADMIN.toast('Uploaded'); await editPo(poNumber);
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    finally { if (btn && document.contains(btn)) { btn.disabled = false; btn.textContent = idleLabel; } }
  }
  async function deleteDoc(fileId, poNumber) {
    if (!confirm('Delete this document?')) return;
    try { await AG_ADMIN.api('ui_delete_po_doc', { drive_file_id: fileId }); await editPo(poNumber); }
    catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  /* Drag-and-drop an invoice straight onto the PO list's Invoice column.
     Uploads to Drive as doc_type 'invoice' (same backend as the drawer), which
     also lights the column via invoice_url. Latest dropped file wins. */
  async function dropInvoice(ev, poNumber) {
    ev.preventDefault(); ev.stopPropagation();
    const cell = ev.currentTarget;
    if (cell) cell.style.outline = '';
    const f = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0];
    if (!f || !poNumber) return;
    if (f.size > 35 * 1024 * 1024) { AG_ADMIN.toast('Invoice too large (35MB max)', true); return; }
    const prev = cell ? cell.innerHTML : '';
    if (cell) cell.innerHTML = '<span class="ag-muted" style="font-size:11px;">⏳ uploading…</span>';
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => { const s = String(r.result); res(s.slice(s.indexOf(',') + 1)); };
        r.onerror = rej; r.readAsDataURL(f);
      });
      const res = await AG_ADMIN.api('ui_upload_po_doc', { po_number: poNumber, doc_type: 'invoice', filename: f.name, mimeType: f.type, base64 });
      const url = (res && res.drive_url) || '';
      if (cell) cell.innerHTML = url
        ? `<a href="${esc(url)}" target="_blank" rel="noopener" onclick="event.stopPropagation();" title="Open invoice, drag a new file to replace">📄 link</a>`
        : '📄';
      if (state.posByNumber && state.posByNumber[poNumber]) state.posByNumber[poNumber].invoice_url = url;
      delete state.cache.pos; saveCache();
      AG_ADMIN.toast('Invoice uploaded');
    } catch (e) {
      if (cell) cell.innerHTML = prev;
      AG_ADMIN.toast(e.message || e, true);
    }
  }
  /* Optimistically recolour the drawer's pre-order buttons after an action.
     the persisted SKU_Live.is_preorder only updates on the next inventory pull,
     so reflect what the operator just did straight away. */
  function _setPreorderBtnState(live, note) {
    const b = document.getElementById('po-preorder-live-btn');
    if (b) {
      if (live) { b.classList.remove('secondary'); b.style.cssText = 'background:var(--ag-ok,#1f7a37);border-color:var(--ag-ok,#1f7a37);color:#fff;'; b.textContent = '● Pre-orders LIVE'; }
      else { b.classList.add('secondary'); b.style.cssText = ''; b.textContent = 'Set pre-orders LIVE'; }
    }
    const hint = document.getElementById('po-preorder-hint');
    if (hint && note) hint.textContent = note;
    /* Mirror the state onto the fold's summary chip and pop the fold open so
       the change is visible even if the operator collapses the card later. */
    const chip = document.getElementById('po-preorder-chip');
    if (chip) {
      const armed = !live && /armed/i.test(note || '');
      chip.textContent = live ? '● LIVE' : armed ? 'ARMED' : 'OFF';
      chip.style.background = live ? '#e8f5ee' : armed ? '#fef3c7' : '#f0f1f3';
      chip.style.color = live ? '#1f7a3d' : armed ? '#92400e' : '#8a919b';
      const fold = chip.closest('details');
      if (fold && (live || armed)) fold.open = true;
    }
  }
  async function syncTags(poNumber) {
    if (!confirm('Set pre-orders live for this PO?\n\n• Bundle PO → arms the pre-order; it goes live automatically when the product stock hits 0.\n• Simple product → pushes the pre-order tag now.')) return;
    try {
      const r = await AG_ADMIN.api('ui_sync_preorder_tags', { po_number: poNumber });
      if (r.bundle) { AG_ADMIN.toast(r.note || `Armed ${r.products_updated || 0} bundle(s)`); _setPreorderBtnState(false, 'armed · opens at stock 0'); return; }
      const capped = (r.skipped_capped || []).length;
      AG_ADMIN.toast(r.products_updated ? `Synced "${r.label}" to ${r.products_updated} product(s)${capped ? ` · ${capped} left capped` : ''}` : 'Nothing to sync (no linked SKUs)');
      if (r.products_updated) _setPreorderBtnState(true, 'live · just synced');
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }
  async function pausePreorderTags(poNumber) {
    if (!confirm('Pause pre-orders for this PO?\n\nBundle → disarms it (won\'t go live). Simple product → strips the pre-order tag. Reversible via Set pre-orders live.')) return;
    try {
      const r = await AG_ADMIN.api('ui_pause_preorder_tags', { po_number: poNumber });
      if (r.bundle) { AG_ADMIN.toast(r.note || `Disarmed ${r.products_updated || 0} bundle(s)`); _setPreorderBtnState(false, 'disarmed'); return; }
      AG_ADMIN.toast(r.products_updated ? `Paused on ${r.products_updated} product(s)` : 'Nothing to pause');
      _setPreorderBtnState(false, 'paused · just now');
    } catch (e) { AG_ADMIN.toast(e.message || e, true); }
  }

  /* ─── Full-page SKU view (hash-routed: #sku/<sku>) ────────── */
  /* The drawer is great for quick actions. For deep dives we open the
     same SKU as a full-width page. Same payload (ui_sku_detail), but
     more layout: bigger chart, recent-sales feed, linked-PO summary,
     wider settings form. URL is shareable / bookmarkable. */
  function parseSkuHash() {
    const h = String(location.hash || '');
    const m = /^#sku\/(.+)$/.exec(h);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function openSkuPage(sku) {
    location.hash = 'sku/' + encodeURIComponent(sku);
  }
  function handleHashRoute() {
    const sku = parseSkuHash();
    if (sku) {
      closeDrawer();
      renderSkuPage(sku);
    } else if (state.skuPageSku) {
      /* Leaving SKU page. Restore the last tab. */
      state.skuPageSku = null;
      const root = $('#inv-content'); root.innerHTML = '';
      const tabsEl = $('#inv-tabs'); if (tabsEl) tabsEl.style.display = '';
      setTab(state.tab || state.settings.default_tab);
    }
  }
  async function renderSkuPage(sku) {
    state.skuPageSku = sku;
    /* Hide tab nav so the SKU page reads as its own surface. */
    const tabsEl = $('#inv-tabs'); if (tabsEl) tabsEl.style.display = 'none';
    const root = $('#inv-content');
    root.innerHTML = '<div class="ag-empty"><span class="ag-spinner"></span> Loading…</div>';
    try {
      const d = await AG_ADMIN.api('ui_sku_detail', { sku });
      paintSkuPage(d);
    } catch (e) {
      root.innerHTML = `<div class="ag-empty">Error loading ${esc(sku)}: ${esc(e.message || e)}</div>`;
    }
  }
  function paintSkuPage(d) {
    const live = d.live || {};
    const s = d.settings || {};
    const e = d.engine || {};
    const bis = d.bis || { signups: 0, variants: [], last_signup: '' };
    const sales = d.sales || [];
    const totalSales90 = sales.reduce((a, p) => a + (p.value || 0), 0);
    const lastSale = (function() {
      for (let i = sales.length - 1; i >= 0; i--) if (sales[i].value > 0) return sales[i].date;
      return null;
    })();
    const root = $('#inv-content');
    root.innerHTML = `
      <div class="ag-toolbar" style="margin-bottom:16px;">
        <a class="ag-btn secondary" href="#" onclick="event.preventDefault();AGInv.backToDashboard();">← Back to dashboard</a>
        <span class="spacer" style="flex:1;"></span>
        <span class="ag-muted" style="font-size:11px;">Updated ${esc(fmtFriendly(d.cached_at || ''))}</span>
      </div>
      <div class="ag-prod" style="margin-bottom:16px;">
        <span class="name" style="font-size:22px;line-height:1.2;">${esc(live.product_title || d.sku)}</span>
        ${live.variant_title ? `<span class="variant" style="font-size:15px;">${esc(live.variant_title)}</span>` : ''}
        <span class="sku" style="font-size:12px;">${esc(d.sku)}</span>
      </div>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(160px,1fr));margin-bottom:24px;">
        ${statCard('On hand', live.on_hand_available || 0, (live.on_hand_available || 0) <= 0 ? 'bad' : '')}
        ${statCard('Status', e.status || '-', '')}
        ${statCard('Daily velocity', fmtNum(e.daily_velocity, 3), '')}
        ${statCard('In-stock velocity', fmtNum(e.daily_velocity_in_stock, 3), '')}
        ${statCard('Annual units', e.annual_units_sold || 0, '')}
        ${statCard('Days cover', e.days_cover || '-', '')}
        ${statCard('Sales (90d)', totalSales90, '')}
        ${statCard('Last sale', lastSale ? fmtFriendly(lastSale) : '-', lastSale ? '' : 'warn')}
      </div>

      <div class="ag-chart-row">
        <div class="ag-chart" style="height:340px;">
          <div class="label">90-day stock, sales &amp; open backlog</div>
          <canvas id="ch-skupg-trend"></canvas>
        </div>
      </div>

      <div class="ag-chart-row" style="grid-template-columns:1fr 1fr;">
        ${bis.signups || (bis.variants && bis.variants.length) ? `
        <div class="ag-alert">
          <h3>Back-in-stock signups<span class="count">${bis.signups || 0}</span></h3>
          <div class="ag-bis-rows">
            ${(bis.variants && bis.variants.length ? bis.variants : [{ variant_title: live.variant_title || '', signups: bis.signups, last_signup: bis.last_signup }])
              .map(v => `<div class="ag-bis-row">
                <span>${esc(v.variant_title || '(default)')}</span>
                <span><span class="qty">${v.signups || 0}</span> <span class="when">· last ${esc(fmtFriendly(v.last_signup))}</span></span>
              </div>`).join('')}
          </div>
        </div>` : '<div></div>'}
        <div class="ag-alert">
          <h3>Engine summary</h3>
          <div class="ag-bis-rows">
            <div class="ag-bis-row"><span>Effective on hand</span><span class="qty">${e.effective_on_hand != null ? e.effective_on_hand : '-'}</span></div>
            <div class="ag-bis-row"><span>Open backlog</span><span class="qty">${e.preorder_committed || 0}</span></div>
            <div class="ag-bis-row"><span>On order</span><span class="qty">${e.on_order_qty || 0}</span></div>
            <div class="ag-bis-row"><span>Next ETA</span><span class="when">${esc(fmtFriendly(e.next_eta))}</span></div>
            <div class="ag-bis-row"><span>Run-out projection</span><span class="when">${esc(fmtFriendly(e.run_out_date))}</span></div>
            <div class="ag-bis-row"><span>Reorder by</span><span class="when">${esc(fmtFriendly(e.reorder_by_date))}</span></div>
            <div class="ag-bis-row"><span>Suggested order qty</span><span class="qty">${e.suggested_order_units || 0}${e.batches_to_order ? ` <span class="when">(${e.batches_to_order} batch)</span>` : ''}</span></div>
          </div>
        </div>
      </div>

      <h3 style="margin-top:24px;">Recent sales (last 30 days)</h3>
      <table class="ag-table" style="margin-bottom:24px;"><thead><tr>
        <th>Date</th><th class="num">Units</th>
      </tr></thead><tbody>
        ${sales.filter(p => p.value > 0).slice(-30).reverse().map(p => `
          <tr><td>${esc(fmtFriendly(p.date))}</td><td class="num">${p.value}</td></tr>
        `).join('') || '<tr><td colspan="2" class="ag-muted">No sales in this window.</td></tr>'}
      </tbody></table>

      <h3>Settings</h3>
      <form id="sku-pg-settings-form" class="ag-form-grid" style="grid-template-columns:1fr 1fr;">
        ${fieldRow('supplier', 'Supplier', s.supplier)}
        ${fieldRow('lead_time_days', 'Lead time (days)', s.lead_time_days, 'number')}
        ${fieldRow('batch_size', 'Batch size', s.batch_size, 'number')}
        ${fieldRow('moq', 'MOQ', s.moq, 'number')}
        ${fieldRow('safety_days', 'Safety stock days', s.safety_days, 'number')}
        ${fieldRow('unit_cost', 'Unit cost (£, ex-VAT)', s.unit_cost, 'number')}
        ${fieldRow('shipping_cost_per_unit', 'Shipping/unit (£, ex-VAT)', s.shipping_cost_per_unit, 'number')}
        ${fieldRow('max_preorder_qty', 'Max pre-order qty', s.max_preorder_qty, 'number')}
        ${fieldRow('linked_po_number', 'Linked PO', s.linked_po_number)}
        ${selectRow('lifecycle_status', 'Lifecycle', s.lifecycle_status, ['', 'Discontinued', 'On Hold'])}
        ${fieldRow('active', 'Active (TRUE/FALSE)', s.active === true ? 'TRUE' : s.active === false ? 'FALSE' : '')}
        <label style="grid-column:span 2;"><span class="label">Notes</span>
          <textarea class="ag-textarea" name="notes" rows="3">${esc(s.notes || '')}</textarea></label>
        <div style="grid-column:span 2;display:flex;gap:8px;">
          <button type="submit" class="ag-btn">Save settings</button>
          <a class="ag-btn secondary" href="#" onclick="event.preventDefault();AGInv.backToDashboard();">Cancel</a>
        </div>
      </form>
    `;
    chartSkuTrend('ch-skupg-trend', d.sales || [], d.snapshots || [], d.backlog || []);
    if (!isSectionAllowed('ops')) { lockSkuSettingsForm('#sku-pg-settings-form'); return; }
    $('#sku-pg-settings-form').addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const fields = {};
      $$('#sku-pg-settings-form input, #sku-pg-settings-form select, #sku-pg-settings-form textarea').forEach(el => {
        const name = el.name; if (!name) return;
        let v = el.value;
        if (name === 'active') v = (String(v).toUpperCase() === 'TRUE');
        fields[name] = v;
      });
      try {
        await AG_ADMIN.api('ui_save_sku_settings', { sku: d.sku, fields });
        AG_ADMIN.toast('Saved');
        delete state.cache.today; delete state.cache.reorder;
        renderSkuPage(d.sku); // re-pull so engine/sales freshen
      } catch (e) { AG_ADMIN.toast(e.message || e, true); }
    });
  }
  function backToDashboard() {
    /* Clearing the hash triggers handleHashRoute which restores the
       last-active dashboard tab. */
    if (location.hash) location.hash = '';
    else handleHashRoute();
  }

  /* ─── Chart helpers ──────────────────────────────────────── */
  /* Lazy-load Chart.js on first chart render. It's no longer in the page
     so tabs that never show a chart skip the ~70KB download/parse. Queues
     concurrent callers and runs them once the library is ready. */
  var _chartLoading = false, _chartQueue = [];
  function ensureChartJs(cb) {
    if (typeof Chart !== 'undefined') { if (cb) cb(); return; }
    if (cb) _chartQueue.push(cb);
    if (_chartLoading) return;
    _chartLoading = true;
    var s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.7/dist/chart.umd.js';
    s.onload = function () { _chartQueue.splice(0).forEach(function (f) { try { f(); } catch (e) {} }); };
    s.onerror = function () { _chartLoading = false; };
    document.head.appendChild(s);
  }
  /* Debounce. Used to stop search inputs re-rendering the whole table on
     every keystroke. */
  function debounce(fn, ms) {
    var t;
    return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms || 180); };
  }
  /* Progressive row rendering. Paints an initial chunk then appends the next
     as a sentinel <tr> scrolls into view (IntersectionObserver). Removes the
     row cap with a fast first paint, copes with variable row heights, and
     only ever APPENDS rows. So an open inline editor survives a scroll.
     Re-call to repaint (it disconnects the previous observer first). */
  function progressiveRows(tbody, rows, renderRow, opts) {
    opts = opts || {};
    var CHUNK = opts.chunk || 150;
    if (tbody._vObs) { tbody._vObs.disconnect(); tbody._vObs = null; }
    var sentinel = null, n = 0;
    function appendChunk() {
      if (sentinel) { obs.unobserve(sentinel); sentinel.remove(); sentinel = null; }
      var slice = rows.slice(n, n + CHUNK), html = '';
      for (var i = 0; i < slice.length; i++) html += renderRow(slice[i], n + i);
      tbody.insertAdjacentHTML('beforeend', html);
      n += slice.length;
      if (n < rows.length) {
        sentinel = document.createElement('tr');
        sentinel.className = 'ag-vsentinel';
        sentinel.innerHTML = '<td style="padding:0;border:0;height:0;"></td>';
        tbody.appendChild(sentinel);
        obs.observe(sentinel);
      }
    }
    var obs = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) appendChunk();
    }, { rootMargin: '800px' });
    tbody._vObs = obs;
    tbody.innerHTML = '';
    appendChunk();
  }
  /* Compact x-axis label: "7 May" for current year, "7 May '25" otherwise. */
  function fmtChartDate(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
    if (!m) return ymd || '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const d = parseInt(m[3], 10), mo = months[parseInt(m[2], 10) - 1];
    const yr = parseInt(m[1], 10), nowYr = new Date().getFullYear();
    return yr === nowYr ? `${d} ${mo}` : `${d} ${mo} '${String(yr).slice(2)}`;
  }
  /* Resolve a CSS var off the body. Keeps chart colours theme-aware
     (light vs dark) without hardcoding. */
  function themeVar(name, fallback) {
    const v = getComputedStyle(document.body).getPropertyValue(name).trim();
    return v || fallback;
  }
  function chartTickColor() { return themeVar('--ag-text-dim', '#5e6671'); }
  function chartTextColor() { return themeVar('--ag-text', '#1a1d21'); }
  function chartGridColor() { return themeVar('--ag-border', '#d9dee5'); }
  function chartLine(elId, series, label, opts) {
    opts = opts || {};
    if (typeof Chart === 'undefined') { ensureChartJs(function () { chartLine(elId, series, label, opts); }); return; }
    const ctx = document.getElementById(elId); if (!ctx) return;
    if (ctx._chart) ctx._chart.destroy();
    const tick = chartTickColor(), grid = chartGridColor();
    const rawDates = (series || []).map(p => p.date);
    const vals = (series || []).map(p => p.value);
    const fmtVal = v => opts.decimals !== undefined ? Number(v).toFixed(opts.decimals) : fmtNum(v);
    const datasets = [{
      label, data: vals,
      borderColor: '#c8102e', backgroundColor: 'rgba(200, 16, 46,0.1)',
      tension: 0.2, pointRadius: 0, pointHoverRadius: 4, borderWidth: 2, fill: true
    }];
    /* Optional dashed average line. Used for the 90-day FX chart so
       the operator can see how today's rate sits relative to the mean. */
    if (opts.meanLine && vals.length) {
      const avg = vals.reduce((a, v) => a + (parseFloat(v) || 0), 0) / vals.length;
      datasets.push({
        label: 'Average', data: vals.map(() => avg),
        borderColor: 'rgba(94, 102, 113, 0.7)', borderDash: [5, 4],
        pointRadius: 0, pointHoverRadius: 0, borderWidth: 1.5, fill: false
      });
    }
    ctx._chart = new Chart(ctx, {
      type: 'line',
      data: { labels: (series || []).map(p => (opts.xFmt ? opts.xFmt(p.date) : fmtChartDate(p.date))), datasets },
      options: { responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: !!opts.meanLine, labels: { color: chartTextColor() } },
          tooltip: {
            mode: 'index', intersect: false,
            callbacks: {
              title: items => items.length ? (opts.titleFmt ? opts.titleFmt(rawDates[items[0].dataIndex]) : fmtFriendly(rawDates[items[0].dataIndex])) : '',
              label: item => (item.dataset.label || label) + ': ' + fmtVal(item.parsed.y)
            }
          }
        },
        scales: {
          x: { ticks: { color: tick, maxTicksLimit: 8, autoSkip: true, maxRotation: 0 }, grid: { color: grid } },
          y: { beginAtZero: !!opts.beginAtZero, ticks: { color: tick, callback: v => fmtVal(v) }, grid: { color: grid } }
        } }
    });
  }
  function chartGateway(elId, gateways) {
    if (typeof Chart === 'undefined') { ensureChartJs(function () { chartGateway(elId, gateways); }); return; }
    const ctx = document.getElementById(elId); if (!ctx) return;
    if (ctx._chart) ctx._chart.destroy();
    const palette = ['#c8102e', '#3b82f6', '#2d8f47', '#d18b1a', '#a855f7', '#8a8a8a'];
    const total = gateways.reduce((a, g) => a + (g.gross_revenue || 0), 0);
    ctx._chart = new Chart(ctx, {
      type: 'doughnut',
      data: { labels: gateways.map(g => g.gateway),
        datasets: [{ data: gateways.map(g => g.gross_revenue), backgroundColor: gateways.map((_, i) => palette[i % palette.length]) }] },
      options: { responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'right', labels: { color: chartTextColor() } },
          tooltip: {
            callbacks: {
              label: item => {
                const v = item.parsed || 0;
                const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
                return ` ${item.label}: £${fmtNum(v)} (${pct}%)`;
              }
            }
          }
        } }
    });
  }
  function chartSkuTrend(elId, sales, snapshots, backlog) {
    if (typeof Chart === 'undefined') { ensureChartJs(function () { chartSkuTrend(elId, sales, snapshots, backlog); }); return; }
    const ctx = document.getElementById(elId); if (!ctx) return;
    if (ctx._chart) ctx._chart.destroy();
    /* All three series share the same date axis. Sales is the longest
       (90 daily points); stock + backlog may be shorter (history starts
       when daily snapshots began). Build a unified date index off sales
       and look up each other series by date. */
    const rawDates = sales.map(p => p.date);
    const dates = sales.map(p => fmtChartDate(p.date));
    const snapByDate = {}; (snapshots || []).forEach(p => { snapByDate[p.date] = p.value; });
    const bklByDate  = {}; (backlog || []).forEach(p => { bklByDate[p.date]  = p.value; });
    const stockData   = rawDates.map(d => snapByDate[d] != null ? snapByDate[d] : null);
    const backlogData = rawDates.map(d => bklByDate[d]  != null ? bklByDate[d]  : null);
    const tick = chartTickColor(), grid = chartGridColor(), text = chartTextColor();
    const datasets = [
      { label: 'Sales (units)', data: sales.map(p => p.value), borderColor: '#c8102e', tension: 0.2, pointRadius: 0, pointHoverRadius: 4, borderWidth: 2, yAxisID: 'y' },
      { label: 'Stock',         data: stockData,               borderColor: '#3b82f6', tension: 0.2, pointRadius: 0, pointHoverRadius: 4, borderWidth: 2, yAxisID: 'y1', spanGaps: true }
    ];
    /* Only attach the backlog series when there's actual data, keeps
       the legend tidy for SKUs with no pre-orders / backorders. */
    if (backlogData.some(v => v != null && v > 0)) {
      datasets.push({
        label: 'Open backlog', data: backlogData,
        borderColor: '#d18b1a', backgroundColor: 'rgba(209,139,26,0.12)',
        tension: 0.2, pointRadius: 0, pointHoverRadius: 4, borderWidth: 2, yAxisID: 'y1',
        spanGaps: true, fill: true
      });
    }
    ctx._chart = new Chart(ctx, {
      type: 'line',
      data: { labels: dates, datasets },
      options: { responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { labels: { color: text } },
          tooltip: {
            mode: 'index', intersect: false,
            callbacks: {
              title: items => items.length ? fmtFriendly(rawDates[items[0].dataIndex]) : '',
              label: item => item.dataset.label + ': ' + (item.parsed.y != null ? fmtNum(item.parsed.y) : '-')
            }
          }
        },
        scales: {
          x: { ticks: { color: tick, maxTicksLimit: 8, autoSkip: true, maxRotation: 0 }, grid: { color: grid } },
          y: { position: 'left', ticks: { color: tick }, grid: { color: grid } },
          y1: { position: 'right', ticks: { color: tick }, grid: { drawOnChartArea: false } }
        } }
    });
  }

  /* ─── Helpers ────────────────────────────────────────────── */
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
  function fmtNum(v, decimals) {
    const n = parseFloat(v);
    if (!isFinite(n)) return v == null ? '' : String(v);
    if (decimals !== undefined) return n.toFixed(decimals);
    if (Math.abs(n) >= 1000) return n.toLocaleString('en-GB', { maximumFractionDigits: 0 });
    return Math.round(n * 100) / 100;
  }
  function fmtDate(v) {
    if (!v) return '';
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
    return s;
  }
  /* Human-friendly date: "today", "yesterday", "5 days ago", "5 Jun",
     "5 Jun 2025". Falls back to fmtDate for unparseable input. Pass
     {mode:'past'} to force past-tense phrasing for stockout dates. */
  function fmtFriendly(v) {
    if (!v) return '-';
    const ymd = fmtDate(v);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || String(v);
    const d = new Date(ymd + 'T00:00:00');
    if (isNaN(d.getTime())) return ymd;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - d) / 86400000);
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays === -1) return 'tomorrow';
    if (diffDays > 1 && diffDays <= 7) return diffDays + ' days ago';
    if (diffDays < -1 && diffDays >= -7) return 'in ' + (-diffDays) + ' days';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const mon = months[d.getMonth()];
    const yr = d.getFullYear();
    if (yr === today.getFullYear()) return `${day} ${mon}`;
    return `${day} ${mon} ${yr}`;
  }
  /* "today at 14:32" / "yesterday at 09:15" / "5 Jun at 10:00".
     Useful where the relative-only label hides intra-day runs (the
     daily trigger plus manual pulls happen multiple times a day). */
  function fmtFriendlyDateTime(v) {
    if (!v) return '-';
    const friendly = fmtFriendly(v);
    const d = new Date(v);
    if (isNaN(d.getTime())) return friendly;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${friendly} at ${hh}:${mm}`;
  }
  function fmtCell(v) {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3);
    return fmtDate(v);
  }
  /* Stacked product/variant/SKU cell. Product title is primary; variant
     is a softer second line; SKU shows small + light grey. Hides any
     "[no-sku]" placeholder. Pass `compact: true` for table rows.

     Defensive [no-sku] handling: synthetic Sales_History keys take the
     form "[no-sku]<title>" for AGPB masters that ship without a SKU.
     If we see that in r.sku, recover the title from the suffix when
     no product_title is otherwise available, and don't render the
     synthetic key as a real SKU. */
  function productCell(r, opts) {
    opts = opts || {};
    let rawSku = r.sku ? String(r.sku) : '';
    let derivedTitle = '';
    if (rawSku.indexOf('[no-sku]') === 0) {
      derivedTitle = rawSku.slice(8);
      rawSku = '';
    }
    const title = r.product_title || r.title || derivedTitle || '';
    const variant = r.variant_title || '';
    const sku = rawSku;
    /* Notes badge, small icon next to the product name when the SKU
       has operator notes set. Tooltip shows the note inline so the
       operator doesn't have to open the SKU page. */
    const note = String(r.notes || '').trim();
    const noteBadge = note
      ? `<span class="ag-note-badge" title="${esc(note)}" style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#fef3c7;color:#92400e;font-size:11px;margin-left:6px;cursor:help;vertical-align:middle;" aria-label="Has operator notes">📝</span>`
      : '';
    return `<div class="ag-prod ${opts.compact ? 'compact' : ''}">
      <span class="name" title="${esc(title)}">${esc(title || '-')}${noteBadge}</span>
      ${variant ? `<span class="variant" title="${esc(variant)}">${esc(variant)}</span>` : ''}
      ${sku ? `<span class="sku">${esc(sku)}</span>` : ''}
    </div>`;
  }
  function applyTheme() {
    const t = (state.settings && state.settings.theme) || 'light';
    document.body.classList.toggle('theme-dark', t === 'dark');
    /* Re-apply inline bg so the lock survives a theme switch
       (the CSS-variable value flips, the inline bg needs to follow). */
    try {
      const bg = getComputedStyle(document.body).getPropertyValue('--ag-bg').trim();
      const fg = getComputedStyle(document.body).getPropertyValue('--ag-text').trim();
      if (bg) document.body.style.setProperty('background', bg, 'important');
      if (fg) document.body.style.setProperty('color', fg, 'important');
      if (bg) document.documentElement.style.setProperty('background', bg, 'important');
    } catch (_) {}
  }
  function sumValues(series) {
    return (series || []).reduce((a, p) => a + (p.value || 0), 0);
  }
  function lastValue(series, decimals) {
    if (!series || !series.length) return '-';
    const v = series[series.length - 1].value;
    return fmtNum(v, decimals);
  }

  /* ─── Tab: Info. Onboarding + reference ──────────────────
     Static content. Lives client-side (no backend call) so it loads
     instantly. Edit this function directly to keep documentation in
     sync with code. There's no separate doc to drift out of date. */
  function renderInfo(root) {
    root.innerHTML = `
      <h2 class="ag-h2">Info & onboarding</h2>
      <p class="ag-muted" style="margin-top:0;font-size:13px;max-width:760px;">
        Reference for how the inventory manager works. Read top-to-bottom for new staff onboarding, or jump to a section below.
      </p>
      <div class="ag-grid" style="grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:12px;margin-bottom:24px;">
        ${infoNavCard('what-it-does',  'What this is',         'High-level overview of what the dashboard does and where data comes from.')}
        ${infoNavCard('how-pulls-work','How pulls work',       'When data syncs from Shopify and how to trigger a pull manually.')}
        ${infoNavCard('stock-buckets', 'Stock buckets',        'Owned vs Collective vs Resell vs Tagged vs Untracked: what excludes what.')}
        ${infoNavCard('tags',          'Product tags',         'The tags that affect dashboard behaviour (resell, pre-order, exclude, etc.).')}
        ${infoNavCard('costs',         'Costs & profitability','Where unit costs come from and what the margin numbers mean.')}
        ${infoNavCard('reorder',       'Reorder engine',       'How "days cover" and reorder priority are calculated.')}
        ${infoNavCard('returns',       'Returns & true margin','How refunds feed into net-margin-after-returns on Profitability.')}
        ${infoNavCard('customers',     'Customers & cohorts',  'Per-customer LTV, repeat rate, and cohort retention.')}
        ${infoNavCard('channels',      'Channels & discounts', 'Where revenue comes from and how discount codes are tracked.')}
        ${infoNavCard('bundles',       'Bundles (3 systems)',  'AGPB / Bundles.app / Inferred: what each does and how they connect.')}
        ${infoNavCard('admin',         'Admin tasks',          'Receiving stock, creating POs, marking pre-orders, etc.')}
        ${infoNavCard('troubleshoot',  'Troubleshooting',      'What to do when numbers look wrong or the dashboard is slow.')}
        ${infoNavCard('reference',     'Reference',            'Apps Script project, sheet, repos, keep-warm cadence.')}
      </div>

      ${infoSection('what-it-does', 'What this is', `
        <p>The Inventory Manager is a Shopify-hosted dashboard backed by a Google Apps Script that talks to Shopify's Admin API and a Google Sheet. It surfaces:</p>
        <ul>
          <li><strong>What stock you have</strong>: on-hand units, value, and what's running low</li>
          <li><strong>What's selling</strong>: revenue, units, gross + net margin per SKU/product</li>
          <li><strong>What to reorder</strong>: days-cover engine with action priority</li>
          <li><strong>Who's buying</strong>: customers, channels, discount-code ROI, cohort retention</li>
        </ul>
        <p><strong>Where data lives:</strong> Shopify is the source of truth. The Apps Script syncs daily into a Google Sheet (one row per SKU / customer / order). The dashboard reads from that sheet, not from Shopify directly, so loads are fast and the sheet acts as a queryable archive.</p>
      `)}

      ${infoSection('how-pulls-work', 'How pulls work', `
        <p>Three trigger modes:</p>
        <ul>
          <li><strong>Daily trigger</strong>: runs at ~3am UK. Full incremental refresh of every tab.</li>
          <li><strong>"Pull this tab" button</strong>: refreshes only the data the current tab needs (~5-30s).</li>
          <li><strong>"Pull all" button</strong>: runs the whole pipeline manually (~45s).</li>
        </ul>
        <p><strong>Incremental architecture (2026-06-06):</strong> the four order-walking syncs (customers, channels, discounts, returns) used to re-walk all 365 days every run. Now they read from <code>Orders_Cache</code> (updated by ONE incremental walk with a 24h overlap window) and rebuild aggregates locally in &lt;2s each.</p>
        <p><strong>Keep-warm</strong>: a trigger fires every 5 minutes that pre-populates the dashboard cache. First click on any tab after a 5-min idle should still feel snappy.</p>
      `)}

      ${infoSection('stock-buckets', 'Stock buckets: what counts as owned', `
        <p>The headline <strong>stock value</strong> on the Today tab only includes <em>owned</em> stock. Everything else falls into a separate bucket:</p>
        <table class="ag-table" style="font-size:13px;">
          <thead><tr><th>Bucket</th><th>What goes here</th><th>Counts toward owned value?</th></tr></thead>
          <tbody>
            <tr><td><strong>Owned</strong></td><td>Normal tracked products you physically hold.</td><td><span class="ag-pill ok">YES</span></td></tr>
            <tr><td><strong>Collective</strong></td><td>Tag contains <code>collective</code>, <code>dropship</code>, or vendor is in <code>Config.collective_vendors</code>. Supplier holds the stock.</td><td><span class="ag-pill err">NO</span></td></tr>
            <tr><td><strong>Non-AG vendor</strong></td><td>Shopify <code>vendor</code> isn't in <code>Config.own_brand_vendors</code> (when that allowlist is set). Third-party brand, not own production.</td><td><span class="ag-pill err">NO</span></td></tr>
            <tr><td><strong>Resell</strong></td><td>Product tagged <code>resell</code> exactly. Third-party stocks it, we list & sell it.</td><td><span class="ag-pill err">NO</span></td></tr>
            <tr><td><strong>Tagged</strong></td><td>Tag matches one in <code>Config.exclude_tags</code> (demo, consignment, etc.).</td><td><span class="ag-pill err">NO</span></td></tr>
            <tr><td><strong>Untracked</strong></td><td>Inventory tracking is OFF in Shopify.</td><td><span class="ag-pill err">NO</span></td></tr>
          </tbody>
        </table>
        <p><strong>To exclude a product</strong>: tag it <code>resell</code> (or one of the collective/exclude tags) in Shopify admin. Next inventory pull will pick it up. Removing the tag puts it back into owned.</p>
      `)}

      ${infoSection('tags', 'Product tags the dashboard cares about', `
        <table class="ag-table" style="font-size:13px;">
          <thead><tr><th>Tag</th><th>Effect</th></tr></thead>
          <tbody>
            <tr><td><code>resell</code></td><td>Excludes from owned stock value. Shows in the "resell" bucket.</td></tr>
            <tr><td><code>collective</code>, <code>dropship</code></td><td>Excludes from owned stock value. Shows in "collective" bucket.</td></tr>
            <tr><td><code>pre-order</code> or <code>pre-order - &lt;label&gt;</code></td><td>Flags the product as pre-order. Backlog tracking + pre-order tab.</td></tr>
            <tr><td><code>outlet-only</code></td><td>Search visibility + Outlet badge + sold-as-seen T&Cs.</td></tr>
            <tr><td>Anything in <code>Config.exclude_tags</code></td><td>Excludes from owned stock value. Shows in "tagged" bucket.</td></tr>
          </tbody>
        </table>
      `)}

      ${infoSection('costs', 'Costs & profitability', `
        <p>Each SKU has a unit cost. The system picks the first available:</p>
        <ol>
          <li><strong>Shopify's inventoryItem.unitCost</strong>: used when set. Shopify is the source of truth.</li>
          <li><strong>SKU_Settings.unit_cost</strong> (manual fallback), used only when Shopify has no cost. Populated by the Cost_Gaps_Queue workflow.</li>
          <li><strong>£0</strong>: flagged in the "no-cost SKUs" KPI on the Today tab</li>
        </ol>
        <p><strong>What this means in practice:</strong> if you set a cost in <code>SKU_Settings.unit_cost</code> as a stopgap and later update the same SKU's cost in Shopify admin, Shopify's value automatically takes over on the next pull. No need to clear the SKU_Settings row.</p>
        <p><strong>Shopify cost-field quirk:</strong> roughly half of variants return null from the GraphQL <code>unitCost</code> field on AG UK. We fill those via a REST fallback. For the rest, the canonical workaround is the Shopify CSV cost backfill (see Admin tasks).</p>
        <p><strong>Profitability tab columns:</strong></p>
        <ul>
          <li><strong>Gross margin</strong> = (revenue − COGS − payment fees) / revenue. Doesn't account for returns.</li>
          <li><strong>Net margin (after returns)</strong> = gross − refunded amount − (returned units × cost). This is the truthful margin.</li>
          <li><strong>Return rate</strong> = returned units / ordered units over 365d.</li>
        </ul>
      `)}

      ${infoSection('reorder', 'Reorder engine', `
        <p>The Reorder tab shows what to buy and when. Key columns:</p>
        <ul>
          <li><strong>Days cover</strong>: how many days of stock you have at current velocity. <em>Calendar days when the SKU has had recent stock; uses in-stock days when stock-out history is long</em>.</li>
          <li><strong>Velocity</strong>: units sold per day, smoothed over the last 30-90 days.</li>
          <li><strong>Run-out date</strong>: when you'll hit zero at current velocity.</li>
          <li><strong>Action priority</strong>: combines days-cover, lead-time, and current open backlog.</li>
        </ul>
        <p>The compact / expert view toggle switches between operator-friendly columns and full detail. <strong>Top-N filter</strong> caps the list to bestsellers, useful when you have hundreds of SKUs and only the top movers matter.</p>
      `)}

      ${infoSection('returns', 'Returns & true margin', `
        <p>Refund data syncs daily via the Shopify orders.refunds GraphQL traversal. For each SKU we track:</p>
        <ul>
          <li>Returned quantity (365d + 30d windows)</li>
          <li>Returned amount (ex-VAT, in GBP)</li>
          <li>Return rate as a % of units sold</li>
        </ul>
        <p>The Profitability tab joins this into the per-SKU table so you can see which SKUs are losing margin to returns vs which are clean sellers.</p>
      `)}

      ${infoSection('customers', 'Customers & cohort retention', `
        <p>The Customers tab shows per-customer LTV over 365 days. <strong>Hero stats</strong> (repeat rate, total customers, avg LTV) come from the full customer base. The <strong>table is capped to the top 500 spenders</strong> for performance. The underlying data has all customers, but rendering 5,000 rows in the browser is slow.</p>
        <p><strong>Cohort retention</strong> (below the customer table) groups customers by first-purchase month and shows what % came back each subsequent month. m0 is always 100% by definition. Read columns vertically to compare same-elapsed-month retention across cohorts, newer cohorts higher than older = retention is improving.</p>
      `)}

      ${infoSection('channels', 'Channels & discounts', `
        <p><strong>Channels tab</strong> splits revenue by Shopify sales channel, Online Store, POS, draft orders, marketplace apps. Resolves numeric source IDs (like "580111") to friendly names.</p>
        <p><strong>Discount Codes tab</strong> shows ROI per code (gross revenue / discount given). Rules of thumb for ROI:</p>
        <ul>
          <li><span class="ag-pill ok">10×+</span> healthy. Keep running</li>
          <li><span class="ag-pill warn">2–5×</span> marginal, review whether it's pulling new customers or just discounting existing ones</li>
          <li><span class="ag-pill err">&lt;2×</span> eating margin, sunset</li>
        </ul>
        <p><strong>Caveat:</strong> ROI doesn't measure incrementality (would the customer have bought without the code?). Use as a relative indicator across codes, not absolute profitability.</p>
      `)}

      ${infoSection('bundles', 'Bundles: three systems', `
        <p>There are three different bundle systems at play. Each lives in a different place. Knowing which is which matters when stock numbers look off.</p>
        <h4 style="margin-bottom:4px;">1. AGPB (AG Product Builder)</h4>
        <p><strong>What it is:</strong> our custom-built product configurator on PDPs like Black/Chrome/Core Edition. Customer picks options; AGPB adds a parent line item + component lines to the cart at order time.</p>
        <p><strong>How we detect it:</strong> order line items carry <code>_bundle_id</code> + <code>_builder === 'true'</code> custom attributes (or <code>_bundle_id</code> starts with <code>agpb-</code>).</p>
        <p><strong>Where the bundle definition lives:</strong> per-order. There's no central definition. The cart records the components customers actually picked.</p>
        <p><strong>Where stock comes from:</strong> the real component SKUs (e.g. SEC415BB, SEC415SB). Parent is virtual.</p>
        <p><strong>What gets recorded:</strong> one row per bundle realisation in the <code>Bundles_Log</code> sheet tab, order ID, parent SKU, components list, revenue. Feeds <code>Profitability_Finished</code>.</p>

        <h4 style="margin-bottom:4px;">2. Bundles.app (Tepo's Shopify Bundles app)</h4>
        <p><strong>What it is:</strong> third-party Shopify app. Bundle definitions are stored on each bundle variant as a metafield (<code>bundles_app:content</code>). When customers buy the bundle, Bundles.app deducts component stock automatically.</p>
        <p><strong>How we detect it:</strong> variant has the <code>bundles_app:content</code> metafield set with a parseable JSON value.</p>
        <p><strong>Where the bundle definition lives:</strong> product-level, deterministic, set once in the Bundles.app dashboard.</p>
        <p><strong>Where stock comes from:</strong> Bundles.app keeps the parent's "available" stock synchronised based on component availability. Component SKUs hold the actual physical stock.</p>
        <p><strong>What gets recorded:</strong> each inventory pull parses every Bundles.app metafield and writes rows to the <code>BOM</code> tab with <code>notes='bundles_app'</code>. Manual / inferred BOM rows are preserved; only the bundles_app-sourced rows get refreshed each pull.</p>

        <h4 style="margin-bottom:4px;">3. Inferred (legacy fallback)</h4>
        <p><strong>What it is:</strong> for products that aren't built via AGPB AND aren't set up in Bundles.app, we infer the bundle from order history.</p>
        <p><strong>How we detect it:</strong> <code>inferBomFromOrders</code> scans 365 days of orders. When an order has exactly one master line (no SKU, has revenue) and multiple £0 component lines, those £0 lines ARE the master's stock decomposition.</p>
        <p><strong>Where the bundle definition lives:</strong> the <code>Suggested_BOM</code> tab. Confidence score reflects how consistent the inference is across orders. You review, tick the approve column, run <code>applyApprovedBomSuggestions</code>, approved rows move into the canonical <code>BOM</code> tab.</p>

        <h4 style="margin-bottom:4px;">4. Bundle Builder app (marketing upsell, IGNORED)</h4>
        <p>"Bundle - Bench + Dumbbell + ..." style carts from a different app. Has <code>_bundle_id</code> on its line items but NO <code>_builder</code> flag. We explicitly skip these because they aren't physical stock decompositions. Just discount wrappers.</p>

        <h4 style="margin-bottom:4px;">How profitability uses all this</h4>
        <p><code>computeProfitability</code> builds <code>Profitability_Finished</code> by joining:</p>
        <ul>
          <li><strong>AGPB bundles</strong>: from <code>Bundles_Log</code> (real per-order data, most accurate)</li>
          <li><strong>Everything else</strong>: from <code>BOM</code> (which now includes Bundles.app rows + manual + approved inferred rows)</li>
        </ul>
        <p>Component costs come from current <code>SKU_Settings.unit_cost</code> (or Shopify cost as fallback). Margins reflect today's cost basis, not historical.</p>
      `)}

      ${infoSection('admin', 'Admin tasks', `
        <h4 style="margin-bottom:4px;">Creating a Purchase Order</h4>
        <p>Purchase Orders tab → <strong>+ New PO</strong>. Add lines (SKU + qty + cost), set ETA, status. Save. When goods arrive, open the PO → <strong>Record Receipt &amp; Push</strong>: this updates Shopify inventory levels directly and writes to Goods_In for history.</p>
        <h4 style="margin-bottom:4px;">Updating costs</h4>
        <p>Easiest: edit <strong>SKU_Settings.unit_cost</strong> in the Google Sheet directly. Saves immediately, reflected on next dashboard refresh.</p>
        <h4 style="margin-bottom:4px;">Bulk CSV cost import</h4>
        <p>When you have many costs to update, export Shopify's inventory CSV, edit the <strong>Cost per item</strong> column, paste into the <code>Cost_Backfill</code> sheet tab, run <code>applyCostBackfill</code> from the Apps Script editor.</p>
        <h4 style="margin-bottom:4px;">Pre-order management</h4>
        <p>Tag a product <code>pre-order - June</code> (or similar dispatch label) in Shopify. The Pre-orders tab tracks open backlog vs caps. <code>max_preorder_qty</code> in SKU_Settings sets the cap.</p>
      `)}

      ${infoSection('troubleshoot', 'Troubleshooting', `
        <h4 style="margin-bottom:4px;">Numbers look stale</h4>
        <p>Click <strong>Pull this tab</strong> at the top right, refreshes only that tab's data source. For a full refresh, <strong>Pull all</strong> (~45s).</p>
        <h4 style="margin-bottom:4px;">Stock value disagrees with Shopify's report</h4>
        <p>Run <code>auditStockValueDeep</code> from the Apps Script editor. Writes a per-(SKU, location) breakdown to Stock_Value_Audit. Common causes: SKU-less variants that Shopify's report can't render, location-filtering mismatch, landed cost vs ex-works.</p>
        <h4 style="margin-bottom:4px;">A tab is slow to first-load</h4>
        <p>The keep-warm trigger pre-populates the cache every 5 minutes. If it's been off (e.g. after a deploy), the first click costs a cold rebuild. Wait 5-10 minutes and retry, or trigger <code>uiKeepWarm</code> manually from the editor.</p>
        <h4 style="margin-bottom:4px;">Stock chart looks like one line going down</h4>
        <p>The backfill formula adjusts for sales AND Goods_In receipts. If your Goods_In tab is empty / incomplete, deliveries don't show as steps UP. Make sure you record receipts via the PO "Record Receipt" button so they land in Goods_In.</p>
      `)}

      ${infoSection('reference', 'Reference', `
        <ul style="line-height:1.8;">
          <li><strong>Dashboard URL</strong>: <code>/pages/admin-inventory</code></li>
          <li><strong>Apps Script project</strong>: <code>AssassinGoods/ag-inventory-script</code> on GitHub, clasp-managed locally at <code>~/Projects/ag-inventory-script/</code></li>
          <li><strong>Google Sheet</strong>: holds all derived data. Sheet ID in Script Properties as <code>SHEET_ID</code>.</li>
          <li><strong>Shopify scopes needed</strong>: <code>read_products</code>, <code>read_inventory</code>, <code>write_inventory</code>, <code>read_orders</code>, <code>read_customers</code>, <code>read_locations</code>, <code>read_reports</code></li>
          <li><strong>Keep-warm cadence</strong>: every 5 minutes via Apps Script time trigger.</li>
          <li><strong>Daily pipeline</strong>: ~3am UK. Logs in the <code>Run_Log</code> tab.</li>
        </ul>
      `)}
    `;
    /* Smooth-scroll nav cards to their sections. */
    root.querySelectorAll('[data-info-target]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.infoTarget;
        const target = root.querySelector('#info-' + id);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }
  function infoNavCard(targetId, title, hint) {
    return `<div class="ag-card" style="padding:12px;cursor:pointer;" data-info-target="${targetId}">
      <div style="font-weight:600;font-size:13px;margin-bottom:4px;">${esc(title)} →</div>
      <div class="ag-muted" style="font-size:11px;">${esc(hint)}</div>
    </div>`;
  }
  function infoSection(id, title, bodyHtml) {
    return `<section id="info-${id}" style="margin-bottom:32px;padding-top:8px;">
      <h3 class="ag-h2" style="font-size:16px;border-bottom:1px solid var(--ag-border, #444);padding-bottom:6px;">${esc(title)}</h3>
      <div style="font-size:13px;line-height:1.6;max-width:840px;">${bodyHtml}</div>
    </section>`;
  }

  /* ─── Public for inline handlers ─────────────────────────── */
  function toggleProfitInsights() {
    state.profitInsightsOpen = !state.profitInsightsOpen;
    const cached = getCached('profit');
    if (cached) paintTab('profit', cached);
  }

  /* CSV export. Turn the current tab's payload into a CSV file and
     trigger a browser download. Per-tab row builders below pull the
     "primary" table out of each payload shape. RFC-4180 quoting:
     wrap any field containing comma / newline / quote in quotes, and
     escape literal quotes by doubling them. */
  function csvEscape(v) {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  }
  function rowsToCsv(headers, rows) {
    const head = headers.map(csvEscape).join(',');
    const body = rows.map(r => headers.map(h => csvEscape(r[h])).join(',')).join('\r\n');
    return head + '\r\n' + body;
  }
  function downloadCsv(filename, csv) {
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  /* Each builder receives the cached payload and returns
     { headers, rows, filename } or null when the tab has nothing
     csv-able. Filenames are dated so multiple exports don't
     overwrite. */
  const CSV_BUILDERS = {
    reorder(p) {
      const items = (p && p.items) || [];
      return {
        filename: 'reorder-engine',
        headers: ['sku', 'product_title', 'variant_title', 'status', 'on_hand',
                  'daily_velocity', 'annual_units_sold', 'days_cover',
                  'run_out_date', 'reorder_by_date', 'suggested_order_units',
                  'supplier', 'bis_signups', 'notes'],
        rows: items
      };
    },
    stock(p) {
      const rows = (p && p.rows) || [];
      return {
        filename: 'stock',
        headers: ['sku', 'product_title', 'variant_title', 'vendor', 'on_hand',
                  'unit_cost', 'cost_source', 'value', 'bucket',
                  'is_preorder', 'notes'],
        rows
      };
    },
    sales(p) {
      const rows = (p && (p.top_units_all || p.top_units)) || [];
      return {
        filename: 'sales-top-units',
        headers: ['sku', 'title', 'units', 'revenue'],
        rows
      };
    },
    profit(p) {
      const rows = (p && p.per_sku) || [];
      return {
        filename: 'profitability-per-sku',
        headers: ['sku', 'product_title', 'units_sold_annual', 'revenue_net_annual',
                  'cost_annual', 'gross_profit_annual', 'gross_margin_pct',
                  'cost_basis', 'note'],
        rows
      };
    },
    pos(p) {
      const rows = (p && (p.rows || p)) || [];
      return {
        filename: 'purchase-orders',
        headers: ['po_number', 'supplier', 'status', 'date_ordered', 'eta',
                  'date_received', 'external_ref', 'lines_count'],
        rows
      };
    },
    preorders(p) {
      const items = (p && p.items) || [];
      return {
        filename: 'pre-orders',
        headers: ['sku', 'product_title', 'variant_title', 'open_qty', 'max',
                  'pct', 'dispatch_label', 'linked_po', 'po_eta', 'po_status'],
        rows: items
      };
    },
    bis(p) {
      const rows = (p && p.rows) || [];
      return {
        filename: 'back-in-stock',
        headers: ['product_title', 'variant_title', 'signups', 'last_signup',
                  'status'],
        rows
      };
    },
    customers(p) {
      const rows = (p && p.rows) || [];
      return {
        filename: 'customers',
        headers: ['customer_id', 'email', 'first_name', 'last_name',
                  'orders_count', 'units_count', 'total_revenue_gbp',
                  'avg_order_value_gbp', 'first_purchase', 'last_purchase',
                  'days_since_last_purchase', 'is_repeat', 'rfm_segment',
                  'r_score', 'f_score', 'm_score'],
        rows
      };
    },
    channels(p) {
      const rows = (p && p.rows) || [];
      return {
        filename: 'channels',
        headers: ['channel_label', 'channel_key', 'orders_count',
                  'cancelled_orders', 'units_count', 'total_revenue_gbp',
                  'revenue_share_pct', 'avg_order_value_gbp', 'refunded_gbp',
                  'refund_rate_pct', 'last_order_at'],
        rows
      };
    },
    discounts(p) {
      const rows = (p && p.rows) || [];
      return {
        filename: 'discounts',
        headers: ['code', 'orders', 'units', 'gross_revenue_gbp',
                  'discount_amount_gbp', 'net_revenue_gbp', 'aov_gbp',
                  'first_use', 'last_use'],
        rows
      };
    }
  };
  function exportTabCsv() {
    const tab = state.tab;
    const payload = getCached(tab);
    if (!payload) {
      AG_ADMIN.toast('Nothing to export. Open the tab first', true);
      return;
    }
    const builder = CSV_BUILDERS[tab];
    if (!builder) {
      AG_ADMIN.toast('CSV export not available on this tab', true);
      return;
    }
    const def = builder(payload);
    if (!def || !def.rows || !def.rows.length) {
      AG_ADMIN.toast('Tab has no rows to export', true);
      return;
    }
    const dateStr = new Date().toISOString().slice(0, 10);
    const filename = `ag-${def.filename}-${dateStr}.csv`;
    downloadCsv(filename, rowsToCsv(def.headers, def.rows));
    AG_ADMIN.toast(`Exported ${def.rows.length} rows`);
  }

  window.AGInv = { setView: setTab, refresh, openSettings, closeSettings, saveSettings: applySettings, addPoPayment, deletePoPayment, syncNewPoPaymentFields,
    closeDrawer, addLine, onSkuChange, onSkuPickerFocus, onSkuPickerInput, onSkuPickerBlur, splitPoLineToComponents, openBomEditor, openShippingConfigModal, newPo, receive, uploadDoc, deleteDoc,
    shiftPoStage, cancelPo, signOffPo, archivePo, deletePo, logLoss, deleteLoss,
    syncTags, pausePreorderTags, manualPull, pullTab, openPoLinesPasteDialog,
    openSkuPage, backToDashboard, openSparklineDrawer, toggleProfitInsights, exportTabCsv,
    pulsePickDay, pulseStepDay, pulseResetDay, pulsePickWeek, pulseStepWeek, pulseResetWeek,
    refreshList, retintPoLine, toggleRecvLock, updatePoUsdRecon, syncGoodsUsdToLines, dropInvoice };

  /* ─── Boot ───────────────────────────────────────────────── */
  AG_ADMIN.onReady(async () => {
    applyTheme();
    state.salesDays = state.settings.default_period || 30;
    renderSectionToggle();
    const lastTab = (() => { try { return localStorage.getItem('agInvLastTab'); } catch (_) { return null; } })();
    let startTab = lastTab && TABS.find(t => t.id === lastTab) ? lastTab : state.settings.default_tab;
    /* Keep the start tab inside this view: last-tab/default_tab is shared
       across admin pages, so a Costs tab could leak onto the Stock page. */
    const startDef = TABS.find(t => t.id === startTab);
    if (!startDef || (startDef.section !== 'common' && !isSectionAllowed(startDef.section))) {
      const ft = tabsForSection(state.section).find(t => t.section !== 'common') || tabsForSection(state.section)[0];
      if (ft) startTab = ft.id;
    }
    /* If the URL points at a SKU page, render that. Otherwise fall through
       to the tabbed dashboard. setTab also primes state.tab so backToDashboard
       can return cleanly. */
    state.tab = startTab;
    const initialSku = parseSkuHash();
    if (initialSku) {
      renderTabs();
      const tabsEl = $('#inv-tabs'); if (tabsEl) tabsEl.style.display = 'none';
      state.skuPageSku = initialSku;
      renderSkuPage(initialSku);
    } else {
      setTab(startTab);
    }
    window.addEventListener('hashchange', handleHashRoute);
    /* Warm bootstrap (suppliers/skus) in background for the PO form. */
    AG_ADMIN.api('ui_bootstrap', {}).then(p => { state.bootstrap = p; setCached('reorder', p); setCached('pos', p); }).catch(() => {});
    /* Real data-age stamp: fetch the last pipeline-run time once, then keep the
       "Nh ago" ticking each minute (cheap; no refetch). */
    loadFreshness();
    setInterval(renderFreshness, 60000);
    /* QW5 focus revalidate: a dashboard left open all day goes stale. When the user
       tabs back, silently revalidate the current tab (routes through the edge/cache,
       fast) + refresh the data-age stamp. Debounced to 30s so rapid tab-flipping
       doesn't spam fetches. */
    let _lastVisRefresh = Date.now();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - _lastVisRefresh < 30000) return;
      _lastVisRefresh = Date.now();
      loadFreshness();
      if (state.tab && !state.skuPageSku) refreshTab(state.tab, true);
    });
  });
})();
/* sync nudge 2026-08-17 15:28. A fresh push event re-kicks a wedged GitHub integration processor; harmless comment, remove any time. */
